// PermitWise - Complete React Web Application
import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import './App.css';

// ===========================================
// CONFIGURATION
// ===========================================
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');
const BASE_URL = process.env.REACT_APP_BASE_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// Helper to get secure file URLs with authentication token
const getSecureFileUrl = (fileUrl) => {
  if (!fileUrl) return '';
  const token = localStorage.getItem('permitwise_token');
  if (!token) return fileUrl;
  
  // If it's already a full URL with the base, extract the path
  const url = fileUrl.includes('/uploads/') ? fileUrl : `${BASE_URL}${fileUrl}`;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${token}`;
};

// Admin-specific secure file URL (uses superadmin token)
const getAdminSecureFileUrl = (fileUrl) => {
  if (!fileUrl) return '';
  const token = localStorage.getItem('superadminToken');
  if (!token) return fileUrl;
  
  const url = fileUrl.includes('/uploads/') ? fileUrl : `${BASE_URL}${fileUrl}`;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${token}`;
};

// ===========================================
// CONTEXT
// ===========================================
const AuthContext = createContext(null);
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Toast Context for notifications
const ToastContext = createContext(null);
const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};

// Toast Provider Component
const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  
  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }
    return id;
  }, []);
  
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  
  const toast = {
    success: (msg, duration) => addToast(msg, 'success', duration),
    error: (msg, duration) => addToast(msg, 'error', duration),
    info: (msg, duration) => addToast(msg, 'info', duration),
    warning: (msg, duration) => addToast(msg, 'warning', duration),
  };
  
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <div className="toast-icon">
              {t.type === 'success' && <Icons.Check />}
              {t.type === 'error' && <Icons.X />}
              {t.type === 'info' && <Icons.Alert />}
              {t.type === 'warning' && <Icons.Alert />}
            </div>
            <span className="toast-message">{t.message}</span>
            <button className="toast-close" onClick={() => removeToast(t.id)}>
              <Icons.X />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Confirm Modal Context
const ConfirmContext = createContext(null);
const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error('useConfirm must be used within ConfirmProvider');
  return context;
};

// Confirm Provider Component
const ConfirmProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'primary', // primary, danger
    onConfirm: null,
    onCancel: null,
  });
  
  const confirm = useCallback(({ title, message, confirmText = 'Confirm', cancelText = 'Cancel', variant = 'primary' }) => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        variant,
        onConfirm: () => {
          setConfirmState(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirmState(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        },
      });
    });
  }, []);
  
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {confirmState.isOpen && (
        <div className="modal-overlay" onClick={confirmState.onCancel}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <h3>{confirmState.title}</h3>
            </div>
            <div className="confirm-modal-body">
              <p>{confirmState.message}</p>
            </div>
            <div className="confirm-modal-footer">
              <button className="btn btn-outline" onClick={confirmState.onCancel}>
                {confirmState.cancelText}
              </button>
              <button 
                className={`btn ${confirmState.variant === 'danger' ? 'btn-danger' : 'btn-primary'}`} 
                onClick={confirmState.onConfirm}
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

// ===========================================
// API HELPER
// ===========================================
const api = {
  async request(endpoint, options = {}) {
    const token = localStorage.getItem('permitwise_token');
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.message || data.error || 'Request failed');
      err.data = data;
      throw err;
    }
    return data;
  },
  get: (endpoint) => api.request(endpoint),
  post: (endpoint, body) => api.request(endpoint, { method: 'POST', body: JSON.stringify(body) }),
  put: (endpoint, body) => api.request(endpoint, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (endpoint, body) => api.request(endpoint, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined }),
  async upload(endpoint, formData) {
    const token = localStorage.getItem('permitwise_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const response = await fetch(`${API_URL}${endpoint}`, { method: 'POST', headers, body: formData });
    const data = await response.json();
    if (!response.ok) {
      const err = new Error(data.message || data.error || 'Upload failed');
      err.data = data;
      throw err;
    }
    return data;
  },
};

// ===========================================
// ICONS (SVG Components)
// ===========================================
const Icons = {
  Dashboard: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1" fill="none"/><rect x="14" y="3" width="7" height="7" rx="1" fill="none"/><rect x="14" y="14" width="7" height="7" rx="1" fill="none"/><rect x="3" y="14" width="7" height="7" rx="1" fill="none"/></svg>,
  Permit: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  Document: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13,2 13,9 20,9"/></svg>,
  Checklist: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  Event: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="none"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  Calendar: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="none"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  Truck: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  Settings: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  Logout: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16,17 21,12 16,7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  Plus: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Search: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Check: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20,6 9,17 4,12"/></svg>,
  X: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Alert: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  Upload: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  Download: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  MapPin: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Menu: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  Clock: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>,
  Shield: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Users: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Trash: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  Edit: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Eye: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  EyeOff: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
  Lock: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" fill="none"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  Bell: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  Info: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
};

// ===========================================
// CONSTANTS
// ===========================================
const VENDOR_TYPES = [
  { value: 'food_truck', label: 'Food Truck' },
  { value: 'food_cart', label: 'Food Cart' },
  { value: 'tent_vendor', label: 'Tent/Booth Vendor' },
  { value: 'mobile_retail', label: 'Mobile Retail' },
  { value: 'farmers_market', label: "Farmer's Market Seller" },
  { value: 'craft_vendor', label: 'Craft Vendor' },
  { value: 'mobile_bartender', label: 'Mobile Bartender' },
  { value: 'mobile_groomer', label: 'Mobile Groomer' },
  { value: 'pop_up_shop', label: 'Pop-Up Shop' },
  { value: 'other', label: 'Other' },
];

// Vendor types that typically handle food (auto-suggest food permits)
const FOOD_VENDOR_TYPES = ['food_truck', 'food_cart', 'mobile_bartender', 'farmers_market'];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// ===========================================
// UTILITY FUNCTIONS
// ===========================================
const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
};

// Format date input as user types - MM/DD/YYYY
const formatDateInput = (value) => {
  if (!value) return '';
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  // Format based on length
  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

// Convert formatted date (MM/DD/YYYY) to ISO string for API
const getDateISO = (formatted) => {
  if (!formatted) return '';
  const digits = formatted.replace(/\D/g, '');
  if (digits.length !== 8) return '';
  const month = parseInt(digits.slice(0, 2), 10);
  const day = parseInt(digits.slice(2, 4), 10);
  const year = parseInt(digits.slice(4, 8), 10);
  // Create date and return ISO string (YYYY-MM-DD)
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

// Convert ISO date (YYYY-MM-DD) to formatted (MM/DD/YYYY) for display in inputs
const isoToFormatted = (isoDate) => {
  if (!isoDate) return '';
  const parts = isoDate.split('T')[0].split('-');
  if (parts.length !== 3) return '';
  return `${parts[1]}/${parts[2]}/${parts[0]}`;
};

// Validate date input
const validateDate = (value, options = {}) => {
  const { required = false, minDate = null, maxDate = null } = options;
  if (!value) {
    if (required) return { valid: false, error: 'Date is required' };
    return { valid: true, error: null };
  }
  const digits = value.replace(/\D/g, '');
  if (digits.length < 8) return { valid: false, error: 'Date is incomplete (MM/DD/YYYY)' };
  if (digits.length > 8) return { valid: false, error: 'Date is too long' };
  
  const month = parseInt(digits.slice(0, 2), 10);
  const day = parseInt(digits.slice(2, 4), 10);
  const year = parseInt(digits.slice(4, 8), 10);
  
  if (month < 1 || month > 12) return { valid: false, error: 'Invalid month (01-12)' };
  if (day < 1 || day > 31) return { valid: false, error: 'Invalid day (01-31)' };
  if (year < 1900 || year > 2100) return { valid: false, error: 'Invalid year' };
  
  // Check if date is valid (e.g., Feb 30 is invalid)
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1) return { valid: false, error: 'Invalid date for this month' };
  
  if (minDate && date < new Date(minDate)) return { valid: false, error: 'Date is too early' };
  if (maxDate && date > new Date(maxDate)) return { valid: false, error: 'Date is too far in the future' };
  
  return { valid: true, error: null };
};

const daysUntil = (date) => {
  if (!date) return null;
  const now = new Date();
  const target = new Date(date);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
};

const getStatusLabel = (status) => {
  const labels = { active: 'Active', expired: 'Expired', pending_renewal: 'Expiring Soon', missing: 'Missing', in_progress: 'In Progress' };
  return labels[status] || status;
};

// Phone number formatting - US format: (###) ###-####
const formatPhoneNumber = (value) => {
  if (!value) return '';
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  // Remove leading 1 if present (country code)
  const phone = digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits;
  // Format based on length
  if (phone.length === 0) return '';
  if (phone.length <= 3) return `(${phone}`;
  if (phone.length <= 6) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
  return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
};

// Extract raw digits from formatted phone (for API)
const getPhoneDigits = (formatted) => {
  if (!formatted) return '';
  const digits = formatted.replace(/\D/g, '');
  // Return with +1 prefix for storage
  return digits.length === 10 ? `+1${digits}` : digits.length === 11 && digits.startsWith('1') ? `+${digits}` : digits;
};

// Validate phone number (must be exactly 10 digits after formatting)
const validatePhone = (value) => {
  if (!value) return { valid: true, error: null }; // Empty is ok (optional field)
  const digits = value.replace(/\D/g, '');
  const phone = digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits;
  if (phone.length < 10) return { valid: false, error: 'Phone number is too short' };
  if (phone.length > 10) return { valid: false, error: 'Phone number is too long' };
  return { valid: true, error: null };
};

// Validate email address
const validateEmail = (email) => {
  if (!email) return { valid: true, error: null }; // Empty is ok (optional field)
  if (!email.includes('@')) return { valid: false, error: 'Email must contain @' };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return { valid: false, error: 'Please enter a valid email address' };
  return { valid: true, error: null };
};

// ===========================================
// COMMON COMPONENTS
// ===========================================
const Button = ({ children, variant = 'primary', size = 'md', loading, disabled, onClick, type = 'button', className = '' }) => (
  <button type={type} className={`btn btn-${variant} btn-${size} ${className}`} onClick={onClick} disabled={disabled || loading}>
    {loading ? <span className="spinner" /> : children}
  </button>
);

const Input = ({ label, error, className = '', ...props }) => (
  <div className="form-group">
    {label && <label className="form-label">{label}</label>}
    <input className={`form-input ${error ? 'error' : ''} ${className}`} {...props} />
    {error && <span className="form-error">{error}</span>}
  </div>
);

// Phone Input with auto-formatting
const PhoneInput = ({ label, value, onChange, error: externalError, required, ...props }) => {
  const [displayValue, setDisplayValue] = useState(formatPhoneNumber(value || ''));
  const [internalError, setInternalError] = useState(null);
  
  useEffect(() => {
    setDisplayValue(formatPhoneNumber(value || ''));
  }, [value]);
  
  const handleChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setDisplayValue(formatted);
    
    // Validate
    const validation = validatePhone(formatted);
    setInternalError(validation.error);
    
    // Pass raw digits to parent for API
    const digits = getPhoneDigits(formatted);
    onChange({ target: { value: digits, name: e.target.name } });
  };
  
  const error = externalError || internalError;
  
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}{required && ' *'}</label>}
      <div className="phone-input-wrapper">
        <span className="phone-prefix">+1</span>
        <input 
          className={`form-input phone-input ${error ? 'error' : ''}`} 
          value={displayValue}
          onChange={handleChange}
          placeholder="(555) 123-4567"
          maxLength={14}
          {...props} 
        />
      </div>
      {error && <span className="form-error">{error}</span>}
    </div>
  );
};

// Email Input with validation
const EmailInput = ({ label, value, onChange, error: externalError, required, onBlur, ...props }) => {
  const [internalError, setInternalError] = useState(null);
  
  const handleBlur = (e) => {
    const validation = validateEmail(e.target.value);
    setInternalError(validation.error);
    if (onBlur) onBlur(e);
  };
  
  const handleChange = (e) => {
    // Clear error on typing
    if (internalError) setInternalError(null);
    onChange(e);
  };
  
  const error = externalError || internalError;
  
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}{required && ' *'}</label>}
      <input 
        type="email"
        className={`form-input ${error ? 'error' : ''}`} 
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="email@example.com"
        {...props} 
      />
      {error && <span className="form-error">{error}</span>}
    </div>
  );
};

// Date Input with auto-formatting and validation (MM/DD/YYYY)
const DateInput = ({ label, value, onChange, error: externalError, required, minDate, maxDate, ...props }) => {
  // value comes in as ISO format (YYYY-MM-DD), display as MM/DD/YYYY
  const [displayValue, setDisplayValue] = useState(isoToFormatted(value || ''));
  const [internalError, setInternalError] = useState(null);
  
  useEffect(() => {
    setDisplayValue(isoToFormatted(value || ''));
  }, [value]);
  
  const handleChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setDisplayValue(formatted);
    
    // Validate
    const validation = validateDate(formatted, { required, minDate, maxDate });
    setInternalError(validation.error);
    
    // Pass ISO format to parent for API
    const isoDate = getDateISO(formatted);
    onChange({ target: { value: isoDate, name: e.target.name } });
  };
  
  const error = externalError || internalError;
  
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}{required && ' *'}</label>}
      <input 
        className={`form-input ${error ? 'error' : ''}`} 
        value={displayValue}
        onChange={handleChange}
        placeholder="MM/DD/YYYY"
        maxLength={10}
        {...props} 
      />
      {error && <span className="form-error">{error}</span>}
    </div>
  );
};

const Select = ({ label, options, error, ...props }) => (
  <div className="form-group">
    {label && <label className="form-label">{label}</label>}
    <select className={`form-select ${error ? 'error' : ''}`} {...props}>
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
    {error && <span className="form-error">{error}</span>}
  </div>
);

const CitySearch = ({ label, state, value, onChange, placeholder = 'Search for your city...', strictMode = false, allowCustom = false }) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isValidSelection, setIsValidSelection] = useState(!!value);
  const [isCustomCity, setIsCustomCity] = useState(false);
  const searchTimeout = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
        // In strict mode without allowCustom, clear invalid entries when clicking outside
        if (strictMode && !allowCustom && !isValidSelection) {
          setSearchTerm('');
          onChange('');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [strictMode, allowCustom, isValidSelection, onChange]);

  // Update search term when value prop changes
  useEffect(() => {
    setSearchTerm(value || '');
    setIsValidSelection(!!value);
  }, [value]);

  const searchCities = async (term) => {
    if (!state || term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const data = await api.get(`/jurisdictions?state=${state}&search=${encodeURIComponent(term)}`);
      setResults(data.jurisdictions || []);
    } catch (err) {
      console.error(err);
      setResults([]);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsValidSelection(false);
    setIsCustomCity(false);
    // In strict mode, don't update parent until a valid selection is made
    if (!strictMode) {
      onChange(term);
    }
    setShowDropdown(true);
    
    // Debounce the API call
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchCities(term), 300);
  };

  const handleSelect = (jurisdiction) => {
    const cityName = jurisdiction.city || jurisdiction.name;
    setSearchTerm(cityName);
    setIsValidSelection(true);
    setIsCustomCity(false);
    onChange(cityName);
    setShowDropdown(false);
    setResults([]);
  };

  const handleUseCustomCity = () => {
    setIsValidSelection(true);
    setIsCustomCity(true);
    onChange(searchTerm);
    setShowDropdown(false);
    setResults([]);
  };

  return (
    <div className="form-group city-search-wrapper" ref={wrapperRef}>
      {label && <label className="form-label">{label}</label>}
      <input
        className={`form-input ${strictMode && searchTerm && !isValidSelection ? 'input-warning' : ''} ${isCustomCity ? 'input-custom' : ''}`}
        placeholder={state ? placeholder : 'Select a state first'}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => state && searchTerm.length >= 2 && setShowDropdown(true)}
        disabled={!state}
      />
      {showDropdown && (results.length > 0 || loading || (searchTerm.length >= 2 && allowCustom)) && (
        <div className="city-search-dropdown">
          {loading && <div className="city-search-loading">Searching...</div>}
          {!loading && results.length > 0 && results.map(j => (
            <div key={j._id} className="city-search-option" onClick={() => handleSelect(j)}>
              <span className="city-name">{j.city || j.name}</span>
              <span className="city-type">{j.type}</span>
            </div>
          ))}
          {!loading && results.length === 0 && searchTerm.length >= 2 && !allowCustom && (
            <div className="city-search-empty">
              {strictMode ? 'No cities found. Please select from the available options.' : `No matches found. You can still enter "${searchTerm}" manually.`}
            </div>
          )}
          {!loading && searchTerm.length >= 2 && allowCustom && (
            <div className="city-search-custom-option" onClick={handleUseCustomCity}>
              <span className="custom-icon">➕</span>
              <span>Use "{searchTerm}" (manual tracking)</span>
            </div>
          )}
        </div>
      )}
      {isCustomCity && (
        <span className="form-hint form-hint-info">
          <Icons.Info /> This city doesn't have permit data. You can track permits manually.
        </span>
      )}
      {strictMode && !allowCustom && searchTerm && !isValidSelection && (
        <span className="form-hint form-hint-warning">Please select a city from the dropdown</span>
      )}
      {!state && <span className="form-hint">Please select a state first</span>}
    </div>
  );
};

const Card = ({ children, className = '', onClick }) => (
  <div className={`card ${className}`} onClick={onClick}>{children}</div>
);

const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal modal-${size}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}><Icons.X /></button>
        </div>
        <div className="modal-content">{children}</div>
      </div>
    </div>
  );
};

const Alert = ({ type = 'info', children, onClose }) => (
  <div className={`alert alert-${type}`}>
    <div className="alert-content">{children}</div>
    {onClose && <button className="alert-close" onClick={onClose}><Icons.X /></button>}
  </div>
);

const LoadingSpinner = () => <div className="loading-spinner"><div className="spinner" /></div>;
const Badge = ({ children, variant = 'default' }) => <span className={`badge badge-${variant}`}>{children}</span>;
const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="empty-state">{Icon && <div className="empty-state-icon"><Icon /></div>}<h3>{title}</h3>{description && <p>{description}</p>}{action}</div>
);

// ===========================================
// STRIPE EMBEDDED PAYMENT MODAL
// ===========================================
let stripePromise = null;
const getStripe = async () => {
  if (stripePromise) return stripePromise;
  // Load Stripe.js if not already loaded
  if (!window.Stripe) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  // Fetch publishable key from server
  const config = await api.get('/stripe/config');
  if (!config.publishableKey) throw new Error('Stripe is not configured');
  stripePromise = window.Stripe(config.publishableKey);
  return stripePromise;
};

const PLAN_DISPLAY = {
  basic: { name: 'Starter', price: '$19/mo' },
  pro: { name: 'Pro', price: '$49/mo' },
  elite: { name: 'Elite', price: '$99/mo' },
  organizer: { name: 'Organizer', price: '$79/mo' }
};

const StripePaymentModal = ({ isOpen, onClose, plan, isOrganizer = false, isRestaurant = false, onSuccess }) => {
  const [step, setStep] = useState('loading'); // loading | form | processing | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const paymentRef = useRef(null);
  const mountedRef = useRef(false);

  const planInfo = PLAN_DISPLAY[plan] || PLAN_DISPLAY.basic;

  useEffect(() => {
    if (!isOpen) {
      setStep('loading');
      setErrorMsg('');
      setStripe(null);
      setElements(null);
      setSubscriptionId(null);
      mountedRef.current = false;
      return;
    }

    let canceled = false;

    const init = async () => {
      try {
        // 1. Load Stripe
        const stripeInstance = await getStripe();
        if (canceled) return;
        setStripe(stripeInstance);

        // 2. Create intent on server
        const endpoint = isOrganizer ? '/organizer/subscription/create-intent' : isRestaurant ? '/restaurant/subscription/create-intent' : '/subscription/create-intent';
        const data = await api.post(endpoint, isOrganizer ? {} : { plan });
        if (canceled) return;

        // Test mode bypass
        if (data.testMode) {
          setStep('success');
          return;
        }

        if (!data.clientSecret) throw new Error('Failed to initialize payment');

        setSubscriptionId(data.subscriptionId);

        // 3. Create Elements
        const elementsInstance = stripeInstance.elements({
          clientSecret: data.clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#4f46e5',
              colorBackground: '#ffffff',
              colorText: '#1e293b',
              colorDanger: '#ef4444',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              borderRadius: '8px',
              spacingUnit: '4px'
            },
            rules: {
              '.Input': { border: '1px solid #d1d5db', padding: '12px', boxShadow: 'none' },
              '.Input:focus': { border: '1px solid #4f46e5', boxShadow: '0 0 0 1px #4f46e5' },
              '.Label': { fontWeight: '500', marginBottom: '6px' }
            }
          }
        });
        if (canceled) return;
        setElements(elementsInstance);
        setStep('form');

        // 4. Mount Payment Element after render
        setTimeout(() => {
          if (canceled || !paymentRef.current) return;
          if (mountedRef.current) return;
          const paymentElement = elementsInstance.create('payment', {
            layout: { type: 'tabs', defaultCollapsed: false },
            fields: { billingDetails: { address: { country: 'auto' } } }
          });
          paymentElement.mount(paymentRef.current);
          mountedRef.current = true;
        }, 100);

      } catch (err) {
        if (canceled) return;
        setErrorMsg(err.message || 'Failed to initialize payment');
        setStep('error');
      }
    };

    init();
    return () => { canceled = true; };
  }, [isOpen, plan, isOrganizer, isRestaurant]);

  // Re-mount element when paymentRef becomes available
  useEffect(() => {
    if (step === 'form' && elements && paymentRef.current && !mountedRef.current) {
      const paymentElement = elements.create('payment', {
        layout: { type: 'tabs', defaultCollapsed: false },
        fields: { billingDetails: { address: { country: 'auto' } } }
      });
      paymentElement.mount(paymentRef.current);
      mountedRef.current = true;
    }
  }, [step, elements]);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setStep('processing');
    setErrorMsg('');

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required'
      });

      if (error) {
        setErrorMsg(error.message);
        setStep('form');
        return;
      }

      // Payment succeeded — confirm on server
      const confirmEndpoint = isOrganizer ? '/organizer/subscription/confirm-payment' : isRestaurant ? '/restaurant/subscription/confirm-payment' : '/subscription/confirm-payment';
      
      // Poll for confirmation (webhook might take a moment)
      let confirmed = false;
      for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 1000));
        try {
          const result = await api.post(confirmEndpoint, { subscriptionId });
          if (result.success) { confirmed = true; break; }
        } catch (e) { /* retry */ }
      }

      if (confirmed) {
        setStep('success');
      } else {
        // Payment went through but webhook hasn't fired yet — still show success
        setStep('success');
      }
    } catch (err) {
      setErrorMsg(err.message || 'Payment failed');
      setStep('form');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(step === 'processing') ? undefined : onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h2>
            {step === 'success' ? 'Payment Successful!' : step === 'processing' ? 'Processing...' : `Subscribe to ${planInfo.name}`}
          </h2>
          {step !== 'processing' && <button className="modal-close" onClick={onClose}><Icons.X /></button>}
        </div>
        <div className="modal-content" style={{ padding: '24px' }}>
          
          {step === 'loading' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div className="spinner" style={{ margin: '0 auto 16px' }} />
              <p style={{ color: 'var(--gray-500)' }}>Preparing payment...</p>
            </div>
          )}

          {step === 'form' && (
            <>
              <div style={{ background: 'var(--gray-50)', borderRadius: '8px', padding: '16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontWeight: 600, margin: 0, color: 'var(--gray-800)' }}>{planInfo.name} Plan</p>
                  <p style={{ fontSize: '0.8125rem', margin: '2px 0 0', color: 'var(--gray-500)' }}>Billed monthly • Cancel anytime</p>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>{planInfo.price}</div>
              </div>

              {errorMsg && (
                <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                  <div className="alert-content">{errorMsg}</div>
                </div>
              )}

              <div ref={paymentRef} style={{ minHeight: '200px', marginBottom: '20px' }} />

              <Button onClick={handleSubmit} className="full-width" style={{ padding: '14px', fontSize: '1rem' }}>
                <Icons.Shield /> Subscribe — {planInfo.price}
              </Button>

              <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', textAlign: 'center', marginTop: '12px' }}>
                Secured by Stripe. Your card details never touch our servers.
              </p>
            </>
          )}

          {step === 'processing' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div className="spinner" style={{ margin: '0 auto 16px' }} />
              <p style={{ color: 'var(--gray-600)', fontWeight: 500 }}>Processing your payment...</p>
              <p style={{ color: 'var(--gray-400)', fontSize: '0.875rem', marginTop: '4px' }}>Please don't close this window.</p>
            </div>
          )}

          {step === 'success' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--success-bg, #dcfce7)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Icons.Check color="var(--success, #16a34a)" />
              </div>
              <h3 style={{ margin: '0 0 8px', color: 'var(--gray-800)' }}>Welcome to {planInfo.name}!</h3>
              <p style={{ color: 'var(--gray-500)', margin: '0 0 24px' }}>Your subscription is now active. Enjoy your new features!</p>
              <Button onClick={() => { onSuccess?.(); onClose(); }} className="full-width">Get Started</Button>
            </div>
          )}

          {step === 'error' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Icons.X color="var(--danger, #ef4444)" />
              </div>
              <h3 style={{ margin: '0 0 8px', color: 'var(--gray-800)' }}>Something went wrong</h3>
              <p style={{ color: 'var(--gray-500)', margin: '0 0 24px' }}>{errorMsg}</p>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Expired Subscription Banner - shows persistent banner for expired users
const ExpiredSubscriptionBanner = () => {
  const { isExpired } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  if (!isExpired) return null;
  
  return (
    <>
      <div className="expired-banner">
        <div className="expired-banner-content">
          <Icons.Alert />
          <span><strong>Your subscription has expired.</strong> You have read-only access. Upgrade to resume full functionality.</span>
        </div>
        <button className="expired-banner-btn" onClick={() => setShowUpgradeModal(true)}>Upgrade Now</button>
      </div>
      <UpgradeRequiredModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} reason="Your subscription has expired" />
    </>
  );
};

// Upgrade Required Modal - shows when expired users try to use premium features
const UpgradeRequiredModal = ({ isOpen, onClose, reason = 'This feature requires an active subscription', feature = null }) => {
  const { user, subscription, subscriptionStatus, fetchUser } = useAuth();
  const toast = useToast();
  const [upgrading, setUpgrading] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const isOrganizer = user?.isOrganizer && !user?.organizerProfile?.disabled;
  const isRestaurantUser = user?.isRestaurant;
  
  const PLAN_RANK = { free: 0, trial: 0, basic: 1, pro: 2, elite: 3, promo: 4, lifetime: 5 };
  const isActiveSub = subscription && !subscriptionStatus?.isExpired && ['active', 'grace_period'].includes(subscription?.status);
  const effectivePlan = isActiveSub ? subscription?.plan : null;
  const currentRank = PLAN_RANK[effectivePlan] || 0;
  
  const vendorPlanFeatures = {
    basic: ['Permit tracking for 1 city', 'Document storage', 'Email reminders', 'Basic compliance dashboard'],
    pro: ['Everything in Basic', 'Multi-city support (up to 3)', 'SMS alerts', 'Inspection checklists', 'PDF autofill'],
    elite: ['Everything in Pro', 'Unlimited cities', 'Event integration', 'Team accounts', 'Priority support', 'API access']
  };
  const vendorPlanPrices = { basic: 19, pro: 49, elite: 99 };
  
  const organizerFeatures = [
    'Unlimited event listings', 'Vendor compliance tracking', 'Automated permit verification',
    'Custom application forms', 'Payment processing', 'Analytics dashboard', 'Priority support'
  ];
  
  const handleOrganizerUpgrade = async () => {
    setPaymentPlan('organizer');
  };
  
  const getPlanAction = (plan) => {
    const targetRank = PLAN_RANK[plan] || 0;
    if (effectivePlan === plan) return 'current';
    if (subscription?.pendingPlanChange === plan) return 'pending';
    if (!effectivePlan) return 'upgrade';
    return targetRank > currentRank ? 'upgrade' : 'downgrade';
  };
  
  const handleVendorPlanAction = async (plan) => {
    const action = getPlanAction(plan);
    setUpgrading(true);
    try {
      if (action === 'downgrade' && isActiveSub) {
        const data = await api.post('/subscription/change-plan', { plan });
        if (data.success) { toast.success(data.message); fetchUser(); onClose(); }
        else if (data.needsCheckout) { setPaymentPlan(plan); }
      } else if (action === 'upgrade' && isActiveSub) {
        const data = await api.post('/subscription/change-plan', { plan });
        if (data.success) { toast.success(data.message); fetchUser(); onClose(); }
        else if (data.needsCheckout) { setPaymentPlan(plan); }
      } else {
        // New or expired subscription — go to checkout
        setPaymentPlan(plan);
      }
    } catch (err) { toast.error(err.message || 'Failed to change plan'); }
    setUpgrading(false);
  };
  
  const getButtonLabel = (plan) => {
    const action = getPlanAction(plan);
    if (action === 'current') return 'Current Plan';
    if (action === 'pending') return 'Downgrade Scheduled';
    if (upgrading) return 'Processing...';
    return action === 'downgrade' ? 'Downgrade' : 'Upgrade';
  };
  
  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} title={effectivePlan ? 'Change Plan' : 'Upgrade Required'} size="lg">
      <div className="upgrade-modal">
        <div className="upgrade-reason">
          <Icons.Lock />
          <div>
            <h3>{reason}</h3>
            <p>{isOrganizer 
              ? 'Subscribe to the Organizer plan to manage events and vendor compliance.' 
              : 'Choose a plan to continue using PermitWise with full access to all features.'}</p>
          </div>
        </div>
        
        {feature && (
          <div className="feature-highlight">
            <strong>Feature needed:</strong> {feature}
          </div>
        )}
        
        {subscription?.pendingPlanChange && (
          <div className="feature-highlight" style={{ borderLeft: '3px solid var(--warning)', background: '#fffbeb' }}>
            <strong>Pending change:</strong> Your plan will switch to {subscription.pendingPlanChange.charAt(0).toUpperCase() + subscription.pendingPlanChange.slice(1)} on {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'next renewal'}.
          </div>
        )}
        
        {isOrganizer ? (
          <div className="plans-grid">
            <div className="plan-card recommended" style={{ maxWidth: '400px', margin: '0 auto' }}>
              <div className="plan-badge">Event Organizer</div>
              <h4>Organizer Plan</h4>
              <div className="plan-price">$79<span>/mo</span></div>
              <ul className="plan-features">
                {organizerFeatures.map((f, i) => <li key={i}><Icons.Check /> {f}</li>)}
              </ul>
              <Button onClick={handleOrganizerUpgrade} disabled={upgrading}>
                {upgrading ? 'Processing...' : 'Subscribe Now'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="plans-grid">
            {['basic', 'pro', 'elite'].map(plan => {
              const action = getPlanAction(plan);
              return (
                <div key={plan} className={`plan-card ${plan === 'pro' ? 'recommended' : ''}`}>
                  {plan === 'pro' && <div className="plan-badge">Most Popular</div>}
                  <h4>{plan.charAt(0).toUpperCase() + plan.slice(1)}</h4>
                  <div className="plan-price">${vendorPlanPrices[plan]}<span>/mo</span></div>
                  <ul className="plan-features">
                    {vendorPlanFeatures[plan].map((f, i) => <li key={i}><Icons.Check /> {f}</li>)}
                  </ul>
                  <Button 
                    onClick={() => handleVendorPlanAction(plan)} 
                    className={action === 'downgrade' ? 'btn-outline btn-warning' : plan === 'pro' ? '' : 'btn-outline'}
                    disabled={upgrading || action === 'current' || action === 'pending'}
                  >
                    {getButtonLabel(plan)}
                  </Button>
                  {action === 'downgrade' && <p className="plan-note">Keeps current features until renewal</p>}
                </div>
              );
            })}
          </div>
        )}
        
        <p className="upgrade-note">All plans include a 14-day free trial. Cancel anytime.</p>
      </div>
    </Modal>

    <StripePaymentModal
      isOpen={!!paymentPlan}
      onClose={() => setPaymentPlan(null)}
      plan={paymentPlan || 'basic'}
      isOrganizer={paymentPlan === 'organizer'}
      isRestaurant={isRestaurantUser}
      onSuccess={() => { fetchUser(); setPaymentPlan(null); onClose(); }}
    />
    </>
  );
};

// Lock Icon overlay for premium sections (utility component for future use)
// eslint-disable-next-line no-unused-vars
const PremiumLock = ({ feature, children }) => {
  const { isExpired, subscriptionStatus } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);
  
  // Check if feature is available
  const hasFeature = subscriptionStatus?.features?.[feature] ?? false;
  const isLocked = isExpired || !hasFeature;
  
  if (!isLocked) return children;
  
  return (
    <>
      <div className="premium-locked" onClick={() => setShowUpgrade(true)}>
        <div className="locked-overlay">
          <Icons.Lock />
          <span>Premium Feature</span>
        </div>
        <div className="locked-content">{children}</div>
      </div>
      <UpgradeRequiredModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature={feature} />
    </>
  );
};

// ===========================================
// AUTH PROVIDER
// ===========================================
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [businessRole, setBusinessRole] = useState(null);
  const [restaurantBusiness, setRestaurantBusiness] = useState(null);
  const [restaurantSubscription, setRestaurantSubscription] = useState(null);
  const [restaurantSubscriptionStatus, setRestaurantSubscriptionStatus] = useState(null);
  const [organizerSubscription, setOrganizerSubscription] = useState(null);
  const [organizerSubscriptionStatus, setOrganizerSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('permitwise_token');
      if (!token) { setLoading(false); return; }
      const data = await api.get('/auth/me');
      setUser(data.user); setBusiness(data.business); setSubscription(data.subscription);
      setSubscriptionStatus(data.subscriptionStatus);
      setBusinessRole(data.businessRole);
      setRestaurantBusiness(data.restaurantBusiness || null);
      setRestaurantSubscription(data.restaurantSubscription || null);
      setRestaurantSubscriptionStatus(data.restaurantSubscriptionStatus || null);
      setOrganizerSubscription(data.organizerSubscription || null);
      setOrganizerSubscriptionStatus(data.organizerSubscriptionStatus || null);
    } catch (error) { localStorage.removeItem('permitwise_token'); } 
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    localStorage.setItem('permitwise_token', data.token);
    setUser(data.user); await fetchUser(); return data;
  };

  const register = async (userData) => {
    const data = await api.post('/auth/register', userData);
    localStorage.setItem('permitwise_token', data.token);
    setUser(data.user); return data;
  };

  const logout = () => { localStorage.removeItem('permitwise_token'); setUser(null); setBusiness(null); setSubscription(null); setSubscriptionStatus(null); setBusinessRole(null); setRestaurantBusiness(null); setRestaurantSubscription(null); setRestaurantSubscriptionStatus(null); setOrganizerSubscription(null); setOrganizerSubscriptionStatus(null); };

  // Check if subscription allows write operations
  const isRestaurant = user?.isRestaurant;
  const isOrganizer = user?.isOrganizer;
  const activeSubscriptionStatus = isRestaurant ? restaurantSubscriptionStatus : isOrganizer ? organizerSubscriptionStatus : subscriptionStatus;
  const canWrite = activeSubscriptionStatus?.canWrite ?? true;
  const isExpired = activeSubscriptionStatus?.isExpired ?? false;
  
  // Role-based permission helpers
  const isOwner = businessRole === 'owner';
  const isManager = businessRole === 'manager' || businessRole === 'owner';
  const canManageTeam = isManager;
  const canManageSubscription = isOwner;
  const canManageBusiness = isManager;

  // Has completed onboarding check
  const hasCompletedOnboarding = isRestaurant ? !!restaurantBusiness : !!business;

  return (
    <AuthContext.Provider value={{ 
      user, business, subscription, subscriptionStatus, businessRole,
      restaurantBusiness, restaurantSubscription, restaurantSubscriptionStatus,
      organizerSubscription, organizerSubscriptionStatus,
      canWrite, isExpired, isOwner, isManager, canManageTeam, canManageSubscription, canManageBusiness,
      loading, login, register, logout, fetchUser, updateBusiness: setBusiness, updateRestaurantBusiness: setRestaurantBusiness, updateSubscription: setSubscription, 
      isAuthenticated: !!user, hasCompletedOnboarding
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// ===========================================
// PAGES
// ===========================================
const LoginPage = ({ onSwitch, onSuccess }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState(''); const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(''); const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login(email, password); onSuccess(); } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  return (
    <div className="auth-page"><div className="auth-container">
      <div className="auth-header"><div className="logo"><img src="/images/LOGOnobackground.png" alt="PermitWise" className="auth-logo-img" /></div><h1>Welcome back</h1></div>
      <form onSubmit={handleSubmit} className="auth-form">
        {error && <Alert type="error">{error}</Alert>}
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <div className="password-input-wrapper">
          <Input label="Password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required />
          <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <Icons.EyeOff /> : <Icons.Eye />}</button>
        </div>
        <div className="forgot-password-link"><button type="button" onClick={() => onSwitch('forgot')}>Forgot password?</button></div>
        <Button type="submit" loading={loading} className="full-width">Log In</Button>
      </form>
      <div className="auth-footer"><p>Don't have an account? <button onClick={() => onSwitch('register')}>Sign up</button></p></div>
    </div></div>
  );
};

const PasswordStrengthIndicator = ({ password }) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  const passedChecks = Object.values(checks).filter(Boolean).length;
  const strength = passedChecks <= 2 ? 'weak' : passedChecks <= 4 ? 'medium' : 'strong';
  const strengthLabel = passedChecks <= 2 ? 'Weak' : passedChecks <= 4 ? 'Medium' : 'Strong';

  if (!password) return null;

  return (
    <div className="password-strength">
      <div className="strength-bar"><div className={`strength-fill ${strength}`} style={{ width: `${(passedChecks / 5) * 100}%` }} /></div>
      <span className={`strength-label ${strength}`}>{strengthLabel}</span>
      <div className="strength-checks">
        <div className={checks.length ? 'check passed' : 'check'}>{checks.length ? <Icons.Check /> : <Icons.X />} At least 8 characters</div>
        <div className={checks.uppercase ? 'check passed' : 'check'}>{checks.uppercase ? <Icons.Check /> : <Icons.X />} Uppercase letter</div>
        <div className={checks.lowercase ? 'check passed' : 'check'}>{checks.lowercase ? <Icons.Check /> : <Icons.X />} Lowercase letter</div>
        <div className={checks.number ? 'check passed' : 'check'}>{checks.number ? <Icons.Check /> : <Icons.X />} Number</div>
        <div className={checks.special ? 'check passed' : 'check'}>{checks.special ? <Icons.Check /> : <Icons.X />} Special character (!@#$%^&*)</div>
      </div>
    </div>
  );
};

const RegisterPage = ({ onSwitch, onSuccess, defaultOrganizer = false, defaultRestaurant = false, referralCode: refCodeProp = '' }) => {
  const { register } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '', phone: '', accountType: defaultOrganizer ? 'organizer' : defaultRestaurant ? 'restaurant' : 'vendor' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState(''); const [loading, setLoading] = useState(false);

  const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  const passwordsDontMatch = formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (!agreedToTerms) { setError('You must agree to the Terms of Service and Privacy Policy'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (formData.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try { await register({ ...formData, isOrganizer: formData.accountType === 'organizer', isRestaurant: formData.accountType === 'restaurant', referralCode: refCodeProp || undefined }); onSuccess(); } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  return (
    <div className="auth-page"><div className="auth-container">
      <div className="auth-header"><div className="logo"><img src="/images/LOGOnobackground.png" alt="PermitWise" className="auth-logo-img" /></div><h1>Create account</h1><p>14-day free trial</p>{refCodeProp && <p style={{ fontSize: '0.8125rem', color: 'var(--primary)', marginTop: 4 }}>Referred by: <strong>{refCodeProp}</strong></p>}</div>
      <form onSubmit={handleSubmit} className="auth-form">
        {error && <Alert type="error">{error}</Alert>}
        
        <div className="account-type-selector">
          <label className="account-type-label">I am a:</label>
          <div className="account-type-options">
            <button type="button" className={`account-type-option ${formData.accountType === 'vendor' ? 'active' : ''}`} onClick={() => setFormData(f => ({ ...f, accountType: 'vendor' }))}>
              <div className="account-type-emoji">🚚</div>
              <div className="account-type-info">
                <span className="account-type-title">Mobile Vendor</span>
                <span className="account-type-desc">Food truck, cart, or mobile business</span>
              </div>
            </button>
            <button type="button" className={`account-type-option ${formData.accountType === 'organizer' ? 'active' : ''}`} onClick={() => setFormData(f => ({ ...f, accountType: 'organizer' }))}>
              <div className="account-type-emoji">🎪</div>
              <div className="account-type-info">
                <span className="account-type-title">Event Organizer</span>
                <span className="account-type-desc">Manage events & vendor compliance</span>
              </div>
            </button>
            <button type="button" className={`account-type-option ${formData.accountType === 'restaurant' ? 'active' : ''}`} onClick={() => setFormData(f => ({ ...f, accountType: 'restaurant' }))}>
              <div className="account-type-emoji">🍽️</div>
              <div className="account-type-info">
                <span className="account-type-title">Restaurant</span>
                <span className="account-type-desc">Restaurant, bar, café, or bakery</span>
              </div>
            </button>
          </div>
        </div>
        
        <div className="form-row"><Input label="First Name" value={formData.firstName} onChange={(e) => setFormData(f => ({ ...f, firstName: e.target.value }))} required /><Input label="Last Name" value={formData.lastName} onChange={(e) => setFormData(f => ({ ...f, lastName: e.target.value }))} required /></div>
        <EmailInput label="Email" value={formData.email} onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))} required />
        <PhoneInput label="Phone" value={formData.phone} onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))} />
        <div className="password-input-wrapper">
          <Input label="Password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData(f => ({ ...f, password: e.target.value }))} required />
          <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <Icons.EyeOff /> : <Icons.Eye />}</button>
        </div>
        <PasswordStrengthIndicator password={formData.password} />
        <div className="password-input-wrapper">
          <Input label="Confirm Password" type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={(e) => setFormData(f => ({ ...f, confirmPassword: e.target.value }))} required className={passwordsMatch ? 'input-success' : passwordsDontMatch ? 'input-error' : ''} />
          <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? <Icons.EyeOff /> : <Icons.Eye />}</button>
        </div>
        {passwordsMatch && <div className="password-match success"><Icons.Check /> Passwords match</div>}
        {passwordsDontMatch && <div className="password-match error"><Icons.X /> Passwords do not match</div>}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', margin: '12px 0 4px', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--gray-600)', lineHeight: '1.5' }}>
          <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} style={{ marginTop: '3px', flexShrink: 0, width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
          <span>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 500 }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 500 }}>Privacy Policy</a></span>
        </label>
        <Button type="submit" loading={loading} className="full-width" disabled={!agreedToTerms}>Create Account</Button>
      </form>
      <div className="auth-footer"><p>Have an account? <button onClick={() => onSwitch('login')}>Log in</button></p></div>
    </div></div>
  );
};

const ForgotPasswordPage = ({ onSwitch }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(''); const [success, setSuccess] = useState(false); const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="auth-page"><div className="auth-container">
        <div className="auth-header"><div className="logo"><img src="/images/LOGOnobackground.png" alt="PermitWise" className="auth-logo-img" /></div><h1>Check your email</h1></div>
        <div className="auth-success">
          <div className="success-icon"><Icons.Check /></div>
          <p>If an account exists for <strong>{email}</strong>, we've sent password reset instructions.</p>
          <p className="muted">Check your spam folder if you don't see it within a few minutes.</p>
        </div>
        <div className="auth-footer"><button onClick={() => onSwitch('login')}>← Back to login</button></div>
      </div></div>
    );
  }

  return (
    <div className="auth-page"><div className="auth-container">
      <div className="auth-header"><div className="logo"><img src="/images/LOGOnobackground.png" alt="PermitWise" className="auth-logo-img" /></div><h1>Reset password</h1><p>Enter your email and we'll send reset instructions</p></div>
      <form onSubmit={handleSubmit} className="auth-form">
        {error && <Alert type="error">{error}</Alert>}
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <Button type="submit" loading={loading} className="full-width">Send Reset Link</Button>
      </form>
      <div className="auth-footer"><button onClick={() => onSwitch('login')}>← Back to login</button></div>
    </div></div>
  );
};

const ResetPasswordPage = ({ token, onSuccess }) => {
  const [password, setPassword] = useState(''); const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(''); const [loading, setLoading] = useState(false);

  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const passwordsDontMatch = password && confirmPassword && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      onSuccess();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  return (
    <div className="auth-page"><div className="auth-container">
      <div className="auth-header"><div className="logo"><img src="/images/LOGOnobackground.png" alt="PermitWise" className="auth-logo-img" /></div><h1>Set new password</h1></div>
      <form onSubmit={handleSubmit} className="auth-form">
        {error && <Alert type="error">{error}</Alert>}
        <div className="password-input-wrapper">
          <Input label="New Password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required />
          <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <Icons.EyeOff /> : <Icons.Eye />}</button>
        </div>
        <PasswordStrengthIndicator password={password} />
        <div className="password-input-wrapper">
          <Input label="Confirm New Password" type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className={passwordsMatch ? 'input-success' : passwordsDontMatch ? 'input-error' : ''} />
          <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? <Icons.EyeOff /> : <Icons.Eye />}</button>
        </div>
        {passwordsMatch && <div className="password-match success"><Icons.Check /> Passwords match</div>}
        {passwordsDontMatch && <div className="password-match error"><Icons.X /> Passwords do not match</div>}
        <Button type="submit" loading={loading} className="full-width">Reset Password</Button>
      </form>
    </div></div>
  );
};

const VerifyEmailPage = ({ token, onSuccess, onError }) => {
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [error, setError] = useState('');
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    let cancelled = false;
    const verifyEmail = async () => {
      try {
        await api.post('/auth/verify-email', { token });
        if (!cancelled) {
          setStatus('success');
          setTimeout(() => onSuccessRef.current(), 2000);
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setError(err.message || 'Verification failed');
        }
      }
    };
    if (token) verifyEmail();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="auth-page">
      <div className="auth-container" style={{ textAlign: 'center' }}>
        <div className="auth-header">
          <div className="logo"><img src="/images/LOGOnobackground.png" alt="PermitWise" className="auth-logo-img" /></div>
          <h1>Email Verification</h1>
        </div>
        <div style={{ padding: '2rem' }}>
          {status === 'verifying' && (
            <>
              <LoadingSpinner />
              <p style={{ marginTop: '1rem', color: 'var(--gray-600)' }}>Verifying your email address...</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div style={{ fontSize: '3rem', color: 'var(--success)', marginBottom: '1rem' }}>✓</div>
              <h2 style={{ color: 'var(--success)', marginBottom: '0.5rem' }}>Email Verified!</h2>
              <p style={{ color: 'var(--gray-600)' }}>Your email has been verified successfully. Redirecting to login...</p>
            </>
          )}
          {status === 'error' && (
            <>
              <div style={{ fontSize: '3rem', color: 'var(--danger)', marginBottom: '1rem' }}>✗</div>
              <h2 style={{ color: 'var(--danger)', marginBottom: '0.5rem' }}>Verification Failed</h2>
              <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>{error}</p>
              <Button onClick={onError}>Go to Login</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PermitChecker = ({ onClose, onGetStarted }) => {
  const [city, setCity] = useState(''); const [state, setState] = useState(''); const [vendorType, setVendorType] = useState('');
  const [results, setResults] = useState(null); const [loading, setLoading] = useState(false);

  const handleCheck = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const data = await api.get(`/permit-types/required?city=${city}&state=${state}&vendorType=${vendorType}`); setResults(data); } 
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const getImportanceLabel = (permit) => {
    if (permit.importanceLevel === 'critical') return { text: 'Critical', variant: 'danger' };
    if (permit.importanceLevel === 'often_forgotten') return { text: 'Often Forgotten', variant: 'warning' };
    if (permit.importanceLevel === 'event_required') return { text: 'Required for Events', variant: 'info' };
    // Fallback to name-based detection
    const name = permit.name?.toLowerCase() || '';
    if (name.includes('health') || name.includes('food') || name.includes('business license')) return { text: 'Critical', variant: 'danger' };
    if (name.includes('fire') || name.includes('commissary')) return { text: 'Often Forgotten', variant: 'warning' };
    if (name.includes('event') || name.includes('special')) return { text: 'Required for Events', variant: 'info' };
    return null;
  };

  const isPartialCoverage = results && results.coverage === 'partial';

  return (
    <div className="permit-checker"><div className="permit-checker-container">
      <button className="close-btn" onClick={onClose}><Icons.X /></button>
      <h1>What permits do you need?</h1>
      <p className="checker-subtitle">Enter your city and business type to see required permits</p>
      <form onSubmit={handleCheck}>
        <div className="form-row"><Input label="City" placeholder="e.g. Austin" value={city} onChange={(e) => setCity(e.target.value)} required /><Select label="State" value={state} onChange={(e) => setState(e.target.value)} options={[{ value: '', label: 'Select' }, ...US_STATES.map(s => ({ value: s, label: s }))]} required /></div>
        <Select label="Business Type" value={vendorType} onChange={(e) => setVendorType(e.target.value)} options={[{ value: '', label: 'Select your business type' }, ...VENDOR_TYPES]} required />
        <Button type="submit" loading={loading} className="full-width">Check Requirements</Button>
      </form>
      {results && (<div className="checker-results">
        {results.permitTypes?.length > 0 ? (<>
          <h2><Icons.Check /> {results.permitTypes.length} permit{results.permitTypes.length > 1 ? 's' : ''} required in {city}, {state}</h2>
          {isPartialCoverage && (
            <div className="coverage-notice">
              <Icons.Alert />
              <p>We're still adding full permit coverage for {city}. You can still track all your permits manually. We'll notify you as soon as full support is ready.</p>
            </div>
          )}
          <ul className="permit-list">{results.permitTypes.map(pt => {
            const label = getImportanceLabel(pt);
            return (<li key={pt._id}>
              <div className="permit-list-header"><h3>{pt.name}</h3>{label && <Badge variant={label.variant}>{label.text}</Badge>}</div>
              <p>{pt.description}</p>
              {pt.issuingAuthorityName && <span className="permit-issuer">Issued by: {pt.issuingAuthorityName}</span>}
              {pt.estimatedCost && <span className="permit-cost">Typical cost: {pt.estimatedCost}</span>}
              <span className="permit-renewal">Renews every {pt.renewalPeriodMonths || 12} months</span>
            </li>);
          })}</ul>
          <div className="checker-cta"><p>Don't let these expire! PermitWise will remind you automatically.</p><Button onClick={() => onGetStarted('register')}>Track My Permits Free →</Button></div>
        </>) : (
          <div className="no-results">
            <Icons.MapPin />
            <h2>We're still building coverage for {city}</h2>
            <p>We're still adding full permit coverage for this city. You can still track all your permits manually — just enter them yourself. We'll notify you as soon as full support is ready.</p>
            <Button onClick={() => onGetStarted('register')}>Start Tracking Manually →</Button>
          </div>
        )}
      </div>)}
    </div></div>
  );
};

const OnboardingPage = ({ onComplete }) => {
  const { updateBusiness } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false); const [error, setError] = useState('');
  const [formData, setFormData] = useState({ businessName: '', primaryVendorType: '', handlesFood: false, operatingCities: [{ city: '', state: '', isPrimary: true }] });
  const [suggestedPermits, setSuggestedPermits] = useState([]);
  const [selectedPermits, setSelectedPermits] = useState([]);
  const [loadingPermits, setLoadingPermits] = useState(false);
  const [coverageStatus, setCoverageStatus] = useState(null); // 'full', 'partial', 'none'
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionSubmitted, setSuggestionSubmitted] = useState(false);

  // Auto-set handlesFood based on vendor type
  useEffect(() => {
    if (formData.primaryVendorType) {
      const autoFood = FOOD_VENDOR_TYPES.includes(formData.primaryVendorType);
      setFormData(f => ({ ...f, handlesFood: autoFood }));
    }
  }, [formData.primaryVendorType]);

  const fetchSuggestedPermits = async () => {
    if (!formData.operatingCities[0].city || !formData.operatingCities[0].state) return;
    setLoadingPermits(true);
    try {
      const data = await api.get(`/permit-types/required?city=${formData.operatingCities[0].city}&state=${formData.operatingCities[0].state}&vendorType=${formData.primaryVendorType}&handlesFood=${formData.handlesFood}`);
      setSuggestedPermits(data.permitTypes || []);
      setSelectedPermits((data.permitTypes || []).map(p => p._id));
      // Use server-returned coverage status (full if jurisdiction exists, none if not)
      setCoverageStatus(data.coverage || (data.permitTypes?.length > 0 ? 'full' : 'none'));
    } catch (err) { console.error(err); setCoverageStatus('none'); }
    finally { setLoadingPermits(false); }
  };

  const handleStep2Complete = () => {
    fetchSuggestedPermits();
    setStep(3);
  };

  const togglePermit = (id) => {
    setSelectedPermits(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const selectAllPermits = () => {
    setSelectedPermits(suggestedPermits.map(p => p._id));
  };

  const submitCitySuggestion = async () => {
    try {
      await api.post('/suggestions', {
        type: 'city_request',
        title: `Add coverage for ${formData.operatingCities[0].city}, ${formData.operatingCities[0].state}`,
        description: `Vendor type: ${formData.primaryVendorType}`,
        cityDetails: {
          city: formData.operatingCities[0].city,
          state: formData.operatingCities[0].state
        }
      });
      setSuggestionSubmitted(true);
      setShowSuggestionModal(false);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try { 
      const data = await api.post('/onboarding/complete', { ...formData, selectedPermitTypes: selectedPermits }); 
      updateBusiness(data.business); 
      localStorage.setItem('permitwise_onboarding_complete', 'true');
      onComplete(); 
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const getImportanceLabel = (permit) => {
    if (permit.importanceLevel === 'critical') return { text: 'Critical', variant: 'danger' };
    if (permit.importanceLevel === 'often_forgotten') return { text: 'Often Forgotten', variant: 'warning' };
    if (permit.importanceLevel === 'event_required') return { text: 'Required for Events', variant: 'info' };
    return null;
  };

  return (
    <div className="onboarding-page"><div className="onboarding-container">
      <div className="onboarding-header">
        <Icons.Shield />
        <div className="onboarding-progress"><span className={step >= 1 ? 'active' : ''}>1</span><span className={step >= 2 ? 'active' : ''}>2</span><span className={step >= 3 ? 'active' : ''}>3</span></div>
      </div>
      {error && <Alert type="error">{error}</Alert>}
      
      {step === 1 && (
        <div className="onboarding-step">
          <h1>Tell us about your business</h1>
          <p className="step-subtitle">This helps us find the right permits for you</p>
          <Input label="Business Name *" placeholder="e.g. Taco Express" value={formData.businessName} onChange={(e) => setFormData(f => ({ ...f, businessName: e.target.value }))} />
          <Select label="What type of vendor are you? *" value={formData.primaryVendorType} onChange={(e) => setFormData(f => ({ ...f, primaryVendorType: e.target.value }))} options={[{ value: '', label: 'Select your business type' }, ...VENDOR_TYPES]} />
          
          {formData.primaryVendorType && !FOOD_VENDOR_TYPES.includes(formData.primaryVendorType) && (
            <label className="checkbox-row">
              <input type="checkbox" checked={formData.handlesFood} onChange={(e) => setFormData(f => ({ ...f, handlesFood: e.target.checked }))} />
              <span>My business handles or serves food</span>
            </label>
          )}
          
          <Button onClick={() => setStep(2)} disabled={!formData.businessName || !formData.primaryVendorType} className="full-width">Continue →</Button>
        </div>
      )}

      {step === 2 && (
        <div className="onboarding-step">
          <h1>Where do you operate?</h1>
          <p className="step-subtitle">We'll find the permits required in your city</p>
          <div className="form-row">
            <Select label="State *" value={formData.operatingCities[0].state} onChange={(e) => setFormData(f => ({ ...f, operatingCities: [{ ...f.operatingCities[0], state: e.target.value, city: '' }] }))} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
            <CitySearch 
              label="Primary City *" 
              state={formData.operatingCities[0].state}
              value={formData.operatingCities[0].city}
              onChange={(city) => setFormData(f => ({ ...f, operatingCities: [{ ...f.operatingCities[0], city }] }))}
              placeholder="Search for your city..."
              allowCustom={true}
            />
          </div>
          <p className="onboarding-note">You can add more cities later in Settings.</p>
          <div className="onboarding-actions">
            <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
            <Button onClick={handleStep2Complete} disabled={!formData.operatingCities[0].city || !formData.operatingCities[0].state}>Find My Permits →</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="onboarding-step">
          <h1>Let's get your permits organized</h1>
          {loadingPermits ? <LoadingSpinner /> : (
            <>
              {coverageStatus === 'full' && (
                <p className="step-subtitle">Here are the permits most {VENDOR_TYPES.find(v => v.value === formData.primaryVendorType)?.label || 'vendors'}s need in {formData.operatingCities[0].city}.</p>
              )}
              {coverageStatus === 'partial' && (
                <div className="coverage-notice">
                  <Icons.Alert />
                  <p>We're still adding full permit coverage for {formData.operatingCities[0].city}. You can still track all your permits manually. We'll notify you as soon as full support is ready.</p>
                  {!suggestionSubmitted && <Button variant="outline" size="sm" onClick={() => setShowSuggestionModal(true)}>Request Full Coverage</Button>}
                  {suggestionSubmitted && <Badge variant="success">Request Submitted!</Badge>}
                </div>
              )}
              {coverageStatus === 'none' && (
                <div className="coverage-notice">
                  <Icons.Alert />
                  <p>We're still building coverage for {formData.operatingCities[0].city}. You can still track all your permits manually — just add them after setup. We'll notify you when full support is ready!</p>
                  {!suggestionSubmitted && <Button variant="outline" size="sm" onClick={() => setShowSuggestionModal(true)}>Request Coverage for My City</Button>}
                  {suggestionSubmitted && <Badge variant="success">Request Submitted - Thanks!</Badge>}
                </div>
              )}
              
              {suggestedPermits.length > 0 && (
                <div className="permit-suggestions">
                  <div className="permit-suggestions-header">
                    <span>{selectedPermits.length} of {suggestedPermits.length} selected</span>
                    <button type="button" onClick={selectAllPermits}>Select All</button>
                  </div>
                  <div className="permit-checkbox-list">
                    {suggestedPermits.map(permit => {
                      const label = getImportanceLabel(permit);
                      return (
                        <label key={permit._id} className={`permit-checkbox ${selectedPermits.includes(permit._id) ? 'selected' : ''}`}>
                          <input type="checkbox" checked={selectedPermits.includes(permit._id)} onChange={() => togglePermit(permit._id)} />
                          <div className="permit-checkbox-content">
                            <div className="permit-checkbox-header">
                              <span className="permit-checkbox-name">{permit.name}</span>
                              {label && <Badge variant={label.variant}>{label.text}</Badge>}
                            </div>
                            {permit.issuingAuthorityName && <span className="permit-issuer">Issued by: {permit.issuingAuthorityName}</span>}
                            <span className="permit-renewal">Renews every {permit.renewalPeriodMonths || 12} months</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="onboarding-actions">
            <Button variant="outline" onClick={() => setStep(2)}>← Back</Button>
            <Button onClick={handleSubmit} loading={loading}>Complete Setup →</Button>
          </div>
        </div>
      )}
      
      {/* City Coverage Suggestion Modal */}
      <Modal isOpen={showSuggestionModal} onClose={() => setShowSuggestionModal(false)} title="Request City Coverage">
        <p>We'll prioritize adding permit data for <strong>{formData.operatingCities[0].city}, {formData.operatingCities[0].state}</strong>.</p>
        <p>Once coverage is ready, you'll be able to see suggested permits and get renewal reminders specific to your city's requirements.</p>
        <div className="modal-actions">
          <Button variant="outline" onClick={() => setShowSuggestionModal(false)}>Cancel</Button>
          <Button onClick={submitCitySuggestion}>Submit Request</Button>
        </div>
      </Modal>
    </div></div>
  );
};

const Dashboard = ({ onNavigate }) => {
  const { user, business, subscription } = useAuth();
  const [stats, setStats] = useState(null); const [loading, setLoading] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => { 
    const fetchStats = async () => { 
      try {
        // First sync to pick up any new permit types
        await api.post('/permits/sync');
        // Then get dashboard stats
        const data = await api.get('/stats/dashboard');
        setStats(data.stats);
      } 
      catch (error) { console.error(error); } 
      finally { setLoading(false); } 
    }; 
    fetchStats();
    // Check for first-time user after onboarding
    if (localStorage.getItem('permitwise_onboarding_complete') === 'true') {
      setShowWelcomeModal(true);
      localStorage.removeItem('permitwise_onboarding_complete');
    }
  }, []);

  const handleResendVerification = async () => {
    try {
      await api.post('/auth/resend-verification');
      setResendMessage('Verification email sent! Check your inbox.');
      setTimeout(() => setResendMessage(''), 5000);
    } catch (error) {
      setResendMessage(error.message || 'Failed to send email. Please try again.');
      setTimeout(() => setResendMessage(''), 5000);
    }
  };

  const getExpiryLabel = (daysUntil) => {
    if (daysUntil <= 0) return { text: 'Expired!', variant: 'danger' };
    if (daysUntil <= 7) return { text: `Expires in ${daysUntil} day${daysUntil > 1 ? 's' : ''} — inspectors often check this first!`, variant: 'danger' };
    if (daysUntil <= 30) return { text: `Expires in ${daysUntil} days — start renewal now`, variant: 'warning' };
    return { text: `${daysUntil} days left`, variant: 'warning' };
  };

  if (loading) return <LoadingSpinner />;

  const needsAttention = (stats?.permits?.missing || 0) + (stats?.permits?.expired || 0) + (stats?.permits?.pendingRenewal || 0);

  return (
    <div className="dashboard">
      {!user?.emailVerified && (
        <div className="email-banner">
          <Icons.Bell /> Verify your email to ensure you receive permit alerts. 
          <button onClick={handleResendVerification}>Resend</button>
          {resendMessage && <span className="resend-message">{resendMessage}</span>}
        </div>
      )}
      <div className="dashboard-header"><div><h1>Welcome, {business?.businessName}!</h1><p>Compliance overview</p></div>{subscription?.status === 'trial' && <Alert type="warning">Trial ends {formatDate(subscription.trialEndsAt)}</Alert>}</div>
      {needsAttention > 0 && (
        <Card className="aha-banner">
          <div className="aha-content">
            <div className="aha-icon"><Icons.Alert /></div>
            <div className="aha-text">
              <h2>Action Required</h2>
              <p>{stats?.permits?.missing > 0 && <span className="aha-item red">⚠️ {stats.permits.missing} permit{stats.permits.missing > 1 ? 's' : ''} missing</span>}
              {stats?.permits?.expired > 0 && <span className="aha-item red">🚨 {stats.permits.expired} expired — act now!</span>}
              {stats?.permits?.pendingRenewal > 0 && <span className="aha-item yellow">🟡 {stats.permits.pendingRenewal} expiring soon — inspectors check these first!</span>}</p>
            </div>
          </div>
          <div className="aha-actions"><Button onClick={() => onNavigate('permits')}>Fix Now →</Button></div>
        </Card>
      )}
      <div className="stats-grid">
        <Card className="stat-card"><div className="stat-icon green"><Icons.Check /></div><div className="stat-info"><span className="stat-value">{stats?.permits?.active || 0}</span><span className="stat-label">Active</span></div></Card>
        <Card className="stat-card"><div className="stat-icon yellow"><Icons.Clock /></div><div className="stat-info"><span className="stat-value">{stats?.permits?.pendingRenewal || 0}</span><span className="stat-label">Expiring</span></div></Card>
        <Card className="stat-card"><div className="stat-icon red"><Icons.Alert /></div><div className="stat-info"><span className="stat-value">{stats?.permits?.expired || 0}</span><span className="stat-label">Expired</span></div></Card>
        <Card className="stat-card"><div className="stat-icon gray"><Icons.Document /></div><div className="stat-info"><span className="stat-value">{stats?.permits?.missing || 0}</span><span className="stat-label">Missing</span></div></Card>
      </div>
      <div className="dashboard-row">
        <Card className="compliance-card"><h2>Compliance Score</h2><div className="compliance-score"><div className="score-circle" style={{ '--score': stats?.complianceScore || 0 }}><span>{stats?.complianceScore || 0}%</span></div></div></Card>
        <Card className="upcoming-card">
          <h2>Upcoming Expirations</h2>
          {stats?.upcomingExpirations?.length > 0 ? (
            <ul className="expiration-list">
              {stats.upcomingExpirations.slice(0, 5).map(p => {
                const label = getExpiryLabel(p.daysUntil);
                return (
                  <li key={p.id} className="expiration-item">
                    <span className="expiration-name">{p.name}</span>
                    <Badge variant={label.variant}>{label.text}</Badge>
                  </li>
                );
              })}
            </ul>
          ) : <p>No upcoming expirations</p>}
        </Card>
      </div>

      {/* Post-Onboarding Welcome Modal */}
      <Modal isOpen={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} title="">
        <div className="welcome-modal">
          <div className="welcome-icon"><Icons.Check /></div>
          <h2>You're all set! 🎉</h2>
          <p>We've added the permits you're likely to need. View them to start tracking expirations and uploading documents.</p>
          <div className="welcome-actions">
            <Button onClick={() => { setShowWelcomeModal(false); onNavigate('permits'); }}><Icons.Document /> View Permits</Button>
            <Button variant="outline" onClick={() => setShowWelcomeModal(false)}>I'll do it later</Button>
          </div>
        </div>
      </Modal>
      <UploadModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} onSuccess={() => setShowUploadModal(false)} />
    </div>
  );
};

const PermitsPage = () => {
  const { business, canWrite, isExpired, updateBusiness } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [permits, setPermits] = useState([]); const [summary, setSummary] = useState(null); const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false); const [showCityModal, setShowCityModal] = useState(false); const [selectedPermit, setSelectedPermit] = useState(null);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestedPermits, setSuggestedPermits] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [addingSuggestions, setAddingSuggestions] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [removingCity, setRemovingCity] = useState(null);

  // Handler for Add Permit button - checks subscription
  const handleAddPermit = () => {
    if (!canWrite) {
      setShowUpgradeModal(true);
      return;
    }
    setShowAddModal(true);
  };
  
  // Remove city handler
  const handleRemoveCity = async (city, state) => {
    // Find the city to check if it's primary
    const cityObj = business?.operatingCities?.find(c => c.city === city && c.state === state);
    if (cityObj?.isPrimary) {
      toast.error('Cannot remove your primary city. Please go to Settings → Cities to set another city as primary first.');
      return;
    }
    
    const confirmed = await confirm({
      title: 'Remove City',
      message: `Are you sure you want to remove ${city}, ${state}? Permits for this city will be deleted. Documents will remain in your vault.`,
      confirmText: 'Remove City',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!confirmed) return;
    
    setRemovingCity(`${city}-${state}`);
    try {
      // Use POST endpoint instead of DELETE with body (more reliable)
      const result = await api.post('/permits/remove-city', { city, state });
      toast.success(result.message);
      // Refresh business data and permits
      const businessData = await api.get('/business');
      updateBusiness(businessData.business);
      fetchPermits();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRemovingCity(null);
    }
  };

  const syncAndFetchPermits = async () => {
    try {
      // First sync to pick up any new permit types
      await api.post('/permits/sync');
      // Then fetch all permits
      const data = await api.get('/permits');
      setPermits(data.permits);
      setSummary(data.summary);
    } catch (error) { console.error(error); } finally { setLoading(false); }
  };
  
  const fetchPermits = async () => { try { const data = await api.get('/permits'); setPermits(data.permits); setSummary(data.summary); } catch (error) { console.error(error); } finally { setLoading(false); } };
  
  const fetchSuggestedPermits = async () => {
    if (!canWrite) return; // Don't show suggestions if can't add permits
    if (!business?.operatingCities?.[0]) return;
    setLoadingSuggestions(true);
    try {
      const city = business.operatingCities.find(c => c.isPrimary) || business.operatingCities[0];
      const data = await api.get(`/permit-types/required?city=${city.city}&state=${city.state}&vendorType=${business.primaryVendorType}`);
      if (data.permitTypes?.length > 0) {
        setSuggestedPermits(data.permitTypes);
        setSelectedSuggestions(data.permitTypes.map(p => p._id));
        setShowSuggestModal(true);
      }
    } catch (err) { console.error(err); }
    finally { setLoadingSuggestions(false); }
  };

  useEffect(() => { 
    syncAndFetchPermits(); // Sync on initial load to pick up new permit types
  }, []);

  // Show suggestion modal when permits are empty and user has a business
  useEffect(() => {
    if (!loading && permits.length === 0 && business?.operatingCities?.length > 0 && canWrite) {
      fetchSuggestedPermits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, permits.length, business, canWrite]);

  const toggleSuggestion = (id) => {
    setSelectedSuggestions(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const addSuggestedPermits = async () => {
    if (selectedSuggestions.length === 0) { setShowSuggestModal(false); return; }
    setAddingSuggestions(true);
    try {
      for (const permitTypeId of selectedSuggestions) {
        const pt = suggestedPermits.find(p => p._id === permitTypeId);
        if (pt) {
          await api.post('/permits', { permitTypeId, jurisdictionId: pt.jurisdictionId._id || pt.jurisdictionId, status: 'missing' });
        }
      }
      setShowSuggestModal(false);
      fetchPermits();
      toast.success('Permits added successfully!');
    } catch (err) { toast.error(err.message); }
    finally { setAddingSuggestions(false); }
  };

  const getImportanceLabel = (level) => {
    if (level === 'critical') return { text: 'Critical', variant: 'danger' };
    if (level === 'often_forgotten') return { text: 'Often Forgotten', variant: 'warning' };
    if (level === 'event_required') return { text: 'Event Required', variant: 'info' };
    return null;
  };

  if (loading) return <LoadingSpinner />;

  // Group permits by location
  const groupPermitsByLocation = (permitsList) => {
    const groups = {};
    for (const permit of permitsList) {
      const jur = permit.jurisdictionId;
      let key, label;
      if (jur?.name === 'National (All US)' || jur?.state === 'US') {
        key = '_NATIONAL'; label = 'National / Federal';
      } else if (permit.isCustom) {
        key = `${permit.customCity}_${permit.customState}`; label = `${permit.customCity}, ${permit.customState}`;
      } else if (jur) {
        key = `${jur.city || jur.name}_${jur.state}`; label = `${jur.city || jur.name}, ${jur.state}`;
      } else {
        key = '_OTHER'; label = 'Other';
      }
      if (!groups[key]) groups[key] = { label, permits: [] };
      groups[key].permits.push(permit);
    }
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '_NATIONAL') return -1; if (b === '_NATIONAL') return 1;
      if (a === '_OTHER') return 1; if (b === '_OTHER') return -1;
      return a.localeCompare(b);
    });
  };

  const hasMultipleCities = (business?.operatingCities?.length || 0) > 1;

  const renderPermitCard = (permit) => (
    <Card key={permit._id} className="permit-card" onClick={() => setSelectedPermit(permit)}>
      <div className="permit-header"><h3>{permit.isCustom ? permit.customName : permit.permitTypeId?.name}</h3><Badge variant={permit.status === 'active' ? 'success' : permit.status === 'expired' ? 'danger' : 'warning'}>{getStatusLabel(permit.status)}</Badge></div>
      <p>{permit.isCustom ? `${permit.customCity}, ${permit.customState}` : `${permit.jurisdictionId?.name}, ${permit.jurisdictionId?.state}`}</p>
      {permit.expiryDate && <div className="expiry-info"><Icons.Clock /><span>Expires {formatDate(permit.expiryDate)}</span></div>}
    </Card>
  );

  return (
    <div className="permits-page">
      <div className="page-header"><div><h1>Permits</h1><p>Track all your permits</p></div><div className="header-actions"><Button variant="outline" onClick={() => setShowCityModal(true)}><Icons.MapPin /> Add City</Button><Button onClick={handleAddPermit}><Icons.Plus /> Add Permit</Button></div></div>
      
      {/* Operating Cities Section */}
      {business?.operatingCities?.length > 0 && (
        <div className="operating-cities-section">
          <h3>Operating Cities</h3>
          <div className="cities-list">
            {business.operatingCities.map((city, idx) => (
              <div key={idx} className={`city-tag ${city.isPrimary ? 'primary' : ''}`}>
                <span>{city.city}, {city.state}</span>
                {city.isPrimary && <Badge variant="primary" size="sm">Primary</Badge>}
                {business.operatingCities.length > 1 && (
                  <button 
                    className="remove-city-btn" 
                    onClick={() => handleRemoveCity(city.city, city.state)}
                    disabled={removingCity === `${city.city}-${city.state}`}
                    title="Remove city"
                  >
                    {removingCity === `${city.city}-${city.state}` ? <LoadingSpinner /> : <Icons.X />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {summary && <div className="permits-summary"><div className="summary-item"><span className="count">{summary.total}</span><span>Total</span></div><div className="summary-item green"><span className="count">{summary.active}</span><span>Active</span></div><div className="summary-item yellow"><span className="count">{summary.pendingRenewal}</span><span>Expiring</span></div><div className="summary-item red"><span className="count">{summary.expired}</span><span>Expired</span></div></div>}
      {permits.length > 0 ? (
        hasMultipleCities ? (
          <div className="permits-grouped">
            {groupPermitsByLocation(permits).map(([key, group]) => (
              <div key={key} className="permit-group">
                <div className="permit-group-header">
                  <h3>{key === '_NATIONAL' ? '🌐' : '📍'} {group.label}</h3>
                  <Badge variant="default">{group.permits.length}</Badge>
                </div>
                <div className="permits-grid">{group.permits.map(renderPermitCard)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="permits-grid">{permits.map(renderPermitCard)}</div>
        )
      ) : (
        <div className="empty-permits">
          <EmptyState icon={Icons.Permit} title="No permits yet" description="Add the permits you need to track or let us suggest them based on your city." action={<div className="empty-actions"><Button onClick={() => canWrite ? fetchSuggestedPermits() : setShowUpgradeModal(true)}><Icons.Search /> Get Suggestions</Button><Button variant="outline" onClick={() => setShowCityModal(true)}><Icons.MapPin /> Add City</Button></div>} />
        </div>
      )}
      <AddCityModal isOpen={showCityModal} onClose={() => setShowCityModal(false)} onSuccess={fetchPermits} updateBusiness={updateBusiness} toast={toast} />
      <AddPermitModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSuccess={fetchPermits} toast={toast} business={business} onAddCity={() => { setShowAddModal(false); setShowCityModal(true); }} />
      <PermitDetailModal permit={selectedPermit} onClose={() => setSelectedPermit(null)} onUpdate={fetchPermits} />
      
      {/* Suggested Permits Modal */}
      <Modal isOpen={showSuggestModal} onClose={() => setShowSuggestModal(false)} title="Suggested Permits" size="lg">
        <div className="suggest-modal">
          <p className="suggest-intro">Based on your business in <strong>{business?.operatingCities?.[0]?.city}, {business?.operatingCities?.[0]?.state}</strong>, here are the permits most {VENDOR_TYPES.find(v => v.value === business?.primaryVendorType)?.label || 'vendors'}s need:</p>
          
          {loadingSuggestions ? <LoadingSpinner /> : (
            <>
              <div className="suggest-header">
                <span>{selectedSuggestions.length} of {suggestedPermits.length} selected</span>
                <button type="button" onClick={() => setSelectedSuggestions(suggestedPermits.map(p => p._id))}>Select All</button>
              </div>
              
              <div className="suggest-list">
                {suggestedPermits.map(permit => {
                  const label = getImportanceLabel(permit.importanceLevel);
                  const isSelected = selectedSuggestions.includes(permit._id);
                  return (
                    <label key={permit._id} className={`suggest-item ${isSelected ? 'selected' : ''}`}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSuggestion(permit._id)} />
                      <div className="suggest-content">
                        <div className="suggest-item-header">
                          <span className="suggest-name">{permit.name}</span>
                          {label && <Badge variant={label.variant}>{label.text}</Badge>}
                        </div>
                        {permit.description && <p className="suggest-desc">{permit.description}</p>}
                        <div className="suggest-meta">
                          {permit.issuingAuthorityName && <span>Issued by: {permit.issuingAuthorityName}</span>}
                          <span>Renews every {permit.renewalPeriodMonths || 12} months</span>
                          {permit.estimatedCost && <span>Cost: {permit.estimatedCost}</span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              
              {suggestedPermits.length === 0 && (
                <div className="suggest-empty">
                  <p>We don't have permit data for your city yet. You can add permits manually.</p>
                </div>
              )}
            </>
          )}
          
          <div className="modal-actions">
            <Button variant="outline" onClick={() => setShowSuggestModal(false)}>Skip for Now</Button>
            <Button onClick={addSuggestedPermits} loading={addingSuggestions} disabled={selectedSuggestions.length === 0}>
              Add {selectedSuggestions.length} Permit{selectedSuggestions.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Upgrade Modal for expired subscriptions */}
      <UpgradeRequiredModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        reason={isExpired ? "Your subscription has expired" : "This feature requires an active subscription"}
        feature="Permit Management"
      />
    </div>
  );
};

const AddCityModal = ({ isOpen, onClose, onSuccess, updateBusiness, toast }) => {
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [jurisdictions, setJurisdictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showCustomEntry, setShowCustomEntry] = useState(false);
  
  // Fetch jurisdictions when state changes
  useEffect(() => {
    if (!state) {
      setJurisdictions([]);
      setCity('');
      setCustomCity('');
      setShowCustomEntry(false);
      return;
    }
    const fetchJurisdictions = async () => {
      try {
        const data = await api.get('/jurisdictions');
        const filtered = (data.jurisdictions || []).filter(j => j.state === state);
        setJurisdictions(filtered);
        setCity('');
        setCustomCity('');
      } catch (err) {
        console.error(err);
      }
    };
    fetchJurisdictions();
  }, [state]);
  
  const handleAdd = async () => {
    const cityToAdd = showCustomEntry ? customCity : city;
    if (!cityToAdd || !state) return;
    
    setLoading(true);
    try {
      const data = await api.post('/permits/add-city', { city: cityToAdd, state });
      if (data.business) updateBusiness?.(data.business);
      toast?.success(data.message || `${cityToAdd}, ${state} added successfully!`);
      onSuccess();
      handleClose();
    } catch (err) {
      toast?.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const cityOptions = jurisdictions.map(j => ({ value: j.city || j.name, label: j.city || j.name }));
  
  const handleCityChange = (e) => {
    const val = e.target.value;
    if (val === '__custom__') {
      setShowCustomEntry(true);
      setCity('');
    } else {
      setCity(val);
      setShowCustomEntry(false);
      setCustomCity('');
    }
  };
  
  const handleClose = () => {
    setState('');
    setCity('');
    setCustomCity('');
    setShowCustomEntry(false);
    setResult(null);
    onClose();
  };
  
  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Operating City">
      {result && <Alert type="success">{result.message}</Alert>}
      <p>Add a city where you operate. Select from cities with permit data, or add a custom city for manual tracking.</p>
      <div className="form-row">
        <Select 
          label="State" 
          value={state} 
          onChange={(e) => setState(e.target.value)} 
          options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} 
        />
        <Select 
          label="City" 
          value={showCustomEntry ? '__custom__' : city} 
          onChange={handleCityChange} 
          options={[
            { value: '', label: state ? 'Select City' : 'Select state first' },
            ...cityOptions,
            ...(state ? [{ value: '__custom__', label: '➕ Add Other City...' }] : [])
          ]}
          disabled={!state}
        />
      </div>
      
      {showCustomEntry && (
        <div className="custom-city-entry">
          <Input 
            label="City Name" 
            value={customCity} 
            onChange={(e) => setCustomCity(e.target.value)} 
            placeholder="Enter city name"
          />
          <p className="form-hint">
            <Icons.Info /> This city doesn't have permit data yet. You can still add it and track permits manually.
          </p>
        </div>
      )}
      
      {state && cityOptions.length === 0 && !showCustomEntry && (
        <Alert type="info">
          No cities with permit data in {state} yet. Select "Add Other City" to add a city for manual tracking.
        </Alert>
      )}
      
      <div className="modal-actions">
        <Button variant="outline" onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleAdd} 
          loading={loading} 
          disabled={!state || (!city && !customCity)}
        >
          Add City
        </Button>
      </div>
    </Modal>
  );
};

const AddPermitModal = ({ isOpen, onClose, onSuccess, toast, business, onAddCity }) => {
  const [selectedCity, setSelectedCity] = useState('');
  const [permitTypes, setPermitTypes] = useState([]);
  const [selectedPermit, setSelectedPermit] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [customState, setCustomState] = useState('');

  const operatingCities = business?.operatingCities || [];
  const vendorType = business?.primaryVendorType || '';

  // Auto-search when city selected
  useEffect(() => {
    if (!selectedCity || !vendorType) return;
    const [city, state] = selectedCity.split('||');
    if (!city || !state) return;
    setSearching(true);
    setSearched(false);
    setSelectedPermit('');
    setPermitTypes([]);
    api.get(`/permit-types/required?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&vendorType=${encodeURIComponent(vendorType)}`)
      .then(data => { setPermitTypes(data.permitTypes || []); setSearched(true); })
      .catch(err => { console.error(err); setSearched(true); })
      .finally(() => setSearching(false));
  }, [selectedCity, vendorType]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSelectedCity(''); setPermitTypes([]); setSelectedPermit(''); setSearched(false); setShowCustom(false); setCustomName(''); setCustomCity(''); setCustomState('');
    }
  }, [isOpen]);

  const handleAdd = async () => {
    if (!selectedPermit) return;
    setLoading(true);
    try {
      const pt = permitTypes.find(p => p._id === selectedPermit);
      await api.post('/permits', { permitTypeId: selectedPermit, jurisdictionId: pt.jurisdictionId._id || pt.jurisdictionId, status: 'missing' });
      onSuccess(); onClose(); toast?.success('Permit added!');
    } catch (err) { toast?.error(err.message); }
    finally { setLoading(false); }
  };

  const handleAddCustom = async () => {
    if (!customName.trim()) return;
    setLoading(true);
    try {
      // For custom permits, use the selected operating city or the custom city
      let city, state;
      if (selectedCity) {
        [city, state] = selectedCity.split('||');
      } else {
        city = customCity; state = customState;
      }
      if (!city || !state) { toast?.error('Please select or enter a city'); setLoading(false); return; }
      
      // Create via custom permit endpoint
      await api.post('/permits/custom', { name: customName.trim(), city, state });
      onSuccess(); onClose(); toast?.success('Custom permit added!');
    } catch (err) { toast?.error(err.message); }
    finally { setLoading(false); }
  };

  const cityOptions = [
    { value: '', label: 'Select an operating city' },
    ...operatingCities.map(c => ({ value: `${c.city}||${c.state}`, label: `${c.city}, ${c.state}${c.isPrimary ? ' (Primary)' : ''}` }))
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Permit" size="lg">
      <Select label="Operating City" value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} options={cityOptions} />
      {operatingCities.length === 0 ? (
        <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: '4px 0 16px' }}>You haven't added any operating cities yet. <button type="button" onClick={() => onAddCity()} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}>Add a city first</button></p>
      ) : (
        <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: '4px 0 16px' }}>Don't see your city? <button type="button" onClick={() => onAddCity()} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}>Add a new operating city</button></p>
      )}

      {searching && <div style={{ textAlign: 'center', padding: '1rem' }}><LoadingSpinner /><p style={{ color: 'var(--gray-500)', marginTop: '0.5rem', fontSize: '0.875rem' }}>Finding required permits...</p></div>}

      {searched && permitTypes.length > 0 && (
        <div className="permit-options" style={{ marginTop: '0.5rem' }}>
          <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Required permits for this city:</p>
          {permitTypes.map(pt => (
            <label key={pt._id} className={`permit-option ${selectedPermit === pt._id ? 'selected' : ''}`}>
              <input type="radio" name="permit" value={pt._id} checked={selectedPermit === pt._id} onChange={(e) => setSelectedPermit(e.target.value)} />
              <span>{pt.name}</span>
            </label>
          ))}
        </div>
      )}

      {searched && permitTypes.length === 0 && (
        <div style={{ background: 'var(--warning-bg, #fef3c7)', border: '1px solid var(--warning-border, #fcd34d)', borderRadius: '8px', padding: '12px 16px', margin: '8px 0' }}>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', margin: 0 }}>No predefined permits found for this city yet. You can add a custom permit below, or <button type="button" onClick={() => {}} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}>suggest this city</button> to our team.</p>
        </div>
      )}

      {(searched || !selectedCity) && (
        <div style={{ borderTop: '1px solid var(--gray-200)', marginTop: '16px', paddingTop: '16px' }}>
          {!showCustom ? (
            <Button variant="outline" size="sm" onClick={() => setShowCustom(true)}>+ Add Custom Permit</Button>
          ) : (
            <div>
              <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Custom Permit</p>
              <Input label="Permit Name *" placeholder="e.g., Mobile Vending License" value={customName} onChange={(e) => setCustomName(e.target.value)} />
              {!selectedCity && (
                <div className="form-row" style={{ marginTop: '0.5rem' }}>
                  <Input label="City *" value={customCity} onChange={(e) => setCustomCity(e.target.value)} />
                  <Select label="State *" value={customState} onChange={(e) => setCustomState(e.target.value)} options={[{ value: '', label: 'Select' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                </div>
              )}
              <div className="modal-actions" style={{ marginTop: '0.75rem' }}>
                <Button variant="outline" size="sm" onClick={() => { setShowCustom(false); setCustomName(''); }}>Cancel</Button>
                <Button size="sm" onClick={handleAddCustom} loading={loading} disabled={!customName.trim()}>Add Custom Permit</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="modal-actions">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        {selectedPermit && <Button onClick={handleAdd} loading={loading}>Add Permit</Button>}
      </div>
    </Modal>
  );
};

const PermitDetailModal = ({ permit, onClose, onUpdate }) => {
  const { subscription } = useAuth();
  const toast = useToast();
  const [editing, setEditing] = useState(false); 
  const [formData, setFormData] = useState({}); 
  const [loading, setLoading] = useState(false); 
  const [uploading, setUploading] = useState(false); 
  const [uploadError, setUploadError] = useState('');
  const [localPermit, setLocalPermit] = useState(null);
  
  useEffect(() => { 
    if (permit) {
      setLocalPermit(permit);
      setFormData({ 
        status: permit.status, 
        permitNumber: permit.permitNumber || '', 
        issueDate: permit.issueDate?.split('T')[0] || '', 
        expiryDate: permit.expiryDate?.split('T')[0] || '', 
        notes: permit.notes || '' 
      });
    }
  }, [permit]);
  
  // Early return if no permit
  if (!permit || !localPermit) return null;
  
  const handleSave = async () => { 
    setLoading(true); 
    try { 
      const response = await api.put(`/permits/${localPermit._id}`, formData); 
      setLocalPermit(response.permit);
      setFormData({ 
        status: response.permit.status, 
        permitNumber: response.permit.permitNumber || '', 
        issueDate: response.permit.issueDate?.split('T')[0] || '', 
        expiryDate: response.permit.expiryDate?.split('T')[0] || '', 
        notes: response.permit.notes || '' 
      });
      onUpdate(); 
      setEditing(false); 
    } catch (error) { console.error(error); } finally { setLoading(false); } 
  };
  
  const handleUpload = async (e) => { 
    const file = e.target.files[0]; 
    if (!file) return;
    setUploadError('');
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(file.type) && !['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(ext)) {
      setUploadError('Invalid file type. Allowed: PDF, JPG, PNG, GIF, WebP');
      e.target.value = ''; return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 10MB.');
      e.target.value = ''; return;
    }
    setUploading(true); 
    const fd = new FormData(); 
    fd.append('file', file); 
    fd.append('category', 'permit'); 
    fd.append('relatedEntityType', 'permit'); 
    fd.append('relatedEntityId', localPermit._id); 
    try { 
      const docResponse = await api.upload('/documents', fd);
      // Update local permit with the new document (add to documents array)
      setLocalPermit(prev => ({
        ...prev,
        documentId: prev.documentId || docResponse.document,
        documents: [...(prev.documents || []), docResponse.document]
      }));
      onUpdate(); 
    } catch (error) { setUploadError(error.message); } finally { setUploading(false); e.target.value = ''; } 
  };
  const handleAutofill = async () => { try { const data = await api.post('/autofill/generate', { permitTypeId: localPermit.permitTypeId._id }); window.open(getSecureFileUrl(data.downloadUrl), '_blank'); } catch (error) { toast.error(error.message); } };
  
  const getImportanceLabel = (level) => {
    if (level === 'critical') return { text: 'Critical', variant: 'danger' };
    if (level === 'often_forgotten') return { text: 'Often Forgotten', variant: 'warning' };
    if (level === 'event_required') return { text: 'Required for Events', variant: 'info' };
    return null;
  };
  const importanceLabel = localPermit.isCustom ? null : getImportanceLabel(localPermit.permitTypeId?.importanceLevel);
  const permitName = localPermit.isCustom ? localPermit.customName : localPermit.permitTypeId?.name;
  const permitLocation = localPermit.isCustom ? `${localPermit.customCity}, ${localPermit.customState}` : `${localPermit.jurisdictionId?.name}, ${localPermit.jurisdictionId?.state}`;

  return (
    <Modal isOpen={!!permit} onClose={onClose} title={permitName} size="lg">
      <div className="permit-detail">
        <div className="detail-header">
          <div className="detail-badges">
            <Badge variant={localPermit.status === 'active' ? 'success' : localPermit.status === 'expired' ? 'danger' : 'warning'}>{getStatusLabel(localPermit.status)}</Badge>
            {importanceLabel && <Badge variant={importanceLabel.variant}>{importanceLabel.text}</Badge>}
            {localPermit.isCustom && <Badge variant="primary">Custom</Badge>}
          </div>
          <span>{permitLocation}</span>
        </div>
        
        {/* Missing Permit CTA */}
        {localPermit.status === 'missing' && (
          <div className="missing-permit-cta">
            <Icons.Alert />
            <p>You don't have this permit yet. Add details now so we can track expiration reminders.</p>
            <Button size="sm" onClick={() => setEditing(true)}>Add Details →</Button>
          </div>
        )}
        
        {/* Permit Type Info Panel */}
        <div className="permit-type-info">
          <h4>Permit Information</h4>
          <div className="info-grid">
            <div className="info-item">
              <Icons.Clock />
              <div><span className="info-label">Renewal Frequency</span><span className="info-value">Renews every {localPermit.permitTypeId?.renewalPeriodMonths || 12} months</span></div>
            </div>
            {localPermit.permitTypeId?.issuingAuthorityName && (
              <div className="info-item">
                <Icons.Shield />
                <div><span className="info-label">Issued By</span><span className="info-value">{localPermit.permitTypeId.issuingAuthorityName}</span></div>
              </div>
            )}
            {localPermit.permitTypeId?.estimatedCost && (
              <div className="info-item">
                <Icons.Document />
                <div><span className="info-label">Typical Cost</span><span className="info-value">{localPermit.permitTypeId.estimatedCost}</span></div>
              </div>
            )}
          </div>
          {localPermit.permitTypeId?.requiredDocuments?.length > 0 && (
            <div className="required-docs">
              <span className="info-label">Usually Required:</span>
              <span className="info-value">{localPermit.permitTypeId.requiredDocuments.join(', ')}</span>
            </div>
          )}
        </div>
        
        {editing ? (
          <div className="detail-form">
            <Select label="Status" value={formData.status} onChange={(e) => setFormData(f => ({ ...f, status: e.target.value }))} options={[{ value: 'missing', label: 'Missing' }, { value: 'in_progress', label: 'In Progress' }, { value: 'active', label: 'Active' }, { value: 'expired', label: 'Expired' }]} />
            <Input label="Permit Number" value={formData.permitNumber} onChange={(e) => setFormData(f => ({ ...f, permitNumber: e.target.value }))} />
            <div className="form-row">
              <DateInput label="Issue Date" value={formData.issueDate} onChange={(e) => setFormData(f => ({ ...f, issueDate: e.target.value }))} />
              <DateInput label="Expiry Date" value={formData.expiryDate} onChange={(e) => setFormData(f => ({ ...f, expiryDate: e.target.value }))} />
            </div>
          </div>
        ) : (
          <div className="detail-info">
            <h4>Your Permit Details</h4>
            <div className="info-row"><span>Permit Number</span><span>{localPermit.permitNumber || 'Not entered'}</span></div>
            <div className="info-row"><span>Issue Date</span><span>{localPermit.issueDate ? formatDate(localPermit.issueDate) : 'Not entered'}</span></div>
            <div className="info-row"><span>Expiry Date</span><span>{localPermit.expiryDate ? formatDate(localPermit.expiryDate) : 'Not entered'}</span></div>
            {localPermit.expiryDate && <div className="info-row highlight"><span>Days Until Expiry</span><span>{daysUntil(localPermit.expiryDate)}</span></div>}
          </div>
        )}
        
        <div className="detail-document">
          <h4>Documents {(localPermit.documents?.length || (localPermit.documentId ? 1 : 0)) > 0 && <span className="doc-count">({localPermit.documents?.length || 1})</span>}</h4>
          {uploadError && <Alert type="error">{uploadError}</Alert>}
          
          {/* Show all documents */}
          {localPermit.documents && localPermit.documents.length > 0 ? (
            <div className="documents-list">
              {localPermit.documents.map((doc, index) => (
                <div key={doc._id || index} className="document-preview">
                  <Icons.Document />
                  <span>{doc.originalName || `Document ${index + 1}`}</span>
                  <a href={getSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer"><Icons.Download /></a>
                </div>
              ))}
            </div>
          ) : localPermit.documentId ? (
            <div className="document-preview">
              <Icons.Document />
              <span>{localPermit.documentId.originalName}</span>
              <a href={getSecureFileUrl(localPermit.documentId.fileUrl)} target="_blank" rel="noopener noreferrer"><Icons.Download /></a>
            </div>
          ) : null}
          
          {/* Always show upload option */}
          <div className="upload-area" style={{ marginTop: (localPermit.documents?.length > 0 || localPermit.documentId) ? '12px' : '0' }}>
            <input type="file" id="permit-upload" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" onChange={handleUpload} hidden />
            <label htmlFor="permit-upload">{uploading ? 'Uploading...' : (localPermit.documents?.length > 0 || localPermit.documentId) ? '+ Add Another Document' : 'Upload Document (PDF, JPG, PNG)'}</label>
          </div>
        </div>
        
        <div className="modal-actions">
          {editing ? (
            <><Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button><Button onClick={handleSave} loading={loading}>Save</Button></>
          ) : (
            <>{subscription?.features?.autofill && !localPermit.isCustom && <Button variant="outline" onClick={handleAutofill}><Icons.Download /> Generate Application</Button>}<Button onClick={() => setEditing(true)}><Icons.Edit /> Edit</Button></>
          )}
        </div>
      </div>
    </Modal>
  );
};

const DocumentsPage = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const [documents, setDocuments] = useState([]); const [loading, setLoading] = useState(true); const [showUploadModal, setShowUploadModal] = useState(false);
  const fetchDocuments = async () => { try { const data = await api.get('/documents'); setDocuments(data.documents); } catch (error) { console.error(error); } finally { setLoading(false); } };
  useEffect(() => { fetchDocuments(); }, []);
  const handleDelete = async (id) => { 
    const confirmed = await confirm({ title: 'Delete Document', message: 'Are you sure you want to delete this document? This action cannot be undone.', confirmText: 'Delete', variant: 'danger' });
    if (!confirmed) return;
    try { await api.delete(`/documents/${id}`); fetchDocuments(); toast.success('Document deleted'); } catch (error) { toast.error(error.message); } 
  };
  const getCategoryLabel = (doc) => {
    const labels = { permit: 'Permit', insurance: 'Insurance', inspection: 'Inspection', food_handler: 'Food Handler', vehicle: 'Vehicle', commissary: 'Commissary', license: 'License', other: 'Other' };
    let label = labels[doc.category] || doc.category;
    if (doc.permitName) label += ` - ${doc.permitName}`;
    return label;
  };
  if (loading) return <LoadingSpinner />;
  return (<div className="documents-page"><div className="page-header"><div><h1>Document Vault</h1><p>Store all your documents</p></div><Button onClick={() => setShowUploadModal(true)}><Icons.Upload /> Upload</Button></div>{documents.length > 0 ? (<div className="documents-grid">{documents.map(doc => (<Card key={doc._id} className="document-card"><div className="document-icon"><Icons.Document /></div><div className="document-info"><h3>{doc.originalName}</h3><span className="doc-category">{getCategoryLabel(doc)}</span><span className="doc-date">{formatDate(doc.createdAt)}</span></div><div className="document-actions"><a href={getSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer"><Icons.Download /></a><button onClick={() => handleDelete(doc._id)}><Icons.Trash /></button></div></Card>))}</div>) : (<EmptyState icon={Icons.Document} title="No documents yet" action={<Button onClick={() => setShowUploadModal(true)}><Icons.Upload /> Upload</Button>} />)}<UploadDocumentModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} onSuccess={fetchDocuments} /></div>);
};

const UploadDocumentModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null); const [category, setCategory] = useState('other'); const [loading, setLoading] = useState(false); const [error, setError] = useState('');
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]; setError('');
    if (!selectedFile) return;
    const ext = '.' + selectedFile.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(ext)) {
      setError(`Invalid file type. Allowed: PDF, JPG, PNG, GIF, WebP`);
      e.target.value = ''; setFile(null); return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      e.target.value = ''; setFile(null); return;
    }
    setFile(selectedFile);
  };
  const handleUpload = async () => { if (!file) return; setLoading(true); const fd = new FormData(); fd.append('file', file); fd.append('category', category); try { await api.upload('/documents', fd); onSuccess(); onClose(); setFile(null); setError(''); } catch (err) { setError(err.message); } finally { setLoading(false); } };
  return (<Modal isOpen={isOpen} onClose={onClose} title="Upload Document"><div className="upload-form">{error && <Alert type="error">{error}</Alert>}<input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" onChange={handleFileChange} /><p className="upload-hint">Accepted: PDF, JPG, PNG, GIF, WebP (max 10MB)</p><Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)} options={[{ value: 'permit', label: 'Permit' }, { value: 'insurance', label: 'Insurance' }, { value: 'inspection', label: 'Inspection' }, { value: 'food_handler', label: 'Food Handler' }, { value: 'vehicle', label: 'Vehicle' }, { value: 'other', label: 'Other' }]} /></div><div className="modal-actions"><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={handleUpload} loading={loading} disabled={!file}>Upload</Button></div></Modal>);
};
const UploadModal = UploadDocumentModal; // Alias for Dashboard use

const InspectionsPage = () => {
  const { subscription, isExpired } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [checklists, setChecklists] = useState([]); // Recommended checklists
  const [userChecklists, setUserChecklists] = useState([]); // User's custom checklists
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true); const [activeChecklist, setActiveChecklist] = useState(null);
  const [inspectionData, setInspectionData] = useState({ items: [], notes: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [viewingInspection, setViewingInspection] = useState(null); // For viewing completed inspection
  const [newChecklist, setNewChecklist] = useState({ name: '', description: '', items: [{ itemText: '' }] });
  const [suggestionText, setSuggestionText] = useState('');

  // Plan check: Pro, Elite, Promo, or Lifetime required
  const hasAccess = subscription?.plan === 'pro' || subscription?.plan === 'elite' || subscription?.plan === 'promo' || subscription?.plan === 'lifetime' || subscription?.features?.inspectionChecklists;

  const fetchData = async () => {
    try { 
      const [cl, ucl, insp] = await Promise.all([
        api.get('/checklists'), 
        api.get('/user-checklists'),
        api.get('/inspections')
      ]); 
      setChecklists(cl.checklists || []); 
      setUserChecklists(ucl.checklists || []);
      setInspections(insp.inspections || []); 
    }
    catch (error) { console.error(error); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!hasAccess) { setLoading(false); return; }
    fetchData();
  }, [hasAccess]);

  const startInspection = (checklist, isUserChecklist = false) => {
    setActiveChecklist({ ...checklist, isUserChecklist });
    const items = checklist.items || [];
    setInspectionData({ items: items.map(item => ({ itemText: item.itemText || item.name, description: item.description, passed: null, notes: '', photos: [] })), notes: '' });
  };

  const updateItem = (index, field, value) => {
    const items = [...inspectionData.items];
    items[index] = { ...items[index], [field]: value };
    setInspectionData(d => ({ ...d, items }));
  };

  const submitInspection = async () => {
    try {
      const payload = { items: inspectionData.items, notes: inspectionData.notes, inspectionDate: new Date() };
      if (activeChecklist.isUserChecklist) {
        payload.userChecklistId = activeChecklist._id;
      } else {
        payload.checklistId = activeChecklist._id;
      }
      await api.post('/inspections', payload);
      setActiveChecklist(null);
      fetchData();
      toast.success('Inspection submitted successfully!');
    } catch (err) { toast.error(err.message); }
  };

  const createUserChecklist = async () => {
    try {
      await api.post('/user-checklists', {
        name: newChecklist.name,
        description: newChecklist.description,
        items: newChecklist.items.filter(i => i.itemText.trim())
      });
      setNewChecklist({ name: '', description: '', items: [{ itemText: '' }] });
      setShowCreateModal(false);
      fetchData();
      toast.success('Checklist created!');
    } catch (err) { toast.error(err.message); }
  };

  const deleteUserChecklist = async (id) => {
    const confirmed = await confirm({ title: 'Delete Checklist', message: 'Are you sure you want to delete this checklist?', confirmText: 'Delete', variant: 'danger' });
    if (!confirmed) return;
    try {
      await api.delete(`/user-checklists/${id}`);
      fetchData();
      toast.success('Checklist deleted');
    } catch (err) { toast.error(err.message); }
  };

  const submitSuggestion = async () => {
    try {
      await api.post('/suggestions', {
        type: 'checklist_request',
        title: 'Checklist suggestion/request',
        description: suggestionText,
        checklistDetails: { isUserSpecific: false }
      });
      setSuggestionText('');
      setShowSuggestModal(false);
      toast.success('Thank you! Your suggestion has been submitted.');
    } catch (err) { toast.error(err.message); }
  };

  if (!hasAccess) {
    return (
      <div className="upgrade-feature-page">
        <div className="upgrade-feature-content">
          <div className="upgrade-feature-icon">{isExpired ? <Icons.Lock /> : <Icons.Checklist />}</div>
          <h1>{isExpired ? 'Subscription Expired' : 'Inspection Checklists'}</h1>
          <Badge variant={isExpired ? 'danger' : 'warning'}>{isExpired ? 'Renew to Access' : 'Pro Plan Required'}</Badge>
          <p className="upgrade-feature-description">
            {isExpired 
              ? 'Your subscription has expired. Renew now to access inspection checklists and ensure you never fail a health inspection.'
              : 'Never fail a health inspection again. Walk through every requirement step-by-step before the inspector arrives.'}
          </p>
          
          <div className="upgrade-feature-grid">
            <div className="upgrade-feature-item">
              <div className="upgrade-feature-item-icon"><Icons.Check /></div>
              <h3>City-Specific Checklists</h3>
              <p>Pre-built checklists based on your operating cities' health department requirements</p>
            </div>
            <div className="upgrade-feature-item">
              <div className="upgrade-feature-item-icon"><Icons.Check /></div>
              <h3>Offline Mode</h3>
              <p>Access checklists anywhere, even without internet connection on your mobile device</p>
            </div>
            <div className="upgrade-feature-item">
              <div className="upgrade-feature-item-icon"><Icons.Check /></div>
              <h3>Photo Documentation</h3>
              <p>Capture photos as proof of compliance for each inspection item</p>
            </div>
            <div className="upgrade-feature-item">
              <div className="upgrade-feature-item-icon"><Icons.Check /></div>
              <h3>Violation Alerts</h3>
              <p>Get warnings about commonly failed inspection items so you can prepare</p>
            </div>
          </div>

          <div className="upgrade-pricing-card">
            <span className="upgrade-plan-label">Pro Plan</span>
            <div className="upgrade-price">$49<span>/month</span></div>
            <p>Also includes SMS alerts, PDF autofill & multi-city support</p>
          </div>

          <Button size="lg" onClick={async () => { try { const data = await api.post('/subscription/checkout', { plan: 'pro' }); if (data.url) window.location.href = data.url; else if (data.message) toast.info(data.message); } catch (err) { toast.error(err.message); } }}>{isExpired ? 'Renew Subscription' : 'Upgrade to Pro'}</Button>
          <p className="upgrade-note">{isExpired ? 'Restore full access immediately' : '14-day free trial • Cancel anytime'}</p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  if (activeChecklist) {
    return (
      <div className="inspection-active">
        <div className="page-header"><button className="back-btn" onClick={() => setActiveChecklist(null)}><Icons.X /></button><h1>{activeChecklist.name}</h1></div>
        <div className="inspection-items">
          {inspectionData.items.map((item, i) => (
            <Card key={i} className={`inspection-item ${item.passed === true ? 'passed' : item.passed === false ? 'failed' : ''}`}>
              <div className="item-header"><span className="item-number">{i + 1}</span><h3>{item.itemText || item.name}</h3></div>
              {item.description && <p className="item-description">{item.description}</p>}
              <div className="item-actions">
                <button className={`pass-btn ${item.passed === true ? 'active' : ''}`} onClick={() => updateItem(i, 'passed', true)}><Icons.Check /> Pass</button>
                <button className={`fail-btn ${item.passed === false ? 'active' : ''}`} onClick={() => updateItem(i, 'passed', false)}><Icons.X /> Fail</button>
              </div>
              {item.passed === false && <Input placeholder="Notes on failure..." value={item.notes} onChange={(e) => updateItem(i, 'notes', e.target.value)} />}
            </Card>
          ))}
        </div>
        <Card className="inspection-summary">
          <h3>Summary</h3>
          <div className="summary-stats">
            <span className="passed">{inspectionData.items.filter(i => i.passed === true).length} Passed</span>
            <span className="failed">{inspectionData.items.filter(i => i.passed === false).length} Failed</span>
            <span className="pending">{inspectionData.items.filter(i => i.passed === null).length} Pending</span>
          </div>
          <Input label="Overall Notes" value={inspectionData.notes} onChange={(e) => setInspectionData(d => ({ ...d, notes: e.target.value }))} />
          <Button onClick={submitInspection} disabled={inspectionData.items.some(i => i.passed === null)}>Complete Inspection</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="inspections-page">
      <div className="page-header">
        <h1>Inspections</h1>
        <div className="page-header-actions">
          <Button variant="outline" onClick={() => setShowSuggestModal(true)}><Icons.Plus /> Suggest Checklist</Button>
          <Button onClick={() => setShowCreateModal(true)}><Icons.Plus /> Create My Own</Button>
        </div>
      </div>
      <div className="inspections-grid">
        <div className="checklists-section">
          <h2>Recommended Checklists</h2>
          {checklists.length > 0 ? checklists.map(cl => (
            <Card key={cl._id} className="checklist-card">
              <div className="checklist-info">
                <h3>{cl.name}</h3>
                <p>{cl.description}</p>
                <span className="item-count">{cl.items?.length || 0} items</span>
                {cl.category && <Badge variant={cl.category === 'health' ? 'danger' : cl.category === 'fire' ? 'warning' : 'default'}>{cl.category}</Badge>}
              </div>
              <Button onClick={() => startInspection(cl)}>Start</Button>
            </Card>
          )) : <EmptyState icon={Icons.Checklist} title="No recommended checklists" description="Checklists for your jurisdiction coming soon. You can create your own!" />}
          
          <h2 style={{ marginTop: '2rem' }}>My Checklists</h2>
          {userChecklists.length > 0 ? userChecklists.map(cl => (
            <Card key={cl._id} className="checklist-card user-checklist">
              <div className="checklist-info">
                <h3>{cl.name} <Badge variant="primary">Custom</Badge></h3>
                <p>{cl.description || 'Custom checklist'}</p>
                <span className="item-count">{cl.items?.length || 0} items</span>
              </div>
              <div className="checklist-actions">
                <Button onClick={() => startInspection(cl, true)}>Start</Button>
                <button className="delete-btn" onClick={() => deleteUserChecklist(cl._id)}><Icons.Trash /></button>
              </div>
            </Card>
          )) : <p className="empty-text">No custom checklists yet. Create one above!</p>}
        </div>
        <div className="history-section">
          <h2>Recent Inspections</h2>
          {inspections.length > 0 ? inspections.map(insp => (
            <Card key={insp._id} className="inspection-history-card clickable" onClick={() => setViewingInspection(insp)}>
              <h3>{insp.checklistId?.name || insp.userChecklistId?.name || 'Inspection'}</h3>
              <p>{formatDate(insp.inspectionDate)}</p>
              <div className="inspection-result">
                <Badge variant={insp.overallStatus === 'pass' ? 'success' : insp.overallStatus === 'fail' ? 'danger' : 'warning'}>
                  {insp.overallStatus === 'pass' ? 'PASSED' : insp.overallStatus === 'fail' ? 'FAILED' : 'INCOMPLETE'}
                </Badge>
              </div>
            </Card>
          )) : <p className="empty-text">No inspections recorded yet</p>}
        </div>
      </div>

      {/* View Inspection Modal */}
      <Modal isOpen={!!viewingInspection} onClose={() => setViewingInspection(null)} title="Inspection Results" size="lg">
        {viewingInspection && (
          <div className="inspection-view">
            <div className="inspection-view-header">
              <div>
                <h3>{viewingInspection.checklistId?.name || viewingInspection.userChecklistId?.name || 'Inspection'}</h3>
                <p className="inspection-date">{formatDate(viewingInspection.inspectionDate)}</p>
              </div>
              <Badge variant={viewingInspection.overallStatus === 'pass' ? 'success' : viewingInspection.overallStatus === 'fail' ? 'danger' : 'warning'} size="lg">
                {viewingInspection.overallStatus === 'pass' ? 'PASSED' : viewingInspection.overallStatus === 'fail' ? 'FAILED' : 'INCOMPLETE'}
              </Badge>
            </div>
            
            <div className="inspection-items-list">
              <h4>Checklist Items</h4>
              {viewingInspection.results?.map((item, i) => (
                <div key={i} className={`inspection-item-result ${item.passed === true ? 'passed' : item.passed === false ? 'failed' : 'pending'}`}>
                  <div className="inspection-item-status">
                    {item.passed === true ? <Icons.Check /> : item.passed === false ? <Icons.X /> : <Icons.Clock />}
                  </div>
                  <div className="inspection-item-content">
                    <span className="inspection-item-text">{item.itemText}</span>
                    {item.notes && <p className="inspection-item-notes">{item.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
            
            {viewingInspection.notes && (
              <div className="inspection-notes">
                <h4>Notes</h4>
                <p>{viewingInspection.notes}</p>
              </div>
            )}
            
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setViewingInspection(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Checklist Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create Custom Checklist" size="lg">
        <Input label="Checklist Name *" placeholder="e.g., Pre-Event Safety Check" value={newChecklist.name} onChange={(e) => setNewChecklist(c => ({ ...c, name: e.target.value }))} />
        <Input label="Description" placeholder="What is this checklist for?" value={newChecklist.description} onChange={(e) => setNewChecklist(c => ({ ...c, description: e.target.value }))} />
        <div className="form-section">
          <label>Checklist Items</label>
          {newChecklist.items.map((item, i) => (
            <div key={i} className="checklist-item-row" style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span style={{ minWidth: '30px' }}>{i + 1}.</span>
              <Input placeholder="Item text" value={item.itemText} onChange={(e) => {
                const items = [...newChecklist.items];
                items[i] = { itemText: e.target.value };
                setNewChecklist(c => ({ ...c, items }));
              }} />
              {newChecklist.items.length > 1 && (
                <button type="button" className="delete-btn" onClick={() => setNewChecklist(c => ({ ...c, items: c.items.filter((_, idx) => idx !== i) }))}><Icons.Trash /></button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setNewChecklist(c => ({ ...c, items: [...c.items, { itemText: '' }] }))}>
            <Icons.Plus /> Add Item
          </Button>
        </div>
        <div className="modal-actions">
          <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button onClick={createUserChecklist} disabled={!newChecklist.name || !newChecklist.items.some(i => i.itemText.trim())}>Create Checklist</Button>
        </div>
      </Modal>

      {/* Suggest Checklist Modal */}
      <Modal isOpen={showSuggestModal} onClose={() => setShowSuggestModal(false)} title="Suggest a Checklist">
        <p>Is there a checklist you'd like us to add for all vendors in your area? Let us know!</p>
        <Input 
          label="Your Suggestion" 
          placeholder="e.g., Fire safety checklist for Austin food trucks, or specific items to add to existing checklists" 
          value={suggestionText} 
          onChange={(e) => setSuggestionText(e.target.value)}
          multiline
        />
        <div className="modal-actions">
          <Button variant="outline" onClick={() => setShowSuggestModal(false)}>Cancel</Button>
          <Button onClick={submitSuggestion} disabled={!suggestionText.trim()}>Submit Suggestion</Button>
        </div>
      </Modal>
    </div>
  );
};

const EventsPage = () => {
  const { user, subscription, isExpired, canWrite, business } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [events, setEvents] = useState([]); const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ eventName: '', organizerName: '', city: '', state: '', startDate: '', endDate: '', eventType: '', website: '', additionalInfo: '' });
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Organizer portal state
  const [organizerEvents, setOrganizerEvents] = useState([]);
  const [organizerTab, setOrganizerTab] = useState('dashboard'); // 'dashboard', 'my-events', 'past-events', 'applications', 'create'
  const [orgDashboard, setOrgDashboard] = useState(null);
  const [eventReadiness, setEventReadiness] = useState([]);
  const [newOrgEvent, setNewOrgEvent] = useState({ eventName: '', description: '', startDate: '', endDate: '', city: '', state: '', address: '', eventType: 'food_event', maxVendors: '', applicationDeadline: '', feeStructure: { applicationFee: 0, boothFee: 0, electricityFee: 0, description: '' }, requiredPermitTypes: [], customPermitRequirements: [], status: 'draft' });
  const [selectedOrgEvent, setSelectedOrgEvent] = useState(null);
  const [vendorApplications, setVendorApplications] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [availablePermitTypes, setAvailablePermitTypes] = useState([]);
  
  // Event proof documents state
  const [eventProofFiles, setEventProofFiles] = useState([]); // Files to upload with new event
  const [previousEventProofs, setPreviousEventProofs] = useState([]); // Proofs from previous events to reuse
  const [selectedPreviousProofs, setSelectedPreviousProofs] = useState([]); // Selected previous proofs to use
  
  // Edit event state
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editPermitTypes, setEditPermitTypes] = useState([]);
  
  // Proof upload modal state (for existing events)
  const [showProofUploadModal, setShowProofUploadModal] = useState(null); // null or event object
  const [proofUploadFiles, setProofUploadFiles] = useState([]);
  const [proofUploading, setProofUploading] = useState(false);
  
  // Jurisdictions for location selection
  const [jurisdictions, setJurisdictions] = useState([]);
  const [showLocationRequestModal, setShowLocationRequestModal] = useState(false);
  const [locationRequest, setLocationRequest] = useState({ city: '', state: '', reason: '' });
  const [locationRequestSubmitting, setLocationRequestSubmitting] = useState(false);
  
  // Vendor events discovery
  const [publishedEvents, setPublishedEvents] = useState([]);
  const [showApplyModal, setShowApplyModal] = useState(null);
  const [applicationNotes, setApplicationNotes] = useState('');
  
  // Withdraw from event state
  const [showWithdrawModal, setShowWithdrawModal] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  
  // Attending events state (self-tracked)
  const [attendingEvents, setAttendingEvents] = useState([]);
  const [showAddAttendingModal, setShowAddAttendingModal] = useState(false);
  const [editingAttendingEvent, setEditingAttendingEvent] = useState(null);
  const [attendingForm, setAttendingForm] = useState({ eventName: '', organizerName: '', description: '', startDate: '', endDate: '', city: '', state: '', address: '', venueName: '', eventType: 'other', notes: '', requiredPermits: [], complianceChecklist: [] });
  const [attendingCitySelection, setAttendingCitySelection] = useState(''); // 'city||state' or '__custom__'
  const [attendingSuggestedPermits, setAttendingSuggestedPermits] = useState([]);
  const [attendingSearchingPermits, setAttendingSearchingPermits] = useState(false);
  const [newPermitName, setNewPermitName] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [selectedAttendingEvent, setSelectedAttendingEvent] = useState(null);
  const [vendorPermitsList, setVendorPermitsList] = useState([]);
  const [selectedExistingPermit, setSelectedExistingPermit] = useState('');
  
  // Registered vendors list (for organizer invites)
  const [registeredVendors, setRegisteredVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [viewingVendorApp, setViewingVendorApp] = useState(null);
  
  // Verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Check if user is an organizer
  const isOrganizer = user?.isOrganizer && !user?.organizerProfile?.disabled;
  const organizerVerified = user?.organizerProfile?.verified;
  const verificationStatus = user?.organizerProfile?.verificationStatus || 'pending';

  // Plan check: Elite, Promo, or Lifetime required for vendors (NOT organizers - they have separate access)
  const hasVendorAccess = !isOrganizer && (subscription?.plan === 'elite' || subscription?.plan === 'promo' || subscription?.plan === 'lifetime' || subscription?.features?.eventIntegration);

  // Fetch organizer data
  const fetchOrganizerData = async () => {
    try {
      const [jurData, orgData, vendorsData] = await Promise.all([
        api.get('/jurisdictions'),
        api.get('/events/organizer/my-events'),
        api.get('/events/organizer/registered-vendors')
      ]);
      setJurisdictions(jurData.jurisdictions || []);
      setOrganizerEvents(orgData.events || []);
      setRegisteredVendors(vendorsData.vendors || []);
      // Fetch dashboard analytics
      try { const dash = await api.get('/organizer/dashboard'); setOrgDashboard(dash); } catch (e) { console.error(e); }
      try { const rd = await api.get('/organizer/event-readiness-summary'); setEventReadiness(rd.events || []); } catch (e) { console.error(e); }
    } catch (error) { 
      console.error('Error fetching organizer data:', error); 
    }
  };

  // Fetch vendor data
  const fetchVendorData = async () => {
    try {
      const [myData, pubData, attData, permData] = await Promise.all([
        api.get('/events/my-events'),
        api.get('/events/published'),
        api.get('/attending-events'),
        api.get('/permits')
      ]);
      setEvents(myData.events || []);
      setPublishedEvents(pubData.events || []);
      setAttendingEvents(attData.attendingEvents || []);
      setVendorPermitsList(permData.permits || []);
    } catch (error) { 
      console.error('Error fetching vendor data:', error); 
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      // Check organizer status directly from user object
      const userIsOrganizer = user.isOrganizer && !user.organizerProfile?.disabled;
      const userHasVendorAccess = !userIsOrganizer && (subscription?.plan === 'elite' || subscription?.plan === 'promo' || subscription?.plan === 'lifetime' || subscription?.features?.eventIntegration);
      
      if (userIsOrganizer) {
        await fetchOrganizerData();
      } else if (userHasVendorAccess) {
        await fetchVendorData();
      }
      
      setLoading(false);
    };
    
    init();
  }, [user, subscription]); // Depend on both user and subscription

  // Sync selectedAttendingEvent with fresh data after re-fetches
  useEffect(() => {
    if (selectedAttendingEvent) {
      const updated = attendingEvents.find(ae => ae._id === selectedAttendingEvent._id);
      if (updated) setSelectedAttendingEvent(updated);
    }
  }, [attendingEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync selectedEvent with fresh data after re-fetches
  useEffect(() => {
    if (selectedEvent) {
      const allEvents = [...events, ...publishedEvents];
      const updated = allEvents.find(e => e._id === selectedEvent._id);
      if (updated) setSelectedEvent(updated);
    }
  }, [events]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show loading spinner for everyone while data loads
  if (loading) return <LoadingSpinner />;

  // Fetch permit types by state for event creation
  const fetchPermitTypesByState = async (state, city = null) => {
    if (!state) {
      setAvailablePermitTypes([]);
      return;
    }
    try {
      // If city is provided, filter by specific city, otherwise get all for state
      const url = city 
        ? `/permit-types/by-state?state=${state}&city=${encodeURIComponent(city)}`
        : `/permit-types/by-state?state=${state}`;
      const ptData = await api.get(url);
      setAvailablePermitTypes(ptData.permitTypes || []);
      // Clear selected permits when location changes
      setNewOrgEvent(ev => ({ ...ev, requiredPermitTypes: [] }));
    } catch (err) {
      console.error('Error fetching permit types:', err);
      // Fallback to all permit types
      const ptData = await api.get('/permit-types/all');
      setAvailablePermitTypes(ptData.permitTypes || []);
    }
  };

  // Handle state change in event creation form
  const handleEventStateChange = (newState) => {
    setNewOrgEvent(ev => ({ ...ev, state: newState, city: '', requiredPermitTypes: [] })); // Clear city and permits when state changes
    setAvailablePermitTypes([]); // Clear permit types until city is selected
  };
  
  // Handle city change in event creation form - fetch permits for specific city
  const handleEventCityChange = (newCity) => {
    setNewOrgEvent(ev => ({ ...ev, city: newCity, requiredPermitTypes: [] }));
    if (newCity && newOrgEvent.state) {
      fetchPermitTypesByState(newOrgEvent.state, newCity);
    } else {
      setAvailablePermitTypes([]);
    }
  };

  // Get unique cities for selected state from jurisdictions
  const getAvailableCities = (state) => {
    if (!state) return [];
    const cities = [...new Set(jurisdictions.filter(j => j.state === state).map(j => j.city))].sort();
    return cities;
  };

  // Submit location request
  const submitLocationRequest = async () => {
    if (!locationRequest.city || !locationRequest.state) return;
    setLocationRequestSubmitting(true);
    try {
      await api.post('/suggestions', {
        type: 'city_request',
        details: `New location request for event: ${locationRequest.city}, ${locationRequest.state}`,
        additionalInfo: locationRequest.reason
      });
      toast.success('Location request submitted! Our team will review and add it soon.');
      setShowLocationRequestModal(false);
      setLocationRequest({ city: '', state: '', reason: '' });
    } catch (error) {
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setLocationRequestSubmitting(false);
    }
  };

  const submitEventRequest = async () => {
    setRequestSubmitting(true);
    try {
      await api.post('/suggestions', {
        type: 'event_request',
        title: `Event Request: ${requestForm.eventName}`,
        description: requestForm.additionalInfo,
        eventDetails: {
          eventName: requestForm.eventName,
          organizerName: requestForm.organizerName,
          city: requestForm.city,
          state: requestForm.state,
          startDate: requestForm.startDate,
          endDate: requestForm.endDate,
          eventType: requestForm.eventType,
          website: requestForm.website,
          additionalInfo: requestForm.additionalInfo
        }
      });
      setRequestSubmitted(true);
      setTimeout(() => { setShowRequestModal(false); setRequestSubmitted(false); setRequestForm({ eventName: '', organizerName: '', city: '', state: '', startDate: '', endDate: '', eventType: '', website: '', additionalInfo: '' }); }, 2000);
    } catch (err) { toast.error(err.message); }
    finally { setRequestSubmitting(false); }
  };

  // Organizer functions
  const createOrganizerEvent = async () => {
    try {
      // Handle custom city
      const cityToUse = newOrgEvent.city === '__custom__' ? newOrgEvent.customCity : newOrgEvent.city;
      
      const eventData = {
        ...newOrgEvent,
        location: { city: cityToUse, state: newOrgEvent.state, address: newOrgEvent.address }
      };
      delete eventData.customCity; // Remove temporary field
      
      const response = await api.post('/events/organizer/create', eventData);
      const createdEvent = response.event;
      
      // Upload new proof documents if any
      if (eventProofFiles.length > 0) {
        for (const fileData of eventProofFiles) {
          const fd = new FormData();
          fd.append('file', fileData.file);
          fd.append('name', fileData.name || fileData.file.name);
          fd.append('category', fileData.category || 'venue_contract');
          await api.upload(`/events/organizer/${createdEvent._id}/proof`, fd);
        }
      }
      
      // Copy selected previous proofs if any
      if (selectedPreviousProofs.length > 0) {
        await api.post(`/events/organizer/${createdEvent._id}/copy-proofs`, { proofIds: selectedPreviousProofs });
      }
      
      const orgData = await api.get('/events/organizer/my-events');
      setOrganizerEvents(orgData.events || []);
      setNewOrgEvent({ eventName: '', description: '', startDate: '', endDate: '', city: '', state: '', address: '', eventType: 'food_event', maxVendors: '', applicationDeadline: '', feeStructure: { applicationFee: 0, boothFee: 0, electricityFee: 0, description: '' }, requiredPermitTypes: [], customPermitRequirements: [], status: 'draft', customCity: '' });
      setEventProofFiles([]);
      setSelectedPreviousProofs([]);
      setOrganizerTab('my-events');
      if (response.message) {
        toast.info(response.message);
      } else {
        toast.success('Event created successfully!');
      }
    } catch (err) { toast.error(err.message); }
  };
  
  // Fetch previous event proofs when switching to create tab
  const fetchPreviousEventProofs = async () => {
    try {
      const data = await api.get('/events/organizer/previous-proofs');
      setPreviousEventProofs(data.proofs || []);
    } catch (err) { console.error(err); }
  };

  const updateEventStatus = async (eventId, status) => {
    // For closing events, show a confirmation dialog
    if (status === 'closed') {
      const confirmed = await confirm({
        title: 'Close Event',
        message: 'Are you sure you want to close this event? This will:\n\n• Mark the event as completed\n• Stop accepting new applications\n• Move it to your Past Events\n\nVendors will still be able to view their participation details.',
        confirmText: 'Yes, Close Event',
        cancelText: 'Keep Open',
        variant: 'primary'
      });
      if (!confirmed) return;
    }
    
    try {
      const result = await api.put(`/events/organizer/${eventId}/status`, { status });
      const orgData = await api.get('/events/organizer/my-events');
      setOrganizerEvents(orgData.events || []);
      if (result.message) {
        toast.info(result.message);
      } else if (result.event?.status === 'published') {
        toast.success('Event published and visible to vendors!');
      } else if (status === 'closed') {
        toast.success('Event closed successfully.');
      }
    } catch (err) { toast.error(err.message); }
  };
  
  // Cancel event with vendor notifications
  const cancelEvent = async (eventId, eventStartDate) => {
    // Check if event date has passed
    const eventDate = new Date(eventStartDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (eventDate <= today) {
      toast.error('Cannot cancel an event that has already started or passed. Use "Close" instead to mark it as completed.');
      return;
    }
    
    // Prompt for cancellation reason
    const reason = window.prompt('Please provide a reason for cancelling this event (this will be shared with all vendors):');
    if (!reason || reason.trim() === '') {
      toast.error('A cancellation reason is required.');
      return;
    }
    
    const confirmed = await confirm({
      title: 'Cancel Event',
      message: `Are you sure you want to cancel this event?\n\nReason: "${reason}"\n\nAll approved vendors and applicants will be notified by email with this reason.`,
      confirmText: 'Yes, Cancel Event',
      cancelText: 'Keep Event',
      variant: 'danger'
    });
    if (!confirmed) return;
    
    try {
      await api.put(`/events/organizer/${eventId}/cancel`, { reason: reason.trim() });
      const orgData = await api.get('/events/organizer/my-events');
      setOrganizerEvents(orgData.events || []);
      toast.success('Event cancelled. All vendors have been notified.');
    } catch (err) { toast.error(err.message); }
  };
  
  // Upload proof documents for existing event
  const uploadEventProofs = async () => {
    if (!showProofUploadModal || proofUploadFiles.length === 0) return;
    setProofUploading(true);
    try {
      for (const fileData of proofUploadFiles) {
        const fd = new FormData();
        fd.append('file', fileData.file);
        fd.append('name', fileData.file.name);
        fd.append('category', fileData.category || 'venue_contract');
        await api.upload(`/events/organizer/${showProofUploadModal._id}/proof`, fd);
      }
      // Refresh events
      const orgData = await api.get('/events/organizer/my-events');
      setOrganizerEvents(orgData.events || []);
      setShowProofUploadModal(null);
      setProofUploadFiles([]);
      toast.success('Documents uploaded successfully!');
    } catch (err) { toast.error(err.message); }
    finally { setProofUploading(false); }
  };

  const fetchEventApplications = async (eventId) => {
    try {
      const data = await api.get(`/events/organizer/${eventId}/applications`);
      setVendorApplications(data.applications || []);
      setSelectedOrgEvent(organizerEvents.find(e => e._id === eventId));
    } catch (err) { toast.error(err.message); }
  };

  const handleApplication = async (applicationId, action) => {
    try {
      await api.put(`/events/organizer/applications/${applicationId}`, { status: action });
      if (selectedOrgEvent) fetchEventApplications(selectedOrgEvent._id);
    } catch (err) { toast.error(err.message); }
  };

  const inviteVendor = async (eventId) => {
    if (!inviteEmail) return;
    try {
      await api.post(`/events/organizer/${eventId}/invite`, { email: inviteEmail });
      setInviteEmail('');
      toast.success('Invitation sent!');
      fetchEventApplications(eventId);
    } catch (err) { toast.error(err.message); }
  };

  // Edit event functions
  const openEditEventModal = async (event) => {
    setEditingEvent({
      ...event,
      eventName: event.eventName || '',
      description: event.description || '',
      startDate: event.startDate?.split('T')[0] || '',
      endDate: event.endDate?.split('T')[0] || '',
      applicationDeadline: event.applicationDeadline?.split('T')[0] || '',
      city: event.location?.city || '',
      state: event.location?.state || '',
      address: event.location?.address || '',
      eventType: event.eventType || 'food_event',
      maxVendors: event.maxVendors || '',
      status: event.status || 'draft',
      feeStructure: event.feeStructure || { applicationFee: 0, boothFee: 0, electricityFee: 0, description: '' },
      requiredPermitTypes: event.requiredPermitTypes?.map(pt => pt._id || pt) || [],
      customPermitRequirements: event.customPermitRequirements || []
    });
    // Fetch permits for the event's city (specific jurisdiction)
    if (event.location?.state && event.location?.city) {
      try {
        const ptData = await api.get(`/permit-types/by-state?state=${event.location.state}&city=${encodeURIComponent(event.location.city)}`);
        setEditPermitTypes(ptData.permitTypes || []);
      } catch (err) { console.error(err); }
    }
    setShowEditEventModal(true);
  };

  const handleEditEventStateChange = async (newState) => {
    setEditingEvent(ev => ({ ...ev, state: newState, city: '', requiredPermitTypes: [] }));
    setEditPermitTypes([]); // Clear permit types until city is selected
  };
  
  const handleEditEventCityChange = async (newCity) => {
    setEditingEvent(ev => ({ ...ev, city: newCity, requiredPermitTypes: [] }));
    if (newCity && editingEvent?.state) {
      try {
        const ptData = await api.get(`/permit-types/by-state?state=${editingEvent.state}&city=${encodeURIComponent(newCity)}`);
        setEditPermitTypes(ptData.permitTypes || []);
      } catch (err) { console.error(err); }
    } else {
      setEditPermitTypes([]);
    }
  };

  const saveEditedEvent = async () => {
    if (!editingEvent) return;
    try {
      // Handle custom city
      const cityToUse = editingEvent.city === '__custom__' ? editingEvent.customCity : editingEvent.city;
      
      const eventData = {
        eventName: editingEvent.eventName,
        description: editingEvent.description,
        startDate: editingEvent.startDate,
        endDate: editingEvent.endDate,
        applicationDeadline: editingEvent.applicationDeadline,
        location: { city: cityToUse, state: editingEvent.state, address: editingEvent.address },
        eventType: editingEvent.eventType,
        maxVendors: editingEvent.maxVendors,
        status: editingEvent.status,
        feeStructure: editingEvent.feeStructure,
        requiredPermitTypes: editingEvent.requiredPermitTypes,
        customPermitRequirements: editingEvent.customPermitRequirements
      };
      await api.put(`/events/organizer/${editingEvent._id}`, eventData);
      const orgData = await api.get('/events/organizer/my-events');
      setOrganizerEvents(orgData.events || []);
      setShowEditEventModal(false);
      setEditingEvent(null);
      toast.success('Event updated successfully!');
    } catch (err) { toast.error(err.message); }
  };

  // Vendor functions
  const applyToEvent = async (eventId) => {
    // Check if user can write (subscription is active)
    if (!canWrite) {
      setShowApplyModal(null);
      setShowUpgradeModal(true);
      return;
    }
    try {
      await api.post(`/events/${eventId}/apply`, { applicationNotes });
      setShowApplyModal(null);
      setApplicationNotes('');
      const pubData = await api.get('/events/published');
      setPublishedEvents(pubData.events || []);
      const data = await api.get('/events/my-events');
      setEvents(data.events || []);
      toast.success('Application submitted!');
    } catch (err) { 
      if (err.message?.includes('subscription') || err.message?.includes('expired')) {
        setShowUpgradeModal(true);
      } else {
        toast.error(err.message); 
      }
    }
  };

  const respondToInvitation = async (eventId, accept) => {
    // Check if user can write (subscription is active)
    if (!canWrite) {
      setShowUpgradeModal(true);
      return;
    }
    try {
      await api.put(`/events/${eventId}/respond-invitation`, { accept });
      const data = await api.get('/events/my-events');
      setEvents(data.events || []);
    } catch (err) { 
      if (err.message?.includes('subscription') || err.message?.includes('expired')) {
        setShowUpgradeModal(true);
      } else {
        toast.error(err.message); 
      }
    }
  };

  const withdrawFromEvent = async () => {
    if (!showWithdrawModal) return;
    setWithdrawing(true);
    try {
      await api.delete(`/events/${showWithdrawModal._id}/withdraw`, { reason: withdrawReason });
      setShowWithdrawModal(null);
      setWithdrawReason('');
      // Refresh events
      const [myData, pubData] = await Promise.all([
        api.get('/events/my-events'),
        api.get('/events/published')
      ]);
      setEvents(myData.events || []);
      setPublishedEvents(pubData.events || []);
      toast.success('Successfully withdrawn from event. The organizer has been notified.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWithdrawing(false);
    }
  };

  // ORGANIZER PORTAL VIEW
  if (isOrganizer) {
    return (
      <div className="events-page organizer-portal">
        <div className="page-header">
          <div>
            <h1>Event Organizer Portal</h1>
            <p>Manage your events, vendors, and compliance</p>
          </div>
          <div className="header-badges">
            <Badge variant="primary">Organizer Account</Badge>
            {organizerVerified ? (
              <Badge variant="success">✓ Verified</Badge>
            ) : (
              <Badge variant="warning">Pending Verification</Badge>
            )}
          </div>
        </div>
        
        {/* Verification Status Banner */}
        {!organizerVerified && verificationStatus === 'pending' && (
          <Alert type="info">
            <strong>⏳ Verification Pending</strong>
            <p style={{ margin: '8px 0 0' }}>Your organizer account is awaiting verification by PermitWise. You can create events as drafts, but publishing requires verification. This usually takes 1-2 business days.</p>
          </Alert>
        )}
        
        {verificationStatus === 'info_needed' && (
          <Alert type="warning">
            <strong>⚠️ Information Needed</strong>
            <p style={{ margin: '8px 0 0' }}>{user?.organizerProfile?.verificationNotes || 'Please upload required verification documents in Settings → Documents.'}</p>
            <Button size="sm" variant="outline" style={{ marginTop: '8px' }} onClick={() => window.location.hash = 'settings'}>Go to Settings</Button>
          </Alert>
        )}
        
        {verificationStatus === 'rejected' && (
          <Alert type="error">
            <strong>❌ Verification Rejected</strong>
            <p style={{ margin: '8px 0 0' }}>{user?.organizerProfile?.verificationNotes || 'Your organizer application was not approved. Please contact support for more information.'}</p>
          </Alert>
        )}
        
        <div className="organizer-tabs">
          {['dashboard', 'my-events', 'past-events', 'applications', 'create'].map(tab => {
            // Calculate counts for badges
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const upcomingCount = organizerEvents.filter(e => {
              const eventDate = new Date(e.endDate || e.startDate);
              return eventDate >= today && e.status !== 'closed' && e.status !== 'cancelled';
            }).length;
            const pastCount = organizerEvents.filter(e => {
              const eventDate = new Date(e.endDate || e.startDate);
              return eventDate < today || e.status === 'closed' || e.status === 'cancelled';
            }).length;
            
            return (
            <button key={tab} className={organizerTab === tab ? 'active' : ''} onClick={() => { 
              if (tab === 'create' && !organizerVerified) {
                setShowVerificationModal(true);
              } else {
                if (tab === 'create') fetchPreviousEventProofs();
                setOrganizerTab(tab); 
                setSelectedOrgEvent(null); 
              }
            }}>
              {tab === 'dashboard' ? '📊 Dashboard' :
               tab === 'my-events' ? `🎪 Upcoming (${upcomingCount})` : 
               tab === 'past-events' ? `📜 Past (${pastCount})` :
               tab === 'applications' ? '📋 Applications' : '➕ Create Event'}
            </button>
          );})}
        </div>

        {/* Organizer Dashboard */}
        {organizerTab === 'dashboard' && (
          <div className="organizer-dashboard">
            {orgDashboard ? (
              <>
                <div className="stats-grid">
                  <Card className="stat-card"><div className="stat-icon blue"><Icons.Event /></div><div className="stat-info"><span className="stat-value">{orgDashboard.stats?.totalEvents || 0}</span><span className="stat-label">Total Events</span></div></Card>
                  <Card className="stat-card"><div className="stat-icon green"><Icons.Check /></div><div className="stat-info"><span className="stat-value">{orgDashboard.stats?.upcomingEvents || 0}</span><span className="stat-label">Upcoming</span></div></Card>
                  <Card className="stat-card"><div className="stat-icon yellow"><Icons.Clock /></div><div className="stat-info"><span className="stat-value">{orgDashboard.stats?.uniqueVendors || 0}</span><span className="stat-label">Unique Vendors</span></div></Card>
                  <Card className="stat-card"><div className="stat-icon gray"><Icons.Document /></div><div className="stat-info"><span className="stat-value">{orgDashboard.stats?.pendingApplications || 0}</span><span className="stat-label">Pending Apps</span></div></Card>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1.5rem' }}>
                  <Card>
                    <h3 style={{ margin: '0 0 1rem' }}>Response Rates</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Application Review Rate</span>
                          <span style={{ fontWeight: 600 }}>{orgDashboard.stats?.applicationResponseRate || 0}%</span>
                        </div>
                        <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${orgDashboard.stats?.applicationResponseRate || 0}%`, background: 'var(--primary)', borderRadius: 4 }} />
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Invitation Accept Rate</span>
                          <span style={{ fontWeight: 600 }}>{orgDashboard.stats?.invitationAcceptRate || 0}%</span>
                        </div>
                        <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${orgDashboard.stats?.invitationAcceptRate || 0}%`, background: 'var(--success)', borderRadius: 4 }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                        <div style={{ textAlign: 'center', flex: 1, padding: '0.5rem', background: '#dcfce7', borderRadius: 8 }}><span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#16a34a' }}>{orgDashboard.stats?.approvedApplications || 0}</span><br/><span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Approved</span></div>
                        <div style={{ textAlign: 'center', flex: 1, padding: '0.5rem', background: '#fee2e2', borderRadius: 8 }}><span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>{orgDashboard.stats?.rejectedApplications || 0}</span><br/><span style={{ fontSize: '0.75rem', color: '#dc2626' }}>Rejected</span></div>
                        <div style={{ textAlign: 'center', flex: 1, padding: '0.5rem', background: '#dbeafe', borderRadius: 8 }}><span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#2563eb' }}>{orgDashboard.stats?.acceptedInvitations || 0}</span><br/><span style={{ fontSize: '0.75rem', color: '#2563eb' }}>Accepted Invites</span></div>
                      </div>
                    </div>
                  </Card>
                  <Card>
                    <h3 style={{ margin: '0 0 1rem' }}>Upcoming Events</h3>
                    {orgDashboard.upcomingEvents?.length > 0 ? orgDashboard.upcomingEvents.map(e => (
                      <div key={e._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                        <div>
                          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9375rem' }}>{e.eventName}</p>
                          <p style={{ margin: '2px 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{e.city}, {e.state} • {e.vendorCount}{e.maxVendors ? `/${e.maxVendors}` : ''} vendors</p>
                        </div>
                        <Badge variant={e.daysUntil <= 7 ? 'danger' : e.daysUntil <= 30 ? 'warning' : 'default'}>{e.daysUntil}d away</Badge>
                      </div>
                    )) : <p className="empty-text">No upcoming events</p>}
                  </Card>
                </div>

                {/* Event Readiness */}
                {eventReadiness.length > 0 && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <h2 style={{ margin: '0 0 1rem', fontSize: '1.125rem' }}>Event Vendor Readiness</h2>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {eventReadiness.map(ev => (
                        <Card key={ev._id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9375rem' }}>{ev.eventName}</p>
                              <span style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{ev.daysUntil}d away</span>
                            </div>
                            <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
                              <div style={{ height: '100%', width: `${ev.complianceRate}%`, background: ev.complianceRate === 100 ? 'var(--success)' : ev.complianceRate >= 50 ? '#ca8a04' : 'var(--danger)', borderRadius: 4 }} />
                            </div>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                              {ev.compliantVendors}/{ev.totalVendors} vendors compliant
                              {ev.requiredPermitCount > 0 && ` • ${ev.requiredPermitCount} permits required`}
                            </p>
                          </div>
                          <div style={{ textAlign: 'center', minWidth: 56 }}>
                            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: ev.complianceRate === 100 ? '#16a34a' : ev.complianceRate >= 50 ? '#ca8a04' : '#dc2626' }}>{ev.complianceRate}%</span>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem' }}><LoadingSpinner /></div>
            )}
          </div>
        )}

        {organizerTab === 'my-events' && !selectedOrgEvent && (
          <div className="organizer-events-list">
            {(() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const upcomingEvents = organizerEvents.filter(e => {
                const eventDate = new Date(e.endDate || e.startDate);
                // Show only future events that are not closed or cancelled
                return eventDate >= today && e.status !== 'closed' && e.status !== 'cancelled';
              });
              return upcomingEvents.length > 0 ? upcomingEvents.map(event => (
              <Card key={event._id} className="organizer-event-card">
                <div className="event-header">
                  <div>
                    <h3>{event.eventName}</h3>
                    <p className="event-date"><Icons.Calendar /> {formatDate(event.startDate)} {event.endDate && event.endDate !== event.startDate && `- ${formatDate(event.endDate)}`}</p>
                    <p className="event-location"><Icons.MapPin /> {event.location?.city}, {event.location?.state}</p>
                  </div>
                  <div className="event-badges">
                    <Badge variant={event.status === 'published' ? 'success' : event.status === 'pending_review' ? 'warning' : event.status === 'closed' ? 'warning' : event.status === 'cancelled' ? 'danger' : 'default'}>
                      {event.status === 'pending_review' ? 'Pending Review' : event.status}
                    </Badge>
                  </div>
                </div>
                
                {/* Pending Review Info Banner */}
                {event.status === 'pending_review' && (
                  <div style={{ background: '#fef3c7', borderLeft: '4px solid #f59e0b', borderRadius: 6, padding: '10px 14px', margin: '8px 0', fontSize: '0.8125rem', color: '#92400e' }}>
                    <strong>Awaiting documentation review</strong> — This event is not yet visible to vendors. Upload proof documents (venue contract, city approval, etc.) and wait for PermitWise to approve them.
                  </div>
                )}
                
                {/* Proof Status Button */}
                {event.requiresProof !== false && event.status !== 'cancelled' && (
                  <button 
                    className={`proof-status-btn ${event.verificationStatus === 'approved' ? 'approved' : event.verificationStatus === 'rejected' ? 'rejected' : 'pending'}`}
                    onClick={() => event.verificationStatus !== 'approved' && setShowProofUploadModal(event)}
                    disabled={event.verificationStatus === 'approved'}
                  >
                    {event.verificationStatus === 'approved' ? (
                      <><Icons.Check /> Verified</>
                    ) : event.verificationStatus === 'rejected' ? (
                      <><Icons.Alert /> Rejected - Upload New Proof</>
                    ) : event.verificationStatus === 'info_needed' ? (
                      <><Icons.Alert /> Info Needed - Upload More</>
                    ) : (event.proofDocuments?.length || 0) > 0 ? (
                      <><Icons.Clock /> Proof Pending Review</>
                    ) : (
                      <><Icons.Upload /> Upload Proof Documents</>
                    )}
                  </button>
                )}
                
                <div className="event-details">
                  <div className="event-detail-row">
                    <span className="event-detail-label">Event Type</span>
                    <span className="event-detail-value">{event.eventType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not set'}</span>
                  </div>
                  <div className="event-detail-row">
                    <span className="event-detail-label">Max Vendors</span>
                    <span className="event-detail-value">{event.maxVendors || 'Unlimited'}</span>
                  </div>
                  <div className="event-detail-row">
                    <span className="event-detail-label">Applications</span>
                    <span className="event-detail-value">{event.vendorApplications?.length || 0}</span>
                  </div>
                  <div className="event-detail-row">
                    <span className="event-detail-label">Approved Vendors</span>
                    <span className="event-detail-value">{event.assignedVendors?.length || 0}</span>
                  </div>
                  {event.requiredPermitTypes?.length > 0 && (
                    <div className="event-detail-row">
                      <span className="event-detail-label">Required Permits</span>
                      <span className="event-detail-value">{event.requiredPermitTypes.length}</span>
                    </div>
                  )}
                  {event.feeStructure?.boothFee > 0 && (
                    <div className="event-detail-row">
                      <span className="event-detail-label">Booth Fee</span>
                      <span className="event-detail-value">${event.feeStructure.boothFee}</span>
                    </div>
                  )}
                </div>
                <div className="event-actions">
                  {event.status === 'closed' || event.status === 'cancelled' ? (
                    <Button size="sm" variant="outline" onClick={() => fetchEventApplications(event._id)}><Icons.Eye /> View Details</Button>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEditEventModal(event)}>
                        <Icons.Edit /> {event.status === 'published' ? 'Edit Name/Address' : 'Edit'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => fetchEventApplications(event._id)}>Manage Vendors</Button>
                    </>
                  )}
                  {event.status === 'draft' && <Button size="sm" onClick={() => updateEventStatus(event._id, 'published')}>Publish</Button>}
                  {event.status === 'pending_review' && <Button size="sm" variant="outline" onClick={() => setShowProofUploadModal(event)}><Icons.Upload /> Upload Proof</Button>}
                  {event.status === 'published' && <Button size="sm" variant="outline" onClick={() => updateEventStatus(event._id, 'closed')}>Close</Button>}
                  {(event.status === 'draft' || event.status === 'published' || event.status === 'pending_review') && (
                    <Button size="sm" variant="danger" onClick={() => cancelEvent(event._id, event.startDate)}>Cancel Event</Button>
                  )}
                </div>
              </Card>
            )) : (
              <EmptyState icon={Icons.Event} title="No upcoming events" description="Create a new event to start accepting vendor applications." />
            );
            })()}
          </div>
        )}
        
        {/* Past Events Tab */}
        {organizerTab === 'past-events' && !selectedOrgEvent && (
          <div className="organizer-events-list past-events">
            <div className="section-description">
              <p>Review your completed events, vendor attendance, and uploaded documentation.</p>
            </div>
            {(() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const pastEvents = organizerEvents.filter(e => {
                const eventDate = new Date(e.endDate || e.startDate);
                // Include events that are either: past their end date OR closed/cancelled
                return eventDate < today || e.status === 'closed' || e.status === 'cancelled';
              });
              return pastEvents.length > 0 ? pastEvents.map(event => {
                const approvedVendors = event.vendorApplications?.filter(v => v.status === 'approved') || [];
                const totalAttended = event.assignedVendors?.length || approvedVendors.length;
                return (
                  <Card key={event._id} className="organizer-event-card past-event-card">
                    <div className="event-header">
                      <div>
                        <h3>{event.eventName}</h3>
                        <p className="event-date"><Icons.Calendar /> {formatDate(event.startDate)} {event.endDate && event.endDate !== event.startDate && `- ${formatDate(event.endDate)}`}</p>
                        <p className="event-location"><Icons.MapPin /> {event.location?.city}, {event.location?.state}</p>
                      </div>
                      <Badge variant={event.status === 'cancelled' ? 'danger' : event.status === 'closed' ? 'warning' : 'default'}>
                        {event.status === 'cancelled' ? 'Cancelled' : event.status === 'closed' ? 'Closed' : 'Completed'}
                      </Badge>
                    </div>
                    
                    <div className="past-event-summary">
                      <div className="summary-stat">
                        <span className="stat-value">{totalAttended}</span>
                        <span className="stat-label">Vendors Attended</span>
                      </div>
                      <div className="summary-stat">
                        <span className="stat-value">{event.vendorApplications?.length || 0}</span>
                        <span className="stat-label">Total Applications</span>
                      </div>
                      <div className="summary-stat">
                        <span className="stat-value">{event.proofDocuments?.length || 0}</span>
                        <span className="stat-label">Documents</span>
                      </div>
                    </div>
                    
                    {/* Vendors who attended */}
                    {totalAttended > 0 && (
                      <div className="past-event-vendors">
                        <h4>Vendors ({totalAttended})</h4>
                        <div className="vendor-list-compact">
                          {(event.assignedVendors || approvedVendors).slice(0, 5).map((v, idx) => (
                            <span key={idx} className="vendor-chip">
                              {v.vendorBusinessId?.businessName || 'Vendor'}
                            </span>
                          ))}
                          {totalAttended > 5 && <span className="vendor-chip more">+{totalAttended - 5} more</span>}
                        </div>
                      </div>
                    )}
                    
                    {/* Event documents/proof */}
                    {event.proofDocuments?.length > 0 && (
                      <div className="past-event-documents">
                        <h4>Event Documents</h4>
                        <div className="document-list-compact">
                          {event.proofDocuments.map((doc, idx) => (
                            <a key={idx} href={getSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer" className="doc-link-chip">
                              <Icons.Document /> {doc.name || doc.category}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="event-actions">
                      <Button size="sm" variant="outline" onClick={() => fetchEventApplications(event._id)}>View Details</Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        try {
                          const result = await api.post(`/events/organizer/${event._id}/duplicate`);
                          toast.success(result.message || 'Event duplicated as draft');
                          fetchOrganizerData();
                          setOrganizerTab('my-events');
                        } catch (err) { toast.error(err.message); }
                      }}>Duplicate as Template</Button>
                    </div>
                  </Card>
                );
              }) : (
                <EmptyState icon={Icons.Clock} title="No past events" description="Your completed events will appear here." />
              );
            })()}
          </div>
        )}

        {organizerTab === 'my-events' && selectedOrgEvent && (
          <div className="event-management">
            <button className="back-link" onClick={() => setSelectedOrgEvent(null)}>← Back to Events</button>
            <Card className="event-management-header">
              <h2>{selectedOrgEvent.eventName}</h2>
              <p>{formatDate(selectedOrgEvent.startDate)} • {selectedOrgEvent.location?.city}, {selectedOrgEvent.location?.state}</p>
              <div className="invite-vendor-section">
                <h4>Invite Vendor</h4>
                <div className="invite-vendor-options">
                  <div className="invite-option">
                    <label>Select from registered vendors:</label>
                    <div className="invite-select-row">
                      <Select 
                        value={selectedVendorId} 
                        onChange={(e) => {
                          setSelectedVendorId(e.target.value);
                          const vendor = registeredVendors.find(v => v._id === e.target.value);
                          if (vendor) setInviteEmail(vendor.email);
                        }}
                        options={[
                          { value: '', label: 'Select a vendor...' },
                          ...registeredVendors.map(v => ({
                            value: v._id,
                            label: `${v.businessName} (${v.vendorType || 'Vendor'})`
                          }))
                        ]}
                      />
                    </div>
                  </div>
                  <div className="invite-divider"><span>OR</span></div>
                  <div className="invite-option">
                    <label>Enter email directly:</label>
                    <Input placeholder="vendor@example.com" value={inviteEmail} onChange={(e) => { setInviteEmail(e.target.value); setSelectedVendorId(''); }} />
                  </div>
                </div>
                <Button onClick={() => inviteVendor(selectedOrgEvent._id)} disabled={!inviteEmail}>Invite Vendor</Button>
              </div>
            </Card>
            
            {/* Summary stats */}
            <div className="vendor-summary-stats" style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="summary-item" style={{ flex: 1, textAlign: 'center', padding: '12px', background: 'var(--gray-50)', borderRadius: 8 }}>
                <span className="count" style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gray-900)' }}>{vendorApplications.length}</span>
                <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>Total</span>
              </div>
              <div className="summary-item" style={{ flex: 1, textAlign: 'center', padding: '12px', background: '#dcfce7', borderRadius: 8 }}>
                <span className="count" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#16a34a' }}>{vendorApplications.filter(a => a.status === 'approved' || a.status === 'accepted').length}</span>
                <span style={{ display: 'block', fontSize: '0.8125rem', color: '#16a34a' }}>Accepted</span>
              </div>
              <div className="summary-item" style={{ flex: 1, textAlign: 'center', padding: '12px', background: '#fef3c7', borderRadius: 8 }}>
                <span className="count" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ca8a04' }}>{vendorApplications.filter(a => a.status === 'pending' || a.status === 'invited').length}</span>
                <span style={{ display: 'block', fontSize: '0.8125rem', color: '#ca8a04' }}>Pending</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Vendor Applications & Invitations</h3>
              <Button size="sm" variant="outline" onClick={async () => {
                try {
                  const token = localStorage.getItem('permitwise_token');
                  const resp = await fetch(`/api/events/organizer/${selectedOrgEvent._id}/export-vendors?format=csv`, { headers: { Authorization: `Bearer ${token}` } });
                  if (!resp.ok) throw new Error('Export failed');
                  const blob = await resp.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = `${selectedOrgEvent.eventName.replace(/[^a-zA-Z0-9]/g, '_')}_vendors.csv`; a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Vendor list exported');
                } catch (err) { toast.error(err.message); }
              }}><Icons.Download /> Export CSV</Button>
            </div>
            {vendorApplications.length > 0 ? (
              <div className="applications-list">
                {vendorApplications.map(app => {
                  const statusLabel = app.status === 'approved' || app.status === 'accepted' ? 'Accepted' : app.status === 'rejected' ? 'Rejected' : app.status === 'waitlist' ? 'Waitlisted' : app.status === 'declined' ? 'Declined' : app.status === 'invited' ? 'Invited' : 'Pending';
                  const statusVariant = app.status === 'approved' || app.status === 'accepted' ? 'success' : app.status === 'rejected' || app.status === 'declined' ? 'danger' : app.status === 'waitlist' ? 'warning' : 'default';
                  return (
                    <Card key={app._id} className="application-card-compact" onClick={() => setViewingVendorApp(app)} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--primary)' }}>
                          {(app.vendorBusinessId?.businessName || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <h4 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600 }}>{app.vendorBusinessId?.businessName || 'Unknown Vendor'}</h4>
                            {app.isInvitation && <span style={{ fontSize: '0.6875rem', color: 'var(--gray-400)', fontWeight: 500 }}>Invitation</span>}
                          </div>
                          <p style={{ margin: '2px 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                            {app.vendorBusinessId?.primaryVendorType && <span>{app.vendorBusinessId.primaryVendorType} • </span>}
                            {app.isInvitation ? 'Invited' : 'Applied'} {formatDate(app.appliedAt)}
                            {app.respondedAt && ` • Responded ${formatDate(app.respondedAt)}`}
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                          <Badge variant={app.complianceStatus === 'ready' ? 'success' : app.complianceStatus === 'partial' ? 'warning' : 'danger'}>
                            {app.complianceStatus === 'ready' ? '✓ Compliant' : app.complianceStatus === 'partial' ? '~ Partial' : '✗ Missing'}
                          </Badge>
                          <Badge variant={statusVariant}>{statusLabel}</Badge>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="empty-text">No applications or invitations yet</p>
            )}
          </div>
        )}

        {/* Vendor Detail Modal */}
        <Modal isOpen={!!viewingVendorApp} onClose={() => setViewingVendorApp(null)} title="Vendor Details" size="lg">
          {viewingVendorApp && (() => {
            const app = viewingVendorApp;
            const statusLabel = app.status === 'approved' || app.status === 'accepted' ? 'Accepted' : app.status === 'rejected' ? 'Rejected' : app.status === 'waitlist' ? 'Waitlisted' : app.status === 'declined' ? 'Declined' : app.status === 'invited' ? 'Invited' : 'Pending';
            const statusVariant = app.status === 'approved' || app.status === 'accepted' ? 'success' : app.status === 'rejected' || app.status === 'declined' ? 'danger' : app.status === 'waitlist' ? 'warning' : 'default';
            const canRevoke = (app.isInvitation && (app.status === 'invited' || app.status === 'accepted')) || (!app.isInvitation && app.status === 'approved');
            const canManageApplication = !app.isInvitation && app.status === 'pending';

            return (
              <div className="vendor-detail-modal">
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
                    {(app.vendorBusinessId?.businessName || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0 }}>{app.vendorBusinessId?.businessName || 'Unknown Vendor'}</h3>
                    <p style={{ margin: '2px 0 0', color: 'var(--gray-500)', fontSize: '0.875rem' }}>{app.vendorBusinessId?.primaryVendorType || 'Vendor'}</p>
                  </div>
                  <Badge variant={statusVariant} size="lg">{statusLabel}</Badge>
                </div>

                {/* Info grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 8 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Source</p>
                    <p style={{ margin: '4px 0 0', fontSize: '0.875rem', fontWeight: 500 }}>{app.isInvitation ? 'Organizer Invitation' : 'Vendor Application'}</p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{app.isInvitation ? 'Invited' : 'Applied'}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '0.875rem', fontWeight: 500 }}>{formatDate(app.appliedAt)}</p>
                  </div>
                  {app.respondedAt && (
                    <div>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Responded</p>
                      <p style={{ margin: '4px 0 0', fontSize: '0.875rem', fontWeight: 500 }}>{formatDate(app.respondedAt)}</p>
                    </div>
                  )}
                  <div>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Compliance</p>
                    <Badge variant={app.complianceStatus === 'ready' ? 'success' : app.complianceStatus === 'partial' ? 'warning' : 'danger'} style={{ marginTop: 4 }}>
                      {app.complianceStatus === 'ready' ? 'Fully Compliant' : app.complianceStatus === 'partial' ? 'Partially Compliant' : 'Missing Permits'}
                    </Badge>
                  </div>
                </div>

                {/* Application notes */}
                {app.applicationNotes && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: 'var(--gray-700)' }}>Vendor Notes</h4>
                    <p style={{ margin: 0, padding: '0.75rem', background: 'var(--gray-50)', borderRadius: 8, fontSize: '0.875rem', color: 'var(--gray-700)', fontStyle: 'italic', borderLeft: '3px solid var(--gray-300)' }}>"{app.applicationNotes}"</p>
                  </div>
                )}

                {/* Permits section */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: 'var(--gray-700)' }}>Vendor Permits ({app.vendorPermits?.length || 0})</h4>
                  {app.vendorPermits?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {app.vendorPermits.map((vp, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.625rem 0.75rem', background: vp.status === 'active' ? '#f0fdf4' : vp.status === 'expired' ? '#fef2f2' : 'var(--gray-50)', borderRadius: 8, borderLeft: `3px solid ${vp.status === 'active' ? '#16a34a' : vp.status === 'expired' ? '#dc2626' : '#9ca3af'}` }}>
                          <div>
                            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray-800)' }}>{vp.name}</p>
                            {vp.expiryDate && <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Expires: {new Date(vp.expiryDate).toLocaleDateString()}</p>}
                          </div>
                          <Badge variant={vp.status === 'active' ? 'success' : vp.status === 'expired' ? 'danger' : 'default'}>{vp.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--gray-500)', fontStyle: 'italic' }}>No permits on file</p>
                  )}
                  {app.missingPermits?.length > 0 && (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: 'var(--danger)' }}>⚠ {app.missingPermits.length} required permit{app.missingPermits.length > 1 ? 's' : ''} missing for this event</p>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '1rem', borderTop: '1px solid var(--gray-200)' }}>
                  {canManageApplication && (
                    <>
                      <Button size="sm" onClick={() => { handleApplication(app._id, 'approved'); setViewingVendorApp(null); }}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => { handleApplication(app._id, 'waitlist'); setViewingVendorApp(null); }}>Waitlist</Button>
                      <Button size="sm" variant="danger" onClick={() => { handleApplication(app._id, 'rejected'); setViewingVendorApp(null); }}>Reject</Button>
                    </>
                  )}
                  {canRevoke && (
                    <Button size="sm" variant="danger" onClick={async () => {
                      try {
                        await api.delete(`/events/organizer/${selectedOrgEvent._id}/invite/${app.vendorBusinessId?._id}`);
                        toast.success((app.status === 'accepted' || app.status === 'approved') ? 'Vendor removed from event' : 'Invitation cancelled');
                        fetchEventApplications(selectedOrgEvent._id);
                        setViewingVendorApp(null);
                      } catch (err) { toast.error(err.message); }
                    }}>{(app.status === 'accepted' || app.status === 'approved') ? 'Remove from Event' : 'Cancel Invite'}</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setViewingVendorApp(null)}>Close</Button>
                </div>
              </div>
            );
          })()}
        </Modal>

        {organizerTab === 'applications' && (
          <div className="all-applications">
            <h3>All Pending Applications</h3>
            <p className="section-description">Review applications across all your events</p>
            {organizerEvents.flatMap(e => (e.vendorApplications || []).filter(a => a.status === 'pending').map(a => ({ ...a, eventName: e.eventName, eventId: e._id }))).length > 0 ? (
              <div className="applications-list">
                {organizerEvents.flatMap(e => (e.vendorApplications || []).filter(a => a.status === 'pending').map(a => ({ ...a, eventName: e.eventName, eventId: e._id }))).map(app => (
                  <Card key={app._id} className="application-card pending">
                    <div className="application-info">
                      <small className="event-name">{app.eventName}</small>
                      <h4>{app.vendorBusinessId?.businessName || 'Unknown Vendor'}</h4>
                      <p className="applied-date">Applied: {formatDate(app.appliedAt)}</p>
                    </div>
                    <div className="application-actions">
                      <Button size="sm" onClick={() => { fetchEventApplications(app.eventId); setOrganizerTab('my-events'); }}>Review</Button>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <EmptyState icon={Icons.Checklist} title="No pending applications" description="All applications have been reviewed" />
            )}
          </div>
        )}

        {organizerTab === 'create' && (
          <Card className="create-event-form">
            <h3>Create New Event</h3>
            <div className="form-section">
              <Input label="Event Name *" placeholder="Downtown Food Festival 2024" value={newOrgEvent.eventName} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, eventName: e.target.value }))} />
              <Input label="Description" placeholder="Annual food truck festival..." value={newOrgEvent.description} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, description: e.target.value }))} />
            </div>
            <div className="form-section">
              <div className="form-section-title">Date & Location</div>
              <div className="form-row">
                <DateInput label="Start Date *" required value={newOrgEvent.startDate} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, startDate: e.target.value }))} />
                <DateInput label="End Date" value={newOrgEvent.endDate} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, endDate: e.target.value }))} />
                <DateInput label="Application Deadline" value={newOrgEvent.applicationDeadline} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, applicationDeadline: e.target.value }))} />
              </div>
              <div className="form-row">
                <Select label="State *" value={newOrgEvent.state} onChange={(e) => handleEventStateChange(e.target.value)} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                <Select label="City *" value={newOrgEvent.city} onChange={(e) => handleEventCityChange(e.target.value)} options={[
                  { value: '', label: newOrgEvent.state ? 'Select City' : 'Select state first' }, 
                  ...getAvailableCities(newOrgEvent.state).map(c => ({ value: c, label: c })),
                  ...(newOrgEvent.state ? [{ value: '__custom__', label: '➕ Add Other City...' }] : [])
                ]} disabled={!newOrgEvent.state} />
              </div>
              {newOrgEvent.city === '__custom__' && (
                <div className="custom-city-entry">
                  <Input 
                    label="City Name *" 
                    value={newOrgEvent.customCity || ''} 
                    onChange={(e) => setNewOrgEvent(ev => ({ ...ev, customCity: e.target.value }))}
                    placeholder="Enter city name"
                  />
                  <p className="form-hint">
                    <Icons.Info /> This city doesn't have permit data yet. You can still create the event and manage permit requirements manually.
                  </p>
                </div>
              )}
              <div className="location-request-link">
                <button type="button" className="text-link" onClick={() => setShowLocationRequestModal(true)}>
                  Location not listed? Request a new location
                </button>
              </div>
              <Input label="Venue Address" placeholder="123 Main Street" value={newOrgEvent.address} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, address: e.target.value }))} />
            </div>
            <div className="form-section">
              <div className="form-section-title">Event Details</div>
              <div className="form-row">
                <Select label="Event Type" value={newOrgEvent.eventType} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, eventType: e.target.value }))} options={[
                  { value: 'food_event', label: 'Food Event' },
                  { value: 'farmers_market', label: 'Farmers Market' },
                  { value: 'festival', label: 'Festival' },
                  { value: 'fair', label: 'Fair' },
                  { value: 'craft_show', label: 'Craft Show' },
                  { value: 'night_market', label: 'Night Market' },
                  { value: 'other', label: 'Other' }
                ]} />
                <Input label="Max Vendors" type="number" placeholder="50" value={newOrgEvent.maxVendors} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, maxVendors: e.target.value }))} />
                <Select label="Status" value={newOrgEvent.status} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, status: e.target.value }))} options={[
                  { value: 'draft', label: 'Draft (not visible)' },
                  { value: 'published', label: 'Published (accepting applications)' }
                ]} />
              </div>
            </div>
            <div className="form-section">
              <div className="form-section-title">Fees</div>
              <div className="form-row">
                <Input label="Application Fee ($)" type="number" value={newOrgEvent.feeStructure.applicationFee} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, feeStructure: { ...ev.feeStructure, applicationFee: parseFloat(e.target.value) || 0 } }))} />
                <Input label="Booth Fee ($)" type="number" value={newOrgEvent.feeStructure.boothFee} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, feeStructure: { ...ev.feeStructure, boothFee: parseFloat(e.target.value) || 0 } }))} />
                <Input label="Electricity Fee ($)" type="number" value={newOrgEvent.feeStructure.electricityFee} onChange={(e) => setNewOrgEvent(ev => ({ ...ev, feeStructure: { ...ev.feeStructure, electricityFee: parseFloat(e.target.value) || 0 } }))} />
              </div>
            </div>
            <div className="form-section">
              <div className="form-section-title">Required Permits</div>
              <p className="form-hint">Select permits vendors must have to participate in your event</p>
              {!newOrgEvent.city ? (
                <p className="empty-text">Please select a city above to see available permits for that location.</p>
              ) : availablePermitTypes.length > 0 ? (
                <div className="permit-type-checkboxes">
                  {availablePermitTypes.map(pt => {
                    const isChecked = (newOrgEvent.requiredPermitTypes || []).includes(pt._id);
                    return (
                      <label key={pt._id} className="checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={isChecked} 
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewOrgEvent(ev => ({ 
                                ...ev, 
                                requiredPermitTypes: [...(ev.requiredPermitTypes || []), pt._id] 
                              }));
                            } else {
                              setNewOrgEvent(ev => ({ 
                                ...ev, 
                                requiredPermitTypes: (ev.requiredPermitTypes || []).filter(id => id !== pt._id) 
                              }));
                            }
                          }} 
                        />
                        <span>{pt.name} <small>({pt.jurisdictionId?.city || 'State-wide'})</small></span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-text">No permits found for {newOrgEvent.state}. You can add custom requirements below.</p>
              )}
            </div>
            <div className="form-section">
              <div className="form-section-title">Custom Requirements</div>
              <p className="form-hint">Add any additional requirements vendors must complete (insurance certificates, health inspections, etc.)</p>
              {(newOrgEvent.customPermitRequirements || []).map((req, index) => (
                <div key={index} className="custom-requirement-row">
                  <Input 
                    placeholder="Requirement name (e.g., General Liability Insurance)" 
                    value={req.name || ''} 
                    onChange={(e) => {
                      const updated = [...(newOrgEvent.customPermitRequirements || [])];
                      updated[index] = { ...updated[index], name: e.target.value };
                      setNewOrgEvent(ev => ({ ...ev, customPermitRequirements: updated }));
                    }} 
                  />
                  <Input 
                    placeholder="Description (optional)" 
                    value={req.description || ''} 
                    onChange={(e) => {
                      const updated = [...(newOrgEvent.customPermitRequirements || [])];
                      updated[index] = { ...updated[index], description: e.target.value };
                      setNewOrgEvent(ev => ({ ...ev, customPermitRequirements: updated }));
                    }} 
                  />
                  <button 
                    type="button" 
                    className="btn btn-outline btn-sm remove-req-btn"
                    onClick={() => {
                      const updated = (newOrgEvent.customPermitRequirements || []).filter((_, i) => i !== index);
                      setNewOrgEvent(ev => ({ ...ev, customPermitRequirements: updated }));
                    }}
                  >
                    <Icons.X /> Remove
                  </button>
                </div>
              ))}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setNewOrgEvent(ev => ({ 
                    ...ev, 
                    customPermitRequirements: [...(ev.customPermitRequirements || []), { name: '', description: '', required: true }] 
                  }));
                }}
              >
                <Icons.Plus /> Add Custom Requirement
              </Button>
            </div>
            
            {/* Event Proof Documents */}
            <div className="form-section">
              <div className="form-section-title">Venue/Event Proof Documents</div>
              <p className="form-hint">Upload documents proving your ownership or rights to host events at this venue (venue contract, city permit, insurance, etc.)</p>
              
              {/* Previous proofs from other events */}
              {previousEventProofs.length > 0 && (
                <div className="previous-proofs-section">
                  <label className="form-label">Use proof from previous events:</label>
                  <div className="previous-proofs-list">
                    {previousEventProofs.map((proof, idx) => (
                      <label key={idx} className="proof-checkbox">
                        <input 
                          type="checkbox" 
                          checked={selectedPreviousProofs.includes(proof._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPreviousProofs([...selectedPreviousProofs, proof._id]);
                            } else {
                              setSelectedPreviousProofs(selectedPreviousProofs.filter(id => id !== proof._id));
                            }
                          }}
                        />
                        <span className="proof-info">
                          <strong>{proof.name}</strong>
                          <small>{proof.category?.replace(/_/g, ' ')} • from {proof.eventName}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Upload new proofs */}
              <div className="upload-new-proofs">
                <label className="form-label">Or upload new documents:</label>
                <input 
                  type="file" 
                  id="event-proof-upload"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const newProofs = files.map(file => ({ file, name: file.name, category: 'venue_contract' }));
                    setEventProofFiles([...eventProofFiles, ...newProofs]);
                    e.target.value = '';
                  }}
                  hidden
                />
                <label htmlFor="event-proof-upload" className="file-input-btn">
                  <Icons.Upload /> Choose Files
                </label>
                <p className="upload-hint" style={{ marginTop: '8px' }}>Accepted: PDF, JPG, PNG (max 10MB each)</p>
              </div>
              
              {/* List of files to upload */}
              {eventProofFiles.length > 0 && (
                <div className="proof-files-list">
                  {eventProofFiles.map((fileData, idx) => (
                    <div key={idx} className="proof-file-item">
                      <Icons.Document />
                      <span className="file-name">{fileData.file.name}</span>
                      <Select 
                        value={fileData.category}
                        onChange={(e) => {
                          const updated = [...eventProofFiles];
                          updated[idx].category = e.target.value;
                          setEventProofFiles(updated);
                        }}
                        options={[
                          { value: 'venue_contract', label: 'Venue Contract' },
                          { value: 'event_permit', label: 'Event Permit' },
                          { value: 'city_approval', label: 'City Approval' },
                          { value: 'insurance', label: 'Insurance' },
                          { value: 'other', label: 'Other' }
                        ]}
                      />
                      <button 
                        type="button" 
                        className="remove-file-btn"
                        onClick={() => setEventProofFiles(eventProofFiles.filter((_, i) => i !== idx))}
                      >
                        <Icons.X />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="form-actions">
              <Button onClick={createOrganizerEvent} disabled={!newOrgEvent.eventName || !newOrgEvent.startDate || !newOrgEvent.state || (!newOrgEvent.city || (newOrgEvent.city === '__custom__' && !newOrgEvent.customCity))}>
                Create Event
              </Button>
            </div>
          </Card>
        )}
        
        {/* Location Request Modal */}
        <Modal isOpen={showLocationRequestModal} onClose={() => setShowLocationRequestModal(false)} title="Request New Location">
          <p style={{ marginBottom: '16px', color: '#6b7280' }}>
            Can't find your event location? Request it below and we'll add it to our system.
          </p>
          <Input 
            label="City *" 
            placeholder="City name" 
            value={locationRequest.city} 
            onChange={(e) => setLocationRequest(r => ({ ...r, city: e.target.value }))} 
          />
          <Select 
            label="State *" 
            value={locationRequest.state}
            onChange={(e) => setLocationRequest(r => ({ ...r, state: e.target.value }))}
            options={[
              { value: '', label: 'Select State' },
              ...US_STATES.map(s => ({ value: s, label: s }))
            ]}
          />
          <Input 
            label="Reason (optional)" 
            placeholder="Why do you need this location?" 
            value={locationRequest.reason} 
            onChange={(e) => setLocationRequest(r => ({ ...r, reason: e.target.value }))} 
          />
          <div className="form-actions">
            <Button variant="outline" onClick={() => setShowLocationRequestModal(false)}>Cancel</Button>
            <Button 
              onClick={submitLocationRequest} 
              disabled={!locationRequest.city || !locationRequest.state || locationRequestSubmitting}
            >
              {locationRequestSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </Modal>
        
        {/* Verification Status Modal */}
        <Modal isOpen={showVerificationModal} onClose={() => setShowVerificationModal(false)} title="Account Verification Required">
          <div className="verification-modal-content">
            {verificationStatus === 'info_needed' && (
              <>
                <div className="verification-icon warning"><Icons.Alert /></div>
                <h3>Additional Information Needed</h3>
                <p className="verification-message">{user?.organizerProfile?.verificationNotes || 'Please upload the required verification documents to continue.'}</p>
                <p>Go to Settings → Documents to upload the requested information.</p>
              </>
            )}
            
            {verificationStatus === 'rejected' && (
              <>
                <div className="verification-icon danger"><Icons.X /></div>
                <h3>Account Not Approved</h3>
                <p className="verification-message">{user?.organizerProfile?.verificationNotes || 'Your organizer application was not approved.'}</p>
                <p>Please contact support for more information about the decision.</p>
              </>
            )}
            
            {verificationStatus === 'pending' && !organizerVerified && (
              <>
                <div className="verification-icon info"><Icons.Clock /></div>
                <h3>Verification Required to Create Events</h3>
                <p>To create events, you must first verify your organizer account by uploading required documentation.</p>
                <div className="verification-requirements">
                  <h4>Required Documents:</h4>
                  <ul>
                    <li>Business License or Registration</li>
                    <li>Government-issued ID</li>
                    <li>Proof of event organizing experience (optional but recommended)</li>
                  </ul>
                </div>
                <p>Once uploaded, our team will review your documents within 1-2 business days.</p>
              </>
            )}
            
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowVerificationModal(false)}>Close</Button>
              <Button onClick={() => { 
                setShowVerificationModal(false); 
                setOrganizerTab('settings');
                // Set a timeout to ensure the page renders before setting the tab
                setTimeout(() => {
                  const event = new CustomEvent('setOrganizerSettingsTab', { detail: 'documents' });
                  window.dispatchEvent(event);
                }, 100);
              }}>
                {verificationStatus === 'pending' ? 'Upload Documents' : 'Go to Settings'}
              </Button>
            </div>
          </div>
        </Modal>
        
        {/* Proof Upload Modal */}
        <Modal isOpen={showProofUploadModal !== null} onClose={() => { setShowProofUploadModal(null); setProofUploadFiles([]); }} title="Upload Event Proof Documents" size="md">
          {showProofUploadModal && (
            <div className="proof-upload-form">
              <p className="form-hint">Upload documents to verify your ownership or rights to host "{showProofUploadModal.eventName}"</p>
              
              {/* Existing documents */}
              {showProofUploadModal.proofDocuments?.length > 0 && (
                <div className="existing-proofs">
                  <h4>Existing Documents</h4>
                  {showProofUploadModal.proofDocuments.map((doc, idx) => (
                    <div key={idx} className={`proof-doc-item ${doc.status}`}>
                      <Icons.Document />
                      <span className="doc-name">{doc.name}</span>
                      <Badge variant={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'}>
                        {doc.status}
                      </Badge>
                      <a href={getSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer" className="view-link">View</a>
                    </div>
                  ))}
                  {showProofUploadModal.proofDocuments.some(d => d.status === 'rejected' && d.reviewNotes) && (
                    <Alert type="warning" style={{ marginTop: '12px' }}>
                      <strong>Reviewer Feedback:</strong>
                      <p style={{ margin: '4px 0 0' }}>
                        {showProofUploadModal.proofDocuments.find(d => d.status === 'rejected')?.reviewNotes}
                      </p>
                    </Alert>
                  )}
                </div>
              )}
              
              {/* Upload new documents */}
              <div className="upload-new-proofs" style={{ marginTop: '16px' }}>
                <label className="form-label">Upload New Documents</label>
                <input 
                  type="file" 
                  id="event-proof-upload-modal"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const newProofs = files.map(file => ({ file, name: file.name, category: 'venue_contract' }));
                    setProofUploadFiles([...proofUploadFiles, ...newProofs]);
                    e.target.value = '';
                  }}
                />
                <label htmlFor="event-proof-upload-modal" className="file-input-btn">
                  <Icons.Upload /> Choose Files
                </label>
                <p className="upload-hint">Accepted: PDF, JPG, PNG (max 10MB each)</p>
              </div>
              
              {/* List of files to upload */}
              {proofUploadFiles.length > 0 && (
                <div className="proof-files-list">
                  {proofUploadFiles.map((fileData, idx) => (
                    <div key={idx} className="proof-file-item">
                      <Icons.Document />
                      <span className="file-name">{fileData.file.name}</span>
                      <Select 
                        value={fileData.category}
                        onChange={(e) => {
                          const updated = [...proofUploadFiles];
                          updated[idx].category = e.target.value;
                          setProofUploadFiles(updated);
                        }}
                        options={[
                          { value: 'venue_contract', label: 'Venue Contract' },
                          { value: 'event_permit', label: 'Event Permit' },
                          { value: 'city_approval', label: 'City Approval' },
                          { value: 'insurance', label: 'Insurance' },
                          { value: 'other', label: 'Other' }
                        ]}
                        style={{ width: '150px', flex: 'none' }}
                      />
                      <button type="button" className="remove-file-btn" onClick={() => setProofUploadFiles(proofUploadFiles.filter((_, i) => i !== idx))}>
                        <Icons.X />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="modal-actions">
                <Button variant="outline" onClick={() => { setShowProofUploadModal(null); setProofUploadFiles([]); }}>Cancel</Button>
                <Button onClick={uploadEventProofs} disabled={proofUploadFiles.length === 0 || proofUploading} loading={proofUploading}>
                  Upload {proofUploadFiles.length > 0 && `(${proofUploadFiles.length})`}
                </Button>
              </div>
            </div>
          )}
        </Modal>
        
        {/* Edit Event Modal */}
        <Modal isOpen={showEditEventModal} onClose={() => { setShowEditEventModal(false); setEditingEvent(null); }} title={editingEvent?.status === 'published' ? 'Edit Event (Limited)' : 'Edit Event'} size="lg">
          {editingEvent && (
            <div className="edit-event-form">
              {editingEvent.status === 'published' && (
                <Alert type="info" style={{ marginBottom: '16px' }}>
                  <strong>Limited Editing</strong>
                  <p style={{ margin: '4px 0 0' }}>Published events can only have their name and address modified. To edit other details, change the event to draft status first.</p>
                </Alert>
              )}
              
              <div className="form-section">
                <Input label="Event Name *" value={editingEvent.eventName} onChange={(e) => setEditingEvent(ev => ({ ...ev, eventName: e.target.value }))} />
                {editingEvent.status !== 'published' && (
                  <Input label="Description" value={editingEvent.description} onChange={(e) => setEditingEvent(ev => ({ ...ev, description: e.target.value }))} />
                )}
              </div>
              
              <div className="form-section">
                <div className="form-section-title">Date & Location</div>
                {editingEvent.status !== 'published' && (
                  <div className="form-row">
                    <DateInput label="Start Date *" required value={editingEvent.startDate} onChange={(e) => setEditingEvent(ev => ({ ...ev, startDate: e.target.value }))} />
                    <DateInput label="End Date" value={editingEvent.endDate} onChange={(e) => setEditingEvent(ev => ({ ...ev, endDate: e.target.value }))} />
                    <DateInput label="Application Deadline" value={editingEvent.applicationDeadline} onChange={(e) => setEditingEvent(ev => ({ ...ev, applicationDeadline: e.target.value }))} />
                  </div>
                )}
                {editingEvent.status !== 'published' && (
                  <div className="form-row">
                    <Select label="State *" value={editingEvent.state} onChange={(e) => handleEditEventStateChange(e.target.value)} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                    <Select label="City *" value={editingEvent.city} onChange={(e) => handleEditEventCityChange(e.target.value)} options={[
                      { value: '', label: editingEvent.state ? 'Select City' : 'Select state first' }, 
                      ...getAvailableCities(editingEvent.state).map(c => ({ value: c, label: c })),
                      ...(editingEvent.state ? [{ value: '__custom__', label: '➕ Add Other City...' }] : [])
                    ]} disabled={!editingEvent.state} />
                  </div>
                )}
                {editingEvent.city === '__custom__' && (
                  <div className="custom-city-entry">
                    <Input 
                      label="City Name *" 
                      value={editingEvent.customCity || ''} 
                      onChange={(e) => setEditingEvent(ev => ({ ...ev, customCity: e.target.value }))}
                      placeholder="Enter city name"
                    />
                    <p className="form-hint">
                      <Icons.Info /> This city doesn't have permit data. Permit requirements can be managed manually.
                    </p>
                  </div>
                )}
                <Input label="Venue Address" value={editingEvent.address || ''} onChange={(e) => setEditingEvent(ev => ({ ...ev, address: e.target.value }))} />
              </div>
              
              {editingEvent.status !== 'published' && (
                <>
                  <div className="form-section">
                    <div className="form-section-title">Event Details</div>
                    <div className="form-row">
                      <Select label="Event Type" value={editingEvent.eventType} onChange={(e) => setEditingEvent(ev => ({ ...ev, eventType: e.target.value }))} options={[
                        { value: 'food_event', label: 'Food Event' },
                        { value: 'farmers_market', label: 'Farmers Market' },
                        { value: 'festival', label: 'Festival' },
                        { value: 'fair', label: 'Fair' },
                        { value: 'craft_show', label: 'Craft Show' },
                        { value: 'night_market', label: 'Night Market' },
                        { value: 'other', label: 'Other' }
                      ]} />
                      <Input label="Max Vendors" type="number" value={editingEvent.maxVendors} onChange={(e) => setEditingEvent(ev => ({ ...ev, maxVendors: e.target.value }))} />
                      <Select label="Status" value={editingEvent.status} onChange={(e) => setEditingEvent(ev => ({ ...ev, status: e.target.value }))} options={[
                        { value: 'draft', label: 'Draft (not visible)' },
                        { value: 'published', label: 'Published (accepting applications)' },
                        { value: 'closed', label: 'Closed (no new applications)' }
                      ]} />
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="form-section-title">Fees</div>
                    <div className="form-row">
                      <Input label="Application Fee ($)" type="number" value={editingEvent.feeStructure?.applicationFee || 0} onChange={(e) => setEditingEvent(ev => ({ ...ev, feeStructure: { ...ev.feeStructure, applicationFee: parseFloat(e.target.value) || 0 } }))} />
                      <Input label="Booth Fee ($)" type="number" value={editingEvent.feeStructure?.boothFee || 0} onChange={(e) => setEditingEvent(ev => ({ ...ev, feeStructure: { ...ev.feeStructure, boothFee: parseFloat(e.target.value) || 0 } }))} />
                      <Input label="Electricity Fee ($)" type="number" value={editingEvent.feeStructure?.electricityFee || 0} onChange={(e) => setEditingEvent(ev => ({ ...ev, feeStructure: { ...ev.feeStructure, electricityFee: parseFloat(e.target.value) || 0 } }))} />
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="form-section-title">Required Permits</div>
                    <p className="form-hint">Select permits vendors must have to participate</p>
                    {!editingEvent.state ? (
                      <p className="empty-text">Please select a state above to see available permits.</p>
                    ) : editPermitTypes.length > 0 ? (
                  <div className="permit-type-checkboxes">
                    {editPermitTypes.map(pt => {
                      const isChecked = (editingEvent.requiredPermitTypes || []).includes(pt._id);
                      return (
                        <label key={pt._id} className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={isChecked} 
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditingEvent(ev => ({ ...ev, requiredPermitTypes: [...(ev.requiredPermitTypes || []), pt._id] }));
                              } else {
                                setEditingEvent(ev => ({ ...ev, requiredPermitTypes: (ev.requiredPermitTypes || []).filter(id => id !== pt._id) }));
                              }
                            }} 
                          />
                          <span>{pt.name} <small>({pt.jurisdictionId?.city || 'State-wide'})</small></span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="empty-text">No permits found for {editingEvent.state}.</p>
                )}
              </div>
              <div className="form-section">
                <div className="form-section-title">Custom Requirements</div>
                <p className="form-hint">Additional requirements vendors must complete</p>
                {(editingEvent.customPermitRequirements || []).map((req, index) => (
                  <div key={index} className="custom-requirement-row">
                    <Input 
                      label="Requirement Name"
                      placeholder="e.g., General Liability Insurance" 
                      value={req.name || ''} 
                      onChange={(e) => {
                        const updated = [...(editingEvent.customPermitRequirements || [])];
                        updated[index] = { ...updated[index], name: e.target.value };
                        setEditingEvent(ev => ({ ...ev, customPermitRequirements: updated }));
                      }} 
                    />
                    <Input 
                      label="Description (optional)"
                      placeholder="Additional details..." 
                      value={req.description || ''} 
                      onChange={(e) => {
                        const updated = [...(editingEvent.customPermitRequirements || [])];
                        updated[index] = { ...updated[index], description: e.target.value };
                        setEditingEvent(ev => ({ ...ev, customPermitRequirements: updated }));
                      }} 
                    />
                    <button 
                      type="button" 
                      className="btn btn-outline btn-sm remove-req-btn"
                      onClick={() => {
                        const updated = (editingEvent.customPermitRequirements || []).filter((_, i) => i !== index);
                        setEditingEvent(ev => ({ ...ev, customPermitRequirements: updated }));
                      }}
                    >
                      <Icons.X /> Remove
                    </button>
                  </div>
                ))}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setEditingEvent(ev => ({ 
                      ...ev, 
                      customPermitRequirements: [...(ev.customPermitRequirements || []), { name: '', description: '', required: true }] 
                    }));
                  }}
                >
                  <Icons.Plus /> Add Custom Requirement
                </Button>
              </div>
                </>
              )}
              
              {/* Proof Documents Section - available for all event statuses except cancelled */}
              {editingEvent.status !== 'cancelled' && (
                <div className="form-section">
                  <div className="form-section-title">Proof Documents</div>
                  <p className="form-hint">Upload documents proving your ownership or rights to host events at this venue</p>
                  
                  {/* Existing documents */}
                  {editingEvent.proofDocuments?.length > 0 && (
                    <div className="existing-proofs" style={{ marginBottom: '12px' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Existing Documents</h4>
                      {editingEvent.proofDocuments.map((doc, idx) => (
                        <div key={idx} className={`proof-doc-item ${doc.status}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: '#f9fafb', borderRadius: '6px', marginBottom: '6px' }}>
                          <Icons.Document />
                          <span style={{ flex: 1 }}>{doc.name}</span>
                          <Badge variant={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'}>
                            {doc.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowEditEventModal(false);
                      setShowProofUploadModal(editingEvent);
                    }}
                  >
                    <Icons.Upload /> Upload Proof Documents
                  </Button>
                </div>
              )}
              
              <div className="form-actions">
                <Button variant="outline" onClick={() => { setShowEditEventModal(false); setEditingEvent(null); }}>Cancel</Button>
                <Button onClick={saveEditedEvent} disabled={!editingEvent.eventName || (editingEvent.status !== 'published' && (!editingEvent.startDate || !editingEvent.state || (!editingEvent.city || (editingEvent.city === '__custom__' && !editingEvent.customCity))))}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    );
  }

  // VENDOR VIEW - requires Elite plan
  // Show different messaging for expired vs no plan
  if (!hasVendorAccess) {
    return (
      <div className="upgrade-feature-page">
        <div className="upgrade-feature-content">
          <div className="upgrade-feature-icon">{isExpired ? <Icons.Lock /> : <Icons.Event />}</div>
          <h1>{isExpired ? 'Subscription Expired' : 'Event Readiness'}</h1>
          <Badge variant={isExpired ? 'danger' : 'warning'}>{isExpired ? 'Renew to Access' : 'Elite Plan Required'}</Badge>
          <p className="upgrade-feature-description">
            {isExpired 
              ? 'Your subscription has expired. Renew now to access event readiness features and see your compliance status for upcoming events.'
              : 'See your permit compliance status for events you\'ve been invited to participate in. Know instantly if you\'re ready or what\'s missing.'}
          </p>
          
          <div className="upgrade-feature-grid">
            <div className="upgrade-feature-item">
              <div className="upgrade-feature-item-icon"><Icons.Check /></div>
              <h3>Readiness Dashboard</h3>
              <p>See at a glance which events you're ready for and which need attention</p>
            </div>
            <div className="upgrade-feature-item">
              <div className="upgrade-feature-item-icon"><Icons.Check /></div>
              <h3>Apply to Events</h3>
              <p>Browse and apply to events looking for vendors like you</p>
            </div>
            <div className="upgrade-feature-item">
              <div className="upgrade-feature-item-icon"><Icons.Check /></div>
              <h3>Missing Permit Alerts</h3>
              <p>Know exactly which permits or documents you need for each event</p>
            </div>
            <div className="upgrade-feature-item">
              <div className="upgrade-feature-item-icon"><Icons.Check /></div>
              <h3>Organizer Integration</h3>
              <p>Event organizers can verify your compliance directly</p>
            </div>
          </div>

          <div className="upgrade-pricing-card elite">
            <span className="upgrade-plan-label">Elite Plan</span>
            <div className="upgrade-price">$99<span>/month</span></div>
            <p>Includes everything in Pro + team accounts & priority support</p>
          </div>

          <Button size="lg" onClick={async () => { try { const data = await api.post('/subscription/checkout', { plan: 'elite' }); if (data.url) window.location.href = data.url; else if (data.message) toast.info(data.message); } catch (err) { toast.error(err.message); } }}>{isExpired ? 'Renew Subscription' : 'Upgrade to Elite'}</Button>
          <p className="upgrade-note">{isExpired ? 'Restore full access immediately' : '14-day free trial • Cancel anytime'}</p>
          
          <div className="request-event-section">
            <p>Know of an event that should be on PermitWise?</p>
            <Button variant="outline" onClick={() => setShowRequestModal(true)}>Request an Event</Button>
          </div>
        </div>
        
        {/* Event Request Modal - available even without Elite */}
        <Modal isOpen={showRequestModal} onClose={() => setShowRequestModal(false)} title="Request an Event">
          {requestSubmitted ? (
            <div className="request-success">
              <div className="success-icon"><Icons.Check /></div>
              <h3>Request Submitted!</h3>
              <p>We'll review your event request and add it to PermitWise soon.</p>
            </div>
          ) : (
            <>
              <p className="modal-intro">Tell us about an event you'd like to see on PermitWise for permit compliance tracking.</p>
              <Input label="Event Name *" placeholder="Downtown Food Festival" value={requestForm.eventName} onChange={(e) => setRequestForm(f => ({ ...f, eventName: e.target.value }))} />
              <Input label="Organizer Name" placeholder="City Events Department" value={requestForm.organizerName} onChange={(e) => setRequestForm(f => ({ ...f, organizerName: e.target.value }))} />
              <div className="form-row">
                <Input label="City *" placeholder="Austin" value={requestForm.city} onChange={(e) => setRequestForm(f => ({ ...f, city: e.target.value }))} />
                <Select label="State *" value={requestForm.state} onChange={(e) => setRequestForm(f => ({ ...f, state: e.target.value }))} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
              </div>
              <div className="form-row">
                <DateInput label="Start Date" value={requestForm.startDate} onChange={(e) => setRequestForm(f => ({ ...f, startDate: e.target.value }))} />
                <DateInput label="End Date" value={requestForm.endDate} onChange={(e) => setRequestForm(f => ({ ...f, endDate: e.target.value }))} />
              </div>
              <Input label="Event Website" placeholder="https://example.com" value={requestForm.website} onChange={(e) => setRequestForm(f => ({ ...f, website: e.target.value }))} />
              <Input label="Additional Info" placeholder="Any other details about the event..." value={requestForm.additionalInfo} onChange={(e) => setRequestForm(f => ({ ...f, additionalInfo: e.target.value }))} />
              <div className="modal-actions">
                <Button variant="outline" onClick={() => setShowRequestModal(false)}>Cancel</Button>
                <Button onClick={submitEventRequest} loading={requestSubmitting} disabled={!requestForm.eventName || !requestForm.city || !requestForm.state}>Submit Request</Button>
              </div>
            </>
          )}
        </Modal>
      </div>
    );
  }

  const getReadinessIcon = (status) => {
    if (status === 'ready') return <Icons.Check />;
    if (status === 'missing_permit' || status === 'expired_permit') return <Icons.Alert />;
    return <Icons.Clock />;
  };

  // ====== ATTENDING EVENT FUNCTIONS ======
  const resetAttendingForm = () => {
    setAttendingForm({ eventName: '', organizerName: '', description: '', startDate: '', endDate: '', city: '', state: '', address: '', venueName: '', eventType: 'other', notes: '', requiredPermits: [], complianceChecklist: [] });
    setAttendingCitySelection('');
    setAttendingSuggestedPermits([]);
    setNewPermitName('');
    setNewChecklistItem('');
    setEditingAttendingEvent(null);
    setSelectedExistingPermit('');
  };

  const openAddAttendingModal = () => {
    resetAttendingForm();
    setShowAddAttendingModal(true);
  };

  const openEditAttendingModal = (ae) => {
    setEditingAttendingEvent(ae);
    const city = ae.location?.city || '';
    const state = ae.location?.state || '';
    // Try to match an operating city
    const opCities = business?.operatingCities || [];
    const match = opCities.find(c => c.city.toLowerCase() === city.toLowerCase() && c.state === state);
    setAttendingCitySelection(match ? `${match.city}||${match.state}` : (city ? '__custom__' : ''));
    setAttendingForm({
      eventName: ae.eventName || '', organizerName: ae.organizerName || '', description: ae.description || '',
      startDate: ae.startDate ? new Date(ae.startDate).toISOString().split('T')[0] : '',
      endDate: ae.endDate ? new Date(ae.endDate).toISOString().split('T')[0] : '',
      city, state, address: ae.location?.address || '',
      venueName: ae.location?.venueName || '', eventType: ae.eventType || 'other', notes: ae.notes || '',
      requiredPermits: (ae.requiredPermits || []).map(p => ({
        name: p.name || '', status: p.status || 'needed', notes: p.notes || '',
        permitTypeId: p.permitTypeId || null, vendorPermitId: p.vendorPermitId || null, dueDate: p.dueDate || null
      })),
      complianceChecklist: (ae.complianceChecklist || []).map(c => ({
        item: c.item || '', completed: c.completed || false, notes: c.notes || ''
      }))
    });
    setSelectedExistingPermit('');
    setShowAddAttendingModal(true);
  };

  const handleAttendingCityChange = async (selection) => {
    setAttendingCitySelection(selection);
    setAttendingSuggestedPermits([]);
    if (!selection || selection === '__custom__') {
      if (selection === '__custom__') {
        setAttendingForm(f => ({ ...f, city: '', state: '' }));
      }
      return;
    }
    const [city, state] = selection.split('||');
    setAttendingForm(f => ({ ...f, city, state }));
    // Auto-search for available permits in this city
    const vendorType = business?.primaryVendorType;
    if (city && state && vendorType) {
      setAttendingSearchingPermits(true);
      try {
        const data = await api.get(`/permit-types/required?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&vendorType=${encodeURIComponent(vendorType)}`);
        setAttendingSuggestedPermits(data.permitTypes || []);
      } catch (err) { console.error(err); }
      finally { setAttendingSearchingPermits(false); }
    }
  };

  const addSuggestedPermitToForm = (pt) => {
    const alreadyAdded = attendingForm.requiredPermits.some(rp => rp.permitTypeId === pt._id);
    if (alreadyAdded) return;
    // Also check if vendor already has this permit
    const vendorPermit = vendorPermitsList.find(vp => vp.permitTypeId?._id === pt._id);
    setAttendingForm(f => ({
      ...f,
      requiredPermits: [...f.requiredPermits, {
        name: pt.name,
        status: vendorPermit?.status === 'active' ? 'obtained' : vendorPermit?.status === 'in_progress' ? 'in_progress' : 'needed',
        permitTypeId: pt._id,
        vendorPermitId: vendorPermit?._id || undefined,
        notes: ''
      }]
    }));
  };

  const addExistingPermitToForm = () => {
    if (!selectedExistingPermit) return;
    const permit = vendorPermitsList.find(p => p._id === selectedExistingPermit);
    if (!permit) return;
    // Avoid duplicates
    const alreadyAdded = attendingForm.requiredPermits.some(rp => rp.vendorPermitId === permit._id);
    if (alreadyAdded) { setSelectedExistingPermit(''); return; }
    const permitName = permit.isCustom ? permit.customName : (permit.permitTypeId?.name || 'Unnamed Permit');
    setAttendingForm(f => ({
      ...f,
      requiredPermits: [...f.requiredPermits, {
        name: permitName,
        status: (permit.status === 'active') ? 'obtained' : (permit.status === 'in_progress') ? 'in_progress' : 'needed',
        permitTypeId: permit.permitTypeId?._id || undefined,
        vendorPermitId: permit._id,
        notes: ''
      }]
    }));
    setSelectedExistingPermit('');
  };

  const addPermitToForm = () => {
    if (!newPermitName.trim()) return;
    setAttendingForm(f => ({ ...f, requiredPermits: [...f.requiredPermits, { name: newPermitName.trim(), status: 'needed', notes: '' }] }));
    setNewPermitName('');
  };

  const removePermitFromForm = (index) => {
    setAttendingForm(f => ({ ...f, requiredPermits: f.requiredPermits.filter((_, i) => i !== index) }));
  };

  const addChecklistItemToForm = () => {
    if (!newChecklistItem.trim()) return;
    setAttendingForm(f => ({ ...f, complianceChecklist: [...f.complianceChecklist, { item: newChecklistItem.trim(), completed: false }] }));
    setNewChecklistItem('');
  };

  const removeChecklistItemFromForm = (index) => {
    setAttendingForm(f => ({ ...f, complianceChecklist: f.complianceChecklist.filter((_, i) => i !== index) }));
  };

  const saveAttendingEvent = async () => {
    try {
      const payload = {
        eventName: attendingForm.eventName,
        organizerName: attendingForm.organizerName,
        description: attendingForm.description,
        location: { city: attendingForm.city, state: attendingForm.state, address: attendingForm.address, venueName: attendingForm.venueName },
        startDate: attendingForm.startDate, endDate: attendingForm.endDate || undefined,
        eventType: attendingForm.eventType, notes: attendingForm.notes,
        requiredPermits: attendingForm.requiredPermits.map(p => ({
          name: p.name, status: p.status || 'needed', notes: p.notes || '',
          permitTypeId: p.permitTypeId || undefined, vendorPermitId: p.vendorPermitId || undefined, dueDate: p.dueDate || undefined
        })),
        complianceChecklist: attendingForm.complianceChecklist.map(c => ({
          item: c.item, completed: c.completed || false, notes: c.notes || ''
        }))
      };
      if (editingAttendingEvent) {
        await api.put(`/attending-events/${editingAttendingEvent._id}`, payload);
        toast.success('Event updated');
      } else {
        await api.post('/attending-events', payload);
        toast.success('Event added to your readiness tracker');
      }
      setShowAddAttendingModal(false);
      resetAttendingForm();
      fetchVendorData();
    } catch (error) { toast.error(error.message || 'Failed to save event'); }
  };

  const deleteAttendingEvent = async (id) => {
    const ok = await confirm({ title: 'Delete Event', message: 'Delete this event from your tracker?', confirmText: 'Delete', variant: 'danger' });
    if (!ok) return;
    try {
      await api.delete(`/attending-events/${id}`);
      toast.success('Event removed');
      fetchVendorData();
      setSelectedAttendingEvent(null);
    } catch (error) { toast.error('Failed to delete'); }
  };

  const updateAttendingPermitStatus = async (eventId, permitIndex, status) => {
    // Optimistic update for real-time UI
    if (selectedAttendingEvent && selectedAttendingEvent._id === eventId) {
      setSelectedAttendingEvent(prev => {
        const updated = { ...prev, requiredPermits: prev.requiredPermits.map((p, i) => i === permitIndex ? { ...p, status } : p) };
        updated.completedPermits = updated.requiredPermits.filter(p => p.status === 'obtained' || p.status === 'not_applicable').length;
        return updated;
      });
    }
    setAttendingEvents(prev => prev.map(ae => {
      if (ae._id !== eventId) return ae;
      const updated = { ...ae, requiredPermits: ae.requiredPermits.map((p, i) => i === permitIndex ? { ...p, status } : p) };
      updated.completedPermits = updated.requiredPermits.filter(p => p.status === 'obtained' || p.status === 'not_applicable').length;
      updated.completedItems = updated.completedPermits + (updated.complianceChecklist?.filter(c => c.completed).length || 0);
      updated.totalItems = (updated.requiredPermits?.length || 0) + (updated.complianceChecklist?.length || 0);
      return updated;
    }));
    try {
      await api.put(`/attending-events/${eventId}/permit/${permitIndex}`, { status });
      fetchVendorData();
    } catch (error) { toast.error('Failed to update'); fetchVendorData(); }
  };

  const updateAttendingChecklistItem = async (eventId, checkIndex, completed) => {
    // Optimistic update for real-time UI
    if (selectedAttendingEvent && selectedAttendingEvent._id === eventId) {
      setSelectedAttendingEvent(prev => {
        const updated = { ...prev, complianceChecklist: prev.complianceChecklist.map((c, i) => i === checkIndex ? { ...c, completed } : c) };
        updated.completedChecklist = updated.complianceChecklist.filter(c => c.completed).length;
        return updated;
      });
    }
    setAttendingEvents(prev => prev.map(ae => {
      if (ae._id !== eventId) return ae;
      const updated = { ...ae, complianceChecklist: ae.complianceChecklist.map((c, i) => i === checkIndex ? { ...c, completed } : c) };
      updated.completedChecklist = updated.complianceChecklist.filter(c => c.completed).length;
      updated.completedItems = (updated.requiredPermits?.filter(p => p.status === 'obtained' || p.status === 'not_applicable').length || 0) + updated.completedChecklist;
      updated.totalItems = (updated.requiredPermits?.length || 0) + (updated.complianceChecklist?.length || 0);
      return updated;
    }));
    try {
      await api.put(`/attending-events/${eventId}/checklist/${checkIndex}`, { completed });
      fetchVendorData();
    } catch (error) { toast.error('Failed to update'); fetchVendorData(); }
  };

  // Separate attending events by status
  const upcomingAttendingEvents = attendingEvents.filter(ae => ae.status === 'upcoming' && new Date(ae.endDate || ae.startDate) >= new Date());
  const pastAttendingEvents = attendingEvents.filter(ae => ae.status !== 'upcoming' || new Date(ae.endDate || ae.startDate) < new Date());

  // Separate events by source
  const organizerInvitations = events.filter(e => e.eventSource === 'organizer_invitation' && e.status !== 'closed' && e.status !== 'cancelled');
  const marketplaceEvents = events.filter(e => (e.eventSource === 'admin_added' || e.eventSource === 'vendor_application') && e.status !== 'closed' && e.status !== 'cancelled');
  
  // Past events: closed, cancelled, or date has passed
  const pastVendorEvents = events.filter(e => {
    if (e.status === 'closed' || e.status === 'cancelled') return true;
    const eventDate = new Date(e.endDate || e.startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  });

  // Check for events vendor can still apply to (not already applied or invited)
  // Use both local events list AND vendorHasApplied flag from server
  const appliedEventIds = new Set([
    ...events.map(e => e._id?.toString?.() || e._id)
  ]);
  const availableEvents = publishedEvents.filter(e => {
    const eventIdStr = e._id?.toString?.() || e._id;
    // Filter out if: in my events list, already applied (server flag), or not published
    return !appliedEventIds.has(eventIdStr) && !e.vendorHasApplied && e.status === 'published';
  });

  const renderEventCard = (event) => (
    <Card key={event._id} className={`event-readiness-card ${event.readinessStatus}`}>
      <div className="event-readiness-header">
        <div className="event-date-badge">
          <span className="month">{new Date(event.startDate).toLocaleDateString('en-US', { month: 'short' })}</span>
          <span className="day">{new Date(event.startDate).getDate()}</span>
        </div>
        <div className="event-info">
          <h3>{event.eventName}</h3>
          <p className="organizer">Organized by {event.organizerName}</p>
          <p className="location"><Icons.MapPin /> {event.location?.city}, {event.location?.state}</p>
          {event.invitationStatus === 'invited' && (
            <div className="invitation-actions">
              <Badge variant="warning">Invitation Pending</Badge>
              <Button size="sm" onClick={() => respondToInvitation(event._id, true)}>Accept</Button>
              <Button size="sm" variant="outline" onClick={() => respondToInvitation(event._id, false)}>Decline</Button>
            </div>
          )}
        </div>
        <div className={`readiness-badge ${event.readinessColor}`}>
          {getReadinessIcon(event.readinessStatus)}
          <span>{event.readinessStatus === 'ready' ? 'Ready' : 'Action Needed'}</span>
        </div>
      </div>
      
      <div className="event-readiness-details">
        <div className="permit-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${event.requiredPermitsCount > 0 ? (event.readyCount / event.requiredPermitsCount) * 100 : 100}%` }}></div>
          </div>
          <span className="progress-text">{event.readyCount}/{event.requiredPermitsCount} permits ready</span>
        </div>
        
        {event.readinessStatus !== 'ready' && (
          <div className="readiness-issues">
            <p className="readiness-label">{event.readinessLabel}</p>
            <Button variant="outline" size="sm" onClick={() => setSelectedEvent(event)}>View Details</Button>
          </div>
        )}
        
        {event.readinessStatus === 'ready' && (
          <div className="readiness-success">
            <Icons.Check /> All permits and documents are in order
          </div>
        )}
      </div>
      
      <div className="event-card-actions">
        <Button variant="outline" size="sm" onClick={() => setSelectedEvent(event)}>View Details</Button>
        <Button variant="outline" size="sm" className="withdraw-btn" onClick={() => setShowWithdrawModal(event)}>Withdraw</Button>
      </div>
    </Card>
  );

  return (
    <div className="events-page">
      <div className="page-header">
        <div>
          <h1>Event Readiness</h1>
          <p>Your compliance status for events</p>
        </div>
        <div className="page-header-actions">
          <Button variant="primary" onClick={openAddAttendingModal}>
            <Icons.Plus /> Add Attending Event
          </Button>
          <Button variant="outline" onClick={() => setShowRequestModal(true)}>
            <Icons.Plus /> Request Event
          </Button>
        </div>
      </div>
      
      {/* Event Readiness Section - Self-tracked attending events */}
      <div className="events-section attending-events-section">
        <div className="events-section-header">
          <h2>📋 Event Readiness <span className="section-count">{upcomingAttendingEvents.length}</span></h2>
          <p className="section-description">Events you're attending — track your permit and compliance readiness</p>
        </div>
        {upcomingAttendingEvents.length > 0 ? (
          <div className="events-readiness-list">
            {upcomingAttendingEvents.map(ae => (
              <Card key={ae._id} className={`readiness-card attending-card ${ae.readinessStatus}`}>
                <div className="readiness-header">
                  <div className="event-date-badge attending">
                    <span className="month">{new Date(ae.startDate).toLocaleDateString('en-US', { month: 'short' })}</span>
                    <span className="day">{new Date(ae.startDate).getDate()}</span>
                  </div>
                  <div className="readiness-info">
                    <h3>{ae.eventName}</h3>
                    {ae.organizerName && <p className="organizer">by {ae.organizerName}</p>}
                    {ae.location?.city && <p className="location"><Icons.MapPin /> {ae.location.city}{ae.location.state ? `, ${ae.location.state}` : ''}</p>}
                  </div>
                  <Badge variant={ae.readinessColor === 'success' ? 'success' : ae.readinessColor === 'warning' ? 'warning' : 'danger'}>
                    {ae.readinessStatus === 'ready' ? 'Ready' : ae.readinessStatus === 'no_requirements' ? 'No Items' : ae.readinessStatus === 'permits_needed' ? 'Permits Needed' : 'In Progress'}
                  </Badge>
                </div>
                
                {ae.totalItems > 0 && (
                  <div className="readiness-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${(ae.completedItems / ae.totalItems) * 100}%` }}></div>
                    </div>
                    <span className="progress-text">{ae.completedItems}/{ae.totalItems} items complete</span>
                  </div>
                )}
                
                <div className="event-card-actions">
                  <Button variant="outline" size="sm" onClick={() => setSelectedAttendingEvent(ae)}>View / Manage</Button>
                  <Button variant="outline" size="sm" onClick={() => openEditAttendingModal(ae)}>Edit</Button>
                  <Button variant="ghost" size="sm" className="delete-btn" onClick={() => deleteAttendingEvent(ae._id)}>
                    <Icons.Trash />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="section-empty">
            <p>No upcoming events tracked. Click "Add Attending Event" to track an event you'll be attending and monitor your permit readiness.</p>
          </div>
        )}
      </div>
      
      {/* Organizer Invitations Section - Always show */}
      <div className="events-section">
        <div className="events-section-header">
          <h2>📩 Organizer Invitations <span className="section-count">{organizerInvitations.length}</span></h2>
          <p className="section-description">Events you've been personally invited to by organizers</p>
        </div>
        {organizerInvitations.length > 0 ? (
          <div className="events-readiness-list">
            {organizerInvitations.map(event => (
              <Card key={event._id} className={`readiness-card invitation-card ${event.invitationStatus === 'invited' ? 'pending-response' : ''}`}>
                <div className="readiness-header">
                  <div className="event-date-badge">
                    <span className="month">{new Date(event.startDate).toLocaleDateString('en-US', { month: 'short' })}</span>
                    <span className="day">{new Date(event.startDate).getDate()}</span>
                  </div>
                  <div className="readiness-info">
                    <h3>{event.eventName}</h3>
                    <p className="organizer">by {event.organizerName}</p>
                    <p className="location"><Icons.MapPin /> {event.location?.city}, {event.location?.state}</p>
                  </div>
                  {event.invitationStatus === 'invited' ? (
                    <Badge variant="warning">Pending Response</Badge>
                  ) : event.invitationStatus === 'accepted' ? (
                    <Badge variant="success">Accepted</Badge>
                  ) : (
                    <Badge variant="danger">Declined</Badge>
                  )}
                </div>
                
                {event.invitationStatus === 'invited' && (
                  <div className="invitation-actions">
                    <p className="invitation-prompt">You've been invited to participate in this event!</p>
                    <div className="invitation-buttons">
                      <Button size="sm" onClick={() => respondToInvitation(event._id, true)}>Accept Invitation</Button>
                      <Button size="sm" variant="outline" onClick={() => respondToInvitation(event._id, false)}>Decline</Button>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedEvent(event)}>View Details</Button>
                    </div>
                  </div>
                )}
                
                {event.invitationStatus === 'accepted' && (
                  <>
                    <div className="readiness-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${event.requiredPermitsCount > 0 ? (event.readyCount / event.requiredPermitsCount) * 100 : 100}%` }}></div>
                      </div>
                      <span className="progress-text">{event.readyCount}/{event.requiredPermitsCount} permits ready</span>
                    </div>
                    <div className="readiness-footer" onClick={() => setSelectedEvent(event)}>
                      {event.readinessStatus === 'ready' ? (
                        <span className="status-ready"><Icons.Check /> All permits ready</span>
                      ) : (
                        <span className="status-issues">{event.readinessLabel} <span className="view-link">View Details →</span></span>
                      )}
                    </div>
                  </>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <div className="section-empty">
            <p>No invitations yet. When organizers invite you to their events, they'll appear here.</p>
          </div>
        )}
      </div>
      
      {/* Available Events to Apply (Marketplace) - Always show */}
      <div className="events-section">
        <div className="events-section-header">
          <h2>🔍 Browse Events <span className="section-count">{availableEvents.length}</span></h2>
          <p className="section-description">Open events accepting vendor applications</p>
        </div>
        {availableEvents.length > 0 ? (
          <div className="available-events-grid">
            {availableEvents.map(event => (
              <Card key={event._id} className="available-event-card">
                <div className="event-date-badge">
                  <span className="month">{new Date(event.startDate).toLocaleDateString('en-US', { month: 'short' })}</span>
                  <span className="day">{new Date(event.startDate).getDate()}</span>
                </div>
                <div className="event-info">
                  <h3>{event.eventName}</h3>
                  <p className="location"><Icons.MapPin /> {event.location?.city}, {event.location?.state}</p>
                  <p className="organizer">By {event.organizerName}</p>
                  {event.applicationDeadline && <p className="deadline">Apply by {formatDate(event.applicationDeadline)}</p>}
                </div>
                <Button size="sm" onClick={() => setShowApplyModal(event)}>Apply</Button>
              </Card>
            ))}
          </div>
        ) : (
          <div className="section-empty">
            <p>No events currently accepting applications. Check back soon!</p>
          </div>
        )}
      </div>
      
      {/* Marketplace / Routine Events (Admin-added or vendor applied) - Always show */}
      <div className="events-section">
        <div className="events-section-header">
          <h2>🎪 Your Participating Events <span className="section-count">{marketplaceEvents.length}</span></h2>
          <p className="section-description">Events you've applied to or routine events in your area</p>
        </div>
        {marketplaceEvents.length > 0 ? (
          <div className="events-readiness-list">
            {marketplaceEvents.map(renderEventCard)}
          </div>
        ) : (
          <div className="section-empty">
            <p>No participating events yet. Apply to events above or wait for admin-assigned routine events.</p>
          </div>
        )}
      </div>
      
      {/* Past Events Section for Vendors */}
      {pastVendorEvents.length > 0 && (
        <div className="events-section past-events-section">
          <div className="events-section-header">
            <h2>📜 Past Events <span className="section-count">{pastVendorEvents.length}</span></h2>
            <p className="section-description">Completed, closed, or cancelled events you participated in</p>
          </div>
          <div className="events-readiness-list">
            {pastVendorEvents.map(event => (
              <Card key={event._id} className={`event-readiness-card past ${event.status === 'cancelled' ? 'cancelled' : ''}`}>
                <div className="event-readiness-header">
                  <div className="event-date-badge past">
                    <span className="month">{new Date(event.startDate).toLocaleDateString('en-US', { month: 'short' })}</span>
                    <span className="day">{new Date(event.startDate).getDate()}</span>
                  </div>
                  <div className="event-info">
                    <h3>{event.eventName}</h3>
                    <p className="organizer">Organized by {event.organizerName}</p>
                    <p className="location"><Icons.MapPin /> {event.location?.city}, {event.location?.state}</p>
                    {event.status === 'cancelled' && (
                      <div className="cancelled-notice">
                        <Badge variant="danger">Cancelled</Badge>
                        {event.cancellationReason && <p className="cancellation-reason">Reason: {event.cancellationReason}</p>}
                      </div>
                    )}
                    {event.status === 'closed' && <Badge variant="warning">Closed</Badge>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSelectedEvent(event)}>View Details</Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
      
      {/* Past Attending Events Section */}
      {pastAttendingEvents.length > 0 && (
        <div className="events-section past-events-section">
          <div className="events-section-header">
            <h2>📋 Past Tracked Events <span className="section-count">{pastAttendingEvents.length}</span></h2>
            <p className="section-description">Attending events that have ended or been completed</p>
          </div>
          <div className="events-readiness-list">
            {pastAttendingEvents.map(ae => (
              <Card key={ae._id} className="readiness-card attending-card past-attending">
                <div className="readiness-header">
                  <div className="event-date-badge past">
                    <span className="month">{new Date(ae.startDate).toLocaleDateString('en-US', { month: 'short' })}</span>
                    <span className="day">{new Date(ae.startDate).getDate()}</span>
                  </div>
                  <div className="readiness-info">
                    <h3>{ae.eventName}</h3>
                    {ae.organizerName && <p className="organizer">by {ae.organizerName}</p>}
                    {ae.location?.city && <p className="location"><Icons.MapPin /> {ae.location.city}{ae.location.state ? `, ${ae.location.state}` : ''}</p>}
                  </div>
                  <Badge variant={ae.readinessStatus === 'ready' ? 'success' : 'warning'}>
                    {ae.completedItems}/{ae.totalItems} done
                  </Badge>
                </div>
                <div className="event-card-actions">
                  <Button variant="outline" size="sm" onClick={() => setSelectedAttendingEvent(ae)}>View Details</Button>
                  <Button variant="ghost" size="sm" className="delete-btn" onClick={() => deleteAttendingEvent(ae._id)}>
                    <Icons.Trash />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Event Details Modal - Enhanced */}
      <Modal isOpen={!!selectedEvent} onClose={() => setSelectedEvent(null)} title={selectedEvent?.eventName}>
        {selectedEvent && (
          <div className="event-detail-modal">
            {/* Event Info Section */}
            <div className="event-detail-section">
              <h4>Event Information</h4>
              <div className="event-info-grid">
                <div className="info-item">
                  <span className="info-label">Date</span>
                  <span className="info-value">{formatDate(selectedEvent.startDate)}{selectedEvent.endDate && selectedEvent.endDate !== selectedEvent.startDate ? ` - ${formatDate(selectedEvent.endDate)}` : ''}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Location</span>
                  <span className="info-value">{selectedEvent.location?.city}, {selectedEvent.location?.state}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Organizer</span>
                  <span className="info-value">{selectedEvent.organizerName}</span>
                </div>
                {selectedEvent.description && (
                  <div className="info-item full-width">
                    <span className="info-label">Description</span>
                    <span className="info-value">{selectedEvent.description}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Invitation Status & Actions */}
            {selectedEvent.eventSource === 'organizer_invitation' && selectedEvent.invitationStatus === 'invited' && (
              <div className="event-detail-section invitation-section">
                <div className="invitation-banner">
                  <Icons.Bell />
                  <div>
                    <h4>You've Been Invited!</h4>
                    <p>The organizer has personally invited you to participate in this event.</p>
                  </div>
                </div>
                <div className="invitation-response-buttons">
                  <Button onClick={() => { respondToInvitation(selectedEvent._id, true); setSelectedEvent(null); }}>Accept Invitation</Button>
                  <Button variant="outline" onClick={() => { respondToInvitation(selectedEvent._id, false); setSelectedEvent(null); }}>Decline</Button>
                </div>
              </div>
            )}
            
            {/* Required Permits Section */}
            {selectedEvent.requiredPermitTypes && selectedEvent.requiredPermitTypes.length > 0 && (
              <div className="event-detail-section">
                <h4>Required Permits ({selectedEvent.requiredPermitTypes.length})</h4>
                <div className="required-permits-list">
                  {selectedEvent.requiredPermitTypes.map((pt, i) => (
                    <div key={i} className="required-permit-item">
                      <Icons.Document />
                      <span>{typeof pt === 'object' ? pt.name : pt}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Compliance Status Section */}
            {(selectedEvent.invitationStatus === 'accepted' || selectedEvent.eventSource !== 'organizer_invitation' || !selectedEvent.invitationStatus) && (
              <div className="event-detail-section">
                <h4>Your Compliance Status</h4>
                <div className="compliance-overview">
                  <div className="compliance-progress">
                    <div className="progress-bar large">
                      <div className="progress-fill" style={{ width: `${selectedEvent.requiredPermitsCount > 0 ? (selectedEvent.readyCount / selectedEvent.requiredPermitsCount) * 100 : 100}%` }}></div>
                    </div>
                    <span className="progress-label">{selectedEvent.readyCount} of {selectedEvent.requiredPermitsCount} permits ready</span>
                  </div>
                </div>
                
                {selectedEvent.issues?.length > 0 ? (
                  <div className="issues-list">
                    <p className="issues-intro">The following issues need to be resolved:</p>
                    {selectedEvent.issues.map((issue, i) => (
                      <div key={i} className={`issue-item ${issue.type} ${issue.vendorPermitId ? 'clickable' : ''}`} 
                        onClick={issue.vendorPermitId ? () => { setSelectedEvent(null); window.location.hash = 'permits'; } : undefined}
                        style={issue.vendorPermitId ? { cursor: 'pointer' } : undefined}>
                        {issue.type === 'missing' && <><Icons.Alert /> <span><strong>{issue.permit}</strong> - Permit not found</span></>}
                        {issue.type === 'expired' && <><Icons.Clock /> <span><strong>{issue.permit}</strong> - Expired or will expire before event</span></>}
                        {issue.type === 'missing_document' && <><Icons.Document /> <span><strong>{issue.permit}</strong> - Document not uploaded</span></>}
                        {issue.type === 'in_progress' && <><Icons.Clock /> <span><strong>{issue.permit}</strong> - Application in progress</span></>}
                        {issue.vendorPermitId && <span style={{ fontSize: '0.75rem', color: 'var(--primary)', marginLeft: 'auto' }}>View →</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="all-good">
                    <Icons.Check /> All requirements met! You're ready for this event.
                  </div>
                )}
              </div>
            )}
            
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setSelectedEvent(null)}>Close</Button>
              {selectedEvent.issues?.length > 0 && (
                <Button onClick={() => { setSelectedEvent(null); window.location.hash = 'permits'; }}>Go to Permits</Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Apply to Event Modal */}
      <Modal isOpen={!!showApplyModal} onClose={() => setShowApplyModal(null)} title={`Apply to ${showApplyModal?.eventName}`}>
        {showApplyModal && (
          <div className="apply-modal">
            <div className="event-summary">
              <p><strong>Date:</strong> {formatDate(showApplyModal.startDate)}</p>
              <p><strong>Location:</strong> {showApplyModal.location?.city}, {showApplyModal.location?.state}</p>
              {showApplyModal.feeStructure?.boothFee > 0 && <p><strong>Booth Fee:</strong> ${showApplyModal.feeStructure.boothFee}</p>}
            </div>
            <Input label="Application Notes (optional)" placeholder="Tell the organizer about your business..." value={applicationNotes} onChange={(e) => setApplicationNotes(e.target.value)} />
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowApplyModal(null)}>Cancel</Button>
              <Button onClick={() => applyToEvent(showApplyModal._id)}>Submit Application</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Withdraw from Event Modal */}
      <Modal isOpen={!!showWithdrawModal} onClose={() => { setShowWithdrawModal(null); setWithdrawReason(''); }} title="Withdraw from Event">
        {showWithdrawModal && (
          <div className="withdraw-modal">
            <div className="withdraw-warning">
              <Icons.Alert />
              <p>You are about to withdraw from <strong>{showWithdrawModal.eventName}</strong>. The event organizer will be notified of your withdrawal.</p>
            </div>
            <div className="event-summary">
              <p><strong>Date:</strong> {formatDate(showWithdrawModal.startDate)}</p>
              <p><strong>Location:</strong> {showWithdrawModal.location?.city}, {showWithdrawModal.location?.state}</p>
            </div>
            <Input 
              label="Reason for withdrawal (optional)" 
              placeholder="e.g., Schedule conflict, unable to attend..." 
              value={withdrawReason} 
              onChange={(e) => setWithdrawReason(e.target.value)} 
            />
            <p className="withdraw-note">This note will be sent to the event organizer.</p>
            <div className="modal-actions">
              <Button variant="outline" onClick={() => { setShowWithdrawModal(null); setWithdrawReason(''); }}>Cancel</Button>
              <Button variant="danger" onClick={withdrawFromEvent} loading={withdrawing}>Withdraw from Event</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Event Request Modal */}
      <Modal isOpen={showRequestModal} onClose={() => setShowRequestModal(false)} title="Request an Event">
        {requestSubmitted ? (
          <div className="request-success">
            <div className="success-icon"><Icons.Check /></div>
            <h3>Request Submitted!</h3>
            <p>We'll review your event request and add it to PermitWise soon.</p>
          </div>
        ) : (
          <>
            <p className="modal-intro">Tell us about an event you'd like to see on PermitWise for permit compliance tracking.</p>
            <Input label="Event Name *" placeholder="Downtown Food Festival" value={requestForm.eventName} onChange={(e) => setRequestForm(f => ({ ...f, eventName: e.target.value }))} />
            <Input label="Organizer Name" placeholder="City Events Department" value={requestForm.organizerName} onChange={(e) => setRequestForm(f => ({ ...f, organizerName: e.target.value }))} />
            <div className="form-row">
              <Input label="City *" placeholder="Austin" value={requestForm.city} onChange={(e) => setRequestForm(f => ({ ...f, city: e.target.value }))} />
              <Select label="State *" value={requestForm.state} onChange={(e) => setRequestForm(f => ({ ...f, state: e.target.value }))} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
            </div>
            <div className="form-row">
              <Input label="Start Date" type="date" value={requestForm.startDate} onChange={(e) => setRequestForm(f => ({ ...f, startDate: e.target.value }))} />
              <Input label="End Date" type="date" value={requestForm.endDate} onChange={(e) => setRequestForm(f => ({ ...f, endDate: e.target.value }))} />
            </div>
            <Input label="Event Website" placeholder="https://example.com" value={requestForm.website} onChange={(e) => setRequestForm(f => ({ ...f, website: e.target.value }))} />
            <Input label="Additional Info" placeholder="Any other details about the event..." value={requestForm.additionalInfo} onChange={(e) => setRequestForm(f => ({ ...f, additionalInfo: e.target.value }))} />
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setShowRequestModal(false)}>Cancel</Button>
              <Button onClick={submitEventRequest} loading={requestSubmitting} disabled={!requestForm.eventName || !requestForm.city || !requestForm.state}>Submit Request</Button>
            </div>
          </>
        )}
      </Modal>
      
      {/* Location Request Modal */}
      <Modal isOpen={showLocationRequestModal} onClose={() => setShowLocationRequestModal(false)} title="Request New Location">
        <p style={{ marginBottom: '16px', color: '#6b7280' }}>Can't find your event location? Request it to be added by our team.</p>
        <Input label="City *" placeholder="Enter city name" value={locationRequest.city} onChange={(e) => setLocationRequest(l => ({ ...l, city: e.target.value }))} />
        <Select label="State *" value={locationRequest.state} onChange={(e) => setLocationRequest(l => ({ ...l, state: e.target.value }))} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
        <Input label="Why do you need this location?" placeholder="Brief explanation..." value={locationRequest.reason} onChange={(e) => setLocationRequest(l => ({ ...l, reason: e.target.value }))} />
        <div className="modal-actions">
          <Button variant="outline" onClick={() => setShowLocationRequestModal(false)}>Cancel</Button>
          <Button onClick={submitLocationRequest} loading={locationRequestSubmitting} disabled={!locationRequest.city || !locationRequest.state}>Submit Request</Button>
        </div>
      </Modal>
      
      {/* Add/Edit Attending Event Modal */}
      <Modal isOpen={showAddAttendingModal} onClose={() => { setShowAddAttendingModal(false); resetAttendingForm(); }} title={editingAttendingEvent ? 'Edit Attending Event' : 'Add Attending Event'}>
        <p className="modal-intro">Track an event you plan to attend and monitor your permit/compliance readiness.</p>
        <Input label="Event Name *" placeholder="Downtown Food Festival" value={attendingForm.eventName} onChange={(e) => setAttendingForm(f => ({ ...f, eventName: e.target.value }))} />
        <Input label="Organizer Name" placeholder="City Events Dept" value={attendingForm.organizerName} onChange={(e) => setAttendingForm(f => ({ ...f, organizerName: e.target.value }))} />
        <Input label="Venue Name" placeholder="Main Street Plaza" value={attendingForm.venueName} onChange={(e) => setAttendingForm(f => ({ ...f, venueName: e.target.value }))} />
        <Select label="City / State" value={attendingCitySelection} onChange={(e) => handleAttendingCityChange(e.target.value)} options={[
          { value: '', label: 'Select a city' },
          ...(business?.operatingCities || []).map(c => ({ value: `${c.city}||${c.state}`, label: `${c.city}, ${c.state}${c.isPrimary ? ' (Primary)' : ''}` })),
          { value: '__custom__', label: '— Enter custom city —' }
        ]} />
        {attendingCitySelection === '__custom__' && (
          <>
            <div className="form-row">
              <Input label="City" placeholder="Austin" value={attendingForm.city} onChange={(e) => setAttendingForm(f => ({ ...f, city: e.target.value }))} />
              <Select label="State" value={attendingForm.state} onChange={(e) => setAttendingForm(f => ({ ...f, state: e.target.value }))} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
            </div>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px', margin: '0 0 12px', fontSize: '0.8125rem', color: '#1e40af' }}>
              💡 Tip: Add this city to your <strong>Operating Cities</strong> in Settings to get permit suggestions and tracking.
            </div>
          </>
        )}
        {!attendingCitySelection && (business?.operatingCities || []).length === 0 && (
          <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: '4px 0 8px' }}>No operating cities yet. Add cities in <strong>Settings → Cities</strong> to get permit suggestions.</p>
        )}
        {attendingSearchingPermits && <div style={{ textAlign: 'center', padding: '0.5rem' }}><LoadingSpinner /><p style={{ color: 'var(--gray-500)', fontSize: '0.8125rem' }}>Finding permits for this city...</p></div>}
        {attendingSuggestedPermits.length > 0 && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', margin: '0 0 12px' }}>
            <p style={{ fontSize: '0.8125rem', fontWeight: 600, margin: '0 0 8px', color: '#166534' }}>Permits required in this city:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {attendingSuggestedPermits.map(pt => {
                const alreadyAdded = attendingForm.requiredPermits.some(rp => rp.permitTypeId === pt._id);
                return (
                  <button key={pt._id} type="button" disabled={alreadyAdded} onClick={() => addSuggestedPermitToForm(pt)}
                    style={{ padding: '6px 12px', borderRadius: '6px', border: `1px solid ${alreadyAdded ? '#d1d5db' : '#10b981'}`, background: alreadyAdded ? '#f3f4f6' : '#ecfdf5', color: alreadyAdded ? '#9ca3af' : '#065f46', fontSize: '0.8125rem', cursor: alreadyAdded ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                    {alreadyAdded ? '✓ ' : '+ '}{pt.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <Input label="Address" placeholder="123 Main St" value={attendingForm.address} onChange={(e) => setAttendingForm(f => ({ ...f, address: e.target.value }))} />
        <div className="form-row">
          <Input label="Start Date *" type="date" value={attendingForm.startDate} onChange={(e) => setAttendingForm(f => ({ ...f, startDate: e.target.value }))} />
          <Input label="End Date" type="date" value={attendingForm.endDate} onChange={(e) => setAttendingForm(f => ({ ...f, endDate: e.target.value }))} />
        </div>
        <Select label="Event Type" value={attendingForm.eventType} onChange={(e) => setAttendingForm(f => ({ ...f, eventType: e.target.value }))} options={[
          { value: 'food_event', label: 'Food Event' }, { value: 'farmers_market', label: 'Farmers Market' }, { value: 'festival', label: 'Festival' },
          { value: 'fair', label: 'Fair' }, { value: 'craft_show', label: 'Craft Show' }, { value: 'night_market', label: 'Night Market' }, { value: 'other', label: 'Other' }
        ]} />
        <Input label="Notes" placeholder="Any notes about this event..." value={attendingForm.notes} onChange={(e) => setAttendingForm(f => ({ ...f, notes: e.target.value }))} />
        
        <div className="form-section-title" style={{ marginTop: '16px' }}>Required Permits</div>
        <p className="form-hint">Select from your existing permits or add custom ones</p>
        {attendingForm.requiredPermits.map((p, i) => (
          <div key={i} className="inline-list-item">
            <span>{p.name} {p.vendorPermitId && <small style={{ color: 'var(--gray-500)' }}>(linked)</small>}</span>
            <button className="remove-btn" onClick={() => removePermitFromForm(i)}>×</button>
          </div>
        ))}
        {vendorPermitsList.length > 0 && (
          <div className="inline-add-row" style={{ marginBottom: '8px' }}>
            <Select 
              value={selectedExistingPermit} 
              onChange={(e) => setSelectedExistingPermit(e.target.value)} 
              options={[
                { value: '', label: '— Select from your permits —' },
                ...vendorPermitsList
                  .filter(p => p.permitTypeId?.name || p.isCustom)
                  .filter(p => !attendingForm.requiredPermits.some(rp => rp.vendorPermitId === p._id))
                  .map(p => ({ 
                    value: p._id, 
                    label: p.isCustom 
                      ? `${p.customName} (${p.customCity || 'Custom'}) — ${p.status}`
                      : `${p.permitTypeId.name} (${p.jurisdictionId?.city || 'Unknown'}) — ${p.status}` 
                  }))
              ]} 
            />
            <Button variant="outline" size="sm" onClick={addExistingPermitToForm} disabled={!selectedExistingPermit}>Add</Button>
          </div>
        )}
        <div className="inline-add-row">
          <Input placeholder="Or type a custom permit name..." value={newPermitName} onChange={(e) => setNewPermitName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPermitToForm()} />
          <Button variant="outline" size="sm" onClick={addPermitToForm} disabled={!newPermitName.trim()}>Add</Button>
        </div>
        
        <div className="form-section-title" style={{ marginTop: '16px' }}>Compliance Checklist</div>
        <p className="form-hint">Add items you need to complete before this event</p>
        {attendingForm.complianceChecklist.map((c, i) => (
          <div key={i} className="inline-list-item">
            <span>{c.item}</span>
            <button className="remove-btn" onClick={() => removeChecklistItemFromForm(i)}>×</button>
          </div>
        ))}
        <div className="inline-add-row">
          <Input placeholder="e.g. Print business license copy" value={newChecklistItem} onChange={(e) => setNewChecklistItem(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addChecklistItemToForm()} />
          <Button variant="outline" size="sm" onClick={addChecklistItemToForm} disabled={!newChecklistItem.trim()}>Add</Button>
        </div>
        
        <div className="modal-actions">
          <Button variant="outline" onClick={() => { setShowAddAttendingModal(false); resetAttendingForm(); }}>Cancel</Button>
          <Button onClick={saveAttendingEvent} disabled={!attendingForm.eventName || !attendingForm.startDate}>{editingAttendingEvent ? 'Save Changes' : 'Add Event'}</Button>
        </div>
      </Modal>
      
      {/* Attending Event Detail Modal */}
      <Modal isOpen={!!selectedAttendingEvent} onClose={() => setSelectedAttendingEvent(null)} title={selectedAttendingEvent?.eventName} size="lg">
        {selectedAttendingEvent && (
          <div className="attending-event-detail">
            <div className="event-detail-section">
              <h4>Event Information</h4>
              <div className="event-info-grid">
                <div className="info-item"><span className="info-label">Date</span><span className="info-value">{formatDate(selectedAttendingEvent.startDate)}{selectedAttendingEvent.endDate ? ` - ${formatDate(selectedAttendingEvent.endDate)}` : ''}</span></div>
                {selectedAttendingEvent.location?.city && <div className="info-item"><span className="info-label">Location</span><span className="info-value">{selectedAttendingEvent.location.venueName ? `${selectedAttendingEvent.location.venueName}, ` : ''}{selectedAttendingEvent.location.city}{selectedAttendingEvent.location.state ? `, ${selectedAttendingEvent.location.state}` : ''}</span></div>}
                {selectedAttendingEvent.organizerName && <div className="info-item"><span className="info-label">Organizer</span><span className="info-value">{selectedAttendingEvent.organizerName}</span></div>}
                {selectedAttendingEvent.notes && <div className="info-item full-width"><span className="info-label">Notes</span><span className="info-value">{selectedAttendingEvent.notes}</span></div>}
              </div>
            </div>
            
            {selectedAttendingEvent.requiredPermits?.length > 0 && (
              <div className="event-detail-section">
                <h4>Required Permits ({selectedAttendingEvent.completedPermits}/{selectedAttendingEvent.requiredPermits.length})</h4>
                <div className="attending-permits-list">
                  {selectedAttendingEvent.requiredPermits.map((p, i) => (
                    <div key={i} className={`attending-permit-item ${p.status}`}>
                      <div className="attending-permit-info">
                        <span className="attending-permit-name">{p.name}</span>
                        {p.linkedPermitStatus && <span className="linked-status">System: {p.linkedPermitStatus}</span>}
                      </div>
                      <Select value={p.status} onChange={(e) => updateAttendingPermitStatus(selectedAttendingEvent._id, i, e.target.value)} options={[
                        { value: 'needed', label: '❌ Needed' }, { value: 'in_progress', label: '🔄 In Progress' },
                        { value: 'obtained', label: '✅ Obtained' }, { value: 'not_applicable', label: '➖ N/A' }
                      ]} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {selectedAttendingEvent.complianceChecklist?.length > 0 && (
              <div className="event-detail-section">
                <h4>Compliance Checklist ({selectedAttendingEvent.completedChecklist}/{selectedAttendingEvent.complianceChecklist.length})</h4>
                <div className="attending-checklist">
                  {selectedAttendingEvent.complianceChecklist.map((c, i) => (
                    <label key={i} className={`checklist-item ${c.completed ? 'completed' : ''}`}>
                      <input type="checkbox" checked={c.completed} onChange={(e) => updateAttendingChecklistItem(selectedAttendingEvent._id, i, e.target.checked)} />
                      <span>{c.item}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <div className="modal-actions">
              <Button variant="outline" onClick={() => { const ae = selectedAttendingEvent; setSelectedAttendingEvent(null); openEditAttendingModal(ae); }}>Edit Event</Button>
              <Button variant="ghost" className="delete-btn" onClick={() => deleteAttendingEvent(selectedAttendingEvent._id)}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
      
      {/* Upgrade Modal for expired subscriptions */}
      <UpgradeRequiredModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        reason={isExpired ? "Your subscription has expired" : "This feature requires an active subscription"}
        feature="Event Integration"
      />
    </div>
  );
};

// ===========================================
// ORGANIZER SETTINGS PAGE
// ===========================================
const OrganizerSettingsPage = () => {
  const { user, fetchUser, logout } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Listen for tab change events from verification modal
  useEffect(() => {
    const handleTabChange = (event) => {
      if (event.detail) {
        setActiveTab(event.detail);
      }
    };
    window.addEventListener('setOrganizerSettingsTab', handleTabChange);
    return () => window.removeEventListener('setOrganizerSettingsTab', handleTabChange);
  }, []);
  
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || ''
  });
  
  const [organizationData, setOrganizationData] = useState({
    companyName: user?.organizerProfile?.companyName || '',
    description: user?.organizerProfile?.description || '',
    website: user?.organizerProfile?.website || '',
    phone: user?.organizerProfile?.phone || '',
    contactEmail: user?.organizerProfile?.contactEmail || user?.email || ''
  });
  
  const [notificationPrefs, setNotificationPrefs] = useState({
    applicationReceived: user?.organizerProfile?.notifications?.applicationReceived ?? true,
    applicationDeadline: user?.organizerProfile?.notifications?.applicationDeadline ?? true,
    vendorCompliance: user?.organizerProfile?.notifications?.vendorCompliance ?? true,
    eventReminders: user?.organizerProfile?.notifications?.eventReminders ?? true,
    emailDigest: user?.organizerProfile?.notifications?.emailDigest ?? 'daily' // 'instant', 'daily', 'weekly', 'none'
  });
  
  const [subscription, setSubscription] = useState(null);
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [docUploadData, setDocUploadData] = useState({ file: null, name: '', category: 'business_license' });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  useEffect(() => {
    // Fetch organizer subscription
    api.get('/organizer/subscription').then(data => setSubscription(data.subscription)).catch(console.error);
    // Fetch organizer documents
    api.get('/organizer/documents').then(data => setDocuments(data.documents || [])).catch(console.error);
  }, []);
  
  const handleDocUpload = async () => {
    if (!docUploadData.file) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append('file', docUploadData.file);
    fd.append('name', docUploadData.name || docUploadData.file.name);
    fd.append('category', docUploadData.category);
    try {
      await api.upload('/organizer/documents', fd);
      const data = await api.get('/organizer/documents');
      setDocuments(data.documents || []);
      setShowUploadDocModal(false);
      setDocUploadData({ file: null, name: '', category: 'business_license' });
      toast.success('Document uploaded successfully');
    } catch (err) { toast.error(err.message); }
    finally { setUploadingDoc(false); }
  };
  
  const handleDocDelete = async (index) => {
    const confirmed = await confirm({ title: 'Delete Document', message: 'Are you sure you want to delete this document?', confirmText: 'Delete', variant: 'danger' });
    if (!confirmed) return;
    try {
      await api.delete(`/organizer/documents/${index}`);
      const data = await api.get('/organizer/documents');
      setDocuments(data.documents || []);
      toast.success('Document deleted');
    } catch (err) { toast.error(err.message); }
  };
  
  const handleProfileSave = async () => {
    setLoading(true);
    try {
      await api.put('/auth/profile', profileData);
      await fetchUser();
      toast.success('Profile updated successfully');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };
  
  const handleOrganizationSave = async () => {
    setLoading(true);
    try {
      await api.put('/organizer/profile', organizationData);
      await fetchUser();
      toast.success('Organization profile updated');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };
  
  const handleNotificationsSave = async () => {
    setLoading(true);
    try {
      await api.put('/organizer/notifications', notificationPrefs);
      await fetchUser();
      toast.success('Notification preferences saved');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };
  
  const handleUpgrade = async () => {
    setOrgPaymentModal(true);
  };

  const [orgPaymentModal, setOrgPaymentModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const handleDeleteAccount = async () => {
    const confirmed = await confirm({
      title: '⚠️ Delete Account Permanently',
      message: 'This action is PERMANENT and cannot be undone.\n\n• All your events, vendor data, and documents will be deleted\n• Your subscription will be automatically cancelled\n• You will not be able to recover any data\n\nAre you absolutely sure?',
      confirmText: 'Delete My Account',
      variant: 'danger'
    });
    if (!confirmed) return;
    const doubleConfirm = await confirm({
      title: 'Final Confirmation',
      message: 'This is your last chance. Type-confirm: your account and all data will be permanently erased and your subscription cancelled immediately.',
      confirmText: 'Yes, Permanently Delete',
      variant: 'danger'
    });
    if (!doubleConfirm) return;
    setDeletingAccount(true);
    try {
      await api.delete('/account');
      toast.success('Account deleted. We\'re sorry to see you go.');
      logout();
    } catch (err) { toast.error(err.message || 'Failed to delete account'); }
    finally { setDeletingAccount(false); }
  };
  
  const handleManageBilling = async () => {
    try {
      const data = await api.post('/organizer/subscription/portal');
      if (data.url) window.location.href = data.url;
      else if (data.message) toast.info(data.message);
    } catch (err) { toast.error(err.message); }
  };
  
  const isVerified = user?.organizerProfile?.verified;
  const tabs = ['profile', 'organization', 'documents', 'notifications', 'billing'];
  
  return (
    <>
    <div className="settings-page organizer-settings">
      <div className="page-header">
        <div>
          <h1>Organizer Settings</h1>
          <p>Manage your event organizer account</p>
        </div>
        <div className="header-badges">
          <Badge variant="primary">Organizer</Badge>
          {isVerified ? (
            <Badge variant="success">✓ Verified</Badge>
          ) : (
            <Badge variant="warning">Pending Verification</Badge>
          )}
        </div>
      </div>
      
      {/* Status Alerts */}
      {user?.organizerProfile?.verificationStatus === 'info_requested' && (
        <Alert type="warning">
          <strong>⚠️ Additional Information Requested</strong>
          <p style={{ marginTop: '8px', marginBottom: '0' }}>
            {user?.organizerProfile?.adminNote || 'Please update your organization profile with the requested information.'}
          </p>
        </Alert>
      )}
      
      {user?.organizerProfile?.verificationStatus === 'rejected' && (
        <Alert type="error">
          <strong>❌ Application Not Approved</strong>
          <p style={{ marginTop: '8px', marginBottom: '0' }}>
            {user?.organizerProfile?.disabledReason || 'Your organizer application was not approved. Please contact support for more information.'}
          </p>
        </Alert>
      )}
      
      {user?.organizerProfile?.disabled && user?.organizerProfile?.verificationStatus !== 'rejected' && (
        <Alert type="error">
          <strong>🚫 Account Disabled</strong>
          <p style={{ marginTop: '8px', marginBottom: '0' }}>
            {user?.organizerProfile?.disabledReason || 'Your organizer account has been disabled. Please contact support.'}
          </p>
        </Alert>
      )}
      
      {!isVerified && !user?.organizerProfile?.disabled && user?.organizerProfile?.verificationStatus !== 'info_requested' && user?.organizerProfile?.verificationStatus !== 'rejected' && (
        <Alert type="info">
          <strong>⏳ Verification Pending</strong>
          <p style={{ marginTop: '8px', marginBottom: '0' }}>
            Your organizer account is awaiting verification by PermitWise. 
            You can create events as drafts, but publishing requires verification. This usually takes 1-2 business days.
          </p>
        </Alert>
      )}
      
      <div className="settings-layout">
        <div className="settings-nav">
          {tabs.map(tab => (
            <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>
              {tab === 'organization' ? 'Organization' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        
        <div className="settings-content">
          {message && <Alert type="info" onClose={() => setMessage('')}>{message}</Alert>}
          
          {activeTab === 'profile' && (
            <div className="settings-section">
              <h2>Your Profile</h2>
              <p className="section-description">Your personal account information</p>
              
              <div className="form-row">
                <Input label="First Name" value={profileData.firstName} onChange={(e) => setProfileData(d => ({ ...d, firstName: e.target.value }))} />
                <Input label="Last Name" value={profileData.lastName} onChange={(e) => setProfileData(d => ({ ...d, lastName: e.target.value }))} />
              </div>
              <Input label="Email" value={user?.email} disabled />
              <PhoneInput label="Phone" value={profileData.phone} onChange={(e) => setProfileData(d => ({ ...d, phone: e.target.value }))} />
              
              <div className="form-actions">
                <Button onClick={handleProfileSave} loading={loading}>Save Profile</Button>
              </div>
              
              <div className="settings-divider" />
              
              <h3>Change Password</h3>
              <p className="section-description">Use a strong password with at least 8 characters</p>
              <ChangePasswordForm />
            </div>
          )}
          
          {activeTab === 'organization' && (
            <div className="settings-section">
              <h2>Organization Profile</h2>
              <p className="section-description">This information is displayed to vendors when they view your events</p>
              
              <Input 
                label="Organization Name *" 
                value={organizationData.companyName} 
                onChange={(e) => setOrganizationData(d => ({ ...d, companyName: e.target.value }))}
                placeholder="e.g., Austin Food Events LLC"
              />
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea 
                  className="form-input form-textarea"
                  value={organizationData.description}
                  onChange={(e) => setOrganizationData(d => ({ ...d, description: e.target.value }))}
                  placeholder="Tell vendors about your organization and the types of events you host..."
                  rows={4}
                />
              </div>
              
              <Input 
                label="Website" 
                value={organizationData.website} 
                onChange={(e) => setOrganizationData(d => ({ ...d, website: e.target.value }))}
                placeholder="https://yourorganization.com"
              />
              
              <div className="form-row">
                <PhoneInput 
                  label="Contact Phone" 
                  value={organizationData.phone} 
                  onChange={(e) => setOrganizationData(d => ({ ...d, phone: e.target.value }))} 
                />
                <EmailInput 
                  label="Contact Email" 
                  value={organizationData.contactEmail} 
                  onChange={(e) => setOrganizationData(d => ({ ...d, contactEmail: e.target.value }))} 
                />
              </div>
              
              <div className="form-actions">
                <Button onClick={handleOrganizationSave} loading={loading}>Save Organization</Button>
              </div>
            </div>
          )}
          
          {activeTab === 'documents' && (
            <div className="settings-section">
              <div className="section-header-row">
                <div>
                  <h2>Verification Documents</h2>
                  <p className="section-description">Upload documents to verify your organization and event hosting authority</p>
                </div>
                <Button onClick={() => setShowUploadDocModal(true)}>
                  <Icons.Upload /> Upload Document
                </Button>
              </div>
              
              {!isVerified && (
                <Alert type="info" style={{ marginBottom: '1rem' }}>
                  <strong>Required for Verification</strong>
                  <p style={{ margin: '8px 0 0' }}>Please upload at least one of the following: business license, venue contract, or event permit.</p>
                </Alert>
              )}
              
              {documents.length > 0 ? (
                <div className="documents-list">
                  {documents.map((doc, index) => (
                    <Card key={index} className="document-item">
                      <div className="document-icon"><Icons.Document /></div>
                      <div className="document-info">
                        <h4>{doc.name}</h4>
                        <span className="doc-category">{doc.category?.replace(/_/g, ' ')}</span>
                        <span className="doc-date">{formatDate(doc.uploadedAt)}</span>
                      </div>
                      <div className="document-status">
                        <Badge variant={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'}>
                          {doc.status === 'approved' ? '✓ Approved' : doc.status === 'rejected' ? '✗ Rejected' : '⏳ Pending'}
                        </Badge>
                        {doc.reviewNotes && <small className="review-notes">{doc.reviewNotes}</small>}
                      </div>
                      <div className="document-actions">
                        <a href={getSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer"><Icons.Download /></a>
                        <button onClick={() => handleDocDelete(index)}><Icons.Trash /></button>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <EmptyState 
                  icon={Icons.Document} 
                  title="No documents uploaded" 
                  description="Upload verification documents to get your organizer account verified"
                  action={<Button onClick={() => setShowUploadDocModal(true)}><Icons.Upload /> Upload Document</Button>}
                />
              )}
              
              {/* Upload Document Modal */}
              <Modal isOpen={showUploadDocModal} onClose={() => setShowUploadDocModal(false)} title="Upload Document">
                <div className="upload-form">
                  <Input 
                    label="Document Name" 
                    value={docUploadData.name}
                    onChange={(e) => setDocUploadData(d => ({ ...d, name: e.target.value }))}
                    placeholder="e.g., Business License 2024"
                  />
                  
                  <Select 
                    label="Document Type"
                    value={docUploadData.category}
                    onChange={(e) => setDocUploadData(d => ({ ...d, category: e.target.value }))}
                    options={[
                      { value: 'business_license', label: 'Business License' },
                      { value: 'event_permit', label: 'Event Permit' },
                      { value: 'venue_contract', label: 'Venue Contract' },
                      { value: 'insurance', label: 'Insurance Certificate' },
                      { value: 'identity', label: 'ID / Identity Verification' },
                      { value: 'other', label: 'Other' }
                    ]}
                  />
                  
                  <input 
                    type="file" 
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setDocUploadData(d => ({ ...d, file: e.target.files[0] }))}
                  />
                  <p className="upload-hint">Accepted: PDF, JPG, PNG (max 10MB)</p>
                </div>
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => setShowUploadDocModal(false)}>Cancel</Button>
                  <Button onClick={handleDocUpload} loading={uploadingDoc} disabled={!docUploadData.file}>Upload</Button>
                </div>
              </Modal>
            </div>
          )}
          
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>Notification Preferences</h2>
              <p className="section-description">Control how and when you receive updates about your events</p>
              
              <Card className="notification-card">
                <label className="toggle-row">
                  <span>New Vendor Applications</span>
                  <input type="checkbox" checked={notificationPrefs.applicationReceived} onChange={(e) => setNotificationPrefs(d => ({ ...d, applicationReceived: e.target.checked }))} />
                </label>
                <p className="toggle-description">Get notified when a vendor applies to your event</p>
              </Card>
              
              <Card className="notification-card">
                <label className="toggle-row">
                  <span>Application Deadlines</span>
                  <input type="checkbox" checked={notificationPrefs.applicationDeadline} onChange={(e) => setNotificationPrefs(d => ({ ...d, applicationDeadline: e.target.checked }))} />
                </label>
                <p className="toggle-description">Reminders before your application deadlines close</p>
              </Card>
              
              <Card className="notification-card">
                <label className="toggle-row">
                  <span>Vendor Compliance Updates</span>
                  <input type="checkbox" checked={notificationPrefs.vendorCompliance} onChange={(e) => setNotificationPrefs(d => ({ ...d, vendorCompliance: e.target.checked }))} />
                </label>
                <p className="toggle-description">Get notified when vendor permit status changes</p>
              </Card>
              
              <Card className="notification-card">
                <label className="toggle-row">
                  <span>Event Reminders</span>
                  <input type="checkbox" checked={notificationPrefs.eventReminders} onChange={(e) => setNotificationPrefs(d => ({ ...d, eventReminders: e.target.checked }))} />
                </label>
                <p className="toggle-description">Reminders before your events start</p>
              </Card>
              
              <div className="form-group">
                <label className="form-label">Email Digest Frequency</label>
                <Select 
                  value={notificationPrefs.emailDigest} 
                  onChange={(e) => setNotificationPrefs(d => ({ ...d, emailDigest: e.target.value }))}
                  options={[
                    { value: 'instant', label: 'Instant - Send immediately' },
                    { value: 'daily', label: 'Daily Digest' },
                    { value: 'weekly', label: 'Weekly Summary' },
                    { value: 'none', label: 'No Emails' }
                  ]}
                />
              </div>
              
              <div className="form-actions">
                <Button onClick={handleNotificationsSave} loading={loading}>Save Preferences</Button>
              </div>
            </div>
          )}
          
          {activeTab === 'billing' && (
            <div className="settings-section">
              <h2>Billing & Subscription</h2>
              
              <Card className="current-plan organizer-plan">
                <div className="plan-header">
                  <h3>Organizer Plan</h3>
                  <Badge variant={subscription?.status === 'active' || subscription?.status === 'trial' ? 'success' : 'warning'}>
                    {subscription?.status?.toUpperCase() || 'NO PLAN'}
                  </Badge>
                </div>
                
                {subscription?.status === 'trial' && subscription?.trialEndsAt && (
                  <p className="trial-warning">
                    <Icons.Clock /> Trial ends {formatDate(subscription.trialEndsAt)} ({Math.max(0, Math.ceil((new Date(subscription.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))} days left)
                  </p>
                )}
                
                {subscription?.status === 'active' && subscription?.currentPeriodEnd && (
                  <p>Next billing: {formatDate(subscription.currentPeriodEnd)}</p>
                )}
              </Card>
              
              <Card className="plan-card organizer-upgrade">
                <div className="plan-badge">Event Organizer</div>
                <h3>Organizer Plan</h3>
                <div className="price">$79<span>/month</span></div>
                <ul className="plan-features">
                  <li><Icons.Check /> Unlimited events</li>
                  <li><Icons.Check /> Unlimited vendor invitations</li>
                  <li><Icons.Check /> Vendor compliance tracking</li>
                  <li><Icons.Check /> Custom permit requirements</li>
                  <li><Icons.Check /> Application management</li>
                  <li><Icons.Check /> Verified organizer badge</li>
                  <li><Icons.Check /> Priority support</li>
                </ul>
                
                {subscription?.status === 'active' ? (
                  <Button variant="outline" onClick={handleManageBilling}>Manage Subscription</Button>
                ) : (
                  <>
                    <Button onClick={handleUpgrade}>
                      {subscription?.status === 'trial' ? 'Upgrade Now' : 'Subscribe - $79/mo'}
                    </Button>
                    <p className="plan-note">14-day free trial • Cancel anytime</p>
                  </>
                )}
              </Card>

              <div style={{ borderTop: '1px solid var(--danger-bg, #fee2e2)', marginTop: '32px', paddingTop: '24px' }}>
                <h3 style={{ color: 'var(--danger)', margin: '0 0 8px' }}>Danger Zone</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', margin: '0 0 16px' }}>
                  Permanently delete your account and all associated data. {subscription?.stripeSubscriptionId ? 'Your subscription will be automatically cancelled.' : ''} This action cannot be undone.
                </p>
                <Button variant="danger" onClick={handleDeleteAccount} loading={deletingAccount}>
                  <Icons.Alert /> Delete Account Permanently
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    <StripePaymentModal
      isOpen={orgPaymentModal}
      onClose={() => setOrgPaymentModal(false)}
      plan="organizer"
      isOrganizer={true}
      onSuccess={() => { fetchUser(); setOrgPaymentModal(false); }}
    />
    </>
  );
};

// Change Password Form Component
const ChangePasswordForm = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setMessage('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await api.put('/auth/change-password', { currentPassword, newPassword });
      setMessage('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) { setMessage(err.message); }
    finally { setLoading(false); }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      {message && <Alert type={message.includes('success') ? 'success' : 'error'}>{message}</Alert>}
      <Input label="Current Password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
      <Input label="New Password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
      <Input label="Confirm New Password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
      <Button type="submit" loading={loading} disabled={!currentPassword || !newPassword || !confirmPassword}>
        Change Password
      </Button>
    </form>
  );
};

const SettingsPage = () => {
  const { user, business, subscription, subscriptionStatus, fetchUser, updateBusiness, isOwner, isManager, canManageTeam, canManageSubscription, canManageBusiness, logout } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('profile'); const [loading, setLoading] = useState(false); const [message, setMessage] = useState('');
  const [profileData, setProfileData] = useState({ firstName: user?.firstName || '', lastName: user?.lastName || '', phone: user?.phone || '', notificationPreferences: user?.notificationPreferences || { email: true, sms: false, push: true } });
  const [businessData, setBusinessData] = useState({ 
    businessName: business?.businessName || '', dbaName: business?.dbaName || '', 
    phone: business?.phone || '', email: business?.email || '', ein: business?.ein || '',
    handlesFood: business?.handlesFood || false,
    address: business?.address || { street: '', city: '', state: '', zip: '' },
    operatingCities: business?.operatingCities || [{ city: '', state: '', isPrimary: true }],
    vehicleDetails: business?.vehicleDetails || { type: '', make: '', model: '', year: '', licensePlate: '' },
    insurance: business?.insurance || { provider: '', policyNumber: '', expiryDate: '' }
  });
  const [teamMembers, setTeamMembers] = useState([]); const [newMember, setNewMember] = useState({ email: '', role: 'staff' });

  useEffect(() => {
    if (subscription?.features?.teamAccounts && canManageTeam) {
      api.get('/team').then(data => setTeamMembers(data.members || [])).catch(console.error);
    }
  }, [subscription, canManageTeam]);

  const handleProfileSave = async () => { setLoading(true); try { await api.put('/auth/profile', profileData); await fetchUser(); setMessage('Profile updated'); } catch (err) { setMessage(err.message); } finally { setLoading(false); } };
  const handleBusinessSave = async () => { 
    if (!canManageBusiness) { toast.error('You do not have permission to edit business settings'); return; }
    setLoading(true); try { const data = await api.put('/business', businessData); updateBusiness(data.business); setMessage('Business updated'); } catch (err) { setMessage(err.message); } finally { setLoading(false); } 
  };
  
  const handleFoodHandlingToggle = async (checked) => {
    if (!canManageBusiness) { toast.error('You do not have permission to change this setting'); return; }
    setBusinessData(d => ({ ...d, handlesFood: checked }));
    setLoading(true);
    try {
      // First save the business with the new handlesFood value
      const data = await api.put('/business', { ...businessData, handlesFood: checked });
      updateBusiness(data.business);
      
      // Then sync permits to add/remove food handling permits
      const syncResult = await api.post('/permits/sync-food-handling', { handlesFood: checked });
      
      if (checked && syncResult.addedCount > 0) {
        setMessage(`Food handling enabled! Added ${syncResult.addedCount} food safety permit(s) to your dashboard.`);
      } else if (!checked && syncResult.removedCount > 0) {
        setMessage(`Food handling disabled. Removed ${syncResult.removedCount} empty food safety permit(s). Permits with documents were kept.`);
      } else {
        setMessage(checked ? 'Food handling enabled.' : 'Food handling disabled.');
      }
    } catch (err) { 
      setMessage(err.message); 
      // Revert on error
      setBusinessData(d => ({ ...d, handlesFood: !checked }));
    } finally { setLoading(false); }
  };
  
  const PLAN_RANK = { free: 0, trial: 0, basic: 1, pro: 2, elite: 3, promo: 4, lifetime: 5 };
  const isActiveSub = subscription && !subscriptionStatus?.isExpired && ['active', 'grace_period'].includes(subscription?.status);
  const effectivePlan = isActiveSub ? subscription?.plan : null;
  const currentRank = PLAN_RANK[effectivePlan] || 0;
  
  const getPlanAction = (planId) => {
    if (effectivePlan === planId) return 'current';
    if (subscription?.pendingPlanChange === planId) return 'pending';
    if (!effectivePlan) return 'upgrade';
    return (PLAN_RANK[planId] || 0) > currentRank ? 'upgrade' : 'downgrade';
  };

  const [paymentModal, setPaymentModal] = useState(null); // null or plan name
  
  const handlePlanAction = async (plan) => { 
    if (!canManageSubscription) { toast.error('Only the business owner can manage subscriptions'); return; }
    const action = getPlanAction(plan);
    try {
      if (action === 'upgrade' && isActiveSub) {
        // In-place upgrade (already has payment method on file)
        const data = await api.post('/subscription/change-plan', { plan });
        if (data.success) { toast.success(data.message); fetchUser(); }
        else if (data.needsCheckout) { setPaymentModal(plan); }
      } else if (action === 'downgrade' && isActiveSub) {
        const ok = await confirm({ title: 'Downgrade Plan', message: `Are you sure you want to downgrade to ${plan.charAt(0).toUpperCase() + plan.slice(1)}? You'll keep your current features until your next billing date.`, confirmText: 'Downgrade', variant: 'warning' });
        if (!ok) return;
        const data = await api.post('/subscription/change-plan', { plan });
        if (data.success) { toast.success(data.message); fetchUser(); }
      } else {
        // New or expired subscription — open payment modal
        setPaymentModal(plan);
      }
    } catch (err) { toast.error(err.message); } 
  };
  
  const handleCancelPendingChange = async () => {
    try {
      const data = await api.post('/subscription/cancel-plan-change');
      if (data.success) { toast.success(data.message); fetchUser(); }
    } catch (err) { toast.error(err.message); }
  };
  
  const handleManageBilling = async () => {
    if (!canManageSubscription) { toast.error('Only the business owner can manage billing'); return; }
    try { const data = await api.post('/subscription/portal'); if (data.url) window.location.href = data.url; else if (data.message) toast.info(data.message); } catch (err) { toast.error(err.message); }
  };

  const [deletingAccount, setDeletingAccount] = useState(false);
  const handleDeleteAccount = async () => {
    const confirmed = await confirm({
      title: '⚠️ Delete Account Permanently',
      message: 'This action is PERMANENT and cannot be undone.\n\n• All your permits, documents, and business data will be deleted\n• Your subscription will be automatically cancelled\n• Team members will lose access\n• You will not be able to recover any data\n\nAre you absolutely sure?',
      confirmText: 'Delete My Account',
      variant: 'danger'
    });
    if (!confirmed) return;
    const doubleConfirm = await confirm({
      title: 'Final Confirmation',
      message: 'This is your last chance. Your account and all data will be permanently erased and your subscription cancelled immediately.',
      confirmText: 'Yes, Permanently Delete',
      variant: 'danger'
    });
    if (!doubleConfirm) return;
    setDeletingAccount(true);
    try {
      await api.delete('/account');
      toast.success('Account deleted. We\'re sorry to see you go.');
      logout();
    } catch (err) { toast.error(err.message || 'Failed to delete account'); }
    finally { setDeletingAccount(false); }
  };

  // Handle Stripe checkout success/cancel URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      toast.success('Subscription activated! Welcome to PermitWise.');
      fetchUser();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('canceled') === 'true') {
      toast.info('Checkout was canceled. You can upgrade anytime.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const addCity = () => {
    const maxCities = subscription?.features?.maxCities || 1;
    const currentCities = businessData.operatingCities.filter(c => c.city && c.state).length;
    if (currentCities >= maxCities) {
      toast.error(`You've reached your plan limit of ${maxCities} operating ${maxCities === 1 ? 'city' : 'cities'}. Please upgrade for more.`);
      return;
    }
    setBusinessData(d => ({ ...d, operatingCities: [...d.operatingCities, { city: '', state: '', isPrimary: false }] }));
  };
  const updateCity = (i, field, value) => { const cities = [...businessData.operatingCities]; cities[i] = { ...cities[i], [field]: value }; setBusinessData(d => ({ ...d, operatingCities: cities })); };
  const removeCity = async (i) => { 
    if (businessData.operatingCities.length <= 1) {
      toast.error('Cannot remove the only operating city. Add another city first.');
      return;
    }
    const cityToRemove = businessData.operatingCities[i];
    
    // Check if trying to remove primary city
    if (cityToRemove.isPrimary) {
      toast.error('Cannot remove your primary city. Please set another city as primary first.');
      return;
    }
    
    if (!cityToRemove.city) {
      // If city is empty, just remove it locally
      setBusinessData(d => ({ ...d, operatingCities: d.operatingCities.filter((_, idx) => idx !== i) }));
      return;
    }
    const confirmed = await confirm({ 
      title: 'Remove City', 
      message: `Are you sure you want to remove ${cityToRemove.city}, ${cityToRemove.state}? Permits for this city will be removed, but any uploaded documents will be preserved in your Document Vault.`, 
      confirmText: 'Remove City', 
      variant: 'danger' 
    });
    if (!confirmed) return;
    setLoading(true);
    try {
      // Call API to remove city and its permits
      const result = await api.post('/permits/remove-city', { city: cityToRemove.city, state: cityToRemove.state });
      // Update local state from API response
      if (result.business?.operatingCities) {
        setBusinessData(d => ({ ...d, operatingCities: result.business.operatingCities }));
        updateBusiness({ ...business, operatingCities: result.business.operatingCities });
      } else {
        // Fallback to local update
        const newCities = businessData.operatingCities.filter((_, idx) => idx !== i);
        setBusinessData(d => ({ ...d, operatingCities: newCities }));
        updateBusiness({ ...business, operatingCities: newCities });
      }
      toast.success(result.message || `Removed ${cityToRemove.city}.`);
    } catch (err) { toast.error(err.message); } 
    finally { setLoading(false); }
  };
  const inviteMember = async () => { 
    if (!canManageTeam) { toast.error('You do not have permission to invite team members'); return; }
    try { await api.post('/team/invite', newMember); setNewMember({ email: '', role: 'staff' }); const data = await api.get('/team'); setTeamMembers(data.members || []); toast.success('Invitation sent!'); } catch (err) { toast.error(err.message); } 
  };
  const removeMember = async (id) => { 
    if (!canManageTeam) { toast.error('You do not have permission to remove team members'); return; }
    const confirmed = await confirm({ title: 'Remove Team Member', message: 'Are you sure you want to remove this team member?', confirmText: 'Remove', variant: 'danger' });
    if (confirmed) { try { await api.delete(`/team/${id}`); setTeamMembers(m => m.filter(t => t._id !== id)); toast.success('Team member removed'); } catch (err) { toast.error(err.message); } } 
  };

  // Build tabs based on permissions
  const tabs = ['profile'];
  if (canManageBusiness) tabs.push('business', 'cities');
  tabs.push('notifications');
  if (subscription?.features?.teamAccounts && canManageTeam) tabs.push('team');
  if (canManageSubscription) tabs.push('billing');

  return (
    <>
    <div className="settings-page">
      <div className="page-header"><h1>Settings</h1></div>
      <div className="settings-layout">
        <div className="settings-nav">{tabs.map(tab => <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</button>)}</div>
        <div className="settings-content">
          {message && <Alert type="info" onClose={() => setMessage('')}>{message}</Alert>}
          
          {activeTab === 'profile' && (
            <div className="settings-section">
              <h2>Profile</h2>
              <div className="form-row"><Input label="First Name" value={profileData.firstName} onChange={(e) => setProfileData(d => ({ ...d, firstName: e.target.value }))} /><Input label="Last Name" value={profileData.lastName} onChange={(e) => setProfileData(d => ({ ...d, lastName: e.target.value }))} /></div>
              <Input label="Email" value={user?.email} disabled />
              <PhoneInput label="Phone" value={profileData.phone} onChange={(e) => setProfileData(d => ({ ...d, phone: e.target.value }))} />
              <Button onClick={handleProfileSave} loading={loading}>Save Profile</Button>
            </div>
          )}
          
          {activeTab === 'business' && (
            <div className="settings-section">
              <h2>Business Information</h2>
              <Input label="Business Name" value={businessData.businessName} onChange={(e) => setBusinessData(d => ({ ...d, businessName: e.target.value }))} />
              <Input label="DBA Name (Doing Business As)" value={businessData.dbaName} onChange={(e) => setBusinessData(d => ({ ...d, dbaName: e.target.value }))} />
              <Input label="EIN (Tax ID)" value={businessData.ein} onChange={(e) => setBusinessData(d => ({ ...d, ein: e.target.value }))} placeholder="XX-XXXXXXX" />
              <div className="form-row"><PhoneInput label="Business Phone" value={businessData.phone} onChange={(e) => setBusinessData(d => ({ ...d, phone: e.target.value }))} /><EmailInput label="Business Email" value={businessData.email} onChange={(e) => setBusinessData(d => ({ ...d, email: e.target.value }))} /></div>
              
              <h3>Food Handling</h3>
              <Card className="notification-card">
                <label className="toggle-row">
                  <span>My business handles or serves food</span>
                  <input type="checkbox" checked={businessData.handlesFood || false} onChange={(e) => handleFoodHandlingToggle(e.target.checked)} disabled={loading} />
                </label>
                <p className="toggle-description">Enable this if your business prepares, serves, or sells food. This will automatically add or remove food safety permits from your dashboard.</p>
              </Card>
              
              <h3>Business Address</h3>
              <Input label="Street Address" value={businessData.address.street} onChange={(e) => setBusinessData(d => ({ ...d, address: { ...d.address, street: e.target.value } }))} />
              <div className="form-row">
                <Input label="City" value={businessData.address.city} onChange={(e) => setBusinessData(d => ({ ...d, address: { ...d.address, city: e.target.value } }))} />
                <Select label="State" value={businessData.address.state} onChange={(e) => setBusinessData(d => ({ ...d, address: { ...d.address, state: e.target.value } }))} options={[{ value: '', label: 'Select' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                <Input label="ZIP" value={businessData.address.zip} onChange={(e) => setBusinessData(d => ({ ...d, address: { ...d.address, zip: e.target.value } }))} />
              </div>
              <h3>Vehicle Information</h3>
              <div className="form-row">
                <Select label="Type" value={businessData.vehicleDetails.type} onChange={(e) => setBusinessData(d => ({ ...d, vehicleDetails: { ...d.vehicleDetails, type: e.target.value } }))} options={[{ value: '', label: 'Select' }, { value: 'truck', label: 'Food Truck' }, { value: 'trailer', label: 'Trailer' }, { value: 'cart', label: 'Cart' }, { value: 'tent', label: 'Tent/Booth' }, { value: 'other', label: 'Other' }]} />
                <Input label="Make" value={businessData.vehicleDetails.make} onChange={(e) => setBusinessData(d => ({ ...d, vehicleDetails: { ...d.vehicleDetails, make: e.target.value } }))} />
              </div>
              <div className="form-row">
                <Input label="Model" value={businessData.vehicleDetails.model} onChange={(e) => setBusinessData(d => ({ ...d, vehicleDetails: { ...d.vehicleDetails, model: e.target.value } }))} />
                <Input label="Year" value={businessData.vehicleDetails.year} onChange={(e) => setBusinessData(d => ({ ...d, vehicleDetails: { ...d.vehicleDetails, year: e.target.value } }))} />
                <Input label="License Plate" value={businessData.vehicleDetails.licensePlate} onChange={(e) => setBusinessData(d => ({ ...d, vehicleDetails: { ...d.vehicleDetails, licensePlate: e.target.value } }))} />
              </div>
              <h3>Insurance</h3>
              <div className="form-row">
                <Input label="Provider" value={businessData.insurance.provider} onChange={(e) => setBusinessData(d => ({ ...d, insurance: { ...d.insurance, provider: e.target.value } }))} />
                <Input label="Policy Number" value={businessData.insurance.policyNumber} onChange={(e) => setBusinessData(d => ({ ...d, insurance: { ...d.insurance, policyNumber: e.target.value } }))} />
                <Input label="Expiry Date" type="date" value={businessData.insurance.expiryDate ? businessData.insurance.expiryDate.split('T')[0] : ''} onChange={(e) => setBusinessData(d => ({ ...d, insurance: { ...d.insurance, expiryDate: e.target.value } }))} />
              </div>
              <Button onClick={handleBusinessSave} loading={loading}>Save Business Info</Button>
            </div>
          )}
          
          {activeTab === 'cities' && (
            <div className="settings-section">
              <h2>Operating Cities</h2>
              <p className="section-description">Add all the cities where you operate. We'll track permit requirements for each location.</p>
              {businessData.operatingCities.map((city, i) => (
                <Card key={i} className="city-card">
                  <div className="form-row city-form-row">
                    <Select label="State" value={city.state} onChange={(e) => updateCity(i, 'state', e.target.value)} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                    <CitySearch 
                      label="City" 
                      state={city.state}
                      value={city.city}
                      onChange={(cityName) => updateCity(i, 'city', cityName)}
                      placeholder="Search for your city..."
                      strictMode={true}
                      allowCustom={true}
                    />
                    {businessData.operatingCities.length > 1 && (
                      <Button 
                        variant="danger" 
                        size="sm" 
                        onClick={() => removeCity(i)}
                        style={{ alignSelf: 'flex-end', marginBottom: '4px' }}
                        title={city.isPrimary ? 'Cannot remove primary city' : 'Remove city'}
                        disabled={city.isPrimary}
                      >
                        <Icons.Trash /> Remove
                      </Button>
                    )}
                  </div>
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={city.isPrimary} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Set this city as primary and unset others
                          const newCities = businessData.operatingCities.map((c, idx) => ({
                            ...c,
                            isPrimary: idx === i
                          }));
                          setBusinessData(d => ({ ...d, operatingCities: newCities }));
                        }
                      }}
                      disabled={city.isPrimary}
                    /> 
                    Primary location {city.isPrimary && <Badge variant="primary" size="sm">Current</Badge>}
                  </label>
                </Card>
              ))}
              <Button variant="outline" onClick={addCity}><Icons.Plus /> Add City</Button>
              <Button onClick={handleBusinessSave} loading={loading} style={{ marginLeft: '1rem' }}>Save Cities</Button>
            </div>
          )}
          
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>Notification Preferences</h2>
              <Card className="notification-card">
                <label className="toggle-row"><span>Email Notifications</span><input type="checkbox" checked={profileData.notificationPreferences.email} onChange={(e) => setProfileData(d => ({ ...d, notificationPreferences: { ...d.notificationPreferences, email: e.target.checked } }))} /></label>
                <p className="toggle-description">Receive permit expiration reminders and updates via email</p>
              </Card>
              <Card className="notification-card">
                <label className="toggle-row"><span>SMS Alerts {!subscription?.features?.smsAlerts && <Badge variant="warning">Pro</Badge>}</span><input type="checkbox" checked={profileData.notificationPreferences.sms} onChange={(e) => setProfileData(d => ({ ...d, notificationPreferences: { ...d.notificationPreferences, sms: e.target.checked } }))} disabled={!subscription?.features?.smsAlerts} /></label>
                <p className="toggle-description">Get text messages for urgent permit deadlines</p>
              </Card>
              <Card className="notification-card">
                <label className="toggle-row"><span>Push Notifications</span><input type="checkbox" checked={profileData.notificationPreferences.push} onChange={(e) => setProfileData(d => ({ ...d, notificationPreferences: { ...d.notificationPreferences, push: e.target.checked } }))} /></label>
                <p className="toggle-description">Receive in-app notifications</p>
              </Card>
              <Button onClick={handleProfileSave} loading={loading}>Save Preferences</Button>
            </div>
          )}
          
          {activeTab === 'team' && subscription?.features?.teamAccounts && (
            <div className="settings-section">
              <h2>Team Management</h2>
              <p className="section-description">Invite team members to help manage permits and documents.</p>
              <Card className="invite-card">
                <h3>Invite Team Member</h3>
                <div className="form-row">
                  <Input label="Email" value={newMember.email} onChange={(e) => setNewMember(m => ({ ...m, email: e.target.value }))} placeholder="team@example.com" />
                  <Select label="Role" value={newMember.role} onChange={(e) => setNewMember(m => ({ ...m, role: e.target.value }))} options={isOwner ? [{ value: 'staff', label: 'Staff' }, { value: 'manager', label: 'Manager' }] : [{ value: 'staff', label: 'Staff' }]} />
                </div>
                <Button onClick={inviteMember} disabled={!newMember.email}>Send Invite</Button>
              </Card>
              <h3>Team Members</h3>
              {teamMembers.length > 0 ? teamMembers.map(member => (
                <Card key={member._id} className="team-member-card">
                  <div className="member-info"><span className="member-name">{member.userId?.firstName} {member.userId?.lastName}</span><span className="member-email">{member.userId?.email}</span></div>
                  <div className="member-meta"><Badge variant={member.role === 'manager' ? 'primary' : 'default'}>{member.role === 'manager' ? 'Manager' : 'Staff'}</Badge></div>
                  {(isOwner || (isManager && member.role !== 'manager')) && <button className="remove-btn" onClick={() => removeMember(member._id)}><Icons.Trash /></button>}
                </Card>
              )) : <p className="empty-text">No team members yet</p>}
            </div>
          )}
          
          {activeTab === 'billing' && (
            <div className="settings-section">
              <h2>Billing & Subscription</h2>
              <Card className="current-plan">
                <div className="plan-header">
                  <h3>{subscriptionStatus?.isExpired ? 'Subscription Expired' : 'Current Plan'}</h3>
                  <Badge variant={subscriptionStatus?.isExpired ? 'danger' : 'primary'}>
                    {subscriptionStatus?.isExpired ? 'EXPIRED' : (subscription?.plan?.toUpperCase() || 'TRIAL')}
                  </Badge>
                </div>
                <p>Status: <strong>{subscriptionStatus?.isExpired ? 'Expired' : subscription?.status}</strong></p>
                {subscription?.status === 'trial' && subscription?.trialEndsAt && <p className="trial-warning">Trial ends {formatDate(subscription.trialEndsAt)}</p>}
                {subscription?.currentPeriodEnd && subscription?.status === 'active' && !subscriptionStatus?.isExpired && <p>Next billing: {formatDate(subscription.currentPeriodEnd)}</p>}
                {subscriptionStatus?.isExpired && <p style={{ color: 'var(--danger)', fontWeight: '500', marginTop: '8px' }}>Your subscription has expired. Choose a plan below to restore access.</p>}
                {subscription?.pendingPlanChange && (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px', fontSize: '0.875rem' }}>
                    <strong>Scheduled change:</strong> Switching to {subscription.pendingPlanChange.charAt(0).toUpperCase() + subscription.pendingPlanChange.slice(1)} on {subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : 'next renewal'}
                    <button onClick={handleCancelPendingChange} style={{ marginLeft: '8px', color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.875rem' }}>Cancel</button>
                  </div>
                )}
                {subscription?.stripeSubscriptionId && canManageSubscription && (
                  <Button variant="outline" onClick={handleManageBilling} style={{ marginTop: '12px' }}><Icons.Settings /> Manage Subscription</Button>
                )}
              </Card>
              <h3>Available Plans</h3>
              <div className="plans-grid">
                {[
                  { id: 'basic', name: 'Starter', price: 19, features: ['Permit tracking', 'Email reminders', 'Document vault', '1 user'] },
                  { id: 'pro', name: 'Pro', price: 49, features: ['Everything in Starter', 'SMS alerts', 'PDF autofill', 'Inspection checklists', 'Multi-city support'] },
                  { id: 'elite', name: 'Elite', price: 99, features: ['Everything in Pro', 'Event readiness', 'Team accounts (5 users)', 'Priority support', 'API access'] }
                ].map(plan => {
                  const action = getPlanAction(plan.id);
                  const btnLabel = action === 'current' ? 'Current Plan' : action === 'pending' ? 'Downgrade Scheduled' : action === 'downgrade' ? 'Downgrade' : 'Upgrade';
                  return (
                    <Card key={plan.id} className={`plan-card ${action === 'current' ? 'current' : ''}`}>
                      <h3>{plan.name}</h3>
                      <div className="price">${plan.price}<span>/mo</span></div>
                      <ul className="plan-features">{plan.features.map((f, i) => <li key={i}><Icons.Check /> {f}</li>)}</ul>
                      <Button 
                        variant={action === 'current' || action === 'pending' ? 'outline' : action === 'downgrade' ? 'outline' : 'primary'} 
                        onClick={() => handlePlanAction(plan.id)} 
                        disabled={action === 'current' || action === 'pending'}
                      >
                        {btnLabel}
                      </Button>
                      {action === 'downgrade' && <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px', textAlign: 'center' }}>Keeps current features until renewal</p>}
                    </Card>
                  );
                })}
              </div>

              <div style={{ borderTop: '1px solid var(--danger-bg, #fee2e2)', marginTop: '32px', paddingTop: '24px' }}>
                <h3 style={{ color: 'var(--danger)', margin: '0 0 8px' }}>Danger Zone</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', margin: '0 0 16px' }}>
                  Permanently delete your account and all associated data. {subscription?.stripeSubscriptionId ? 'Your subscription will be automatically cancelled.' : ''} This action cannot be undone.
                </p>
                <Button variant="danger" onClick={handleDeleteAccount} loading={deletingAccount}>
                  <Icons.Alert /> Delete Account Permanently
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    <StripePaymentModal
      isOpen={!!paymentModal}
      onClose={() => setPaymentModal(null)}
      plan={paymentModal || 'basic'}
      onSuccess={() => { fetchUser(); setPaymentModal(null); }}
    />
    </>
  );
};

// ===========================================
// LEGAL PAGES
// ===========================================
const TermsPage = ({ onBack }) => (
  <div className="legal-page">
    <div className="legal-container">
      <button className="back-link" onClick={onBack}><Icons.X /> Back</button>
      <h1>Terms of Service</h1>
      <p className="legal-updated">Last Updated: January 2025</p>
      
      <div className="legal-content">
        <p>Welcome to PermitWise ("PermitWise," "we," "our," or "us"). PermitWise provides software tools that help mobile vendors organize permits, track compliance deadlines, store documents, and receive reminders.</p>
        <p>By creating an account or using PermitWise in any way, you agree to these Terms of Service ("Terms"). If you do not agree, do not use the Service.</p>
        
        <h2>1. Eligibility</h2>
        <p>You must be at least 18 years old and legally capable of entering into contracts. You are responsible for ensuring that your use of PermitWise complies with local, state, and federal laws.</p>
        
        <h2>2. Description of Service</h2>
        <p>PermitWise is a software platform that offers:</p>
        <ul>
          <li>Permit tracking and reminders</li>
          <li>Document storage</li>
          <li>Compliance checklists</li>
          <li>Auto-filled permit application tools (where applicable)</li>
          <li>Multi-city permit requirement data</li>
          <li>Optional SMS/email notifications</li>
          <li>Team accounts (for higher-tier plans)</li>
        </ul>
        <p><strong>PermitWise does not file permits on your behalf or guarantee approval of any permits or inspections.</strong></p>
        
        <h2>3. No Legal or Regulatory Advice</h2>
        <p>PermitWise is not a legal service, regulatory authority, or compliance consultant. All information presented in the app is for general guidance only and may not reflect the most current regulations. You are solely responsible for confirming requirements with the appropriate agencies.</p>
        
        <h2>4. User Responsibilities</h2>
        <p>You agree to:</p>
        <ul>
          <li>Provide accurate and complete information</li>
          <li>Maintain the confidentiality of your credentials</li>
          <li>Upload only documents you have the right to store</li>
          <li>Use PermitWise only for lawful purposes</li>
          <li>Not abuse, harm, or attempt to reverse-engineer the platform</li>
        </ul>
        <p>You are fully responsible for any activity under your account.</p>
        
        <h2>5. Subscriptions & Billing</h2>
        <p>PermitWise offers monthly and yearly subscription plans. By subscribing, you authorize us or our payment processor to charge the applicable fees. All fees are non-refundable unless required by law. You may cancel at any time; service will remain active until the end of the billing period. We may modify pricing with advance notice.</p>
        
        <h2>6. Data Storage & Security</h2>
        <p>PermitWise stores uploaded documents securely using modern cloud storage practices. However, no system is 100% secure. You understand and accept this risk.</p>
        
        <h2>7. Termination</h2>
        <p>We may suspend or terminate your account for violating these Terms, non-payment, misuse of the platform, or fraudulent activity. You may cancel your account at any time through your settings.</p>
        
        <h2>8. Disclaimers</h2>
        <p>PermitWise is provided "as is" without warranties of any kind. We do not guarantee permit approvals, accuracy or completeness of regulatory data, avoidance of fines, shutdowns, or penalties, or continuous or error-free service.</p>
        
        <h2>9. Limitation of Liability</h2>
        <p>To the fullest extent permitted by law, PermitWise shall not be liable for loss of revenue, missed deadlines or expired permits, errors in regulatory information, damages arising from reliance on the platform, or indirect or consequential damages. Our maximum liability is limited to the amount you paid in the past 12 months.</p>
        
        <h2>10. Changes to Terms</h2>
        <p>We may update these Terms at any time. Continued use of PermitWise after changes constitutes acceptance.</p>
        
        <h2>11. Contact</h2>
        <p>For questions: <a href="mailto:support@permitwise.com">support@permitwise.com</a></p>
      </div>
    </div>
  </div>
);

const PrivacyPage = ({ onBack }) => (
  <div className="legal-page">
    <div className="legal-container">
      <button className="back-link" onClick={onBack}><Icons.X /> Back</button>
      <h1>Privacy Policy</h1>
      <p className="legal-updated">Last Updated: January 2025</p>
      
      <div className="legal-content">
        <p>PermitWise ("PermitWise," "we," "our") respects your privacy. This Privacy Policy explains how we collect, use, store, and protect information when you use our services.</p>
        
        <h2>1. Information We Collect</h2>
        <h3>1.1 Information You Provide Directly</h3>
        <ul>
          <li>Name and contact details</li>
          <li>Business information</li>
          <li>Permit details</li>
          <li>Uploaded documents (permits, insurance, inspections, etc.)</li>
          <li>Payment information (processed by Stripe—we do NOT store card numbers)</li>
        </ul>
        
        <h3>1.2 Automatically Collected Information</h3>
        <ul>
          <li>Device data (browser, OS, IP address)</li>
          <li>Usage logs</li>
          <li>Cookies and analytics data</li>
        </ul>
        
        <h3>1.3 Third-Party Data</h3>
        <p>We may collect data from publicly available sources or city websites to build permit requirement templates.</p>
        
        <h2>2. How We Use Your Data</h2>
        <ul>
          <li>Delivering core app functionality</li>
          <li>Sending reminders and notifications</li>
          <li>Providing customer support</li>
          <li>Improving platform performance</li>
          <li>Processing payments</li>
          <li>Researching compliance requirements across cities</li>
        </ul>
        <p><strong>We never sell your data.</strong></p>
        
        <h2>3. Document Storage</h2>
        <p>Documents you upload (permits, inspections, insurance, etc.) are encrypted in transit and stored securely. You control what files are uploaded and when files are removed.</p>
        
        <h2>4. Communication</h2>
        <p>By creating an account, you may receive renewal reminders, account-related communications, security alerts, and optional SMS notifications (Pro plans). Marketing messages are opt-in and can be opted out anytime.</p>
        
        <h2>5. Sharing of Information</h2>
        <p>We may share limited data with service providers (email, SMS, cloud hosting), payment processors (for subscription management), and regulatory agencies (only when you explicitly request it via application export). We do not sell personal data to advertisers or third parties.</p>
        
        <h2>6. Data Retention</h2>
        <p>We retain data for as long as your account is active. You may request deletion at any time. Deleted data is permanently removed from active systems within a reasonable timeframe.</p>
        
        <h2>7. Security</h2>
        <p>We use encryption, access controls, secure API design, and industry-standard hosting. However, no platform is 100% secure. You agree to use PermitWise at your own risk.</p>
        
        <h2>8. Your Rights</h2>
        <p>Depending on your jurisdiction, you may have rights to access your data, correct inaccuracies, delete your data, request copies of your documents, and opt out of marketing. Requests can be sent to <a href="mailto:privacy@permitwise.com">privacy@permitwise.com</a>.</p>
        
        <h2>9. Children's Privacy</h2>
        <p>PermitWise is not intended for individuals under 18.</p>
        
        <h2>10. Changes to This Policy</h2>
        <p>We may update this Privacy Policy at any time.</p>
        
        <h2>11. Contact</h2>
        <p>For privacy-related questions: <a href="mailto:privacy@permitwise.com">privacy@permitwise.com</a></p>
      </div>
    </div>
  </div>
);

// ===========================================
// RESTAURANT PAGES
// ===========================================

const RESTAURANT_TYPES = [
  { value: 'restaurant', label: 'Restaurant', emoji: '🍽️' },
  { value: 'bar_lounge', label: 'Bar / Lounge', emoji: '🍸' },
  { value: 'cafe_coffee_shop', label: 'Café / Coffee Shop', emoji: '☕' },
  { value: 'bakery', label: 'Bakery', emoji: '🥐' },
  { value: 'fast_food', label: 'Fast Food', emoji: '🍔' },
  { value: 'fine_dining', label: 'Fine Dining', emoji: '🥂' },
  { value: 'catering', label: 'Catering', emoji: '🍱' },
  { value: 'ghost_kitchen', label: 'Ghost Kitchen', emoji: '👻' }
];

const RestaurantOnboardingPage = ({ onComplete }) => {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    businessName: '', dbaName: '', phone: '', email: '',
    primaryRestaurantType: '', seatingCapacity: '', cuisineType: '', servesAlcohol: false,
    address: { street: '', city: '', state: '', zip: '' },
    operatingCities: [{ city: '', state: '', isPrimary: true }]
  });
  const [suggestedPermits, setSuggestedPermits] = useState([]);
  const [selectedPermits, setSelectedPermits] = useState([]);
  const [loadingPermits, setLoadingPermits] = useState(false);
  const [coverageStatus, setCoverageStatus] = useState(null);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionSubmitted, setSuggestionSubmitted] = useState(false);

  const handleFindPermits = async () => {
    setStep(3);
    setLoadingPermits(true);
    try {
      const city = form.operatingCities[0].city;
      const state = form.operatingCities[0].state;
      const data = await api.get(`/restaurant/permit-types?city=${encodeURIComponent(city)}&state=${state}&restaurantType=${form.primaryRestaurantType}&servesAlcohol=${form.servesAlcohol}`);
      setSuggestedPermits(data.permitTypes || []);
      setSelectedPermits((data.permitTypes || []).map(p => p._id));
      setCoverageStatus(data.coverage || (data.permitTypes?.length > 0 ? 'full' : 'none'));
    } catch (err) { console.error(err); setCoverageStatus('none'); }
    finally { setLoadingPermits(false); }
  };

  const submitCitySuggestion = async () => {
    try {
      await api.post('/suggestions', {
        type: 'city_request',
        title: `Add restaurant coverage for ${form.operatingCities[0].city}, ${form.operatingCities[0].state}`,
        description: `Restaurant type: ${form.primaryRestaurantType}, Serves alcohol: ${form.servesAlcohol}`,
        cityDetails: { city: form.operatingCities[0].city, state: form.operatingCities[0].state }
      });
      setSuggestionSubmitted(true);
      setShowSuggestionModal(false);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/restaurant/onboarding/complete', {
        ...form,
        seatingCapacity: form.seatingCapacity ? parseInt(form.seatingCapacity) : null,
        operatingCities: form.operatingCities.filter(c => c.city && c.state),
        selectedPermitTypes: selectedPermits
      });
      localStorage.setItem('permitwise_restaurant_onboarding_complete', 'true');
      toast.success('Restaurant setup complete!');
      onComplete();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        <div className="onboarding-header">
          <div className="logo"><Icons.Shield /><span>PermitWise</span></div>
          <div className="onboarding-progress">
            <div className="progress-bar"><div className="progress-fill" style={{ width: `${(step / 3) * 100}%` }} /></div>
            <span>Step {step} of 3</span>
          </div>
        </div>

        {step === 1 && (
          <div className="onboarding-step">
            <h2>Tell us about your restaurant</h2>
            <p className="step-subtitle">This helps us find the right permits for your establishment</p>
            <Input label="Business Name *" value={form.businessName} onChange={e => setForm(f => ({ ...f, businessName: e.target.value }))} />
            <Input label="DBA Name" value={form.dbaName} onChange={e => setForm(f => ({ ...f, dbaName: e.target.value }))} placeholder="If different from business name" />
            <Select label="Restaurant Type *" value={form.primaryRestaurantType} onChange={e => setForm(f => ({ ...f, primaryRestaurantType: e.target.value }))} options={[{ value: '', label: 'Select your restaurant type' }, ...RESTAURANT_TYPES.map(t => ({ value: t.value, label: `${t.emoji} ${t.label}` }))]} />
            <div className="form-row">
              <Input label="Seating Capacity" type="number" value={form.seatingCapacity} onChange={e => setForm(f => ({ ...f, seatingCapacity: e.target.value }))} placeholder="e.g. 50" />
              <Input label="Cuisine Type" value={form.cuisineType} onChange={e => setForm(f => ({ ...f, cuisineType: e.target.value }))} placeholder="e.g. Italian, Mexican" />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '12px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.servesAlcohol} onChange={e => setForm(f => ({ ...f, servesAlcohol: e.target.checked }))} style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
              <span style={{ fontSize: '0.9rem', color: 'var(--gray-700)' }}>This establishment serves alcohol</span>
            </label>
            <Button onClick={() => setStep(2)} disabled={!form.businessName || !form.primaryRestaurantType} className="full-width">Continue →</Button>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-step">
            <h1>Where is your restaurant located?</h1>
            <p className="step-subtitle">We'll find the permits required in your city</p>
            <div className="form-row">
              <Select label="State *" value={form.operatingCities[0].state} onChange={(e) => setForm(f => ({ ...f, operatingCities: [{ ...f.operatingCities[0], state: e.target.value, city: '' }] }))} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
              <CitySearch 
                label="Primary City *" 
                state={form.operatingCities[0].state}
                value={form.operatingCities[0].city}
                onChange={(city) => setForm(f => ({ ...f, operatingCities: [{ ...f.operatingCities[0], city }] }))}
                placeholder="Search for your city..."
                allowCustom={true}
              />
            </div>
            <p className="onboarding-note">You can add more locations later in Settings.</p>
            <div className="onboarding-actions">
              <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
              <Button onClick={handleFindPermits} disabled={!form.operatingCities[0]?.city || !form.operatingCities[0]?.state}>Find My Permits →</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-step">
            <h1>Your restaurant permits</h1>
            {loadingPermits ? <LoadingSpinner /> : (
              <>
                {coverageStatus === 'full' && suggestedPermits.length > 0 && (
                  <>
                    <p className="step-subtitle">Here are the permits typically required for {RESTAURANT_TYPES.find(t => t.value === form.primaryRestaurantType)?.label || 'restaurants'} in {form.operatingCities[0].city}, {form.operatingCities[0].state}.</p>
                    <div className="permit-suggestions">
                      <div className="permit-suggestions-header">
                        <span>{selectedPermits.length} of {suggestedPermits.length} selected</span>
                        <button type="button" onClick={() => setSelectedPermits(suggestedPermits.map(p => p._id))}>Select All</button>
                      </div>
                      <div className="permit-checkbox-list">
                        {suggestedPermits.map(permit => (
                          <label key={permit._id} className={`permit-checkbox ${selectedPermits.includes(permit._id) ? 'selected' : ''}`}>
                            <input type="checkbox" checked={selectedPermits.includes(permit._id)} onChange={() => setSelectedPermits(prev => prev.includes(permit._id) ? prev.filter(p => p !== permit._id) : [...prev, permit._id])} />
                            <div className="permit-checkbox-content">
                              <div className="permit-checkbox-header">
                                <span className="permit-checkbox-name">{permit.name}</span>
                                {permit.importanceLevel === 'critical' && <Badge variant="danger">Critical</Badge>}
                                {permit.importanceLevel === 'often_forgotten' && <Badge variant="warning">Often Forgotten</Badge>}
                              </div>
                              {permit.issuingAuthorityName && <span className="permit-issuer">Issued by: {permit.issuingAuthorityName}</span>}
                              {permit.estimatedCost && <span className="permit-renewal">Est. cost: {permit.estimatedCost}</span>}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {coverageStatus === 'none' && (
                  <div className="coverage-notice">
                    <Icons.Alert />
                    <p>We're still building permit coverage for {form.operatingCities[0].city}. You can still track all your permits manually — just add them after setup. We'll notify you when full support is ready!</p>
                    {!suggestionSubmitted && <Button variant="outline" size="sm" onClick={() => setShowSuggestionModal(true)}>Request Coverage for My City</Button>}
                    {suggestionSubmitted && <Badge variant="success">Request Submitted — Thanks!</Badge>}
                  </div>
                )}
                {coverageStatus === 'partial' && suggestedPermits.length > 0 && (
                  <>
                    <div className="coverage-notice">
                      <Icons.Alert />
                      <p>We're still adding full permit coverage for {form.operatingCities[0].city}. Below are the universal permits most restaurants need. City-specific permits can be added later.</p>
                      {!suggestionSubmitted && <Button variant="outline" size="sm" onClick={() => setShowSuggestionModal(true)}>Request Full Coverage</Button>}
                      {suggestionSubmitted && <Badge variant="success">Request Submitted!</Badge>}
                    </div>
                    <div className="permit-suggestions">
                      <div className="permit-suggestions-header">
                        <span>{selectedPermits.length} of {suggestedPermits.length} selected</span>
                        <button type="button" onClick={() => setSelectedPermits(suggestedPermits.map(p => p._id))}>Select All</button>
                      </div>
                      <div className="permit-checkbox-list">
                        {suggestedPermits.map(permit => (
                          <label key={permit._id} className={`permit-checkbox ${selectedPermits.includes(permit._id) ? 'selected' : ''}`}>
                            <input type="checkbox" checked={selectedPermits.includes(permit._id)} onChange={() => setSelectedPermits(prev => prev.includes(permit._id) ? prev.filter(p => p !== permit._id) : [...prev, permit._id])} />
                            <div className="permit-checkbox-content">
                              <div className="permit-checkbox-header">
                                <span className="permit-checkbox-name">{permit.name}</span>
                                {permit.importanceLevel === 'critical' && <Badge variant="danger">Critical</Badge>}
                                {permit.importanceLevel === 'often_forgotten' && <Badge variant="warning">Often Forgotten</Badge>}
                              </div>
                              {permit.issuingAuthorityName && <span className="permit-issuer">Issued by: {permit.issuingAuthorityName}</span>}
                              {permit.estimatedCost && <span className="permit-renewal">Est. cost: {permit.estimatedCost}</span>}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            <div className="onboarding-actions">
              <Button variant="outline" onClick={() => setStep(2)}>← Back</Button>
              <Button onClick={handleSubmit} loading={loading}>Complete Setup →</Button>
            </div>
          </div>
        )}

        <Modal isOpen={showSuggestionModal} onClose={() => setShowSuggestionModal(false)} title="Request City Coverage">
          <p>We'll prioritize adding restaurant permit data for <strong>{form.operatingCities[0].city}, {form.operatingCities[0].state}</strong>.</p>
          <p>Once coverage is ready, you'll see suggested permits and get renewal reminders specific to your city's requirements.</p>
          <div className="modal-actions">
            <Button variant="outline" onClick={() => setShowSuggestionModal(false)}>Cancel</Button>
            <Button onClick={submitCitySuggestion}>Submit Request</Button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

const RestaurantDashboard = ({ onNavigate }) => {
  const { user, restaurantBusiness, restaurantSubscription } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const result = await api.get('/restaurant/dashboard');
        setStats(result.stats);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchDashboard();
    if (localStorage.getItem('permitwise_restaurant_onboarding_complete') === 'true') {
      setShowWelcomeModal(true);
      localStorage.removeItem('permitwise_restaurant_onboarding_complete');
    }
  }, []);

  const getExpiryLabel = (daysUntil) => {
    if (daysUntil <= 0) return { text: 'Expired!', variant: 'danger' };
    if (daysUntil <= 7) return { text: `Expires in ${daysUntil} day${daysUntil > 1 ? 's' : ''}`, variant: 'danger' };
    if (daysUntil <= 30) return { text: `Expires in ${daysUntil} days`, variant: 'warning' };
    return { text: `${daysUntil} days left`, variant: 'warning' };
  };

  if (loading) return <LoadingSpinner />;

  const needsAttention = (stats?.missingPermits || 0) + (stats?.expiredPermits || 0) + (stats?.expiringPermits || 0);

  return (
    <div className="dashboard">
      {!user?.emailVerified && (
        <div className="email-banner"><Icons.Bell /> Verify your email to ensure you receive permit alerts.</div>
      )}
      <div className="dashboard-header">
        <div><h1>Welcome, {restaurantBusiness?.businessName}!</h1><p>Restaurant compliance overview</p></div>
        {restaurantSubscription?.status === 'trial' && <Alert type="warning">Trial ends {new Date(restaurantSubscription.trialEndsAt).toLocaleDateString()}</Alert>}
      </div>

      {needsAttention > 0 && (
        <Card className="aha-banner">
          <div className="aha-content">
            <div className="aha-icon"><Icons.Alert /></div>
            <div className="aha-text">
              <h2>Action Required</h2>
              <p>
                {stats?.missingPermits > 0 && <span className="aha-item red">⚠️ {stats.missingPermits} permit{stats.missingPermits > 1 ? 's' : ''} missing</span>}
                {stats?.expiredPermits > 0 && <span className="aha-item red">🚨 {stats.expiredPermits} expired — act now!</span>}
                {stats?.expiringPermits > 0 && <span className="aha-item yellow">🟡 {stats.expiringPermits} expiring soon</span>}
              </p>
            </div>
          </div>
          <div className="aha-actions"><Button onClick={() => onNavigate('restaurant-permits')}>Fix Now →</Button></div>
        </Card>
      )}

      <div className="stats-grid">
        <Card className="stat-card"><div className="stat-icon green"><Icons.Check /></div><div className="stat-info"><span className="stat-value">{stats?.activePermits || 0}</span><span className="stat-label">Active</span></div></Card>
        <Card className="stat-card"><div className="stat-icon yellow"><Icons.Clock /></div><div className="stat-info"><span className="stat-value">{stats?.expiringPermits || 0}</span><span className="stat-label">Expiring</span></div></Card>
        <Card className="stat-card"><div className="stat-icon red"><Icons.Alert /></div><div className="stat-info"><span className="stat-value">{stats?.expiredPermits || 0}</span><span className="stat-label">Expired</span></div></Card>
        <Card className="stat-card"><div className="stat-icon gray"><Icons.Document /></div><div className="stat-info"><span className="stat-value">{stats?.missingPermits || 0}</span><span className="stat-label">Missing</span></div></Card>
      </div>

      <div className="dashboard-row">
        <Card className="compliance-card">
          <h2>Compliance Score</h2>
          <div className="compliance-score">
            <div className="score-circle" style={{ '--score': stats?.complianceScore || 0 }}><span>{stats?.complianceScore || 0}%</span></div>
          </div>
        </Card>
        <Card className="upcoming-card">
          <h2>Upcoming Expirations</h2>
          {stats?.upcomingRenewals?.length > 0 ? (
            <ul className="expiration-list">
              {stats.upcomingRenewals.slice(0, 5).map(p => {
                const days = Math.ceil((new Date(p.expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
                const label = getExpiryLabel(days);
                return (
                  <li key={p._id} className="expiration-item">
                    <span className="expiration-name">{p.permitTypeId?.name || p.customName}</span>
                    <Badge variant={label.variant}>{label.text}</Badge>
                  </li>
                );
              })}
            </ul>
          ) : <p>No upcoming expirations</p>}
        </Card>
      </div>

      <Modal isOpen={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} title="">
        <div className="welcome-modal">
          <div className="welcome-icon"><Icons.Check /></div>
          <h2>You're all set! 🎉</h2>
          <p>We've added the permits your restaurant is likely to need. Review them and start uploading documents to track expirations.</p>
          <div className="welcome-actions">
            <Button onClick={() => { setShowWelcomeModal(false); onNavigate('restaurant-permits'); }}>View My Permits</Button>
            <Button variant="outline" onClick={() => setShowWelcomeModal(false)}>I'll do it later</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const RestaurantPermitsPage = () => {
  const { restaurantBusiness, canWrite, updateRestaurantBusiness } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  const [removingCity, setRemovingCity] = useState(null);

  const fetchPermits = useCallback(async () => {
    try {
      const data = await api.get('/restaurant/permits');
      setPermits(data.permits || []);
    } catch (err) { toast.error('Failed to load permits'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetchPermits(); }, [fetchPermits]);

  const handleDelete = async (id) => {
    const ok = await confirm({ title: 'Delete Permit', message: 'Are you sure you want to delete this permit?', variant: 'danger', confirmText: 'Delete' });
    if (!ok) return;
    try {
      await api.delete(`/restaurant/permits/${id}`);
      toast.success('Permit deleted');
      fetchPermits();
    } catch (err) { toast.error(err.message); }
  };

  const handleRemoveCity = async (city, state) => {
    const cityObj = restaurantBusiness?.operatingCities?.find(c => c.city === city && c.state === state);
    if (cityObj?.isPrimary) {
      toast.error('Cannot remove your primary city. Please go to Settings → Cities to set another city as primary first.');
      return;
    }
    const confirmed = await confirm({
      title: 'Remove City',
      message: `Are you sure you want to remove ${city}, ${state}? Permits for this city will be deleted. Documents will remain in your vault.`,
      confirmText: 'Remove City',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!confirmed) return;
    setRemovingCity(`${city}-${state}`);
    try {
      const result = await api.post('/restaurant/permits/remove-city', { city, state });
      toast.success(result.message);
      if (result.business) updateRestaurantBusiness(result.business);
      fetchPermits();
    } catch (err) { toast.error(err.message); }
    finally { setRemovingCity(null); }
  };

  const getStatusLabel = (status) => {
    const labels = { active: 'Active', pending_renewal: 'Expiring Soon', expired: 'Expired', missing: 'Missing', in_progress: 'In Progress' };
    return labels[status] || status;
  };

  const statusVariant = (status) => {
    const map = { active: 'success', pending_renewal: 'warning', expired: 'danger', missing: 'default', in_progress: 'info' };
    return map[status] || 'default';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '';

  if (loading) return <LoadingSpinner />;

  const summary = {
    total: permits.length,
    active: permits.filter(p => p.status === 'active').length,
    expiring: permits.filter(p => p.status === 'pending_renewal').length,
    expired: permits.filter(p => p.status === 'expired').length,
    missing: permits.filter(p => p.status === 'missing').length
  };

  // Group permits by location
  const groupPermitsByLocation = () => {
    const groups = {};
    for (const permit of permits) {
      const jur = permit.jurisdictionId;
      let key, label;
      if (jur?.name === 'National (All US)' || jur?.state === 'US') {
        key = '_NATIONAL';
        label = 'National / Federal';
      } else if (permit.isCustom) {
        key = `${permit.customCity}_${permit.customState}`;
        label = `${permit.customCity}, ${permit.customState}`;
      } else if (jur) {
        key = `${jur.city || jur.name}_${jur.state}`;
        label = `${jur.city || jur.name}, ${jur.state}`;
      } else {
        key = '_OTHER';
        label = 'Other';
      }
      if (!groups[key]) groups[key] = { label, permits: [] };
      groups[key].permits.push(permit);
    }
    // Sort: National first, then alphabetically
    const sorted = Object.entries(groups).sort(([a], [b]) => {
      if (a === '_NATIONAL') return -1;
      if (b === '_NATIONAL') return 1;
      if (a === '_OTHER') return 1;
      if (b === '_OTHER') return -1;
      return a.localeCompare(b);
    });
    return sorted;
  };

  const hasMultipleCities = (restaurantBusiness?.operatingCities?.length || 0) > 1;
  const groupedPermits = groupPermitsByLocation();

  return (
    <div className="page">
      <div className="page-header">
        <div><h1>Restaurant Permits</h1><p>{permits.length} permits tracked for {restaurantBusiness?.businessName}</p></div>
        <div className="header-actions">
          <Button variant="outline" onClick={() => setShowCityModal(true)}><Icons.MapPin /> Add City</Button>
          {canWrite && <Button onClick={() => setShowAddModal(true)}>+ Add Permit</Button>}
        </div>
      </div>

      {/* Operating Cities Section */}
      {restaurantBusiness?.operatingCities?.length > 0 && (
        <div className="operating-cities-section">
          <h3>Operating Cities</h3>
          <div className="cities-list">
            {restaurantBusiness.operatingCities.map((city, idx) => (
              <div key={idx} className={`city-tag ${city.isPrimary ? 'primary' : ''}`}>
                <span>{city.city}, {city.state}</span>
                {city.isPrimary && <Badge variant="primary" size="sm">Primary</Badge>}
                {restaurantBusiness.operatingCities.length > 1 && !city.isPrimary && (
                  <button 
                    className="remove-city-btn" 
                    onClick={() => handleRemoveCity(city.city, city.state)}
                    disabled={removingCity === `${city.city}-${city.state}`}
                    title="Remove city"
                  >
                    {removingCity === `${city.city}-${city.state}` ? <LoadingSpinner /> : <Icons.X />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="permits-summary">
        <div className="summary-item"><span className="count">{summary.total}</span><span>Total</span></div>
        <div className="summary-item green"><span className="count">{summary.active}</span><span>Active</span></div>
        <div className="summary-item yellow"><span className="count">{summary.expiring}</span><span>Expiring</span></div>
        <div className="summary-item red"><span className="count">{summary.expired}</span><span>Expired</span></div>
      </div>

      {permits.length === 0 ? (
        <EmptyState icon={Icons.Permit} title="No permits yet" description="Add a city to auto-populate permits, or add permits manually." action={canWrite && <div className="empty-actions"><Button onClick={() => setShowCityModal(true)}><Icons.MapPin /> Add City</Button><Button variant="outline" onClick={() => setShowAddModal(true)}>+ Add Permit</Button></div>} />
      ) : hasMultipleCities ? (
        <div className="permits-grouped">
          {groupedPermits.map(([key, group]) => (
            <div key={key} className="permit-group">
              <div className="permit-group-header">
                <h3>{key === '_NATIONAL' ? '🌐' : '📍'} {group.label}</h3>
                <Badge variant="default">{group.permits.length}</Badge>
              </div>
              <div className="permits-grid">
                {group.permits.map(permit => (
                  <Card key={permit._id} className="permit-card" onClick={() => setSelectedPermit(permit)}>
                    <div className="permit-header">
                      <h3>{permit.isCustom ? permit.customName : permit.permitTypeId?.name}</h3>
                      <Badge variant={statusVariant(permit.status)}>{getStatusLabel(permit.status)}</Badge>
                    </div>
                    <p>{permit.isCustom ? `${permit.customCity}, ${permit.customState}` : `${permit.jurisdictionId?.name || permit.jurisdictionId?.city}, ${permit.jurisdictionId?.state}`}</p>
                    {permit.expiryDate && <div className="expiry-info"><Icons.Clock /><span>Expires {formatDate(permit.expiryDate)}</span></div>}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="permits-grid">
          {permits.map(permit => (
            <Card key={permit._id} className="permit-card" onClick={() => setSelectedPermit(permit)}>
              <div className="permit-header">
                <h3>{permit.isCustom ? permit.customName : permit.permitTypeId?.name}</h3>
                <Badge variant={statusVariant(permit.status)}>{getStatusLabel(permit.status)}</Badge>
              </div>
              <p>{permit.isCustom ? `${permit.customCity}, ${permit.customState}` : `${permit.jurisdictionId?.name || permit.jurisdictionId?.city}, ${permit.jurisdictionId?.state}`}</p>
              {permit.expiryDate && <div className="expiry-info"><Icons.Clock /><span>Expires {formatDate(permit.expiryDate)}</span></div>}
            </Card>
          ))}
        </div>
      )}

      {/* Add Permit Modal */}
      <RestaurantAddPermitModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSuccess={fetchPermits} toast={toast} business={restaurantBusiness} />

      {/* Add City Modal */}
      <RestaurantAddCityModal isOpen={showCityModal} onClose={() => setShowCityModal(false)} onSuccess={fetchPermits} updateRestaurantBusiness={updateRestaurantBusiness} toast={toast} />

      {/* Permit Detail Modal */}
      {selectedPermit && (
        <RestaurantPermitDetailModal permit={selectedPermit} onClose={() => setSelectedPermit(null)} onUpdate={fetchPermits} onDelete={handleDelete} />
      )}
    </div>
  );
};

const RestaurantAddPermitModal = ({ isOpen, onClose, onSuccess, toast, business }) => {
  const [selectedCity, setSelectedCity] = useState('');
  const [permitTypes, setPermitTypes] = useState([]);
  const [selectedPermit, setSelectedPermit] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [customState, setCustomState] = useState('');

  const operatingCities = business?.operatingCities || [];

  useEffect(() => {
    if (!selectedCity) return;
    const [city, state] = selectedCity.split('||');
    if (!city || !state) return;
    setSearching(true); setSearched(false); setSelectedPermit(''); setPermitTypes([]);
    api.get(`/restaurant/permit-types?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`)
      .then(data => { setPermitTypes(data.permitTypes || []); setSearched(true); })
      .catch(err => { console.error(err); setSearched(true); })
      .finally(() => setSearching(false));
  }, [selectedCity]);

  useEffect(() => {
    if (!isOpen) { setSelectedCity(''); setPermitTypes([]); setSelectedPermit(''); setSearched(false); setShowCustom(false); setCustomName(''); setCustomCity(''); setCustomState(''); }
  }, [isOpen]);

  const handleAdd = async () => {
    if (!selectedPermit) return;
    setLoading(true);
    try {
      const pt = permitTypes.find(p => p._id === selectedPermit);
      await api.post('/restaurant/permits', { permitTypeId: selectedPermit, jurisdictionId: pt.jurisdictionId._id || pt.jurisdictionId, status: 'missing' });
      onSuccess(); onClose(); toast?.success('Permit added!');
    } catch (err) { toast?.error(err.message); }
    finally { setLoading(false); }
  };

  const handleAddCustom = async () => {
    if (!customName.trim()) return;
    setLoading(true);
    try {
      let city, state;
      if (selectedCity) { [city, state] = selectedCity.split('||'); } else { city = customCity; state = customState; }
      if (!city || !state) { toast?.error('Please select or enter a city'); setLoading(false); return; }
      await api.post('/restaurant/permits', { isCustom: true, customName: customName.trim(), customCity: city, customState: state, status: 'missing' });
      onSuccess(); onClose(); toast?.success('Custom permit added!');
    } catch (err) { toast?.error(err.message); }
    finally { setLoading(false); }
  };

  const cityOptions = [
    { value: '', label: 'Select an operating city' },
    ...operatingCities.map(c => ({ value: `${c.city}||${c.state}`, label: `${c.city}, ${c.state}${c.isPrimary ? ' (Primary)' : ''}` }))
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Restaurant Permit" size="lg">
      <Select label="Operating City" value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} options={cityOptions} />

      {searching && <div style={{ textAlign: 'center', padding: '1rem' }}><LoadingSpinner /><p style={{ color: 'var(--gray-500)', marginTop: '0.5rem', fontSize: '0.875rem' }}>Finding required permits...</p></div>}

      {searched && permitTypes.length > 0 && (
        <div className="permit-options" style={{ marginTop: '0.5rem' }}>
          <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Required permits for this city:</p>
          {permitTypes.map(pt => (
            <label key={pt._id} className={`permit-option ${selectedPermit === pt._id ? 'selected' : ''}`}>
              <input type="radio" name="permit" value={pt._id} checked={selectedPermit === pt._id} onChange={(e) => setSelectedPermit(e.target.value)} />
              <span>{pt.name}</span>
            </label>
          ))}
        </div>
      )}

      {searched && permitTypes.length === 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px 16px', margin: '8px 0' }}>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', margin: 0 }}>No predefined permits found for this city yet. You can add a custom permit below.</p>
        </div>
      )}

      {(searched || !selectedCity) && (
        <div style={{ borderTop: '1px solid var(--gray-200)', marginTop: '16px', paddingTop: '16px' }}>
          {!showCustom ? (
            <Button variant="outline" size="sm" onClick={() => setShowCustom(true)}>+ Add Custom Permit</Button>
          ) : (
            <div>
              <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Custom Permit</p>
              <Input label="Permit Name *" placeholder="e.g., Health Department Permit" value={customName} onChange={(e) => setCustomName(e.target.value)} />
              {!selectedCity && (
                <div className="form-row" style={{ marginTop: '0.5rem' }}>
                  <Input label="City *" value={customCity} onChange={(e) => setCustomCity(e.target.value)} />
                  <Select label="State *" value={customState} onChange={(e) => setCustomState(e.target.value)} options={[{ value: '', label: 'Select' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                </div>
              )}
              <div className="modal-actions" style={{ marginTop: '0.75rem' }}>
                <Button variant="outline" size="sm" onClick={() => { setShowCustom(false); setCustomName(''); }}>Cancel</Button>
                <Button size="sm" onClick={handleAddCustom} loading={loading} disabled={!customName.trim()}>Add Custom Permit</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="modal-actions">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        {selectedPermit && <Button onClick={handleAdd} loading={loading}>Add Permit</Button>}
      </div>
    </Modal>
  );
};

const RestaurantAddCityModal = ({ isOpen, onClose, onSuccess, updateRestaurantBusiness, toast }) => {
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [jurisdictions, setJurisdictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCustomEntry, setShowCustomEntry] = useState(false);
  
  useEffect(() => {
    if (!state) { setJurisdictions([]); setCity(''); setCustomCity(''); setShowCustomEntry(false); return; }
    const fetchJurisdictions = async () => {
      try {
        const data = await api.get('/jurisdictions');
        setJurisdictions((data.jurisdictions || []).filter(j => j.state === state));
        setCity(''); setCustomCity('');
      } catch (err) { console.error(err); }
    };
    fetchJurisdictions();
  }, [state]);
  
  const handleAdd = async () => {
    const cityToAdd = showCustomEntry ? customCity : city;
    if (!cityToAdd || !state) return;
    setLoading(true);
    try {
      const data = await api.post('/restaurant/permits/add-city', { city: cityToAdd, state });
      if (data.business) updateRestaurantBusiness?.(data.business);
      toast?.success(data.message || `${cityToAdd}, ${state} added successfully!`);
      onSuccess();
      handleClose();
    } catch (err) { toast?.error(err.message); }
    finally { setLoading(false); }
  };
  
  const cityOptions = jurisdictions.map(j => ({ value: j.city || j.name, label: j.city || j.name }));
  
  const handleCityChange = (e) => {
    const val = e.target.value;
    if (val === '__custom__') { setShowCustomEntry(true); setCity(''); }
    else { setCity(val); setShowCustomEntry(false); setCustomCity(''); }
  };
  
  const handleClose = () => { setState(''); setCity(''); setCustomCity(''); setShowCustomEntry(false); onClose(); };
  
  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Operating City">
      <p>Add a city where your restaurant operates. We'll auto-populate the permits required for that location.</p>
      <div className="form-row">
        <Select label="State" value={state} onChange={(e) => setState(e.target.value)} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
        <Select label="City" value={showCustomEntry ? '__custom__' : city} onChange={handleCityChange} options={[
          { value: '', label: state ? 'Select City' : 'Select state first' },
          ...cityOptions,
          ...(state ? [{ value: '__custom__', label: '➕ Add Other City...' }] : [])
        ]} />
      </div>
      {showCustomEntry && (
        <Input label="City Name" placeholder="Enter city name" value={customCity} onChange={e => setCustomCity(e.target.value)} />
      )}
      <div className="modal-actions">
        <Button variant="outline" onClick={handleClose}>Cancel</Button>
        <Button onClick={handleAdd} loading={loading} disabled={!(showCustomEntry ? customCity : city) || !state}>Add City</Button>
      </div>
    </Modal>
  );
};

const RestaurantPermitDetailModal = ({ permit, onClose, onUpdate, onDelete }) => {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [localPermit, setLocalPermit] = useState(null);

  // Fetch fresh permit data (with populated documents) when modal opens
  useEffect(() => {
    if (permit) {
      setLocalPermit(permit);
      setFormData({
        status: permit.status,
        permitNumber: permit.permitNumber || '',
        issueDate: permit.issueDate?.split('T')[0] || '',
        expiryDate: permit.expiryDate?.split('T')[0] || '',
        notes: permit.notes || ''
      });
      // Fetch fresh data to ensure documents are populated
      api.get(`/restaurant/permits/${permit._id}`).then(data => {
        if (data.permit) setLocalPermit(data.permit);
      }).catch(() => {});
    }
  }, [permit]);

  if (!permit || !localPermit) return null;

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await api.put(`/restaurant/permits/${localPermit._id}`, formData);
      setLocalPermit(response.permit);
      onUpdate();
      setEditing(false);
      toast.success('Permit updated');
    } catch (error) { toast.error(error.message); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError('');
    if (file.size > 10 * 1024 * 1024) { setUploadError('File too large. Max 10MB.'); e.target.value = ''; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', 'permit');
    fd.append('relatedEntityType', 'permit');
    fd.append('relatedEntityId', localPermit._id);
    fd.append('permitId', localPermit._id);
    try {
      const token = localStorage.getItem('permitwise_token');
      const res = await fetch(`${API_URL}/restaurant/documents`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setLocalPermit(prev => ({ ...prev, documents: [...(prev.documents || []), data.document] }));
      onUpdate();
      toast.success('Document uploaded');
    } catch (error) { setUploadError(error.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await api.delete(`/restaurant/documents/${docId}`);
      setLocalPermit(prev => ({ ...prev, documents: (prev.documents || []).filter(d => (d._id || d) !== docId) }));
      onUpdate();
      toast.success('Document removed');
    } catch (error) { toast.error(error.message); }
  };

  const permitName = localPermit.isCustom ? localPermit.customName : localPermit.permitTypeId?.name;
  const permitLocation = localPermit.isCustom ? `${localPermit.customCity}, ${localPermit.customState}` : `${localPermit.jurisdictionId?.name || localPermit.jurisdictionId?.city || ''}, ${localPermit.jurisdictionId?.state || ''}`;
  const getStatusLabel = (s) => ({ active: 'Active', pending_renewal: 'Expiring', expired: 'Expired', missing: 'Missing', in_progress: 'In Progress' }[s] || s);
  const statusVar = (s) => ({ active: 'success', expired: 'danger', pending_renewal: 'warning', missing: 'default', in_progress: 'info' }[s] || 'default');
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : 'Not entered';

  return (
    <Modal isOpen={!!permit} onClose={onClose} title={permitName} size="lg">
      <div className="permit-detail">
        <div className="detail-header">
          <div className="detail-badges">
            <Badge variant={statusVar(localPermit.status)}>{getStatusLabel(localPermit.status)}</Badge>
            {localPermit.isCustom && <Badge variant="primary">Custom</Badge>}
            {localPermit.permitTypeId?.importanceLevel === 'critical' && <Badge variant="danger">Critical</Badge>}
          </div>
          <span>{permitLocation}</span>
        </div>

        {localPermit.status === 'missing' && (
          <div className="missing-permit-cta"><Icons.Alert /><p>You don't have this permit yet. Add details now so we can track expiration reminders.</p><Button size="sm" onClick={() => setEditing(true)}>Add Details →</Button></div>
        )}

        {!localPermit.isCustom && localPermit.permitTypeId && (
          <div className="permit-type-info">
            <h4>Permit Information</h4>
            <div className="info-grid">
              <div className="info-item"><Icons.Clock /><div><span className="info-label">Renewal Frequency</span><span className="info-value">Every {localPermit.permitTypeId?.renewalPeriodMonths || 12} months</span></div></div>
              {localPermit.permitTypeId?.issuingAuthorityName && <div className="info-item"><Icons.Shield /><div><span className="info-label">Issued By</span><span className="info-value">{localPermit.permitTypeId.issuingAuthorityName}</span></div></div>}
              {localPermit.permitTypeId?.estimatedCost && <div className="info-item"><Icons.Document /><div><span className="info-label">Typical Cost</span><span className="info-value">{localPermit.permitTypeId.estimatedCost}</span></div></div>}
            </div>
            {localPermit.permitTypeId?.requiredDocuments?.length > 0 && (
              <div className="required-docs"><span className="info-label">Usually Required:</span><span className="info-value">{localPermit.permitTypeId.requiredDocuments.join(', ')}</span></div>
            )}
          </div>
        )}

        {editing ? (
          <div className="detail-form">
            <Select label="Status" value={formData.status} onChange={(e) => setFormData(f => ({ ...f, status: e.target.value }))} options={[{ value: 'missing', label: 'Missing' }, { value: 'in_progress', label: 'In Progress' }, { value: 'active', label: 'Active' }, { value: 'expired', label: 'Expired' }]} />
            <Input label="Permit Number" value={formData.permitNumber} onChange={(e) => setFormData(f => ({ ...f, permitNumber: e.target.value }))} />
            <div className="form-row">
              <Input label="Issue Date" type="date" value={formData.issueDate} onChange={(e) => setFormData(f => ({ ...f, issueDate: e.target.value }))} />
              <Input label="Expiry Date" type="date" value={formData.expiryDate} onChange={(e) => setFormData(f => ({ ...f, expiryDate: e.target.value }))} />
            </div>
            <Input label="Notes" value={formData.notes} onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))} />
          </div>
        ) : (
          <div className="detail-info">
            <h4>Your Permit Details</h4>
            <div className="info-row"><span>Permit Number</span><span>{localPermit.permitNumber || 'Not entered'}</span></div>
            <div className="info-row"><span>Issue Date</span><span>{formatDate(localPermit.issueDate)}</span></div>
            <div className="info-row"><span>Expiry Date</span><span>{formatDate(localPermit.expiryDate)}</span></div>
            {localPermit.expiryDate && <div className="info-row highlight"><span>Days Until Expiry</span><span>{Math.ceil((new Date(localPermit.expiryDate) - new Date()) / (1000 * 60 * 60 * 24))}</span></div>}
          </div>
        )}

        <div className="detail-document">
          <h4>Documents {(localPermit.documents?.length || 0) > 0 && <span className="doc-count">({localPermit.documents.length})</span>}</h4>
          {uploadError && <Alert type="error">{uploadError}</Alert>}
          {localPermit.documents && localPermit.documents.length > 0 ? (
            <div className="documents-list">
              {localPermit.documents.map((doc, index) => (
                <div key={doc._id || index} className="document-preview">
                  <Icons.Document />
                  <span>{doc.originalName || `Document ${index + 1}`}</span>
                  <a href={getSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer"><Icons.Download /></a>
                  <button className="delete-btn" onClick={() => handleDeleteDoc(doc._id)} title="Remove document" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '4px' }}><Icons.X /></button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="upload-area" style={{ marginTop: localPermit.documents?.length > 0 ? '12px' : '0' }}>
            <input type="file" id="restaurant-permit-upload" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" onChange={handleUpload} hidden />
            <label htmlFor="restaurant-permit-upload">{uploading ? 'Uploading...' : localPermit.documents?.length > 0 ? '+ Add Another Document' : 'Upload Document (PDF, JPG, PNG)'}</label>
          </div>
        </div>

        <div className="modal-actions">
          {editing ? (
            <><Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button><Button onClick={handleSave} loading={loading}>Save</Button></>
          ) : (
            <>
              <Button variant="outline" className="danger" onClick={() => { onDelete(localPermit._id); onClose(); }}>Delete</Button>
              <Button onClick={() => setEditing(true)}><Icons.Edit /> Edit</Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

const RestaurantDocumentsPage = () => {
  const { canWrite } = useAuth();
  const toast = useToast();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      const data = await api.get('/restaurant/documents');
      setDocuments(data.documents || []);
    } catch (err) { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/restaurant/documents/${id}`);
      toast.success('Document deleted');
      fetchDocs();
    } catch (err) { toast.error(err.message); }
  };

  const handleUpload = async (file, category) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category || 'permit');
    try {
      const token = localStorage.getItem('permitwise_token');
      const res = await fetch(`${API_URL}/restaurant/documents`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success('Document uploaded');
      fetchDocs();
      setShowUploadModal(false);
    } catch (err) { toast.error(err.message); throw err; }
  };

  const getCategoryLabel = (doc) => {
    const labels = { permit: 'Permit', insurance: 'Insurance', inspection: 'Inspection', food_handler: 'Food Handler', license: 'License', certification: 'Team Certification', other: 'Other' };
    let label = labels[doc.category] || doc.category || 'Other';
    if (doc.permitName) label += ` — ${doc.permitName}`;
    if (doc.certEmployeeName) label = `${doc.certLabel || 'Certification'} — ${doc.certEmployeeName}`;
    return label;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '';

  if (loading) return <LoadingSpinner />;

  return (
    <div className="documents-page">
      <div className="page-header">
        <div><h1>Document Vault</h1><p>Store all your restaurant documents</p></div>
        {canWrite && <Button onClick={() => setShowUploadModal(true)}><Icons.Upload /> Upload</Button>}
      </div>
      {documents.length > 0 ? (
        <div className="documents-grid">
          {documents.map(doc => (
            <Card key={doc._id} className="document-card">
              <div className="document-icon"><Icons.Document /></div>
              <div className="document-info">
                <h3>{doc.certEmployeeName ? `${doc.certEmployeeName} — ${doc.certLabel || 'Certification'}` : doc.originalName}</h3>
                <span className="doc-category">{getCategoryLabel(doc)}</span>
                <span className="doc-date">{formatDate(doc.createdAt)}</span>
              </div>
              <div className="document-actions">
                <a href={getSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer"><Icons.Download /></a>
                <button onClick={() => handleDelete(doc._id)}><Icons.Trash /></button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState icon={Icons.Document} title="No documents yet" description="Upload permits, licenses, and other important documents." action={canWrite && <Button onClick={() => setShowUploadModal(true)}><Icons.Upload /> Upload</Button>} />
      )}

      {/* Upload Modal */}
      <RestaurantUploadModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} onUpload={handleUpload} />
    </div>
  );
};

const RestaurantUploadModal = ({ isOpen, onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('permit');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (!isOpen) { setFile(null); setCategory('permit'); setError(''); } }, [isOpen]);

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true); setError('');
    try { await onUpload(file, category); } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upload Document">
      {error && <Alert type="error">{error}</Alert>}
      <div className="upload-area">
        <input type="file" id="restaurant-doc-upload" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx" onChange={(e) => setFile(e.target.files[0])} hidden />
        <label htmlFor="restaurant-doc-upload" style={{ cursor: 'pointer' }}>
          {file ? file.name : 'Choose a file (PDF, JPG, PNG, DOC)'}
        </label>
      </div>
      <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)} options={[
        { value: 'permit', label: 'Permit' }, { value: 'insurance', label: 'Insurance' },
        { value: 'inspection', label: 'Inspection' }, { value: 'food_handler', label: 'Food Handler' },
        { value: 'license', label: 'License' }, { value: 'other', label: 'Other' }
      ]} />
      <div className="modal-actions">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} loading={uploading} disabled={!file}>Upload</Button>
      </div>
    </Modal>
  );
};

// ===========================================
// RESTAURANT INSPECTIONS PAGE
// ===========================================

const RestaurantInspectionsPage = () => {
  const { restaurantSubscription } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [checklists, setChecklists] = useState([]);
  const [userChecklists, setUserChecklists] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [inspectionData, setInspectionData] = useState({ items: [], notes: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [viewingInspection, setViewingInspection] = useState(null);
  const [newChecklist, setNewChecklist] = useState({ name: '', description: '', items: [{ itemText: '' }] });
  const [suggestionText, setSuggestionText] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingScheduleIdx, setEditingScheduleIdx] = useState(null);
  const [newSchedule, setNewSchedule] = useState({ title: '', frequency: 'monthly', nextDate: '', checklistId: '', reminderDaysBefore: [7, 1] });

  const hasAccess = restaurantSubscription?.plan === 'pro' || restaurantSubscription?.plan === 'elite' || restaurantSubscription?.plan === 'promo' || restaurantSubscription?.plan === 'lifetime' || restaurantSubscription?.features?.inspectionChecklists;

  const fetchData = async () => {
    try {
      const [cl, ucl, insp] = await Promise.all([
        api.get('/restaurant/checklists'),
        api.get('/restaurant/user-checklists'),
        api.get('/restaurant/inspections')
      ]);
      setChecklists(cl.checklists || []);
      setUserChecklists(ucl.checklists || []);
      setInspections(insp.inspections || []);
      try { const sch = await api.get('/restaurant/inspection-schedules'); setSchedules(sch.schedules || []); } catch (e) { console.error(e); }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleCreateSchedule = async () => {
    if (!newSchedule.title || !newSchedule.nextDate) { toast.error('Title and next date required'); return; }
    try {
      if (editingScheduleIdx !== null) {
        await api.put(`/restaurant/inspection-schedules/${editingScheduleIdx}`, newSchedule);
        toast.success('Schedule updated');
      } else {
        await api.post('/restaurant/inspection-schedules', newSchedule);
        toast.success('Inspection scheduled');
      }
      setShowScheduleModal(false);
      setEditingScheduleIdx(null);
      setNewSchedule({ title: '', frequency: 'monthly', nextDate: '', checklistId: '', reminderDaysBefore: [7, 1] });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const openEditSchedule = (sch, idx) => {
    setEditingScheduleIdx(idx);
    setNewSchedule({
      title: sch.title || '',
      frequency: sch.frequency || 'monthly',
      nextDate: sch.nextDate ? new Date(sch.nextDate).toISOString().split('T')[0] : '',
      checklistId: sch.checklistId || '',
      reminderDaysBefore: sch.reminderDaysBefore || [7, 1]
    });
    setShowScheduleModal(true);
  };

  const openAddSchedule = () => {
    setEditingScheduleIdx(null);
    setNewSchedule({ title: '', frequency: 'monthly', nextDate: '', checklistId: '', reminderDaysBefore: [7, 1] });
    setShowScheduleModal(true);
  };

  const handleDeleteSchedule = async (idx) => {
    const ok = await confirm({ title: 'Delete Schedule', message: 'Remove this scheduled inspection?', confirmText: 'Delete', variant: 'danger' });
    if (!ok) return;
    try { await api.delete(`/restaurant/inspection-schedules/${idx}`); toast.success('Schedule removed'); fetchData(); }
    catch (err) { toast.error(err.message); }
  };

  useEffect(() => { if (!hasAccess) { setLoading(false); return; } fetchData(); }, [hasAccess]);

  const startInspection = (checklist, isUserChecklist = false) => {
    setActiveChecklist({ ...checklist, isUserChecklist });
    setInspectionData({ items: (checklist.items || []).map(item => ({ itemText: item.itemText, description: item.description, passed: null, notes: '' })), notes: '' });
  };

  const updateItem = (index, field, value) => {
    const items = [...inspectionData.items];
    items[index] = { ...items[index], [field]: value };
    setInspectionData(d => ({ ...d, items }));
  };

  const submitInspection = async () => {
    try {
      const payload = { items: inspectionData.items, notes: inspectionData.notes, inspectionDate: new Date() };
      if (activeChecklist.isUserChecklist) payload.userChecklistId = activeChecklist._id;
      else payload.checklistId = activeChecklist._id;
      await api.post('/restaurant/inspections', payload);
      setActiveChecklist(null);
      fetchData();
      toast.success('Inspection submitted successfully!');
    } catch (err) { toast.error(err.message); }
  };

  const createUserChecklist = async () => {
    try {
      await api.post('/restaurant/user-checklists', {
        name: newChecklist.name, description: newChecklist.description,
        items: newChecklist.items.filter(i => i.itemText.trim())
      });
      setNewChecklist({ name: '', description: '', items: [{ itemText: '' }] });
      setShowCreateModal(false);
      fetchData();
      toast.success('Checklist created!');
    } catch (err) { toast.error(err.message); }
  };

  const deleteUserChecklist = async (id) => {
    const confirmed = await confirm({ title: 'Delete Checklist', message: 'Are you sure?', confirmText: 'Delete', variant: 'danger' });
    if (!confirmed) return;
    try { await api.delete(`/restaurant/user-checklists/${id}`); fetchData(); toast.success('Checklist deleted'); }
    catch (err) { toast.error(err.message); }
  };

  const submitSuggestion = async () => {
    try {
      await api.post('/suggestions', { type: 'checklist_request', title: 'Restaurant checklist suggestion', description: suggestionText, checklistDetails: { isUserSpecific: false } });
      setSuggestionText(''); setShowSuggestModal(false);
      toast.success('Thank you! Your suggestion has been submitted.');
    } catch (err) { toast.error(err.message); }
  };

  if (!hasAccess) {
    return (
      <div className="upgrade-feature-page">
        <div className="upgrade-feature-content">
          <div className="upgrade-feature-icon"><Icons.Checklist /></div>
          <h1>Inspection Checklists</h1>
          <Badge variant="warning">Pro Plan Required</Badge>
          <p className="upgrade-feature-description">Stay audit-ready with daily checklists for food safety, sanitation, fire code, and more. Walk through every requirement before inspectors arrive.</p>
          <div className="upgrade-feature-grid">
            <div className="upgrade-feature-item"><div className="upgrade-feature-item-icon"><Icons.Check /></div><h3>Daily Safety Checklists</h3><p>Pre-built opening, closing, and temperature log checklists</p></div>
            <div className="upgrade-feature-item"><div className="upgrade-feature-item-icon"><Icons.Check /></div><h3>Custom Checklists</h3><p>Create your own checklists tailored to your restaurant</p></div>
            <div className="upgrade-feature-item"><div className="upgrade-feature-item-icon"><Icons.Check /></div><h3>Inspection History</h3><p>Keep a record of every completed inspection for audits</p></div>
            <div className="upgrade-feature-item"><div className="upgrade-feature-item-icon"><Icons.Check /></div><h3>Pass/Fail Tracking</h3><p>Identify recurring issues and improve compliance over time</p></div>
          </div>
          <div className="upgrade-pricing-card"><span className="upgrade-plan-label">Pro Plan</span><div className="upgrade-price">$49<span>/month</span></div><p>Also includes SMS alerts & multi-city support</p></div>
          <Button size="lg" onClick={async () => { try { const data = await api.post('/restaurant/subscription/checkout', { plan: 'pro' }); if (data.url) window.location.href = data.url; else if (data.message) toast.info(data.message); } catch (err) { toast.error(err.message); } }}>Upgrade to Pro</Button>
          <p className="upgrade-note">14-day free trial • Cancel anytime</p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  if (activeChecklist) {
    return (
      <div className="inspection-active">
        <div className="page-header"><button className="back-btn" onClick={() => setActiveChecklist(null)}><Icons.X /></button><h1>{activeChecklist.name}</h1></div>
        <div className="inspection-items">
          {inspectionData.items.map((item, i) => (
            <Card key={i} className={`inspection-item ${item.passed === true ? 'passed' : item.passed === false ? 'failed' : ''}`}>
              <div className="item-header"><span className="item-number">{i + 1}</span><h3>{item.itemText}</h3></div>
              {item.description && <p className="item-description">{item.description}</p>}
              <div className="item-actions">
                <button className={`pass-btn ${item.passed === true ? 'active' : ''}`} onClick={() => updateItem(i, 'passed', true)}><Icons.Check /> Pass</button>
                <button className={`fail-btn ${item.passed === false ? 'active' : ''}`} onClick={() => updateItem(i, 'passed', false)}><Icons.X /> Fail</button>
              </div>
              {item.passed === false && <Input placeholder="Notes on failure..." value={item.notes} onChange={(e) => updateItem(i, 'notes', e.target.value)} />}
            </Card>
          ))}
        </div>
        <Card className="inspection-summary">
          <h3>Summary</h3>
          <div className="summary-stats">
            <span className="passed">{inspectionData.items.filter(i => i.passed === true).length} Passed</span>
            <span className="failed">{inspectionData.items.filter(i => i.passed === false).length} Failed</span>
            <span className="pending">{inspectionData.items.filter(i => i.passed === null).length} Pending</span>
          </div>
          <Input label="Overall Notes" value={inspectionData.notes} onChange={(e) => setInspectionData(d => ({ ...d, notes: e.target.value }))} />
          <Button onClick={submitInspection} disabled={inspectionData.items.some(i => i.passed === null)}>Complete Inspection</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="inspections-page">
      <div className="page-header">
        <h1>Inspections</h1>
        <div className="page-header-actions">
          <Button variant="outline" onClick={() => setShowSuggestModal(true)}><Icons.Plus /> Suggest Checklist</Button>
          <Button onClick={() => setShowCreateModal(true)}><Icons.Plus /> Create My Own</Button>
        </div>
      </div>
      <div className="inspections-grid">
        <div className="checklists-section">
          <h2>Recommended Checklists</h2>
          {checklists.length > 0 ? checklists.map(cl => (
            <Card key={cl._id} className="checklist-card">
              <div className="checklist-info">
                <h3>{cl.name}</h3>
                <p>{cl.description}</p>
                <span className="item-count">{cl.items?.length || 0} items</span>
                {cl.category && <Badge variant={cl.category === 'health' ? 'danger' : cl.category === 'fire' ? 'warning' : 'default'}>{cl.category}</Badge>}
              </div>
              <Button onClick={() => startInspection(cl)}>Start</Button>
            </Card>
          )) : <EmptyState icon={Icons.Checklist} title="No recommended checklists yet" description="Checklists for your restaurant type are coming soon. Create your own in the meantime!" />}
          <h2 style={{ marginTop: '2rem' }}>My Checklists</h2>
          {userChecklists.length > 0 ? userChecklists.map(cl => (
            <Card key={cl._id} className="checklist-card user-checklist">
              <div className="checklist-info">
                <h3>{cl.name} <Badge variant="primary">Custom</Badge></h3>
                <p>{cl.description || 'Custom checklist'}</p>
                <span className="item-count">{cl.items?.length || 0} items</span>
              </div>
              <div className="checklist-actions">
                <Button onClick={() => startInspection(cl, true)}>Start</Button>
                <button className="delete-btn" onClick={() => deleteUserChecklist(cl._id)}><Icons.Trash /></button>
              </div>
            </Card>
          )) : <p className="empty-text">No custom checklists yet. Create one above!</p>}
        </div>
        <div className="history-section">
          <h2>Recent Inspections</h2>
          {inspections.length > 0 ? inspections.map(insp => (
            <Card key={insp._id} className="inspection-history-card clickable" onClick={() => setViewingInspection(insp)}>
              <h3>{insp.checklistId?.name || insp.userChecklistId?.name || 'Inspection'}</h3>
              <p>{formatDate(insp.inspectionDate)}</p>
              <div className="inspection-result">
                <Badge variant={insp.overallStatus === 'pass' ? 'success' : insp.overallStatus === 'fail' ? 'danger' : 'warning'}>
                  {insp.overallStatus === 'pass' ? 'PASSED' : insp.overallStatus === 'fail' ? 'FAILED' : 'INCOMPLETE'}
                </Badge>
              </div>
            </Card>
          )) : <p className="empty-text">No inspections recorded yet</p>}
        </div>
      </div>

      {/* Scheduled Inspections */}
      <div style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Scheduled Inspections</h2>
          <Button size="sm" onClick={openAddSchedule}><Icons.Plus /> Schedule</Button>
        </div>
        {schedules.length > 0 ? (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {schedules.map((sch, idx) => {
              const daysUntilNext = sch.nextDate ? Math.ceil((new Date(sch.nextDate) - new Date()) / 86400000) : null;
              return (
                <Card key={idx} className="clickable" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', cursor: 'pointer' }} onClick={() => openEditSchedule(sch, idx)}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 4px' }}>{sch.title}</h4>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                      {sch.frequency === 'daily' ? 'Daily' : sch.frequency === 'weekly' ? 'Weekly' : sch.frequency === 'monthly' ? 'Monthly' : sch.frequency === 'quarterly' ? 'Quarterly' : sch.frequency === 'annually' ? 'Annually' : sch.frequency}
                      {sch.nextDate && ` • Next: ${new Date(sch.nextDate).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {daysUntilNext !== null && daysUntilNext <= 7 && daysUntilNext >= 0 && <Badge variant={daysUntilNext <= 1 ? 'danger' : 'warning'}>{daysUntilNext <= 0 ? 'Today!' : `${daysUntilNext}d`}</Badge>}
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteSchedule(idx); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Icons.Trash /></button>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : <p className="empty-text">No scheduled inspections. Schedule health department visits and routine checks.</p>}
      </div>

      {/* Schedule Modal */}
      <Modal isOpen={showScheduleModal} onClose={() => { setShowScheduleModal(false); setEditingScheduleIdx(null); }} title={editingScheduleIdx !== null ? 'Edit Schedule' : 'Schedule Inspection'}>
        <Input label="Title *" placeholder="e.g., Health Department Visit" value={newSchedule.title} onChange={e => setNewSchedule(s => ({ ...s, title: e.target.value }))} />
        <Select label="Frequency" value={newSchedule.frequency} onChange={e => setNewSchedule(s => ({ ...s, frequency: e.target.value }))} options={[
          { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' }, { value: 'annually', label: 'Annually' }
        ]} />
        <Input label="Next Date *" type="date" value={newSchedule.nextDate} onChange={e => setNewSchedule(s => ({ ...s, nextDate: e.target.value }))} />
        <Select label="Linked Checklist (optional)" value={newSchedule.checklistId} onChange={e => setNewSchedule(s => ({ ...s, checklistId: e.target.value }))} options={[
          { value: '', label: 'None' },
          ...[...checklists, ...userChecklists].map(c => ({ value: c._id, label: c.name }))
        ]} />
        <div style={{ marginTop: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: 8 }}>Remind me before (days)</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[1, 3, 7, 14, 30].map(d => (
              <button key={d} onClick={() => setNewSchedule(s => ({ ...s, reminderDaysBefore: s.reminderDaysBefore.includes(d) ? s.reminderDaysBefore.filter(x => x !== d) : [...s.reminderDaysBefore, d].sort((a, b) => b - a) }))}
                style={{ padding: '6px 14px', borderRadius: 20, border: `2px solid ${newSchedule.reminderDaysBefore.includes(d) ? 'var(--primary)' : 'var(--gray-200)'}`, background: newSchedule.reminderDaysBefore.includes(d) ? 'var(--primary-light)' : 'white', color: newSchedule.reminderDaysBefore.includes(d) ? 'var(--primary)' : 'var(--gray-600)', fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer' }}>{d}d</button>
            ))}
          </div>
        </div>
        <div style={{ marginTop: '1rem' }}><Button onClick={handleCreateSchedule}>{editingScheduleIdx !== null ? 'Update Schedule' : 'Create Schedule'}</Button></div>
      </Modal>

      <Modal isOpen={!!viewingInspection} onClose={() => setViewingInspection(null)} title="Inspection Results" size="lg">
        {viewingInspection && (
          <div className="inspection-view">
            <div className="inspection-view-header">
              <div><h3>{viewingInspection.checklistId?.name || viewingInspection.userChecklistId?.name || 'Inspection'}</h3><p className="inspection-date">{formatDate(viewingInspection.inspectionDate)}</p></div>
              <Badge variant={viewingInspection.overallStatus === 'pass' ? 'success' : viewingInspection.overallStatus === 'fail' ? 'danger' : 'warning'} size="lg">
                {viewingInspection.overallStatus === 'pass' ? 'PASSED' : viewingInspection.overallStatus === 'fail' ? 'FAILED' : 'INCOMPLETE'}
              </Badge>
            </div>
            <div className="inspection-items-list">
              <h4>Checklist Items</h4>
              {viewingInspection.results?.map((item, i) => (
                <div key={i} className={`inspection-item-result ${item.passed === true ? 'passed' : item.passed === false ? 'failed' : 'pending'}`}>
                  <div className="inspection-item-status">{item.passed === true ? <Icons.Check /> : item.passed === false ? <Icons.X /> : <Icons.Clock />}</div>
                  <div className="inspection-item-content"><span className="inspection-item-text">{item.itemText}</span>{item.notes && <p className="inspection-item-notes">{item.notes}</p>}</div>
                </div>
              ))}
            </div>
            {viewingInspection.notes && (<div className="inspection-notes"><h4>Notes</h4><p>{viewingInspection.notes}</p></div>)}
            <div className="modal-actions"><Button variant="outline" onClick={() => setViewingInspection(null)}>Close</Button></div>
          </div>
        )}
      </Modal>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create Custom Checklist" size="lg">
        <Input label="Checklist Name *" placeholder="e.g., Weekly Deep Clean" value={newChecklist.name} onChange={(e) => setNewChecklist(c => ({ ...c, name: e.target.value }))} />
        <Input label="Description" placeholder="What is this checklist for?" value={newChecklist.description} onChange={(e) => setNewChecklist(c => ({ ...c, description: e.target.value }))} />
        <div className="form-section">
          <label>Checklist Items</label>
          {newChecklist.items.map((item, i) => (
            <div key={i} className="checklist-item-row" style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span style={{ minWidth: '30px' }}>{i + 1}.</span>
              <Input placeholder="Item text" value={item.itemText} onChange={(e) => { const items = [...newChecklist.items]; items[i] = { itemText: e.target.value }; setNewChecklist(c => ({ ...c, items })); }} />
              {newChecklist.items.length > 1 && (<button type="button" className="delete-btn" onClick={() => setNewChecklist(c => ({ ...c, items: c.items.filter((_, idx) => idx !== i) }))}><Icons.Trash /></button>)}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setNewChecklist(c => ({ ...c, items: [...c.items, { itemText: '' }] }))}><Icons.Plus /> Add Item</Button>
        </div>
        <div className="modal-actions">
          <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button onClick={createUserChecklist} disabled={!newChecklist.name || !newChecklist.items.some(i => i.itemText.trim())}>Create Checklist</Button>
        </div>
      </Modal>

      <Modal isOpen={showSuggestModal} onClose={() => setShowSuggestModal(false)} title="Suggest a Checklist">
        <p>Is there a checklist you'd like us to add for restaurants? Let us know!</p>
        <Input label="Your Suggestion" placeholder="e.g., HACCP compliance checklist, allergen management, etc." value={suggestionText} onChange={(e) => setSuggestionText(e.target.value)} multiline />
        <div className="modal-actions">
          <Button variant="outline" onClick={() => setShowSuggestModal(false)}>Cancel</Button>
          <Button onClick={submitSuggestion} disabled={!suggestionText.trim()}>Submit Suggestion</Button>
        </div>
      </Modal>
    </div>
  );
};

// ===========================================
// RESTAURANT TEAM CERTIFICATIONS PAGE
// ===========================================
const CERT_TYPES = [
  { value: 'food_handler', label: 'Food Handler Card' },
  { value: 'servsafe_manager', label: 'ServSafe Manager' },
  { value: 'alcohol_service', label: 'Alcohol Service (TIPS/RBS)' },
  { value: 'allergen_awareness', label: 'Allergen Awareness' },
  { value: 'fire_safety', label: 'Fire Safety' },
  { value: 'first_aid_cpr', label: 'First Aid / CPR' },
  { value: 'haccp', label: 'HACCP Certification' },
  { value: 'other', label: 'Other' }
];

const RestaurantTeamCertsPage = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [viewingCert, setViewingCert] = useState(null);
  const [form, setForm] = useState({ employeeName: '', employeeRole: '', certType: 'food_handler', customCertName: '', certNumber: '', issueDate: '', expiryDate: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const fetchCerts = async () => {
    try { const data = await api.get('/restaurant/employee-certs'); setCerts(data.certifications || []); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchCerts(); }, []);

  const openAdd = () => { setEditingCert(null); setPendingFile(null); setForm({ employeeName: '', employeeRole: '', certType: 'food_handler', customCertName: '', certNumber: '', issueDate: '', expiryDate: '', notes: '' }); setShowModal(true); };
  const openEdit = (cert) => {
    setEditingCert(cert); setPendingFile(null);
    setForm({ employeeName: cert.employeeName, employeeRole: cert.employeeRole || '', certType: cert.certType, customCertName: cert.customCertName || '', certNumber: cert.certNumber || '', issueDate: cert.issueDate ? new Date(cert.issueDate).toISOString().split('T')[0] : '', expiryDate: cert.expiryDate ? new Date(cert.expiryDate).toISOString().split('T')[0] : '', notes: cert.notes || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.employeeName.trim()) { toast.error('Employee name is required'); return; }
    setSaving(true);
    try {
      let certId;
      if (editingCert) {
        await api.put(`/restaurant/employee-certs/${editingCert._id}`, form);
        certId = editingCert._id;
      } else {
        const result = await api.post('/restaurant/employee-certs', form);
        certId = result.certification?._id;
      }
      // Upload pending file if selected
      if (pendingFile && certId) {
        const fd = new FormData();
        fd.append('file', pendingFile);
        fd.append('category', 'certification');
        fd.append('certId', certId);
        await api.upload('/restaurant/documents', fd);
      }
      toast.success(editingCert ? 'Certification updated' : 'Certification added');
      setPendingFile(null); setShowModal(false); fetchCerts();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({ title: 'Delete Certification', message: 'Remove this employee certification?', confirmText: 'Delete', variant: 'danger' });
    if (!ok) return;
    try { await api.delete(`/restaurant/employee-certs/${id}`); toast.success('Certification deleted'); fetchCerts(); }
    catch (err) { toast.error(err.message); }
  };

  const handleDocUpload = async (certId, file) => {
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'certification');
      fd.append('certId', certId);
      await api.upload('/restaurant/documents', fd);
      toast.success('Document uploaded');
      const data = await api.get('/restaurant/employee-certs');
      setCerts(data.certifications || []);
      // Refresh viewingCert with updated data
      if (viewingCert) {
        const updated = (data.certifications || []).find(c => c._id === viewingCert._id);
        if (updated) setViewingCert(updated);
      }
    } catch (err) { toast.error(err.message); }
    finally { setUploadingDoc(false); }
  };

  if (loading) return <LoadingSpinner />;

  const filtered = filter === 'all' ? certs : certs.filter(c => c.status === filter);
  const stats = { total: certs.length, active: certs.filter(c => c.status === 'active').length, expiring: certs.filter(c => c.status === 'expiring').length, expired: certs.filter(c => c.status === 'expired').length };
  const grouped = {};
  filtered.forEach(c => { const name = c.employeeName || 'Unknown'; if (!grouped[name]) grouped[name] = []; grouped[name].push(c); });

  return (
    <div className="page">
      <div className="page-header">
        <div><h1>Team Certifications</h1><p>Track employee food handler cards, alcohol service certs, and more</p></div>
        <Button onClick={openAdd}><Icons.Plus /> Add Certification</Button>
      </div>

      <div className="stats-grid">
        <Card className="stat-card"><div className="stat-icon green"><Icons.Check /></div><div className="stat-info"><span className="stat-value">{stats.active}</span><span className="stat-label">Active</span></div></Card>
        <Card className="stat-card"><div className="stat-icon yellow"><Icons.Clock /></div><div className="stat-info"><span className="stat-value">{stats.expiring}</span><span className="stat-label">Expiring Soon</span></div></Card>
        <Card className="stat-card"><div className="stat-icon red"><Icons.Alert /></div><div className="stat-info"><span className="stat-value">{stats.expired}</span><span className="stat-label">Expired</span></div></Card>
        <Card className="stat-card"><div className="stat-icon gray"><Icons.Document /></div><div className="stat-info"><span className="stat-value">{stats.total}</span><span className="stat-label">Total</span></div></Card>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {['all', 'active', 'expiring', 'expired'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600, background: filter === f ? 'var(--primary)' : 'var(--gray-100)', color: filter === f ? 'white' : 'var(--gray-600)' }}>
            {f === 'all' ? `All (${certs.length})` : `${f.charAt(0).toUpperCase() + f.slice(1)} (${certs.filter(c => c.status === f).length})`}
          </button>
        ))}
      </div>

      {Object.keys(grouped).length > 0 ? Object.entries(grouped).map(([name, employeeCerts]) => (
        <div key={name} style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: 'var(--gray-700)' }}>{name} <span style={{ fontWeight: 400, fontSize: '0.8125rem', color: 'var(--gray-400)' }}>({employeeCerts[0]?.employeeRole || ''})</span></h3>
          {employeeCerts.map(cert => (
            <Card key={cert._id} className="clickable" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', marginBottom: '0.5rem', cursor: 'pointer' }} onClick={() => setViewingCert(cert)}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9375rem' }}>{CERT_TYPES.find(t => t.value === cert.certType)?.label || cert.customCertName || cert.certType}</p>
                <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                  {cert.certNumber && `#${cert.certNumber} • `}
                  {cert.expiryDate ? `Expires ${new Date(cert.expiryDate).toLocaleDateString()}` : 'No expiry set'}
                </p>
                {cert.documentId && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--primary)', marginTop: 4 }}><Icons.Document /> Document attached</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Badge variant={cert.status === 'active' ? 'success' : cert.status === 'expiring' ? 'warning' : cert.status === 'expired' ? 'danger' : 'default'}>{cert.status}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )) : <EmptyState icon={Icons.Checklist} title="No certifications tracked" description="Add your team's food handler cards, alcohol certs, and other required certifications." action={<Button onClick={openAdd}><Icons.Plus /> Add First Certification</Button>} />}

      {/* Cert Detail Modal */}
      <Modal isOpen={!!viewingCert} onClose={() => setViewingCert(null)} title={CERT_TYPES.find(t => t.value === viewingCert?.certType)?.label || viewingCert?.customCertName || 'Certification'} size="lg">
        {viewingCert && (
          <div className="permit-detail">
            <div className="detail-header">
              <div className="detail-badges">
                <Badge variant={viewingCert.status === 'active' ? 'success' : viewingCert.status === 'expiring' ? 'warning' : viewingCert.status === 'expired' ? 'danger' : 'default'}>{viewingCert.status}</Badge>
              </div>
              <span>{viewingCert.employeeName}{viewingCert.employeeRole ? ` — ${viewingCert.employeeRole}` : ''}</span>
            </div>

            <div className="detail-info">
              <h4>Certificate Details</h4>
              <div className="info-row"><span>Employee</span><span>{viewingCert.employeeName}</span></div>
              <div className="info-row"><span>Role</span><span>{viewingCert.employeeRole || 'Not set'}</span></div>
              <div className="info-row"><span>Certificate Number</span><span>{viewingCert.certNumber || 'Not entered'}</span></div>
              <div className="info-row"><span>Issue Date</span><span>{viewingCert.issueDate ? new Date(viewingCert.issueDate).toLocaleDateString() : 'Not entered'}</span></div>
              <div className="info-row"><span>Expiry Date</span><span>{viewingCert.expiryDate ? new Date(viewingCert.expiryDate).toLocaleDateString() : 'Not entered'}</span></div>
              {viewingCert.expiryDate && <div className="info-row highlight"><span>Days Until Expiry</span><span>{Math.ceil((new Date(viewingCert.expiryDate) - new Date()) / 86400000)}</span></div>}
              {viewingCert.notes && <div className="info-row"><span>Notes</span><span>{viewingCert.notes}</span></div>}
            </div>

            <div className="detail-document">
              <h4>Document</h4>
              {viewingCert.documentId ? (
                <div className="document-preview">
                  <Icons.Document />
                  <span>{viewingCert.documentId.originalName || 'Certificate Document'}</span>
                  <a href={getSecureFileUrl(viewingCert.documentId.fileUrl)} target="_blank" rel="noopener noreferrer"><Icons.Download /></a>
                </div>
              ) : null}
              <div className="upload-area" style={{ marginTop: viewingCert.documentId ? '12px' : '0' }}>
                <input type="file" id="cert-detail-upload" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => { if (e.target.files[0]) { const f = e.target.files[0]; if (f.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; } handleDocUpload(viewingCert._id, f); } }} hidden disabled={uploadingDoc} />
                <label htmlFor="cert-detail-upload">{uploadingDoc ? 'Uploading...' : viewingCert.documentId ? '+ Replace Document' : 'Upload Document (PDF, JPG, PNG)'}</label>
              </div>
            </div>

            <div className="modal-actions">
              <Button variant="danger" onClick={() => { handleDelete(viewingCert._id); setViewingCert(null); }}><Icons.Trash /> Delete</Button>
              <Button onClick={() => { openEdit(viewingCert); setViewingCert(null); }}><Icons.Edit /> Edit</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add / Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingCert ? 'Edit Certification' : 'Add Certification'}>
        <Input label="Employee Name *" value={form.employeeName} onChange={e => setForm(f => ({ ...f, employeeName: e.target.value }))} placeholder="e.g., John Smith" />
        <Input label="Role" value={form.employeeRole} onChange={e => setForm(f => ({ ...f, employeeRole: e.target.value }))} placeholder="e.g., Line Cook, Server, Manager" />
        <Select label="Certification Type *" value={form.certType} onChange={e => setForm(f => ({ ...f, certType: e.target.value }))} options={CERT_TYPES} />
        {form.certType === 'other' && <Input label="Certification Name" value={form.customCertName} onChange={e => setForm(f => ({ ...f, customCertName: e.target.value }))} placeholder="Enter certification name" />}
        <Input label="Certificate Number" value={form.certNumber} onChange={e => setForm(f => ({ ...f, certNumber: e.target.value }))} placeholder="Optional" />
        <div className="form-row">
          <Input label="Issue Date" type="date" value={form.issueDate} onChange={e => setForm(f => ({ ...f, issueDate: e.target.value }))} />
          <Input label="Expiry Date" type="date" value={form.expiryDate} onChange={e => setForm(f => ({ ...f, expiryDate: e.target.value }))} />
        </div>
        <Input label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" />
        <div className="detail-document" style={{ marginTop: '0.5rem' }}>
          <h4>Certificate Document</h4>
          {editingCert?.documentId && (
            <div className="document-preview">
              <Icons.Document />
              <span>{editingCert.documentId.originalName || 'Certificate Document'}</span>
              <a href={getSecureFileUrl(editingCert.documentId.fileUrl)} target="_blank" rel="noopener noreferrer"><Icons.Download /></a>
            </div>
          )}
          {pendingFile && (
            <div className="document-preview" style={{ background: '#eff6ff' }}>
              <Icons.Document />
              <span>{pendingFile.name}</span>
              <button onClick={() => setPendingFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Icons.X /></button>
            </div>
          )}
          <div className="upload-area" style={{ marginTop: (editingCert?.documentId || pendingFile) ? '12px' : '0' }}>
            <input type="file" id="cert-modal-upload" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => { if (e.target.files[0]) { const f = e.target.files[0]; if (f.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; } if (editingCert) { handleDocUpload(editingCert._id, f); } else { setPendingFile(f); } } }} hidden disabled={uploadingDoc} />
            <label htmlFor="cert-modal-upload">{uploadingDoc ? 'Uploading...' : pendingFile ? 'Change File' : editingCert?.documentId ? '+ Replace Document' : 'Attach Certificate Document (PDF, JPG, PNG)'}</label>
          </div>
        </div>
        <Button onClick={handleSave} loading={saving} style={{ marginTop: '1rem' }}>{editingCert ? 'Update Certification' : 'Add Certification'}</Button>
      </Modal>
    </div>
  );
};

const RestaurantSettingsPage = () => {
  const { restaurantBusiness, restaurantSubscription, restaurantSubscriptionStatus, fetchUser, logout } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [form, setForm] = useState({
    businessName: restaurantBusiness?.businessName || '',
    dbaName: restaurantBusiness?.dbaName || '',
    phone: restaurantBusiness?.phone || '',
    email: restaurantBusiness?.email || '',
    seatingCapacity: restaurantBusiness?.seatingCapacity || '',
    cuisineType: restaurantBusiness?.cuisineType || '',
    servesAlcohol: restaurantBusiness?.servesAlcohol || false,
    primaryRestaurantType: restaurantBusiness?.primaryRestaurantType || '',
    address: restaurantBusiness?.address || { street: '', city: '', state: '', zip: '' },
    operatingCities: restaurantBusiness?.operatingCities?.length > 0 ? restaurantBusiness.operatingCities : [{ city: '', state: '', isPrimary: true }]
  });

  const tabs = ['profile', 'business', 'cities', 'billing'];

  // Phone formatting
  const formatPhoneInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // Email validation
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSave = async () => {
    if (form.email && !isValidEmail(form.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (form.phone && phoneDigits.length > 0 && phoneDigits.length !== 10) {
      toast.error('Phone number must be 10 digits');
      return;
    }
    setLoading(true);
    try {
      const result = await api.put('/restaurant/business', {
        ...form,
        seatingCapacity: form.seatingCapacity ? parseInt(form.seatingCapacity) : null,
        operatingCities: form.operatingCities.filter(c => c.city && c.state)
      });
      if (result.permitsAdded) {
        toast.success(result.message || `Settings saved. ${result.permitsAdded} new permit(s) added.`);
      } else {
        toast.success('Settings saved');
      }
      fetchUser();
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const [restPaymentModal, setRestPaymentModal] = useState(false);
  const [restPaymentPlan, setRestPaymentPlan] = useState('pro');

  const handleManageRestBilling = async () => {
    try {
      const data = await api.post('/restaurant/subscription/portal');
      if (data.url) window.location.href = data.url;
      else toast.info(data.message || 'Manage your subscription');
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteAccount = async () => {
    const confirmed = await confirm({
      title: '⚠️ Delete Restaurant Account',
      message: 'This action is PERMANENT and cannot be undone.\n\n• All your permits, documents, and restaurant data will be deleted\n• Your subscription will be automatically cancelled\n• You will not be able to recover any data\n\nAre you absolutely sure?',
      confirmText: 'Delete My Account',
      variant: 'danger'
    });
    if (!confirmed) return;
    setDeletingAccount(true);
    try {
      await api.delete('/account');
      toast.success('Account permanently deleted');
      logout();
    } catch (err) { toast.error(err.message || 'Failed to delete account'); }
    finally { setDeletingAccount(false); }
  };

  return (
    <>
    <div className="page">
      <div className="page-header"><h1>Restaurant Settings</h1></div>
      <div className="settings-layout">
        <div className="settings-nav">{tabs.map(tab => <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</button>)}</div>
        <div className="settings-content">
          {activeTab === 'profile' && (
            <Card>
              <h3>Restaurant Profile</h3>
              <Input label="Business Name" value={form.businessName} onChange={e => setForm(f => ({ ...f, businessName: e.target.value }))} />
              <Input label="DBA Name" value={form.dbaName} onChange={e => setForm(f => ({ ...f, dbaName: e.target.value }))} />
              <Select label="Restaurant Type" value={form.primaryRestaurantType} onChange={e => setForm(f => ({ ...f, primaryRestaurantType: e.target.value }))} options={RESTAURANT_TYPES.map(t => ({ value: t.value, label: `${t.emoji} ${t.label}` }))} />
              <div className="form-row">
                <Input label="Seating Capacity" type="number" value={form.seatingCapacity} onChange={e => setForm(f => ({ ...f, seatingCapacity: e.target.value }))} />
                <Input label="Cuisine Type" value={form.cuisineType} onChange={e => setForm(f => ({ ...f, cuisineType: e.target.value }))} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '12px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.servesAlcohol} onChange={e => setForm(f => ({ ...f, servesAlcohol: e.target.checked }))} style={{ width: 18, height: 18, accentColor: 'var(--primary)' }} />
                <span style={{ fontSize: '0.9rem', color: 'var(--gray-700)' }}>Serves Alcohol</span>
              </label>
              <Input label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: formatPhoneInput(e.target.value) }))} placeholder="(555) 123-4567" />
              <Input label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="restaurant@example.com" />
              {form.email && !isValidEmail(form.email) && <p style={{ color: 'var(--danger)', fontSize: '0.8rem', margin: '-8px 0 8px' }}>Please enter a valid email address</p>}
              <Button onClick={handleSave} loading={loading}>Save Changes</Button>
            </Card>
          )}
          {activeTab === 'business' && (
            <Card>
              <h3>Business Address</h3>
              <Input label="Street" value={form.address.street} onChange={e => setForm(f => ({ ...f, address: { ...f.address, street: e.target.value } }))} />
              <div className="form-row">
                <Input label="City" value={form.address.city} onChange={e => setForm(f => ({ ...f, address: { ...f.address, city: e.target.value } }))} />
                <Select label="State" value={form.address.state} onChange={e => setForm(f => ({ ...f, address: { ...f.address, state: e.target.value } }))} options={[{ value: '', label: 'Select' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                <Input label="ZIP" value={form.address.zip} onChange={e => setForm(f => ({ ...f, address: { ...f.address, zip: e.target.value } }))} />
              </div>
              <Button onClick={handleSave} loading={loading}>Save Changes</Button>
            </Card>
          )}
          {activeTab === 'cities' && (
            <Card>
              <h3>Operating Cities</h3>
              <p style={{ color: 'var(--gray-500)', marginBottom: 16, fontSize: '0.875rem' }}>Permits are tracked per city. Add all locations where your restaurant operates.</p>
              {form.operatingCities.map((city, i) => (
                <div key={i} className="form-row" style={{ alignItems: 'flex-end' }}>
                  <Select label="State" value={city.state} onChange={e => { const cities = [...form.operatingCities]; cities[i].state = e.target.value; cities[i].city = ''; setForm(f => ({ ...f, operatingCities: cities })); }} options={[{ value: '', label: 'Select' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                  <CitySearch label="City" state={city.state} value={city.city} onChange={(c) => { const cities = [...form.operatingCities]; cities[i].city = c; setForm(f => ({ ...f, operatingCities: cities })); }} placeholder="Search city..." allowCustom={true} />
                  {form.operatingCities.length > 1 && (
                    <Button variant="outline" style={{ color: 'var(--danger)', borderColor: 'var(--danger)', minWidth: 40, padding: '8px' }} onClick={() => setForm(f => ({ ...f, operatingCities: f.operatingCities.filter((_, idx) => idx !== i) }))}>
                      <Icons.X />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" onClick={() => setForm(f => ({ ...f, operatingCities: [...f.operatingCities, { city: '', state: '', isPrimary: false }] }))}>+ Add City</Button>
              <div style={{ marginTop: 16 }}><Button onClick={handleSave} loading={loading}>Save Changes</Button></div>
            </Card>
          )}
          {activeTab === 'billing' && (
            <Card>
              <h3>Subscription</h3>
              <div className="billing-info">
                <div className="billing-row"><span>Plan:</span><Badge variant="primary">{restaurantSubscription?.plan?.toUpperCase() || 'TRIAL'}</Badge></div>
                <div className="billing-row"><span>Status:</span><Badge variant={restaurantSubscriptionStatus?.isActive ? 'success' : 'error'}>{restaurantSubscriptionStatus?.status || 'Unknown'}</Badge></div>
                {restaurantSubscriptionStatus?.expirationDate && (
                  <div className="billing-row"><span>Expires:</span><span>{new Date(restaurantSubscriptionStatus.expirationDate).toLocaleDateString()}</span></div>
                )}
              </div>
              
              <div className="plans-grid" style={{ marginTop: '1.5rem' }}>
                {[
                  { key: 'basic', name: 'Starter', price: 19, features: ['Permit tracking for 1 city', 'Document storage', 'Email reminders'] },
                  { key: 'pro', name: 'Pro', price: 49, popular: true, features: ['Everything in Basic', 'Multi-city support (up to 3)', 'SMS alerts', 'Inspection checklists'] },
                  { key: 'elite', name: 'Elite', price: 99, features: ['Everything in Pro', 'Unlimited cities', 'Team accounts', 'Priority support'] }
                ].map(plan => {
                  const isCurrent = restaurantSubscription?.status === 'active' && restaurantSubscription?.plan === plan.key;
                  return (
                    <Card key={plan.key} className={`plan-card ${plan.popular ? 'recommended' : ''}`}>
                      {plan.popular && <div className="plan-badge">Most Popular</div>}
                      <h4>{plan.name}</h4>
                      <div className="plan-price">${plan.price}<span>/mo</span></div>
                      <ul className="plan-features">
                        {plan.features.map((f, i) => <li key={i}><Icons.Check /> {f}</li>)}
                      </ul>
                      {isCurrent ? (
                        <Button variant="outline" onClick={handleManageRestBilling}>Manage Subscription</Button>
                      ) : (
                        <Button onClick={() => { setRestPaymentPlan(plan.key); setRestPaymentModal(true); }}>
                          {restaurantSubscription?.status === 'trial' ? 'Upgrade Now' : `Subscribe - $${plan.price}/mo`}
                        </Button>
                      )}
                    </Card>
                  );
                })}
              </div>
              <p style={{ textAlign: 'center', color: 'var(--gray-500)', fontSize: '0.8125rem', marginTop: '1rem' }}>All plans include a 14-day free trial • Cancel anytime</p>

              <ChangePasswordForm />
              
              <div style={{ borderTop: '1px solid var(--danger-bg, #fee2e2)', marginTop: '32px', paddingTop: '24px' }}>
                <h3 style={{ color: 'var(--danger)', margin: '0 0 8px' }}>Danger Zone</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', margin: '0 0 16px' }}>
                  Permanently delete your account and all associated data. {restaurantSubscription?.stripeSubscriptionId ? 'Your subscription will be automatically cancelled.' : ''} This action cannot be undone.
                </p>
                <Button variant="danger" onClick={handleDeleteAccount} loading={deletingAccount}>
                  <Icons.Alert /> Delete Account Permanently
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>

    <StripePaymentModal
      isOpen={restPaymentModal}
      onClose={() => setRestPaymentModal(false)}
      plan={restPaymentPlan}
      isRestaurant={true}
      onSuccess={() => { fetchUser(); setRestPaymentModal(false); }}
    />
    </>
  );
};

// ===========================================
// PUBLIC REFERRAL TRACKING PAGE (no auth)
// ===========================================
const ReferralTrackingPage = ({ code, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        const resp = await fetch(`/api/referral-tracking/${encodeURIComponent(code)}`);
        if (!resp.ok) { const err = await resp.json(); throw new Error(err.error || 'Code not found'); }
        const result = await resp.json();
        setData(result);
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    };
    fetchTracking();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchTracking, 60000);
    return () => clearInterval(interval);
  }, [code]);

  if (loading) return <div className="auth-page"><div style={{ textAlign: 'center' }}><LoadingSpinner /><p style={{ color: '#4a6741', marginTop: 12 }}>Loading tracking data...</p></div></div>;

  if (error) return (
    <div className="auth-page">
      <div className="auth-container" style={{ textAlign: 'center' }}>
        <div className="auth-header"><div className="logo"><img src="/images/LOGOnobackground.png" alt="PermitWise" className="auth-logo-img" /></div><h1>Referral Tracking</h1></div>
        <Card style={{ padding: '2rem' }}>
          <Icons.Alert /><p style={{ margin: '1rem 0', color: 'var(--gray-600)' }}>{error}</p>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-400)' }}>Check that the code is correct and try again.</p>
        </Card>
        <button onClick={onBack} style={{ marginTop: '1.5rem', background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.875rem' }}>← Back to PermitWise</button>
      </div>
    </div>
  );

  return (
    <div className="auth-page" style={{ alignItems: 'flex-start', paddingTop: '2rem' }}>
      <div style={{ width: '100%', maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img src="/images/LOGOnobackground.png" alt="PermitWise" style={{ width: 120, height: 'auto', marginBottom: 12 }} />
          <h1 style={{ fontSize: '1.75rem', color: '#1a3320', margin: '0 0 4px' }}>Referral Tracking</h1>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'white', padding: '8px 20px', borderRadius: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginTop: 8 }}>
            <span style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>Code:</span>
            <code style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--primary)', letterSpacing: 1 }}>{data.code}</code>
          </div>
          {data.organizerName && <p style={{ margin: '8px 0 0', fontSize: '0.9375rem', color: '#4a6741' }}>{data.organizerName}</p>}
          {data.description && <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--gray-400)' }}>{data.description}</p>}
        </div>

        {/* Stats Grid */}
        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          <Card className="stat-card"><span className="stat-icon">👤</span><div className="stat-content"><span className="stat-value">{data.totalSignups}</span><span className="stat-label">Total Signups</span></div></Card>
          <Card className="stat-card" style={{ borderLeft: '4px solid #16a34a' }}><span className="stat-icon">💰</span><div className="stat-content"><span className="stat-value" style={{ color: '#16a34a' }}>{data.paidConversions}</span><span className="stat-label">Paid Conversions</span></div></Card>
          <Card className="stat-card" style={{ borderLeft: '4px solid #16a34a' }}><span className="stat-icon">📈</span><div className="stat-content"><span className="stat-value" style={{ color: data.conversionRate >= 20 ? '#16a34a' : data.conversionRate >= 10 ? '#ca8a04' : '#dc2626' }}>{data.conversionRate}%</span><span className="stat-label">Conversion Rate</span></div></Card>
          <Card className="stat-card"><span className="stat-icon">📅</span><div className="stat-content"><span className="stat-value">{data.recentSignups}</span><span className="stat-label">Last 30 Days</span></div></Card>
        </div>

        {/* Conversion Funnel */}
        <Card style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: 'var(--gray-800)' }}>Conversion Funnel</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>Signups → Paid</span>
                <span style={{ fontWeight: 700 }}>{data.paidConversions} / {data.totalSignups}</span>
              </div>
              <div style={{ height: 12, background: 'var(--gray-100)', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${data.conversionRate}%`, background: 'linear-gradient(90deg, #3b82f6, #16a34a)', borderRadius: 6, transition: 'width 0.5s' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Still on trial/free: {data.totalSignups - data.paidConversions}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Last 30d: {data.recentPaid || 0} paid of {data.recentSignups} signups</span>
              </div>
            </div>
            <div style={{ textAlign: 'center', minWidth: 64, padding: '0.75rem', background: data.conversionRate >= 20 ? '#dcfce7' : data.conversionRate >= 10 ? '#fef3c7' : '#fee2e2', borderRadius: 10 }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 700, color: data.conversionRate >= 20 ? '#16a34a' : data.conversionRate >= 10 ? '#ca8a04' : '#dc2626' }}>{data.conversionRate}%</span>
            </div>
          </div>
        </Card>

        {/* By Type */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <Card style={{ flex: 1, textAlign: 'center', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>{data.byType?.vendor || 0}</p>
            <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>🚚 Vendors</p>
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>{data.byTypePaid?.vendor || 0} paid</p>
          </Card>
          <Card style={{ flex: 1, textAlign: 'center', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>{data.byType?.organizer || 0}</p>
            <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>🎪 Organizers</p>
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>{data.byTypePaid?.organizer || 0} paid</p>
          </Card>
          <Card style={{ flex: 1, textAlign: 'center', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>{data.byType?.restaurant || 0}</p>
            <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>🍽️ Restaurants</p>
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>{data.byTypePaid?.restaurant || 0} paid</p>
          </Card>
        </div>

        {/* Signups Table */}
        <Card style={{ padding: '1.25rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: 'var(--gray-800)' }}>Signups ({data.signups?.length || 0})</h3>
          {data.signups?.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table" style={{ fontSize: '0.875rem' }}>
                <thead><tr><th>Name</th><th>Type</th><th>Status</th><th>Plan</th><th>Date</th></tr></thead>
                <tbody>
                  {data.signups.map((s, i) => (
                    <tr key={i}>
                      <td>{s.name}</td>
                      <td><Badge variant={s.accountType === 'vendor' ? 'primary' : s.accountType === 'restaurant' ? 'warning' : 'default'}>{s.accountType}</Badge></td>
                      <td>{s.isPaid ? <Badge variant="success">Paid</Badge> : <Badge variant="default">Free/Trial</Badge>}</td>
                      <td style={{ fontWeight: s.isPaid ? 600 : 400, color: s.isPaid ? '#16a34a' : 'var(--gray-400)' }}>{s.paidPlan ? s.paidPlan.charAt(0).toUpperCase() + s.paidPlan.slice(1) : '—'}</td>
                      <td style={{ color: 'var(--gray-500)' }}>{new Date(s.date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '1.5rem' }}>No signups yet for this code.</p>}
        </Card>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '2rem', paddingBottom: '2rem' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>This page auto-refreshes every 60 seconds</p>
          <button onClick={onBack} style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.875rem' }}>← Back to PermitWise</button>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// ADMIN PAGE
// ===========================================
const SuperAdminPage = ({ onBack }) => {
  const toast = useToast();
  const confirm = useConfirm();
  
  // Admin authentication state
  const [adminAuthenticated, setAdminAuthenticated] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Admin panel state
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ users: [], businesses: [], permits: [], jurisdictions: [], permitTypes: [], checklists: [], events: [], organizers: [], restaurants: [], verifications: { organizers: [], events: [] }, stats: null });
  const [message, setMessage] = useState('');
  const [newJurisdiction, setNewJurisdiction] = useState({ name: '', city: '', state: '', county: '', type: 'city' });
  const [newPermitType, setNewPermitType] = useState({ name: '', description: '', jurisdictionId: '', vendorTypes: [], requiresFoodHandling: false, renewalPeriodMonths: 12, importanceLevel: 'critical', issuingAuthorityName: '', estimatedCost: '', requiredDocuments: '', fees: { application: 0, renewal: 0 } });
  const [searchTerm, setSearchTerm] = useState('');
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateJurisdiction, setDuplicateJurisdiction] = useState('');
  const [editingPermitType, setEditingPermitType] = useState(null);
  
  // Subscription management state
  const [managingSubscription, setManagingSubscription] = useState(null); // Business being managed
  const [subscriptionAction, setSubscriptionAction] = useState(''); // 'extend', 'grant', 'revoke'
  const [subscriptionForm, setSubscriptionForm] = useState({ days: 30, plan: 'promo', durationDays: 90, note: '' });
  
  // Organizer subscription management state
  const [managingOrgSub, setManagingOrgSub] = useState(null); // Organizer being managed
  const [orgSubAction, setOrgSubAction] = useState('');
  const [orgSubForm, setOrgSubForm] = useState({ days: 30, plan: 'organizer', durationDays: 90, note: '' });
  
  // User management state
  const [managingUser, setManagingUser] = useState(null);
  const [userAction, setUserAction] = useState(''); // 'reset-password', 'send-reset-email', 'edit'
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  
  // Checklist management state
  const [newChecklist, setNewChecklist] = useState({ name: '', description: '', jurisdictionId: '', vendorTypes: [], category: 'health', items: [{ itemText: '', description: '', required: true }] });
  const [editingChecklist, setEditingChecklist] = useState(null);
  
  // Suggestion/ticket management state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionFilter, setSuggestionFilter] = useState('pending');
  
  // Referral management state
  const [referralCodes, setReferralCodes] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [editingReferral, setEditingReferral] = useState(null);
  const [referralForm, setReferralForm] = useState({ code: '', organizerId: '', organizerName: '', description: '' });
  const [viewingReferral, setViewingReferral] = useState(null);
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  
  // Event management state
  const [newEvent, setNewEvent] = useState({ eventName: '', organizerName: '', description: '', startDate: '', endDate: '', city: '', state: '', address: '', vendorFee: '', maxVendors: '', status: 'draft', requiredPermits: [] });
  
  // Event vendor management state
  const [managingEvent, setManagingEvent] = useState(null);
  const [vendorToAssign, setVendorToAssign] = useState('');

  // Verify existing admin token on mount
  useEffect(() => {
    const verifyToken = async () => {
      const adminToken = localStorage.getItem('superadminToken');
      if (!adminToken) {
        setAdminLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/admin/verify`, {
          headers: { 'X-Admin-Token': adminToken }
        });
        if (response.ok) {
          setAdminAuthenticated(true);
        } else {
          localStorage.removeItem('superadminToken');
        }
      } catch (err) {
        localStorage.removeItem('superadminToken');
      }
      setAdminLoading(false);
    };
    verifyToken();
  }, []);

  // Admin login
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Login failed');
      localStorage.setItem('superadminToken', result.adminToken);
      setAdminAuthenticated(true);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  // Admin logout
  const handleAdminLogout = () => {
    localStorage.removeItem('superadminToken');
    setAdminAuthenticated(false);
    setLoginData({ username: '', password: '' });
  };

  // Admin API helper
  const adminApi = async (endpoint, method = 'GET', body = null) => {
    const adminToken = localStorage.getItem('superadminToken');
    const options = { method, headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken } };
    if (body) options.body = JSON.stringify(body);
    const response = await fetch(`${API_URL}${endpoint}`, options);
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Request failed');
    return result;
  };

  const fetchData = async (type, overrideStatus) => {
    setLoading(true);
    try {
      if (type === 'users') { const result = await adminApi('/admin/users'); setData(d => ({ ...d, users: result.users || [] })); }
      else if (type === 'businesses') { const result = await adminApi('/admin/businesses'); setData(d => ({ ...d, businesses: result.businesses || [] })); }
      else if (type === 'organizers') { const result = await adminApi('/admin/organizers'); setData(d => ({ ...d, organizers: result.organizers || [] })); }
      else if (type === 'verifications') { const result = await adminApi('/admin/verifications'); setData(d => ({ ...d, verifications: result.verifications || { organizers: [], events: [] } })); }
      else if (type === 'jurisdictions') { const result = await adminApi('/admin/jurisdictions'); setData(d => ({ ...d, jurisdictions: result.jurisdictions || [] })); }
      else if (type === 'permitTypes') { const result = await adminApi('/admin/permit-types'); setData(d => ({ ...d, permitTypes: result.permitTypes || [] })); }
      else if (type === 'checklists') { const result = await adminApi('/admin/checklists'); setData(d => ({ ...d, checklists: result.checklists || [] })); }
      else if (type === 'events') { const result = await adminApi('/admin/events'); setData(d => ({ ...d, events: result.events || [] })); }
      else if (type === 'restaurants') { const result = await adminApi('/admin/restaurants'); setData(d => ({ ...d, restaurants: result.restaurants || [] })); }
      else if (type === 'suggestions') { const filterStatus = overrideStatus || suggestionFilter; const result = await adminApi(`/admin/suggestions?status=${filterStatus}`); setSuggestions(result.suggestions || []); }
      else if (type === 'referrals') { const [codesResult, statsResult] = await Promise.all([adminApi('/admin/referral-codes'), adminApi('/admin/referral-stats')]); setReferralCodes(codesResult.referralCodes || []); setReferralStats(statsResult); }
      else if (type === 'analytics') { const result = await adminApi(`/admin/analytics?days=${analyticsDays}`); setAnalyticsData(result); }
      else if (type === 'stats') { const result = await adminApi('/admin/stats'); setData(d => ({ ...d, stats: result })); }
    } catch (err) { setMessage(err.message); }
    finally { setLoading(false); }
  };

  // Organizer management state
  const [organizerModal, setOrganizerModal] = useState({ show: false, organizer: null, action: null, reason: '' });

  // Organizer management functions
  const toggleOrganizerStatus = async (userId, disabled, reason = '') => {
    try {
      await adminApi(`/admin/organizers/${userId}/status`, 'PUT', { disabled, disabledReason: reason });
      fetchData('organizers');
      setMessage(disabled ? 'Organizer disabled' : 'Organizer enabled');
    } catch (err) { setMessage(err.message); }
  };

  const approveOrganizer = async (userId) => {
    try {
      await adminApi(`/admin/organizers/${userId}/approve`, 'PUT');
      fetchData('organizers');
      setMessage('Organizer approved successfully');
      setOrganizerModal({ show: false, organizer: null, action: null, reason: '' });
    } catch (err) { setMessage(err.message); }
  };

  const rejectOrganizer = async (userId, reason) => {
    try {
      await adminApi(`/admin/organizers/${userId}/reject`, 'PUT', { reason });
      fetchData('organizers');
      setMessage('Organizer rejected');
      setOrganizerModal({ show: false, organizer: null, action: null, reason: '' });
    } catch (err) { setMessage(err.message); }
  };

  const requestOrganizerInfo = async (userId, reason) => {
    try {
      await adminApi(`/admin/organizers/${userId}/request-info`, 'PUT', { reason });
      fetchData('organizers');
      setMessage('Information request sent');
      setOrganizerModal({ show: false, organizer: null, action: null, reason: '' });
    } catch (err) { setMessage(err.message); }
  };

  const handleOrganizerAction = () => {
    if (!organizerModal.organizer) return;
    const { action, reason } = organizerModal;
    const userId = organizerModal.organizer._id;
    
    if (action === 'approve') {
      approveOrganizer(userId);
    } else if (action === 'reject') {
      if (!reason) { setMessage('Please provide a reason for rejection'); return; }
      rejectOrganizer(userId, reason);
    } else if (action === 'request-info') {
      if (!reason) { setMessage('Please specify what information is needed'); return; }
      requestOrganizerInfo(userId, reason);
    } else if (action === 'disable') {
      toggleOrganizerStatus(userId, true, reason);
      setOrganizerModal({ show: false, organizer: null, action: null, reason: '' });
    }
  };

  const createOrganizerAccount = async (email) => {
    if (!email) { setMessage('Please enter an email address'); return; }
    try {
      await adminApi('/admin/organizers', 'POST', { email });
      fetchData('organizers');
      setMessage('Organizer account created');
      document.getElementById('newOrganizerEmail').value = '';
    } catch (err) { setMessage(err.message); }
  };
  
  // Document verification functions
  const reviewOrganizerDocument = async (organizerId, docIndex, status, reviewNotes = '') => {
    try {
      await adminApi(`/admin/organizers/${organizerId}/documents/${docIndex}/review`, 'PUT', { status, reviewNotes });
      fetchData('verifications');
      fetchData('organizers');
      setMessage(`Document ${status}`);
    } catch (err) { setMessage(err.message); }
  };
  
  const reviewEventDocument = async (eventId, docIndex, status, reviewNotes = '') => {
    try {
      await adminApi(`/admin/events/${eventId}/documents/${docIndex}/review`, 'PUT', { status, reviewNotes });
      fetchData('verifications');
      setMessage(`Document ${status}`);
    } catch (err) { setMessage(err.message); }
  };
  
  const updateEventVerification = async (eventId, verificationStatus, verificationNotes = '') => {
    try {
      await adminApi(`/admin/events/${eventId}/verification`, 'PUT', { verificationStatus, verificationNotes });
      fetchData('verifications');
      fetchData('events');
      setMessage(`Event verification updated to ${verificationStatus}`);
    } catch (err) { setMessage(err.message); }
  };

  const convertEventRequestToEvent = async (suggestion) => {
    try {
      const eventDetails = suggestion.eventDetails;
      await adminApi('/admin/events', 'POST', {
        eventName: eventDetails.eventName,
        organizerName: eventDetails.organizerName || 'TBD',
        description: eventDetails.additionalInfo,
        startDate: eventDetails.startDate,
        endDate: eventDetails.endDate || eventDetails.startDate,
        city: eventDetails.city,
        state: eventDetails.state,
        status: 'draft'
      });
      await updateSuggestion(suggestion._id, 'completed', 'Converted to event');
      setMessage('Event created from request');
    } catch (err) { setMessage(err.message); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (adminAuthenticated) fetchData(activeTab); }, [activeTab, adminAuthenticated]);
  
  // Initial stats fetch
  useEffect(() => { if (adminAuthenticated) fetchData('stats'); }, [adminAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const createJurisdiction = async () => {
    try {
      await adminApi('/admin/jurisdictions', 'POST', newJurisdiction);
      setNewJurisdiction({ name: '', city: '', state: '', county: '', type: 'city' });
      fetchData('jurisdictions');
      setMessage('Jurisdiction created');
    } catch (err) { setMessage(err.message); }
  };

  const createPermitType = async () => {
    const payload = { ...newPermitType, requiredDocuments: newPermitType.requiredDocuments.split(',').map(d => d.trim()).filter(Boolean) };
    try {
      await adminApi('/admin/permit-types', 'POST', payload);
      setNewPermitType({ name: '', description: '', jurisdictionId: '', vendorTypes: [], requiresFoodHandling: false, renewalPeriodMonths: 12, importanceLevel: 'critical', issuingAuthorityName: '', estimatedCost: '', requiredDocuments: '', fees: { application: 0, renewal: 0 } });
      fetchData('permitTypes');
      setMessage('Permit type created');
    } catch (err) { setMessage(err.message); }
  };

  const duplicatePermitType = async () => {
    if (!duplicateTarget) return;
    try {
      await adminApi(`/admin/permit-types/${duplicateTarget}/duplicate`, 'POST', { newJurisdictionId: duplicateJurisdiction || undefined });
      setDuplicateTarget(null);
      setDuplicateJurisdiction('');
      fetchData('permitTypes');
      setMessage('Permit type duplicated');
    } catch (err) { setMessage(err.message); }
  };

  const deleteUser = async (id) => { 
    const confirmed = await confirm({ title: 'Delete User', message: 'Are you sure you want to delete this user? This action cannot be undone.', confirmText: 'Delete', variant: 'danger' });
    if (confirmed) { try { await adminApi(`/admin/users/${id}`, 'DELETE'); fetchData('users'); toast.success('User deleted'); } catch (err) { toast.error(err.message); } } 
  };
  
  const resetUserPassword = async () => {
    if (!managingUser || !resetPasswordValue) return;
    if (resetPasswordValue.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try {
      const result = await adminApi(`/admin/users/${managingUser._id}/reset-password`, 'POST', { newPassword: resetPasswordValue });
      toast.success(result.message);
      setManagingUser(null);
      setUserAction('');
      setResetPasswordValue('');
    } catch (err) { toast.error(err.message); }
  };
  
  const sendResetEmail = async () => {
    if (!managingUser) return;
    try {
      const result = await adminApi(`/admin/users/${managingUser._id}/send-reset-email`, 'POST');
      toast.success(result.message);
      setManagingUser(null);
      setUserAction('');
    } catch (err) { toast.error(err.message); }
  };
  
  const toggleEmailVerified = async (user) => {
    try {
      await adminApi(`/admin/users/${user._id}`, 'PUT', { emailVerified: !user.emailVerified });
      fetchData('users');
      toast.success(`Email ${!user.emailVerified ? 'verified' : 'unverified'} for ${user.email}`);
    } catch (err) { toast.error(err.message); }
  };
  
  const openUserManager = (user) => {
    setManagingUser(user);
    setUserAction('');
    setResetPasswordValue('');
  };
  
  // Filtered users
  const filteredUsers = data.users.filter(u => {
    if (!userSearchTerm) return true;
    const search = userSearchTerm.toLowerCase();
    return (u.email?.toLowerCase().includes(search) || u.firstName?.toLowerCase().includes(search) || u.lastName?.toLowerCase().includes(search));
  });
  const deleteBusiness = async (id) => { 
    const confirmed = await confirm({ title: 'Delete Business', message: 'Are you sure you want to delete this business?', confirmText: 'Delete', variant: 'danger' });
    if (confirmed) { try { await adminApi(`/admin/businesses/${id}`, 'DELETE'); fetchData('businesses'); toast.success('Business deleted'); } catch (err) { toast.error(err.message); } } 
  };
  const deleteJurisdiction = async (id) => { 
    const confirmed = await confirm({ title: 'Delete Jurisdiction', message: 'Are you sure you want to delete this jurisdiction?', confirmText: 'Delete', variant: 'danger' });
    if (confirmed) { try { await adminApi(`/admin/jurisdictions/${id}`, 'DELETE'); fetchData('jurisdictions'); toast.success('Jurisdiction deleted'); } catch (err) { toast.error(err.message); } } 
  };
  const deletePermitType = async (id) => { 
    const confirmed = await confirm({ title: 'Delete Permit Type', message: 'Are you sure you want to delete this permit type?', confirmText: 'Delete', variant: 'danger' });
    if (confirmed) { try { await adminApi(`/admin/permit-types/${id}`, 'DELETE'); fetchData('permitTypes'); toast.success('Permit type deleted'); } catch (err) { toast.error(err.message); } } 
  };
  
  // Checklist CRUD functions
  const createChecklist = async () => {
    try {
      await adminApi('/admin/checklists', 'POST', {
        name: newChecklist.name,
        description: newChecklist.description,
        jurisdictionId: newChecklist.jurisdictionId || null,
        vendorTypes: newChecklist.vendorTypes || [],
        category: newChecklist.category,
        items: newChecklist.items.filter(i => i.itemText.trim())
      });
      setNewChecklist({ name: '', description: '', jurisdictionId: '', vendorTypes: [], category: 'health', items: [{ itemText: '', description: '', required: true }] });
      fetchData('checklists');
      setMessage('Checklist created');
    } catch (err) { setMessage(err.message); }
  };
  
  const updateChecklist = async () => {
    if (!editingChecklist) return;
    try {
      await adminApi(`/admin/checklists/${editingChecklist._id}`, 'PUT', {
        name: editingChecklist.name,
        description: editingChecklist.description,
        jurisdictionId: editingChecklist.jurisdictionId || null,
        vendorTypes: editingChecklist.vendorTypes || [],
        category: editingChecklist.category,
        items: editingChecklist.items?.filter(i => i.itemText?.trim()) || [],
        active: editingChecklist.active
      });
      setEditingChecklist(null);
      fetchData('checklists');
      setMessage('Checklist updated');
    } catch (err) { setMessage(err.message); }
  };
  
  const deleteChecklist = async (id) => { 
    const confirmed = await confirm({ title: 'Delete Checklist', message: 'Are you sure you want to delete this checklist?', confirmText: 'Delete', variant: 'danger' });
    if (confirmed) { try { await adminApi(`/admin/checklists/${id}`, 'DELETE'); fetchData('checklists'); toast.success('Checklist deleted'); } catch (err) { toast.error(err.message); } } 
  };

  // Suggestion functions
  const updateSuggestion = async (id, status, adminNotes) => {
    try {
      await adminApi(`/admin/suggestions/${id}`, 'PUT', { status, adminNotes });
      // Re-fetch suggestions with current filter
      const listResult = await adminApi(`/admin/suggestions?status=${suggestionFilter}`);
      setSuggestions(listResult.suggestions || []);
      toast.success(`Suggestion marked as ${status.replace('_', ' ')}`);
    } catch (err) { toast.error(err.message); }
  };
  const deleteSuggestion = async (id) => { 
    const confirmed = await confirm({ title: 'Delete Suggestion', message: 'Are you sure you want to delete this suggestion?', confirmText: 'Delete', variant: 'danger' });
    if (confirmed) { try { await adminApi(`/admin/suggestions/${id}`, 'DELETE'); const result = await adminApi(`/admin/suggestions?status=${suggestionFilter}`); setSuggestions(result.suggestions || []); toast.success('Suggestion deleted'); } catch (err) { toast.error(err.message); } } 
  };

  // Event CRUD functions
  const createEvent = async () => {
    try {
      await adminApi('/admin/events', 'POST', {
        eventName: newEvent.eventName,
        organizerName: newEvent.organizerName,
        description: newEvent.description,
        startDate: newEvent.startDate,
        endDate: newEvent.endDate || newEvent.startDate,
        city: newEvent.city,
        state: newEvent.state,
        address: newEvent.address,
        vendorFee: newEvent.vendorFee,
        maxVendors: newEvent.maxVendors,
        status: newEvent.status,
        requiredPermitTypes: newEvent.requiredPermits || []
      });
      setNewEvent({ eventName: '', organizerName: '', description: '', startDate: '', endDate: '', city: '', state: '', address: '', vendorFee: '', maxVendors: '', status: 'draft', requiredPermits: [] });
      fetchData('events');
      toast.success('Event created');
    } catch (err) { toast.error(err.message); }
  };
  const deleteEvent = async (id) => { 
    const confirmed = await confirm({ title: 'Delete Event', message: 'Are you sure you want to delete this event?', confirmText: 'Delete', variant: 'danger' });
    if (confirmed) { try { await adminApi(`/admin/events/${id}`, 'DELETE'); fetchData('events'); toast.success('Event deleted'); } catch (err) { toast.error(err.message); } } 
  };
  
  // Event vendor assignment functions
  const assignVendorToEvent = async () => {
    if (!managingEvent || !vendorToAssign) return;
    try {
      const result = await adminApi(`/admin/events/${managingEvent._id}/assign-vendor`, 'POST', { vendorBusinessId: vendorToAssign });
      setManagingEvent(result.event);
      setVendorToAssign('');
      fetchData('events');
      toast.success('Vendor assigned to event');
    } catch (err) { toast.error(err.message); }
  };
  
  const removeVendorFromEvent = async (vendorId) => {
    if (!managingEvent) return;
    const confirmed = await confirm({ title: 'Remove Vendor', message: 'Are you sure you want to remove this vendor from the event?', confirmText: 'Remove', variant: 'danger' });
    if (!confirmed) return;
    try {
      await adminApi(`/admin/events/${managingEvent._id}/remove-vendor/${vendorId}`, 'DELETE');
      setManagingEvent(prev => ({
        ...prev,
        assignedVendors: prev.assignedVendors.filter(v => v.vendorBusinessId?._id !== vendorId)
      }));
      fetchData('events');
      setMessage('Vendor removed from event');
    } catch (err) { setMessage(err.message); }
  };
  
  const updatePermitType = async () => {
    if (!editingPermitType) return;
    try {
      const payload = {
        ...editingPermitType,
        jurisdictionId: editingPermitType.jurisdictionId?._id || editingPermitType.jurisdictionId,
        requiredDocuments: typeof editingPermitType.requiredDocuments === 'string' 
          ? editingPermitType.requiredDocuments.split(',').map(d => d.trim()).filter(Boolean)
          : editingPermitType.requiredDocuments
      };
      await adminApi(`/admin/permit-types/${editingPermitType._id}`, 'PUT', payload);
      setEditingPermitType(null);
      fetchData('permitTypes');
      setMessage('Permit type updated');
    } catch (err) { setMessage(err.message); }
  };

  // Subscription management functions
  const extendTrial = async () => {
    if (!managingSubscription) return;
    try {
      const result = await adminApi(`/admin/subscriptions/${managingSubscription._id}/extend-trial`, 'POST', {
        days: subscriptionForm.days,
        note: subscriptionForm.note
      });
      setMessage(result.message);
      setManagingSubscription(null);
      setSubscriptionAction('');
      fetchData('businesses');
    } catch (err) { setMessage(err.message); }
  };

  const grantPromo = async () => {
    if (!managingSubscription) return;
    try {
      const result = await adminApi(`/admin/subscriptions/${managingSubscription._id}/grant-promo`, 'POST', {
        plan: subscriptionForm.plan,
        durationDays: subscriptionForm.plan === 'promo' ? subscriptionForm.durationDays : null,
        note: subscriptionForm.note
      });
      setMessage(result.message);
      setManagingSubscription(null);
      setSubscriptionAction('');
      fetchData('businesses');
    } catch (err) { setMessage(err.message); }
  };

  const revokeSubscription = async () => {
    if (!managingSubscription) return;
    const confirmed = await confirm({ 
      title: 'Revoke Subscription', 
      message: 'Are you sure you want to revoke this subscription? The user will lose access to paid features.', 
      confirmText: 'Revoke Access', 
      variant: 'danger' 
    });
    if (!confirmed) return;
    try {
      const result = await adminApi(`/admin/subscriptions/${managingSubscription._id}/revoke`, 'POST', {
        note: subscriptionForm.note
      });
      toast.success(result.message);
      setManagingSubscription(null);
      setSubscriptionAction('');
      fetchData('businesses');
    } catch (err) { toast.error(err.message); }
  };

  const openSubscriptionManager = (business) => {
    setManagingSubscription(business);
    setSubscriptionAction('');
    setSubscriptionForm({ days: 30, plan: 'promo', durationDays: 90, note: '' });
  };

  const getSubscriptionStatusColor = (sub) => {
    if (!sub) return 'gray';
    if (sub.status === 'lifetime') return 'purple';
    if (sub.status === 'promo') return 'blue';
    if (sub.status === 'active') return 'green';
    if (sub.status === 'trial') {
      const daysLeft = sub.trialEndsAt ? Math.ceil((new Date(sub.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
      return daysLeft > 0 ? 'yellow' : 'red';
    }
    return 'red';
  };

  const getImportanceLabel = (level) => {
    if (level === 'critical') return { text: 'Critical', variant: 'danger' };
    if (level === 'often_forgotten') return { text: 'Often Forgotten', variant: 'warning' };
    if (level === 'event_required') return { text: 'Event Required', variant: 'info' };
    return { text: 'Critical', variant: 'danger' };
  };

  // Organizer subscription management functions
  const extendOrgTrial = async () => {
    if (!managingOrgSub) return;
    try {
      const result = await adminApi(`/admin/organizer-subscriptions/${managingOrgSub._id}/extend-trial`, 'POST', { days: orgSubForm.days, note: orgSubForm.note });
      setMessage(result.message); setManagingOrgSub(null); setOrgSubAction(''); fetchData('organizers');
    } catch (err) { setMessage(err.message); }
  };
  const grantOrgPromo = async () => {
    if (!managingOrgSub) return;
    try {
      const result = await adminApi(`/admin/organizer-subscriptions/${managingOrgSub._id}/grant-promo`, 'POST', {
        plan: orgSubForm.plan, durationDays: orgSubForm.plan === 'promo' ? orgSubForm.durationDays : null, note: orgSubForm.note
      });
      setMessage(result.message); setManagingOrgSub(null); setOrgSubAction(''); fetchData('organizers');
    } catch (err) { setMessage(err.message); }
  };
  const revokeOrgSubscription = async () => {
    if (!managingOrgSub) return;
    const confirmed = await confirm({ title: 'Revoke Organizer Subscription', message: 'Are you sure? The organizer will lose access to paid features.', confirmText: 'Revoke Access', variant: 'danger' });
    if (!confirmed) return;
    try {
      const result = await adminApi(`/admin/organizer-subscriptions/${managingOrgSub._id}/revoke`, 'POST', { note: orgSubForm.note });
      toast.success(result.message); setManagingOrgSub(null); setOrgSubAction(''); fetchData('organizers');
    } catch (err) { toast.error(err.message); }
  };
  const openOrgSubManager = (org) => {
    setManagingOrgSub(org); setOrgSubAction(''); setOrgSubForm({ days: 30, plan: 'organizer', durationDays: 90, note: '' });
  };

  // Restaurant subscription management state & functions
  const [managingRestSub, setManagingRestSub] = useState(null);
  const [restSubAction, setRestSubAction] = useState('');
  const [restSubForm, setRestSubForm] = useState({ days: 30, plan: 'promo', durationDays: 90, note: '' });
  
  const extendRestTrial = async () => {
    if (!managingRestSub) return;
    try {
      const result = await adminApi(`/admin/restaurant-subscriptions/${managingRestSub._id}/extend-trial`, 'POST', { days: restSubForm.days, note: restSubForm.note });
      setMessage(result.message); setManagingRestSub(null); setRestSubAction(''); fetchData('restaurants');
    } catch (err) { setMessage(err.message); }
  };
  const grantRestPromo = async () => {
    if (!managingRestSub) return;
    try {
      const result = await adminApi(`/admin/restaurant-subscriptions/${managingRestSub._id}/grant-promo`, 'POST', {
        plan: restSubForm.plan, durationDays: restSubForm.plan === 'promo' ? restSubForm.durationDays : null, note: restSubForm.note
      });
      setMessage(result.message); setManagingRestSub(null); setRestSubAction(''); fetchData('restaurants');
    } catch (err) { setMessage(err.message); }
  };
  const revokeRestSubscription = async () => {
    if (!managingRestSub) return;
    const confirmed = await confirm({ title: 'Revoke Restaurant Subscription', message: 'Are you sure?', confirmText: 'Revoke Access', variant: 'danger' });
    if (!confirmed) return;
    try {
      const result = await adminApi(`/admin/restaurant-subscriptions/${managingRestSub._id}/revoke`, 'POST', { note: restSubForm.note });
      toast.success(result.message); setManagingRestSub(null); setRestSubAction(''); fetchData('restaurants');
    } catch (err) { toast.error(err.message); }
  };
  const deleteRestaurant = async (id) => {
    const confirmed = await confirm({ title: 'Delete Restaurant', message: 'Delete this restaurant and all its data? This cannot be undone.', confirmText: 'Delete', variant: 'danger' });
    if (!confirmed) return;
    try { await adminApi(`/admin/restaurants/${id}`, 'DELETE'); toast.success('Restaurant deleted'); fetchData('restaurants'); }
    catch (err) { toast.error(err.message); }
  };

  // Filter permit types by search
  const filteredPermitTypes = data.permitTypes.filter(pt => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return pt.name?.toLowerCase().includes(search) || pt.jurisdictionId?.name?.toLowerCase().includes(search) || pt.jurisdictionId?.city?.toLowerCase().includes(search);
  });

  // Show loading while checking auth
  if (adminLoading) {
    return (
      <div className="admin-login">
        <LoadingSpinner />
      </div>
    );
  }

  // Show login form if not authenticated
  if (!adminAuthenticated) {
    return (
      <div className="admin-login">
        <button className="back-link" onClick={onBack}><Icons.X /> Back to Site</button>
        <Card className="admin-login-card">
          <div className="admin-login-icon"><Icons.Lock /></div>
          <h1>Super Admin</h1>
          <p>Enter your credentials to access the admin dashboard.</p>
          <form onSubmit={handleAdminLogin} className="admin-login-form">
            {loginError && <Alert type="error">{loginError}</Alert>}
            <Input 
              label="Username" 
              value={loginData.username} 
              onChange={(e) => setLoginData(d => ({ ...d, username: e.target.value }))} 
              required 
              autoFocus
            />
            <Input 
              label="Password" 
              type="password" 
              value={loginData.password} 
              onChange={(e) => setLoginData(d => ({ ...d, password: e.target.value }))} 
              required 
            />
            <Button type="submit" loading={loginLoading} className="full-width">Login</Button>
          </form>
          <p className="admin-login-hint">**********</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="back-link" onClick={onBack}><Icons.X /> Exit</button>
        <div className="admin-header-brand">
          <h1>PermitWise</h1>
          <span className="admin-badge">SUPER ADMIN</span>
        </div>
        <Button variant="outline" onClick={handleAdminLogout}><Icons.Logout /> Logout</Button>
      </div>
      {message && <Alert type="info" onClose={() => setMessage('')}>{message}</Alert>}
      
      <div className="admin-tabs">
        {[
          { key: 'stats', label: 'Dashboard', icon: '📊' },
          { key: 'users', label: 'Users', icon: '👤' },
          { key: 'businesses', label: 'Businesses', icon: '🏢' },
          { key: 'organizers', label: 'Organizers', icon: '🎪' },
          { key: 'restaurants', label: 'Restaurants', icon: '🍽️' },
          { key: 'verifications', label: 'Verifications', icon: '✅' },
          { key: 'jurisdictions', label: 'Jurisdictions', icon: '🏛️' },
          { key: 'permitTypes', label: 'Permit Types', icon: '📋' },
          { key: 'checklists', label: 'Checklists', icon: '☑️' },
          { key: 'events', label: 'Events', icon: '📅' },
          { key: 'suggestions', label: 'Suggestions', icon: '💡' },
          { key: 'referrals', label: 'Referrals', icon: '🔗' },
          { key: 'analytics', label: 'Analytics', icon: '📊' }
        ].map(tab => (
          <button key={tab.key} className={activeTab === tab.key ? 'active' : ''} onClick={() => setActiveTab(tab.key)}>
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="admin-content">
        {loading && <LoadingSpinner />}
        
        {activeTab === 'stats' && data.stats && (
          <div className="admin-stats">
            <div className="stats-grid">
              <Card className="stat-card stat-primary"><span className="stat-icon">👤</span><div className="stat-content"><span className="stat-value">{data.stats.totalUsers || 0}</span><span className="stat-label">Total Users</span></div></Card>
              <Card className="stat-card stat-success"><span className="stat-icon">🏢</span><div className="stat-content"><span className="stat-value">{data.stats.totalBusinesses || 0}</span><span className="stat-label">Businesses</span></div></Card>
              <Card className="stat-card stat-info"><span className="stat-icon">📋</span><div className="stat-content"><span className="stat-value">{data.stats.totalPermits || 0}</span><span className="stat-label">Permits Tracked</span></div></Card>
              <Card className="stat-card stat-warning"><span className="stat-icon">⭐</span><div className="stat-content"><span className="stat-value">{data.stats.activeSubscriptions || 0}</span><span className="stat-label">Active Subscriptions</span></div></Card>
              <Card className="stat-card stat-purple"><span className="stat-icon">🏛️</span><div className="stat-content"><span className="stat-value">{data.stats.totalJurisdictions || 0}</span><span className="stat-label">Jurisdictions</span></div></Card>
              <Card className="stat-card stat-teal"><span className="stat-icon">📝</span><div className="stat-content"><span className="stat-value">{data.stats.totalPermitTypes || 0}</span><span className="stat-label">Permit Types</span></div></Card>
            </div>
            <div className="stats-detail-row">
              <Card className="recent-signups">
                <h3>📈 Activity Overview</h3>
                <div className="activity-grid">
                  <div className="activity-item"><span className="activity-value">{data.stats.newUsersToday || 0}</span><span className="activity-label">New Users Today</span></div>
                  <div className="activity-item"><span className="activity-value">{data.stats.newUsersThisWeek || 0}</span><span className="activity-label">New This Week</span></div>
                  <div className="activity-item"><span className="activity-value">${data.stats.mrr || 0}</span><span className="activity-label">Total MRR</span></div>
                </div>
              </Card>
              <Card className="recent-signups">
                <h3>💰 Revenue Breakdown</h3>
                <div className="activity-grid">
                  <div className="activity-item"><span className="activity-value">${data.stats.vendorMrr || 0}</span><span className="activity-label">Vendor MRR</span></div>
                  <div className="activity-item"><span className="activity-value">${data.stats.organizerMrr || 0}</span><span className="activity-label">Organizer MRR</span></div>
                  <div className="activity-item"><span className="activity-value">${data.stats.restaurantMrr || 0}</span><span className="activity-label">Restaurant MRR</span></div>
                </div>
              </Card>
              <Card className="recent-signups">
                <h3>👥 User Types</h3>
                <div className="activity-grid">
                  <div className="activity-item"><span className="activity-value">{data.stats.totalOrganizers || 0}</span><span className="activity-label">Organizers ({data.stats.activeOrgSubscriptions || 0} active)</span></div>
                  <div className="activity-item"><span className="activity-value">{data.stats.totalRestaurants || 0}</span><span className="activity-label">Restaurants ({data.stats.activeRestSubscriptions || 0} active)</span></div>
                  <div className="activity-item"><span className="activity-value">{data.stats.totalBusinesses || 0}</span><span className="activity-label">Vendors ({data.stats.activeSubscriptions || 0} active)</span></div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <div className="admin-section-header">
              <h3>👤 User Management ({data.users.length})</h3>
              <div className="admin-search-inline">
                <input type="text" className="admin-search-input" placeholder="Search users by name or email..." value={userSearchTerm} onChange={(e) => setUserSearchTerm(e.target.value)} />
              </div>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Verified</th><th>Created</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr><td colSpan="6" className="empty-row">{userSearchTerm ? 'No matching users found' : 'No users yet'}</td></tr>
                  ) : filteredUsers.map(user => (
                    <tr key={user._id}>
                      <td><strong>{user.firstName} {user.lastName}</strong></td>
                      <td className="email-cell">{user.email}</td>
                      <td><Badge variant={user.role === 'admin' ? 'primary' : user.isOrganizer ? 'info' : 'default'}>{user.isOrganizer ? 'organizer' : user.role}</Badge></td>
                      <td>
                        <button className={`verify-toggle ${user.emailVerified ? 'verified' : 'unverified'}`} onClick={() => toggleEmailVerified(user)} title={user.emailVerified ? 'Click to unverify' : 'Click to verify'}>
                          {user.emailVerified ? '✓ Verified' : '✗ Unverified'}
                        </button>
                      </td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td className="actions-cell">
                        <button className="action-btn edit" onClick={() => openUserManager(user)} title="Manage User"><Icons.Settings /></button>
                        <button className="delete-btn" onClick={() => deleteUser(user._id)} title="Delete"><Icons.Trash /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'businesses' && (
          <div>
            <div className="admin-section-header">
              <h3>🏢 Business Management ({data.businesses.length})</h3>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead><tr><th>Business Name</th><th>Owner</th><th>Type</th><th>Cities</th><th>Plan / Status</th><th>Expires</th><th>Actions</th></tr></thead>
                <tbody>
                  {data.businesses.length === 0 ? (
                    <tr><td colSpan="7" className="empty-row">No businesses yet</td></tr>
                  ) : data.businesses.map(biz => {
                    const sub = biz.subscription;
                    const statusColor = getSubscriptionStatusColor(sub);
                    const isLifetimeOrAdmin = sub?.status === 'lifetime' || (sub?.promoGrantedBy === 'admin' && !sub?.promoExpiresAt && ['active', 'promo'].includes(sub?.status));
                    const expiryDate = isLifetimeOrAdmin ? null : (sub?.promoExpiresAt || sub?.trialEndsAt || sub?.currentPeriodEnd);
                    const isExpired = !isLifetimeOrAdmin && expiryDate && new Date(expiryDate) < new Date();
                    return (
                      <tr key={biz._id} className={isExpired ? 'expired-row' : ''}>
                        <td><strong>{biz.businessName}</strong></td>
                        <td className="email-cell">{biz.ownerId?.email || 'N/A'}</td>
                        <td><Badge variant="default">{biz.primaryVendorType?.replace(/_/g, ' ')}</Badge></td>
                        <td>{biz.operatingCities?.map(c => c.city).join(', ') || '—'}</td>
                        <td>
                          <Badge variant={statusColor === 'green' ? 'success' : statusColor === 'red' ? 'danger' : statusColor === 'purple' ? 'primary' : 'warning'}>
                            {sub?.plan?.toUpperCase() || 'TRIAL'}
                          </Badge>
                          {sub?.promoGrantedBy === 'admin' && <span className="promo-note" title={sub.promoNote || 'Admin granted'}>🎁</span>}
                        </td>
                        <td className={isExpired ? 'text-danger' : ''}>
                          {isLifetimeOrAdmin ? <span className="lifetime-badge">∞ Permanent</span> : (expiryDate ? formatDate(expiryDate) : 'N/A')}
                          {isExpired && ' (Expired)'}
                        </td>
                        <td className="actions-cell">
                          <button className="action-btn edit" onClick={() => openSubscriptionManager(biz)} title="Manage Subscription"><Icons.Settings /></button>
                          <button className="delete-btn" onClick={() => deleteBusiness(biz._id)} title="Delete"><Icons.Trash /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'organizers' && (
          <div>
            <Card className="create-form admin-form-enhanced">
              <div className="form-header">
                <h3>👤 Make User an Organizer</h3>
                <p className="form-hint">Enter the email of an existing user to grant them organizer access.</p>
              </div>
              <div className="form-section">
                <div className="form-row">
                  <Input label="User Email" placeholder="organizer@example.com" id="newOrganizerEmail" />
                  <Button onClick={() => createOrganizerAccount(document.getElementById('newOrganizerEmail').value)}>
                    <Icons.Plus /> Make Organizer
                  </Button>
                </div>
              </div>
            </Card>
            
            <div className="admin-section-header">
              <h3>🎪 Event Organizers ({data.organizers.length})</h3>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Events</th><th>Subscription</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {data.organizers.length === 0 ? (
                    <tr><td colSpan="7" className="empty-row">No organizers yet</td></tr>
                  ) : data.organizers.map(org => {
                    const status = org.organizerProfile?.disabled ? 'disabled' : 
                                   org.organizerProfile?.verified ? 'verified' : 
                                   org.organizerProfile?.verificationStatus === 'info_requested' ? 'info_requested' :
                                   org.organizerProfile?.verificationStatus === 'rejected' ? 'rejected' : 'pending';
                    const sub = org.subscription;
                    const subColor = getSubscriptionStatusColor(sub);
                    const isLifetimeOrAdmin = sub?.status === 'lifetime' || (sub?.promoGrantedBy === 'admin' && !sub?.promoExpiresAt && ['active', 'promo'].includes(sub?.status));
                    const subExpiry = isLifetimeOrAdmin ? null : (sub?.promoExpiresAt || sub?.trialEndsAt || sub?.currentPeriodEnd);
                    const subExpired = !isLifetimeOrAdmin && subExpiry && new Date(subExpiry) < new Date();
                    return (
                      <tr key={org._id} className={status === 'pending' ? 'highlight-row' : ''}>
                        <td><strong>{org.firstName} {org.lastName}</strong></td>
                        <td className="email-cell">{org.email}</td>
                        <td>{org.organizerProfile?.companyName || <em className="text-muted">Not set</em>}</td>
                        <td>{org.eventCount || 0}</td>
                        <td>
                          <Badge variant={subColor === 'green' ? 'success' : subColor === 'purple' ? 'primary' : subColor === 'yellow' ? 'warning' : 'danger'}>
                            {sub?.plan?.toUpperCase() || 'TRIAL'}
                          </Badge>
                          {sub?.promoGrantedBy === 'admin' && <span className="promo-note" title={sub.promoNote || 'Admin granted'}>🎁</span>}
                          {subExpired && <span style={{ color: 'var(--danger)', fontSize: '0.75rem', marginLeft: 4 }}>Expired</span>}
                          {isLifetimeOrAdmin && <span style={{ color: 'var(--primary)', fontSize: '0.75rem', marginLeft: 4 }}>∞</span>}
                        </td>
                        <td>
                          <Badge variant={
                            status === 'disabled' ? 'danger' : 
                            status === 'verified' ? 'success' : 
                            status === 'rejected' ? 'danger' :
                            status === 'info_requested' ? 'info' : 'warning'
                          }>
                            {status === 'disabled' ? '🚫 Disabled' : 
                             status === 'verified' ? '✓ Verified' : 
                             status === 'rejected' ? '✗ Rejected' :
                             status === 'info_requested' ? '❓ Info Requested' : '⏳ Pending'}
                          </Badge>
                        </td>
                        <td className="admin-note-cell">
                          {org.organizerProfile?.adminNote && (
                            <span className="admin-note-preview" title={org.organizerProfile.adminNote}>
                              {org.organizerProfile.adminNote.substring(0, 30)}...
                            </span>
                          )}
                        </td>
                        <td className="actions-cell organizer-actions">
                          <button className="action-btn edit" onClick={() => openOrgSubManager(org)} title="Manage Subscription"><Icons.Settings /></button>
                          {status === 'disabled' ? (
                            <Button size="sm" variant="success" onClick={() => toggleOrganizerStatus(org._id, false)}>
                              Enable
                            </Button>
                          ) : status === 'verified' ? (
                            <Button size="sm" variant="danger" onClick={() => setOrganizerModal({ show: true, organizer: org, action: 'disable', reason: '' })}>
                              Disable
                            </Button>
                          ) : (
                            <div className="action-btn-group">
                              <Button size="sm" variant="success" onClick={() => setOrganizerModal({ show: true, organizer: org, action: 'approve', reason: '' })}>
                                ✓ Approve
                              </Button>
                              <Button size="sm" variant="warning" onClick={() => setOrganizerModal({ show: true, organizer: org, action: 'request-info', reason: '' })}>
                                ❓ Request Info
                              </Button>
                              <Button size="sm" variant="danger" onClick={() => setOrganizerModal({ show: true, organizer: org, action: 'reject', reason: '' })}>
                                ✗ Reject
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Organizer Action Modal */}
            <Modal isOpen={organizerModal.show} onClose={() => setOrganizerModal({ show: false, organizer: null, action: null, reason: '' })} 
                   title={organizerModal.action === 'approve' ? 'Approve Organizer' : 
                          organizerModal.action === 'reject' ? 'Reject Organizer' : 
                          organizerModal.action === 'request-info' ? 'Request More Information' : 
                          organizerModal.action === 'disable' ? 'Disable Organizer' : 'Organizer Action'}
                   size="lg">
              {organizerModal.organizer && (
                <div className="organizer-action-modal">
                  <div className="organizer-info-preview">
                    <p><strong>Name:</strong> {organizerModal.organizer.firstName} {organizerModal.organizer.lastName}</p>
                    <p><strong>Email:</strong> {organizerModal.organizer.email}</p>
                    <p><strong>Company:</strong> {organizerModal.organizer.organizerProfile?.companyName || 'Not provided'}</p>
                    <p><strong>Website:</strong> {organizerModal.organizer.organizerProfile?.website || 'Not provided'}</p>
                    <p><strong>Phone:</strong> {organizerModal.organizer.organizerProfile?.phone || 'Not provided'}</p>
                  </div>
                  
                  {/* Show organizer documents for review */}
                  {organizerModal.organizer.organizerProfile?.documents?.length > 0 && (
                    <div className="organizer-documents-section">
                      <h4>📄 Verification Documents ({organizerModal.organizer.organizerProfile.documents.length})</h4>
                      <div className="documents-review-list">
                        {organizerModal.organizer.organizerProfile.documents.map((doc, idx) => (
                          <div key={idx} className="document-review-item">
                            <div className="doc-info">
                              <span className="doc-name">{doc.name}</span>
                              <Badge variant={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'}>
                                {doc.status || 'pending'}
                              </Badge>
                              <span className="doc-type">{doc.category?.replace(/_/g, ' ')}</span>
                            </div>
                            <a href={getSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer" className="doc-link">
                              <Icons.Download /> View
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {(!organizerModal.organizer.organizerProfile?.documents || organizerModal.organizer.organizerProfile.documents.length === 0) && (
                    <Alert type="warning" style={{ margin: '16px 0' }}>
                      <strong>No documents uploaded</strong>
                      <p style={{ margin: '4px 0 0' }}>This organizer hasn't uploaded any verification documents yet.</p>
                    </Alert>
                  )}
                  
                  {organizerModal.action === 'approve' ? (
                    <div className="action-confirm">
                      <p className="action-description success-text">
                        <Icons.Check /> This will verify the organizer and allow them to publish events. They will receive an email notification.
                      </p>
                    </div>
                  ) : (
                    <div className="reason-input">
                      <label className="form-label">
                        {organizerModal.action === 'reject' ? 'Rejection Reason *' : 
                         organizerModal.action === 'request-info' ? 'What information is needed? *' : 
                         'Reason (optional)'}
                      </label>
                      <textarea 
                        className="form-input form-textarea"
                        value={organizerModal.reason}
                        onChange={(e) => setOrganizerModal(m => ({ ...m, reason: e.target.value }))}
                        placeholder={
                          organizerModal.action === 'reject' ? 'Please explain why this application is being rejected...' :
                          organizerModal.action === 'request-info' ? 'Please specify what additional information is needed (e.g., proof of business registration, event history)...' :
                          'Optional: provide a reason for disabling this account...'
                        }
                        rows={4}
                      />
                      {(organizerModal.action === 'reject' || organizerModal.action === 'request-info') && (
                        <p className="form-hint">The organizer will receive an email with this message.</p>
                      )}
                    </div>
                  )}
                  
                  <div className="modal-actions">
                    <Button variant="outline" onClick={() => setOrganizerModal({ show: false, organizer: null, action: null, reason: '' })}>
                      Cancel
                    </Button>
                    <Button 
                      variant={organizerModal.action === 'approve' ? 'success' : organizerModal.action === 'request-info' ? 'warning' : 'danger'}
                      onClick={handleOrganizerAction}
                    >
                      {organizerModal.action === 'approve' ? 'Approve & Verify' : 
                       organizerModal.action === 'reject' ? 'Reject Application' : 
                       organizerModal.action === 'request-info' ? 'Send Request' : 
                       'Disable Organizer'}
                    </Button>
                  </div>
                </div>
              )}
            </Modal>
          </div>
        )}
        
        {/* Verifications Tab - Document verification for organizers and events */}
        {activeTab === 'verifications' && (
          <div>
            <div className="admin-section-header">
              <h3>📋 Document Verifications</h3>
              <p className="section-description">Review and approve documents uploaded by organizers and for events</p>
            </div>
            
            {/* Organizer Documents Verification */}
            <Card className="verification-section">
              <h4>👤 Organizer Account Documents</h4>
              <p className="form-hint">Documents uploaded by organizers to verify their accounts</p>
              
              {data.verifications?.organizers?.length > 0 ? (
                <div className="verification-list">
                  {data.verifications.organizers.map((org, orgIdx) => (
                    <div key={org._id} className="verification-item">
                      <div className="verification-header">
                        <div className="verification-info">
                          <strong>{org.organizerProfile?.companyName || `${org.firstName} ${org.lastName}`}</strong>
                          <span className="verification-meta">{org.email}</span>
                        </div>
                        <Badge variant={org.organizerProfile?.verified ? 'success' : 'warning'}>
                          {org.organizerProfile?.verified ? 'Verified' : 'Pending Verification'}
                        </Badge>
                      </div>
                      
                      <div className="documents-to-review">
                        {(org.organizerProfile?.documents || []).map((doc, docIdx) => (
                          <div key={docIdx} className={`doc-review-row ${doc.status === 'approved' ? 'doc-approved' : doc.status === 'rejected' ? 'doc-rejected' : ''}`}>
                            <div className="doc-review-info">
                              <Icons.Document />
                              <span className="doc-name">{doc.name}</span>
                              <Badge>{doc.category?.replace(/_/g, ' ')}</Badge>
                              <Badge variant={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'}>
                                {doc.status}
                              </Badge>
                            </div>
                            <div className="doc-review-actions">
                              <a href={getAdminSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline">
                                <Icons.Download /> View
                              </a>
                              {doc.status === 'pending' && (
                                <>
                                  <Button size="sm" variant="success" onClick={() => reviewOrganizerDocument(org._id, docIdx, 'approved')}>
                                    ✓ Approve
                                  </Button>
                                  <Button size="sm" variant="danger" onClick={() => {
                                    const reason = prompt('Rejection reason:');
                                    if (reason) reviewOrganizerDocument(org._id, docIdx, 'rejected', reason);
                                  }}>
                                    ✗ Reject
                                  </Button>
                                </>
                              )}
                            </div>
                            {doc.reviewNotes && (
                              <div className="doc-review-notes">
                                <small><strong>Review Notes:</strong> {doc.reviewNotes}</small>
                              </div>
                            )}
                          </div>
                        ))}
                        {(org.organizerProfile?.documents || []).length === 0 && (
                          <p className="empty-text" style={{ margin: '8px 0' }}>No documents uploaded</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-text">No organizers with documents to review</p>
              )}
            </Card>
            
            {/* Event Proof Documents Verification */}
            <Card className="verification-section" style={{ marginTop: '24px' }}>
              <h4>🎪 Event Proof Documents</h4>
              <p className="form-hint">Documents uploaded by organizers to prove venue/event ownership</p>
              
              {data.verifications?.events?.length > 0 ? (
                <div className="verification-list">
                  {data.verifications.events.map((event, eventIdx) => (
                    <div key={event._id} className="verification-item">
                      <div className="verification-header">
                        <div className="verification-info">
                          <strong>{event.eventName}</strong>
                          <span className="verification-meta">
                            {event.organizerName} • {event.location?.city}, {event.location?.state} • {formatDate(event.startDate)}
                          </span>
                        </div>
                        <Badge variant={event.verificationStatus === 'approved' ? 'success' : event.verificationStatus === 'rejected' ? 'danger' : 'warning'}>
                          {event.verificationStatus || 'pending'}
                        </Badge>
                        {event.status === 'pending_review' && <Badge variant="warning">Event Pending Review</Badge>}
                      </div>
                      
                      {/* Document History */}
                      <div className="documents-to-review">
                        {(event.proofDocuments || []).map((doc, docIdx) => (
                          <div key={docIdx} className={`doc-review-row ${doc.status === 'approved' ? 'doc-approved' : doc.status === 'rejected' ? 'doc-rejected' : ''}`}>
                            <div className="doc-review-info">
                              <Icons.Document />
                              <span className="doc-name">{doc.name}</span>
                              <Badge>{doc.category?.replace(/_/g, ' ')}</Badge>
                              <Badge variant={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'}>
                                {doc.status}
                              </Badge>
                            </div>
                            <div className="doc-review-actions">
                              <a href={getAdminSecureFileUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline">
                                <Icons.Download /> View
                              </a>
                              {doc.status === 'pending' && (
                                <>
                                  <Button size="sm" variant="success" onClick={() => reviewEventDocument(event._id, docIdx, 'approved')}>
                                    ✓ Approve
                                  </Button>
                                  <Button size="sm" variant="danger" onClick={() => {
                                    const reason = prompt('Rejection reason:');
                                    if (reason) reviewEventDocument(event._id, docIdx, 'rejected', reason);
                                  }}>
                                    ✗ Reject
                                  </Button>
                                </>
                              )}
                            </div>
                            {doc.reviewNotes && (
                              <div className="doc-review-notes">
                                <small><strong>Review Notes:</strong> {doc.reviewNotes}</small>
                              </div>
                            )}
                          </div>
                        ))}
                        {(event.proofDocuments || []).length === 0 && (
                          <p className="empty-text" style={{ margin: '8px 0' }}>No documents uploaded</p>
                        )}
                      </div>
                      
                      {/* Event-level verification notes */}
                      {event.verificationNotes && (
                        <div className="event-verification-notes">
                          <small><strong>Event Notes:</strong> {event.verificationNotes}</small>
                        </div>
                      )}
                      
                      {/* Quick actions for entire event */}
                      {event.verificationStatus !== 'approved' && (
                        <div className="event-verification-actions">
                          <Button size="sm" variant="success" onClick={() => updateEventVerification(event._id, 'approved')}>
                            Approve All & Verify Event
                          </Button>
                          <Button size="sm" variant="warning" onClick={() => {
                            const note = prompt('What information is needed?');
                            if (note) updateEventVerification(event._id, 'info_needed', note);
                          }}>
                            Request More Info
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => {
                            const reason = prompt('Rejection reason:');
                            if (reason) updateEventVerification(event._id, 'rejected', reason);
                          }}>
                            Reject Event
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-text">No event documents to review</p>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'restaurants' && (
          <div>
            <div className="admin-section-header">
              <h3>🍽️ Restaurant Management ({data.restaurants.length})</h3>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead><tr><th>Restaurant</th><th>Owner</th><th>Type</th><th>Cities</th><th>Permits</th><th>Plan / Status</th><th>Expires</th><th>Actions</th></tr></thead>
                <tbody>
                  {data.restaurants.length === 0 ? (
                    <tr><td colSpan="8" className="empty-row">No restaurants yet</td></tr>
                  ) : data.restaurants.map(rest => {
                    const sub = rest.subscription;
                    const statusColor = getSubscriptionStatusColor(sub);
                    const isLifetimeOrAdmin = sub?.status === 'lifetime' || (sub?.promoGrantedBy === 'admin' && !sub?.promoExpiresAt && ['active', 'promo'].includes(sub?.status));
                    const expiryDate = isLifetimeOrAdmin ? null : (sub?.promoExpiresAt || sub?.trialEndsAt || sub?.currentPeriodEnd);
                    const isExpired = !isLifetimeOrAdmin && expiryDate && new Date(expiryDate) < new Date();
                    return (
                      <tr key={rest._id} className={isExpired ? 'expired-row' : ''}>
                        <td><strong>{rest.businessName}</strong>{rest.dbaName && <span className="text-muted" style={{ display: 'block', fontSize: '0.75rem' }}>DBA: {rest.dbaName}</span>}</td>
                        <td className="email-cell">{rest.ownerId?.email || 'N/A'}</td>
                        <td><Badge variant="default">{rest.primaryRestaurantType?.replace(/_/g, ' ')}</Badge>{rest.servesAlcohol && <span title="Serves alcohol" style={{ marginLeft: 4 }}>🍷</span>}</td>
                        <td>{rest.operatingCities?.map(c => c.city).join(', ') || '—'}</td>
                        <td>{rest.permitCount || 0}</td>
                        <td>
                          <Badge variant={statusColor === 'green' ? 'success' : statusColor === 'red' ? 'danger' : statusColor === 'purple' ? 'primary' : 'warning'}>
                            {sub?.plan?.toUpperCase() || 'TRIAL'}
                          </Badge>
                          {sub?.promoGrantedBy === 'admin' && <span className="promo-note" title={sub.promoNote || 'Admin granted'}>🎁</span>}
                        </td>
                        <td className={isExpired ? 'text-danger' : ''}>
                          {isLifetimeOrAdmin ? <span className="lifetime-badge">∞ Permanent</span> : (expiryDate ? formatDate(expiryDate) : 'N/A')}
                          {isExpired && ' (Expired)'}
                        </td>
                        <td className="actions-cell">
                          <button className="action-btn edit" onClick={() => { setManagingRestSub(rest); setRestSubAction(''); setRestSubForm({ days: 30, plan: 'promo', durationDays: 90, note: '' }); }} title="Manage Subscription"><Icons.Settings /></button>
                          <button className="delete-btn" onClick={() => deleteRestaurant(rest._id)} title="Delete"><Icons.Trash /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Restaurant Subscription Management Modal */}
        <Modal isOpen={!!managingRestSub} onClose={() => { setManagingRestSub(null); setRestSubAction(''); }} title="Manage Restaurant Subscription" size="lg">
          {managingRestSub && (
            <div className="subscription-manager">
              <div className="sub-business-header">
                <h3>{managingRestSub.businessName}</h3>
                <p className="text-muted">{managingRestSub.ownerId?.email} • {managingRestSub.primaryRestaurantType?.replace(/_/g, ' ')}</p>
              </div>
              <div className="sub-current-status">
                <h4>Current Subscription</h4>
                <div className="sub-info-grid">
                  <div className="sub-info-item"><span className="sub-label">Plan</span> <Badge variant={getSubscriptionStatusColor(managingRestSub.subscription) === 'green' ? 'success' : 'warning'}>{managingRestSub.subscription?.plan?.toUpperCase() || 'TRIAL'}</Badge></div>
                  <div className="sub-info-item"><span className="sub-label">Status</span> <span className="sub-value">{managingRestSub.subscription?.status || 'trial'}</span></div>
                  <div className="sub-info-item"><span className="sub-label">Permits</span> <span className="sub-value">{managingRestSub.permitCount || 0}</span></div>
                  <div className="sub-info-item"><span className="sub-label">Expires</span> <span className="sub-value">{managingRestSub.subscription?.status === 'lifetime' ? '∞ Never' : formatDate(managingRestSub.subscription?.promoExpiresAt || managingRestSub.subscription?.trialEndsAt || managingRestSub.subscription?.currentPeriodEnd)}</span></div>
                  {managingRestSub.subscription?.promoNote && <div className="sub-info-item full-width"><span className="sub-label">Admin Note</span> <span className="sub-value">{managingRestSub.subscription.promoNote}</span></div>}
                </div>
              </div>
              <div className="sub-actions">
                <h4>Quick Actions</h4>
                <div className="sub-action-buttons">
                  <Button variant={restSubAction === 'extend' ? 'primary' : 'outline'} onClick={() => setRestSubAction('extend')}><Icons.Clock /> Extend Trial</Button>
                  <Button variant={restSubAction === 'grant' ? 'primary' : 'outline'} onClick={() => setRestSubAction('grant')}><Icons.Check /> Grant Plan</Button>
                  <Button variant={restSubAction === 'revoke' ? 'danger' : 'outline'} onClick={() => setRestSubAction('revoke')}><Icons.X /> Revoke Access</Button>
                </div>
              </div>
              {restSubAction === 'extend' && (
                <div className="sub-action-form">
                  <h4>🕐 Extend Restaurant Trial</h4>
                  <Input label="Days to Add" type="number" min="1" max="365" value={restSubForm.days} onChange={(e) => setRestSubForm(f => ({ ...f, days: parseInt(e.target.value) || 30 }))} />
                  <Input label="Note (optional)" value={restSubForm.note} onChange={(e) => setRestSubForm(f => ({ ...f, note: e.target.value }))} />
                  <div className="modal-actions"><Button variant="outline" onClick={() => setRestSubAction('')}>Cancel</Button><Button onClick={extendRestTrial}>Extend by {restSubForm.days} Days</Button></div>
                </div>
              )}
              {restSubAction === 'grant' && (
                <div className="sub-action-form">
                  <h4>🎁 Grant Restaurant Subscription</h4>
                  <Select label="Plan Type" value={restSubForm.plan} onChange={(e) => setRestSubForm(f => ({ ...f, plan: e.target.value }))} options={[
                    { value: 'promo', label: '🎉 Promo (Time-Limited)' },
                    { value: 'basic', label: '📋 Basic Plan (Permanent)' },
                    { value: 'pro', label: '⭐ Pro Plan (Permanent)' },
                    { value: 'elite', label: '👑 Elite Plan (Permanent)' },
                    { value: 'lifetime', label: '♾️ Lifetime (Permanent)' }
                  ]} />
                  {restSubForm.plan === 'promo' && <Input label="Duration (days)" type="number" min="1" max="3650" value={restSubForm.durationDays} onChange={(e) => setRestSubForm(f => ({ ...f, durationDays: parseInt(e.target.value) || 90 }))} />}
                  <Input label="Note (required)" value={restSubForm.note} onChange={(e) => setRestSubForm(f => ({ ...f, note: e.target.value }))} />
                  <div className="modal-actions"><Button variant="outline" onClick={() => setRestSubAction('')}>Cancel</Button><Button onClick={grantRestPromo} disabled={!restSubForm.note}>Grant Access</Button></div>
                </div>
              )}
              {restSubAction === 'revoke' && (
                <div className="sub-action-form">
                  <h4>⚠️ Revoke Restaurant Subscription</h4>
                  <p className="form-hint" style={{ color: 'var(--danger)' }}>This removes paid access and resets to expired trial.</p>
                  <Input label="Reason (required)" value={restSubForm.note} onChange={(e) => setRestSubForm(f => ({ ...f, note: e.target.value }))} />
                  <div className="modal-actions"><Button variant="outline" onClick={() => setRestSubAction('')}>Cancel</Button><Button variant="danger" onClick={revokeRestSubscription} disabled={!restSubForm.note}>Revoke Access</Button></div>
                </div>
              )}
            </div>
          )}
        </Modal>

        {activeTab === 'jurisdictions' && (
          <div>
            <Card className="create-form admin-form-enhanced">
              <div className="form-header">
                <h3>➕ Add New Jurisdiction (City/County)</h3>
                <p className="form-hint">A jurisdiction represents a city or county that issues permits.</p>
              </div>
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group">
                    <Select label="Type *" value={newJurisdiction.type} onChange={(e) => setNewJurisdiction(j => ({ ...j, type: e.target.value }))} options={[{ value: 'city', label: 'City' }, { value: 'county', label: 'County' }, { value: 'state', label: 'State' }]} />
                  </div>
                  <div className="form-group">
                    <Input label="City *" placeholder="Austin" value={newJurisdiction.city} onChange={(e) => {
                      const city = e.target.value;
                      setNewJurisdiction(j => ({ ...j, city, name: city ? `City of ${city}` : '' }));
                    }} />
                  </div>
                  <div className="form-group">
                    <Select label="State *" value={newJurisdiction.state} onChange={(e) => setNewJurisdiction(j => ({ ...j, state: e.target.value }))} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                  </div>
                </div>
                <Input label="Display Name" placeholder="City of Austin (auto-generated)" value={newJurisdiction.name} onChange={(e) => setNewJurisdiction(j => ({ ...j, name: e.target.value }))} />
                <Input label="County (optional)" placeholder="Travis County" value={newJurisdiction.county || ''} onChange={(e) => setNewJurisdiction(j => ({ ...j, county: e.target.value }))} />
              </div>
              <div className="form-actions">
                <Button onClick={createJurisdiction} disabled={!newJurisdiction.city || !newJurisdiction.state}>
                  <Icons.Plus /> Add Jurisdiction
                </Button>
                {newJurisdiction.city && newJurisdiction.state && (
                  <span className="form-preview">Will create: <strong>{newJurisdiction.name || `City of ${newJurisdiction.city}`}</strong> ({newJurisdiction.city}, {newJurisdiction.state})</span>
                )}
              </div>
            </Card>
            
            <div className="admin-section-header">
              <h3>📍 Existing Jurisdictions ({data.jurisdictions.length})</h3>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>City</th><th>State</th><th>Permit Types</th><th>Actions</th></tr></thead>
                <tbody>
                  {data.jurisdictions.length === 0 ? (
                    <tr><td colSpan="5" className="empty-row">No jurisdictions yet. Add your first one above!</td></tr>
                  ) : data.jurisdictions.map(j => (
                    <tr key={j._id}>
                      <td><strong>{j.name}</strong></td>
                      <td>{j.city}</td>
                      <td>{j.state}</td>
                      <td><Badge variant={j.permitTypesCount > 0 ? 'success' : 'default'}>{j.permitTypesCount || 0} permit{j.permitTypesCount !== 1 ? 's' : ''}</Badge></td>
                      <td><button className="delete-btn" onClick={() => deleteJurisdiction(j._id)} title="Delete"><Icons.Trash /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'permitTypes' && (
          <div>
            <Card className="create-form admin-form-enhanced">
              <div className="form-header">
                <h3>📋 Add New Permit Type</h3>
                <p className="form-hint">Define a permit that vendors need in a specific jurisdiction.</p>
              </div>
              
              {/* Basic Info Section */}
              <div className="form-section">
                <div className="form-section-title">Basic Information</div>
                <div className="form-row">
                  <div className="form-group flex-2">
                    <Input label="Permit Name *" placeholder="Mobile Food Vendor Permit" value={newPermitType.name} onChange={(e) => setNewPermitType(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <Select label="Jurisdiction *" value={newPermitType.jurisdictionId} onChange={(e) => setNewPermitType(p => ({ ...p, jurisdictionId: e.target.value }))} options={[{ value: '', label: 'Select Jurisdiction' }, ...data.jurisdictions.map(j => ({ value: j._id, label: `${j.city}, ${j.state}` }))]} />
                  </div>
                </div>
                <Input label="Description" placeholder="Required permit for operating a mobile food establishment..." value={newPermitType.description} onChange={(e) => setNewPermitType(p => ({ ...p, description: e.target.value }))} />
              </div>
              
              {/* Classification Section */}
              <div className="form-section">
                <div className="form-section-title">Classification & Priority</div>
                <div className="form-row">
                  <div className="form-group">
                    <Select label="Importance Level *" value={newPermitType.importanceLevel} onChange={(e) => setNewPermitType(p => ({ ...p, importanceLevel: e.target.value }))} options={[
                      { value: 'critical', label: '🔴 Critical - Required to operate' },
                      { value: 'often_forgotten', label: '🟡 Often Forgotten - Easy to miss' },
                      { value: 'event_required', label: '🔵 Event Required - For special events' }
                    ]} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Vendor Types (who needs this?)</label>
                    <div className="vendor-type-checkboxes">
                      {VENDOR_TYPES.map(vt => (
                        <label key={vt.value} className="vendor-checkbox">
                          <input 
                            type="checkbox" 
                            checked={newPermitType.vendorTypes?.includes(vt.value)} 
                            onChange={(e) => {
                              const types = newPermitType.vendorTypes || [];
                              if (e.target.checked) {
                                setNewPermitType(p => ({ ...p, vendorTypes: [...types, vt.value] }));
                              } else {
                                setNewPermitType(p => ({ ...p, vendorTypes: types.filter(t => t !== vt.value) }));
                              }
                            }}
                          />
                          <span>{vt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <label className="checkbox-row" style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                  <input 
                    type="checkbox" 
                    checked={newPermitType.requiresFoodHandling || false}
                    onChange={(e) => setNewPermitType(p => ({ ...p, requiresFoodHandling: e.target.checked }))}
                  />
                  <span>Required for any business that handles food</span>
                </label>
                <p className="form-hint" style={{ marginTop: '0' }}>When checked, this permit will be suggested to ALL vendors who have "handles food" enabled, regardless of their vendor type. Use for food handler certificates, health permits, etc.</p>
                <div className="importance-hints">
                  <div className="hint critical"><strong>Critical:</strong> Health permits, business licenses, food handler certs</div>
                  <div className="hint warning"><strong>Often Forgotten:</strong> Fire inspections, commissary agreements, vehicle permits</div>
                  <div className="hint info"><strong>Event Required:</strong> Special event permits, temporary vendor licenses</div>
                </div>
              </div>
              
              {/* Authority & Cost Section */}
              <div className="form-section">
                <div className="form-section-title">Issuing Authority & Costs</div>
                <div className="form-row">
                  <Input label="Issuing Authority" placeholder="Austin Public Health" value={newPermitType.issuingAuthorityName} onChange={(e) => setNewPermitType(p => ({ ...p, issuingAuthorityName: e.target.value }))} />
                  <Input label="Estimated Cost Range" placeholder="$300–$500" value={newPermitType.estimatedCost} onChange={(e) => setNewPermitType(p => ({ ...p, estimatedCost: e.target.value }))} />
                </div>
                <div className="form-row">
                  <Input label="Renewal Period (months)" type="number" min="1" value={newPermitType.renewalPeriodMonths} onChange={(e) => setNewPermitType(p => ({ ...p, renewalPeriodMonths: parseInt(e.target.value) || 12 }))} />
                  <Input label="Application Fee ($)" type="number" min="0" value={newPermitType.fees.application} onChange={(e) => setNewPermitType(p => ({ ...p, fees: { ...p.fees, application: parseFloat(e.target.value) || 0 } }))} />
                  <Input label="Renewal Fee ($)" type="number" min="0" value={newPermitType.fees.renewal} onChange={(e) => setNewPermitType(p => ({ ...p, fees: { ...p.fees, renewal: parseFloat(e.target.value) || 0 } }))} />
                </div>
              </div>
              
              {/* Documents Section */}
              <div className="form-section">
                <div className="form-section-title">Required Documents</div>
                <Input label="Documents (comma-separated)" placeholder="Food Handler Certificate, Proof of Insurance, Commissary Agreement, Vehicle Registration" value={newPermitType.requiredDocuments} onChange={(e) => setNewPermitType(p => ({ ...p, requiredDocuments: e.target.value }))} />
                <p className="form-hint">Common documents: Food Handler Certificate, Proof of Insurance, Commissary Agreement, Business License, Vehicle Registration, Fire Extinguisher Certificate</p>
              </div>
              
              {/* Preview & Submit */}
              <div className="form-actions">
                <Button onClick={createPermitType} disabled={!newPermitType.name || !newPermitType.jurisdictionId}>
                  <Icons.Plus /> Create Permit Type
                </Button>
                {newPermitType.name && newPermitType.jurisdictionId && (
                  <div className="permit-preview">
                    <strong>Preview:</strong> {newPermitType.name} • {data.jurisdictions.find(j => j._id === newPermitType.jurisdictionId)?.city} • 
                    <Badge variant={newPermitType.importanceLevel === 'critical' ? 'danger' : newPermitType.importanceLevel === 'often_forgotten' ? 'warning' : 'info'}>
                      {newPermitType.importanceLevel === 'critical' ? 'Critical' : newPermitType.importanceLevel === 'often_forgotten' ? 'Often Forgotten' : 'Event Required'}
                    </Badge> • 
                    Renews every {newPermitType.renewalPeriodMonths} months
                  </div>
                )}
              </div>
            </Card>
            
            {/* Search Bar */}
            <div className="admin-section-header">
              <h3>📋 Existing Permit Types ({data.permitTypes.length})</h3>
              <div className="admin-search inline">
                <Input placeholder="Search by name, city..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                <span className="search-results">{filteredPermitTypes.length} result{filteredPermitTypes.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
            
            <div className="admin-table-container">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Jurisdiction</th><th>Importance</th><th>Renewal</th><th>App Fee</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredPermitTypes.length === 0 ? (
                    <tr><td colSpan="6" className="empty-row">{searchTerm ? 'No permits match your search.' : 'No permit types yet. Add your first one above!'}</td></tr>
                  ) : filteredPermitTypes.map(pt => {
                    const label = getImportanceLabel(pt.importanceLevel);
                    return (
                      <tr key={pt._id}>
                        <td><strong>{pt.name}</strong>{pt.description && <span className="table-subtitle">{pt.description.substring(0, 50)}...</span>}</td>
                        <td>{pt.jurisdictionId?.city}, {pt.jurisdictionId?.state}</td>
                        <td><Badge variant={label.variant}>{label.text}</Badge></td>
                        <td>{pt.renewalPeriodMonths} mo</td>
                        <td>${pt.fees?.application || 0}</td>
                        <td className="actions-cell">
                          <button className="action-btn edit" onClick={() => setEditingPermitType({...pt, requiredDocuments: pt.requiredDocuments?.join(', ') || ''})} title="Edit"><Icons.Edit /></button>
                          <button className="action-btn duplicate" onClick={() => setDuplicateTarget(pt._id)} title="Duplicate to another city"><Icons.Plus /></button>
                          <button className="delete-btn" onClick={() => deletePermitType(pt._id)} title="Delete"><Icons.Trash /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'checklists' && (
          <div>
            <Card className="create-form admin-form-enhanced">
              <div className="form-header">
                <h3>➕ Create Inspection Checklist</h3>
                <p className="form-hint">Create checklists for vendors to self-inspect before official inspections.</p>
              </div>
              <div className="form-section">
                <Input label="Checklist Name *" placeholder="Health Inspection Pre-Check" value={newChecklist.name} onChange={(e) => setNewChecklist(c => ({ ...c, name: e.target.value }))} />
                <Input label="Description" placeholder="Standard health department inspection items" value={newChecklist.description} onChange={(e) => setNewChecklist(c => ({ ...c, description: e.target.value }))} />
                <div className="form-row">
                  <Select label="Category" value={newChecklist.category} onChange={(e) => setNewChecklist(c => ({ ...c, category: e.target.value }))} options={[
                    { value: 'health', label: '🏥 Health' },
                    { value: 'fire', label: '🔥 Fire Safety' },
                    { value: 'safety', label: '⚠️ General Safety' },
                    { value: 'general', label: '📋 General' }
                  ]} />
                  <Select label="Jurisdiction (optional)" value={newChecklist.jurisdictionId} onChange={(e) => setNewChecklist(c => ({ ...c, jurisdictionId: e.target.value }))} options={[{ value: '', label: 'All Jurisdictions' }, ...data.jurisdictions.map(j => ({ value: j._id, label: `${j.city}, ${j.state}` }))]} />
                  <Select label="For Organization (optional)" value={newChecklist.forOrganization || ''} onChange={(e) => setNewChecklist(c => ({ ...c, forOrganization: e.target.value }))} options={[{ value: '', label: 'All Vendors (Public)' }, ...data.businesses.map(b => ({ value: b._id, label: b.businessName }))]} />
                </div>
                <div className="form-section-title">Applicable Vendor Types</div>
                <p className="form-hint">Select which vendor types this checklist applies to. Leave all unchecked for "All Vendor Types".</p>
                <div className="vendor-type-checkboxes">
                  {VENDOR_TYPES.map(vt => (
                    <label key={vt.value} className="checkbox-inline">
                      <input 
                        type="checkbox" 
                        checked={newChecklist.vendorTypes?.includes(vt.value) || false} 
                        onChange={(e) => {
                          const types = newChecklist.vendorTypes || [];
                          if (e.target.checked) {
                            setNewChecklist(c => ({ ...c, vendorTypes: [...types, vt.value] }));
                          } else {
                            setNewChecklist(c => ({ ...c, vendorTypes: types.filter(t => t !== vt.value) }));
                          }
                        }}
                      />
                      <span>{vt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-section">
                <div className="form-section-title">Checklist Items</div>
                {newChecklist.items.map((item, i) => (
                  <div key={i} className="checklist-item-row">
                    <span className="item-number">{i + 1}.</span>
                    <Input placeholder="Item text (e.g., 'Food stored at proper temperature')" value={item.itemText} onChange={(e) => {
                      const items = [...newChecklist.items];
                      items[i] = { ...items[i], itemText: e.target.value };
                      setNewChecklist(c => ({ ...c, items }));
                    }} />
                    <button type="button" className="delete-btn" onClick={() => {
                      if (newChecklist.items.length > 1) {
                        setNewChecklist(c => ({ ...c, items: c.items.filter((_, idx) => idx !== i) }));
                      }
                    }}><Icons.Trash /></button>
                  </div>
                ))}
                <Button variant="outline" onClick={() => setNewChecklist(c => ({ ...c, items: [...c.items, { itemText: '', description: '', required: true }] }))}>
                  <Icons.Plus /> Add Item
                </Button>
              </div>
              <div className="form-actions">
                <Button onClick={createChecklist} disabled={!newChecklist.name || !newChecklist.items.some(i => i.itemText)}>
                  <Icons.Plus /> Create Checklist
                </Button>
              </div>
            </Card>
            
            <div className="admin-section-header">
              <h3>📋 Existing Checklists ({data.checklists.length})</h3>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Category</th><th>Jurisdiction</th><th>Vendor Types</th><th>Items</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {data.checklists.map(cl => (
                    <tr key={cl._id}>
                      <td>
                        <strong>{cl.name}</strong>
                        {cl.description && <><br /><small>{cl.description}</small></>}
                        {cl.forOrganization && <><br /><Badge variant="info">Organization-Specific</Badge></>}
                      </td>
                      <td><Badge variant={cl.category === 'health' ? 'danger' : cl.category === 'fire' ? 'warning' : 'default'}>{cl.category}</Badge></td>
                      <td>{cl.jurisdictionId ? `${cl.jurisdictionId.city}, ${cl.jurisdictionId.state}` : 'All'}</td>
                      <td>{cl.vendorTypes?.length > 0 ? cl.vendorTypes.map(vt => VENDOR_TYPES.find(v => v.value === vt)?.label || vt).join(', ') : (cl.vendorType || 'All')}</td>
                      <td>{cl.items?.length || 0}</td>
                      <td><Badge variant={cl.active ? 'success' : 'default'}>{cl.active ? 'Active' : 'Inactive'}</Badge></td>
                      <td className="actions-cell">
                        <button className="action-btn edit" onClick={() => setEditingChecklist({...cl, vendorTypes: cl.vendorTypes || (cl.vendorType ? [cl.vendorType] : [])})} title="Edit"><Icons.Edit /></button>
                        <button className="delete-btn" onClick={() => deleteChecklist(cl._id)} title="Delete"><Icons.Trash /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Edit Checklist Modal */}
            {editingChecklist && (
              <Modal isOpen={!!editingChecklist} onClose={() => setEditingChecklist(null)} title="Edit Checklist" size="lg">
                <Input label="Checklist Name *" value={editingChecklist.name} onChange={(e) => setEditingChecklist(c => ({ ...c, name: e.target.value }))} />
                <Input label="Description" value={editingChecklist.description || ''} onChange={(e) => setEditingChecklist(c => ({ ...c, description: e.target.value }))} />
                <div className="form-row">
                  <Select label="Category" value={editingChecklist.category} onChange={(e) => setEditingChecklist(c => ({ ...c, category: e.target.value }))} options={[
                    { value: 'health', label: '🏥 Health' },
                    { value: 'fire', label: '🔥 Fire Safety' },
                    { value: 'safety', label: '⚠️ General Safety' },
                    { value: 'general', label: '📋 General' }
                  ]} />
                  <Select label="Status" value={editingChecklist.active ? 'active' : 'inactive'} onChange={(e) => setEditingChecklist(c => ({ ...c, active: e.target.value === 'active' }))} options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' }
                  ]} />
                </div>
                <div className="form-section-title">Applicable Vendor Types</div>
                <div className="vendor-type-checkboxes">
                  {VENDOR_TYPES.map(vt => (
                    <label key={vt.value} className="checkbox-inline">
                      <input 
                        type="checkbox" 
                        checked={editingChecklist.vendorTypes?.includes(vt.value) || false} 
                        onChange={(e) => {
                          const types = editingChecklist.vendorTypes || [];
                          if (e.target.checked) {
                            setEditingChecklist(c => ({ ...c, vendorTypes: [...types, vt.value] }));
                          } else {
                            setEditingChecklist(c => ({ ...c, vendorTypes: types.filter(t => t !== vt.value) }));
                          }
                        }}
                      />
                      <span>{vt.label}</span>
                    </label>
                  ))}
                </div>
                <div className="form-section-title">Checklist Items</div>
                {editingChecklist.items?.map((item, i) => (
                  <div key={i} className="checklist-item-row" style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ minWidth: '30px' }}>{i + 1}.</span>
                    <Input placeholder="Item text" value={item.itemText || ''} onChange={(e) => {
                      const items = [...(editingChecklist.items || [])];
                      items[i] = { ...items[i], itemText: e.target.value };
                      setEditingChecklist(c => ({ ...c, items }));
                    }} />
                    <button type="button" className="delete-btn" onClick={() => {
                      if (editingChecklist.items?.length > 1) {
                        setEditingChecklist(c => ({ ...c, items: c.items.filter((_, idx) => idx !== i) }));
                      }
                    }}><Icons.Trash /></button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setEditingChecklist(c => ({ ...c, items: [...(c.items || []), { itemText: '', description: '', required: true }] }))}>
                  <Icons.Plus /> Add Item
                </Button>
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => setEditingChecklist(null)}>Cancel</Button>
                  <Button onClick={updateChecklist}>Save Changes</Button>
                </div>
              </Modal>
            )}
          </div>
        )}

        {activeTab === 'events' && (
          <div>
            <Card className="create-form admin-form-enhanced">
              <div className="form-header">
                <h3>➕ Create Event</h3>
                <p className="form-hint">Create events for organizers using PermitWise. Vendors will see their compliance status for assigned events.</p>
              </div>
              <div className="form-section">
                <Input label="Event Name *" placeholder="Downtown Food Festival" value={newEvent.eventName} onChange={(e) => setNewEvent(ev => ({ ...ev, eventName: e.target.value }))} />
                <Input label="Organizer Name" placeholder="City Events Department" value={newEvent.organizerName} onChange={(e) => setNewEvent(ev => ({ ...ev, organizerName: e.target.value }))} />
                <Input label="Description" placeholder="Annual food truck festival featuring local vendors" value={newEvent.description} onChange={(e) => setNewEvent(ev => ({ ...ev, description: e.target.value }))} />
              </div>
              <div className="form-section">
                <div className="form-section-title">Date & Location</div>
                <div className="form-row">
                  <Input label="Start Date *" type="date" value={newEvent.startDate} onChange={(e) => setNewEvent(ev => ({ ...ev, startDate: e.target.value }))} />
                  <Input label="End Date" type="date" value={newEvent.endDate} onChange={(e) => setNewEvent(ev => ({ ...ev, endDate: e.target.value }))} />
                </div>
                <div className="form-row">
                  <Input label="City *" placeholder="Austin" value={newEvent.city} onChange={(e) => setNewEvent(ev => ({ ...ev, city: e.target.value }))} />
                  <Select label="State *" value={newEvent.state} onChange={(e) => setNewEvent(ev => ({ ...ev, state: e.target.value }))} options={[{ value: '', label: 'Select State' }, ...US_STATES.map(s => ({ value: s, label: s }))]} />
                </div>
                <Input label="Address" placeholder="123 Main Street" value={newEvent.address} onChange={(e) => setNewEvent(ev => ({ ...ev, address: e.target.value }))} />
              </div>
              <div className="form-section">
                <div className="form-section-title">Requirements & Status</div>
                <div className="form-row">
                  <Select label="Status" value={newEvent.status} onChange={(e) => setNewEvent(ev => ({ ...ev, status: e.target.value }))} options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'published', label: 'Published' },
                    { value: 'closed', label: 'Closed' }
                  ]} />
                  <Input label="Vendor Fee ($)" type="number" placeholder="150" value={newEvent.vendorFee} onChange={(e) => setNewEvent(ev => ({ ...ev, vendorFee: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Required Permits</label>
                  <p className="form-hint">Select which permits vendors must have to participate</p>
                  <div className="permit-type-checkboxes">
                    {data.permitTypes.slice(0, 20).map(pt => (
                      <label key={pt._id} className="checkbox-label">
                        <input type="checkbox" checked={newEvent.requiredPermits?.includes(pt._id)} onChange={(e) => {
                          if (e.target.checked) {
                            setNewEvent(ev => ({ ...ev, requiredPermits: [...(ev.requiredPermits || []), pt._id] }));
                          } else {
                            setNewEvent(ev => ({ ...ev, requiredPermits: (ev.requiredPermits || []).filter(id => id !== pt._id) }));
                          }
                        }} />
                        <span>{pt.name} <small>({pt.jurisdictionId?.city}, {pt.jurisdictionId?.state})</small></span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="form-actions">
                <Button onClick={createEvent} disabled={!newEvent.eventName || !newEvent.startDate || !newEvent.city || !newEvent.state}>
                  <Icons.Plus /> Create Event
                </Button>
              </div>
            </Card>
            
            <div className="admin-section-header">
              <h3>🎪 Events ({data.events.length})</h3>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead><tr><th>Event</th><th>Date</th><th>Location</th><th>Required Permits</th><th>Assigned Vendors</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {data.events.map(ev => (
                    <tr key={ev._id}>
                      <td><strong>{ev.eventName}</strong>{ev.organizerName && <><br /><small>by {ev.organizerName}</small></>}</td>
                      <td>{formatDate(ev.startDate)}</td>
                      <td>{ev.location?.city}, {ev.location?.state}</td>
                      <td>{ev.requiredPermitTypes?.length || 0} permits</td>
                      <td>{ev.assignedVendors?.length || 0} vendors</td>
                      <td><Badge variant={ev.status === 'published' ? 'success' : ev.status === 'canceled' ? 'danger' : 'default'}>{ev.status}</Badge></td>
                      <td className="actions-cell">
                        <button className="action-btn edit" onClick={() => setManagingEvent(ev)} title="Manage Vendors"><Icons.Settings /></button>
                        <button className="delete-btn" onClick={() => deleteEvent(ev._id)} title="Delete"><Icons.Trash /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'suggestions' && (
          <div>
            <div className="admin-section-header">
              <h3>💡 User Suggestions & Requests</h3>
              <div className="filter-tabs">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'pending', label: '⏳ Pending' },
                  { key: 'in_progress', label: '🔄 In Progress' },
                  { key: 'completed', label: '✅ Completed' },
                  { key: 'rejected', label: '❌ Rejected' }
                ].map(f => (
                  <button 
                    key={f.key} 
                    className={suggestionFilter === f.key ? 'active' : ''} 
                    onClick={() => { setSuggestionFilter(f.key); fetchData('suggestions', f.key); }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead><tr><th>Type</th><th>Title</th><th>User</th><th>Business</th><th>Details</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
                <tbody>
                  {suggestions.length === 0 ? (
                    <tr><td colSpan="8" className="empty-row">No {suggestionFilter === 'all' ? '' : suggestionFilter.replace('_', ' ')} suggestions</td></tr>
                  ) : suggestions.map(s => (
                    <tr key={s._id}>
                      <td><Badge variant={s.type === 'city_request' ? 'primary' : s.type === 'checklist_request' ? 'warning' : s.type === 'event_request' ? 'success' : s.type === 'permit_type_request' ? 'info' : 'default'}>{s.type.replace(/_/g, ' ')}</Badge></td>
                      <td><strong>{s.title}</strong>{s.description && <><br /><small className="text-muted">{s.description.substring(0, 100)}{s.description.length > 100 ? '...' : ''}</small></>}</td>
                      <td>{s.userId?.firstName} {s.userId?.lastName}<br /><small className="text-muted">{s.userId?.email}</small></td>
                      <td>{s.vendorBusinessId?.businessName || '—'}</td>
                      <td>
                        {s.cityDetails && <small>📍 {s.cityDetails.city}, {s.cityDetails.state}</small>}
                        {s.checklistDetails?.name && <small>☑️ {s.checklistDetails.name}</small>}
                        {s.eventDetails && (
                          <div className="event-request-details">
                            <small><strong>{s.eventDetails.eventName}</strong></small><br />
                            <small>📍 {s.eventDetails.city}, {s.eventDetails.state}</small>
                            {s.eventDetails.startDate && <><br /><small>📅 {formatDate(s.eventDetails.startDate)}</small></>}
                            {s.eventDetails.organizerName && <><br /><small>👤 {s.eventDetails.organizerName}</small></>}
                            {s.eventDetails.website && <><br /><small><a href={s.eventDetails.website} target="_blank" rel="noopener noreferrer">🔗 Website</a></small></>}
                          </div>
                        )}
                        {s.adminNotes && <div className="admin-notes-inline"><small>📝 {s.adminNotes}</small></div>}
                      </td>
                      <td>
                        <Badge variant={s.status === 'pending' ? 'warning' : s.status === 'in_progress' ? 'info' : s.status === 'completed' ? 'success' : 'danger'}>
                          {s.status === 'pending' ? '⏳ Pending' : s.status === 'in_progress' ? '🔄 In Progress' : s.status === 'completed' ? '✅ Done' : '❌ Rejected'}
                        </Badge>
                      </td>
                      <td className="date-cell">{formatDate(s.createdAt)}</td>
                      <td className="actions-cell suggestion-actions">
                        {/* Convert event request to event (available for pending & in_progress) */}
                        {s.type === 'event_request' && s.eventDetails && ['pending', 'in_progress'].includes(s.status) && (
                          <button className="action-btn primary" onClick={() => convertEventRequestToEvent(s)} title="Convert to Event"><Icons.Plus /></button>
                        )}
                        {/* Status transition buttons based on current status */}
                        {s.status === 'pending' && (
                          <>
                            <button className="action-btn progress" onClick={() => updateSuggestion(s._id, 'in_progress')} title="Mark In Progress"><Icons.Clock /></button>
                            <button className="action-btn success" onClick={() => updateSuggestion(s._id, 'completed')} title="Mark Completed"><Icons.Check /></button>
                            <button className="action-btn reject" onClick={() => updateSuggestion(s._id, 'rejected')} title="Reject"><Icons.X /></button>
                          </>
                        )}
                        {s.status === 'in_progress' && (
                          <>
                            <button className="action-btn success" onClick={() => updateSuggestion(s._id, 'completed')} title="Mark Completed"><Icons.Check /></button>
                            <button className="action-btn reject" onClick={() => updateSuggestion(s._id, 'rejected')} title="Reject"><Icons.X /></button>
                          </>
                        )}
                        {(s.status === 'completed' || s.status === 'rejected') && (
                          <button className="action-btn reopen" onClick={() => updateSuggestion(s._id, 'pending')} title="Re-open">↩</button>
                        )}
                        <button className="delete-btn" onClick={() => deleteSuggestion(s._id)} title="Delete"><Icons.Trash /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div>
            {/* Referral Stats */}
            {referralStats && (
              <>
                <div className="stats-grid" style={{ marginBottom: '0.75rem' }}>
                  <Card className="stat-card"><span className="stat-icon">🔗</span><div className="stat-content"><span className="stat-value">{referralStats.totalCodes}</span><span className="stat-label">Total Codes</span></div></Card>
                  <Card className="stat-card stat-primary"><span className="stat-icon">✅</span><div className="stat-content"><span className="stat-value">{referralStats.activeCodes}</span><span className="stat-label">Active Codes</span></div></Card>
                  <Card className="stat-card stat-success"><span className="stat-icon">👤</span><div className="stat-content"><span className="stat-value">{referralStats.totalSignups}</span><span className="stat-label">Total Signups</span></div></Card>
                  <Card className="stat-card stat-info"><span className="stat-icon">📅</span><div className="stat-content"><span className="stat-value">{referralStats.recentSignups}</span><span className="stat-label">Last 30 Days</span></div></Card>
                  <Card className="stat-card" style={{ borderLeft: '4px solid #16a34a' }}><span className="stat-icon">💰</span><div className="stat-content"><span className="stat-value" style={{ color: '#16a34a' }}>{referralStats.totalPaidConversions}</span><span className="stat-label">Paid Conversions</span></div></Card>
                  <Card className="stat-card" style={{ borderLeft: '4px solid #16a34a' }}><span className="stat-icon">📈</span><div className="stat-content"><span className="stat-value" style={{ color: referralStats.overallConversionRate >= 20 ? '#16a34a' : referralStats.overallConversionRate >= 10 ? '#ca8a04' : '#dc2626' }}>{referralStats.overallConversionRate}%</span><span className="stat-label">Conversion Rate</span></div></Card>
                </div>

                {/* Conversion funnel */}
                <Card style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 1rem', fontSize: '0.9375rem' }}>Referral Conversion Funnel</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}><span style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>Signups → Paid</span><span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{referralStats.totalPaidConversions} / {referralStats.totalSignups}</span></div>
                      <div style={{ height: 12, background: 'var(--gray-100)', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                        <div style={{ height: '100%', width: `${referralStats.overallConversionRate}%`, background: 'linear-gradient(90deg, #3b82f6, #16a34a)', borderRadius: 6, transition: 'width 0.5s' }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Still on trial / free: {referralStats.totalSignups - referralStats.totalPaidConversions}</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Last 30d: {referralStats.recentPaid} paid of {referralStats.recentSignups} signups</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 70, padding: '0.75rem', background: referralStats.overallConversionRate >= 20 ? '#dcfce7' : referralStats.overallConversionRate >= 10 ? '#fef3c7' : '#fee2e2', borderRadius: 10 }}>
                      <span style={{ fontSize: '1.5rem', fontWeight: 700, color: referralStats.overallConversionRate >= 20 ? '#16a34a' : referralStats.overallConversionRate >= 10 ? '#ca8a04' : '#dc2626' }}>{referralStats.overallConversionRate}%</span>
                    </div>
                  </div>
                </Card>
              </>
            )}

            {/* Signups by type with conversions */}
            {referralStats?.byType && (
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <Card style={{ flex: 1, textAlign: 'center', padding: '1rem' }}>
                  <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>{referralStats.byType.vendor}</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>🚚 Vendors</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>{referralStats.byTypePaid?.vendor || 0} paid</p>
                </Card>
                <Card style={{ flex: 1, textAlign: 'center', padding: '1rem' }}>
                  <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>{referralStats.byType.organizer}</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>🎪 Organizers</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>{referralStats.byTypePaid?.organizer || 0} paid</p>
                </Card>
                <Card style={{ flex: 1, textAlign: 'center', padding: '1rem' }}>
                  <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>{referralStats.byType.restaurant}</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>🍽️ Restaurants</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>{referralStats.byTypePaid?.restaurant || 0} paid</p>
                </Card>
              </div>
            )}

            <div className="admin-section-header">
              <h3>🔗 Referral Codes</h3>
              <Button onClick={() => { setEditingReferral(null); setReferralForm({ code: '', organizerId: '', organizerName: '', description: '' }); setShowReferralModal(true); }}><Icons.Plus /> Create Code</Button>
            </div>

            {/* Link format info */}
            <Card style={{ padding: '1rem', marginBottom: '1.5rem', background: '#eff6ff', borderLeft: '4px solid #3b82f6' }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: '#1e40af' }}>QR Code Link Format</p>
              <code style={{ display: 'block', marginTop: 6, padding: '8px 12px', background: 'white', borderRadius: 6, fontSize: '0.8125rem', color: '#1e40af', wordBreak: 'break-all' }}>https://permitwise.app/app?register=true&ref=CODE</code>
              <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#3b82f6' }}>Replace CODE with the referral code. The code is case-insensitive.</p>
            </Card>

            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Code</th><th>Organizer</th><th>Description</th><th>Signups</th><th>Paid</th><th>Conv. Rate</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {referralCodes.length > 0 ? referralCodes.map(rc => (
                    <tr key={rc._id}>
                      <td><code style={{ fontSize: '0.875rem', fontWeight: 600, background: 'var(--gray-100)', padding: '2px 8px', borderRadius: 4 }}>{rc.code}</code></td>
                      <td>{rc.organizerName || rc.organizerId?.organizerProfile?.companyName || rc.organizerId?.firstName || 'Unlinked'}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rc.description || '—'}</td>
                      <td><button onClick={() => setViewingReferral(rc)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, textDecoration: 'underline' }}>{rc.totalSignups || 0}</button></td>
                      <td style={{ fontWeight: 600, color: '#16a34a' }}>{rc.paidConversions || 0}</td>
                      <td>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.8125rem', fontWeight: 600, background: (rc.conversionRate || 0) >= 20 ? '#dcfce7' : (rc.conversionRate || 0) >= 10 ? '#fef3c7' : (rc.totalSignups || 0) === 0 ? 'var(--gray-100)' : '#fee2e2', color: (rc.conversionRate || 0) >= 20 ? '#16a34a' : (rc.conversionRate || 0) >= 10 ? '#ca8a04' : (rc.totalSignups || 0) === 0 ? 'var(--gray-400)' : '#dc2626' }}>
                          {rc.totalSignups > 0 ? `${rc.conversionRate || 0}%` : '—'}
                        </span>
                      </td>
                      <td><Badge variant={rc.active ? 'success' : 'default'}>{rc.active ? 'Active' : 'Inactive'}</Badge></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => { setEditingReferral(rc); setReferralForm({ code: rc.code, organizerId: rc.organizerId?._id || '', organizerName: rc.organizerName || '', description: rc.description || '' }); setShowReferralModal(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Icons.Edit /></button>
                          <button onClick={async () => { await adminApi(`/admin/referral-codes/${rc._id}`, 'PUT', { active: !rc.active }); fetchData('referrals'); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }} title={rc.active ? 'Deactivate' : 'Activate'}>{rc.active ? '⏸' : '▶️'}</button>
                          <button onClick={async () => { const ok = await confirm({ title: 'Delete Code', message: `Delete referral code "${rc.code}"? This cannot be undone.`, confirmText: 'Delete', variant: 'danger' }); if (ok) { await adminApi(`/admin/referral-codes/${rc._id}`, 'DELETE'); fetchData('referrals'); } }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Icons.Trash /></button>
                        </div>
                      </td>
                    </tr>
                  )) : <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>No referral codes yet. Create one above.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0 }}>📊 Website Analytics</h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {[7, 14, 30, 90].map(d => (
                  <button key={d} onClick={() => { setAnalyticsDays(d); setTimeout(() => fetchData('analytics'), 0); }}
                    style={{ padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600, background: analyticsDays === d ? 'var(--primary)' : 'var(--gray-100)', color: analyticsDays === d ? 'white' : 'var(--gray-600)' }}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            {analyticsData ? (
              <>
                {/* Top Stats */}
                <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
                  <Card className="stat-card"><span className="stat-icon">👁️</span><div className="stat-content"><span className="stat-value">{analyticsData.totalViews}</span><span className="stat-label">Total Views ({analyticsDays}d)</span></div></Card>
                  <Card className="stat-card stat-primary"><span className="stat-icon">🌐</span><div className="stat-content"><span className="stat-value">{analyticsData.uniqueIPs}</span><span className="stat-label">Unique Visitors</span></div></Card>
                  <Card className="stat-card stat-success"><span className="stat-icon">📅</span><div className="stat-content"><span className="stat-value">{analyticsData.todayViews}</span><span className="stat-label">Today Views</span></div></Card>
                  <Card className="stat-card stat-info"><span className="stat-icon">👤</span><div className="stat-content"><span className="stat-value">{analyticsData.todayUnique}</span><span className="stat-label">Today Unique</span></div></Card>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  {/* Traffic by Day */}
                  <Card style={{ padding: '1.25rem' }}>
                    <h4 style={{ margin: '0 0 1rem' }}>Daily Traffic</h4>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120 }}>
                      {(analyticsData.byDay || []).slice(-30).map(([day, count]) => {
                        const max = Math.max(...(analyticsData.byDay || []).map(d => d[1]), 1);
                        return (
                          <div key={day} title={`${day}: ${count} views`} style={{ flex: 1, minWidth: 4, height: `${Math.max((count / max) * 100, 4)}%`, background: 'var(--primary)', borderRadius: '2px 2px 0 0', opacity: 0.8 }} />
                        );
                      })}
                    </div>
                    {analyticsData.byDay?.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                        <span style={{ fontSize: '0.6875rem', color: 'var(--gray-400)' }}>{analyticsData.byDay[0]?.[0]}</span>
                        <span style={{ fontSize: '0.6875rem', color: 'var(--gray-400)' }}>{analyticsData.byDay[analyticsData.byDay.length - 1]?.[0]}</span>
                      </div>
                    )}
                  </Card>

                  {/* Device Breakdown */}
                  <Card style={{ padding: '1.25rem' }}>
                    <h4 style={{ margin: '0 0 1rem' }}>Devices</h4>
                    {(analyticsData.byDevice || []).map(([device, count]) => (
                      <div key={device} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: '0.875rem', textTransform: 'capitalize' }}>{device === 'desktop' ? '🖥️' : device === 'mobile' ? '📱' : device === 'tablet' ? '📟' : '❓'} {device}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 80, height: 6, background: 'var(--gray-100)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${analyticsData.totalViews > 0 ? (count / analyticsData.totalViews) * 100 : 0}%`, background: 'var(--primary)', borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: '0.8125rem', fontWeight: 600, minWidth: 30, textAlign: 'right' }}>{count}</span>
                        </div>
                      </div>
                    ))}
                  </Card>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  {/* Top Pages */}
                  <Card style={{ padding: '1.25rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem' }}>Top Pages</h4>
                    {(analyticsData.byPage || []).slice(0, 8).map(([page, count]) => (
                      <div key={page} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--gray-50)' }}>
                        <code style={{ fontSize: '0.8125rem', color: 'var(--gray-700)' }}>{page}</code>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                  </Card>

                  {/* Top Countries */}
                  <Card style={{ padding: '1.25rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem' }}>Top Countries</h4>
                    {(analyticsData.byCountry || []).slice(0, 8).map(([country, count]) => (
                      <div key={country} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--gray-50)' }}>
                        <span style={{ fontSize: '0.8125rem' }}>{country}</span>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                    {(!analyticsData.byCountry || analyticsData.byCountry.length === 0) && <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>No geo data yet</p>}
                  </Card>

                  {/* Top Cities */}
                  <Card style={{ padding: '1.25rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem' }}>Top Cities</h4>
                    {(analyticsData.byCity || []).slice(0, 8).map(([city, count]) => (
                      <div key={city} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--gray-50)' }}>
                        <span style={{ fontSize: '0.8125rem' }}>{city}</span>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                    {(!analyticsData.byCity || analyticsData.byCity.length === 0) && <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>No geo data yet</p>}
                  </Card>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  {/* Browsers */}
                  <Card style={{ padding: '1.25rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem' }}>Browsers</h4>
                    {(analyticsData.byBrowser || []).map(([browser, count]) => (
                      <div key={browser} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                        <span style={{ fontSize: '0.8125rem' }}>{browser}</span>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                  </Card>

                  {/* OS */}
                  <Card style={{ padding: '1.25rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem' }}>Operating Systems</h4>
                    {(analyticsData.byOS || []).map(([os, count]) => (
                      <div key={os} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                        <span style={{ fontSize: '0.8125rem' }}>{os}</span>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                  </Card>

                  {/* Referrers */}
                  <Card style={{ padding: '1.25rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem' }}>Top Referrers</h4>
                    {(analyticsData.byReferrer || []).slice(0, 8).map(([ref, count]) => (
                      <div key={ref} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--gray-50)' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--gray-600)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ref}</span>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                    {(!analyticsData.byReferrer || analyticsData.byReferrer.length === 0) && <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>No referrer data</p>}
                  </Card>
                </div>

                {/* UTM & Ref Codes */}
                {((analyticsData.byUtmSource?.length > 0) || (analyticsData.byRefCode?.length > 0)) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                    {analyticsData.byUtmSource?.length > 0 && (
                      <Card style={{ padding: '1.25rem' }}>
                        <h4 style={{ margin: '0 0 0.75rem' }}>UTM Sources</h4>
                        {analyticsData.byUtmSource.map(([src, count]) => (
                          <div key={src} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                            <code style={{ fontSize: '0.8125rem' }}>{src}</code>
                            <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{count}</span>
                          </div>
                        ))}
                      </Card>
                    )}
                    {analyticsData.byRefCode?.length > 0 && (
                      <Card style={{ padding: '1.25rem' }}>
                        <h4 style={{ margin: '0 0 0.75rem' }}>Referral Code Visits</h4>
                        {analyticsData.byRefCode.map(([code, count]) => (
                          <div key={code} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                            <code style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--primary)' }}>{code}</code>
                            <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{count} visits</span>
                          </div>
                        ))}
                      </Card>
                    )}
                  </div>
                )}

                {/* Recent Visitors */}
                <Card style={{ padding: '1.25rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem' }}>Recent Visitors (Last 50 Unique)</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="admin-table" style={{ fontSize: '0.8125rem' }}>
                      <thead><tr><th>IP</th><th>Location</th><th>Page</th><th>Device</th><th>Browser</th><th>OS</th><th>Referrer</th><th>Time</th></tr></thead>
                      <tbody>
                        {(analyticsData.recentVisitors || []).map((v, i) => (
                          <tr key={i}>
                            <td><code style={{ fontSize: '0.75rem' }}>{v.ip}</code></td>
                            <td style={{ whiteSpace: 'nowrap' }}>{v.city ? `${v.city}, ${v.region}` : v.country || '—'}</td>
                            <td><code>{v.page}</code></td>
                            <td>{v.device === 'mobile' ? '📱' : v.device === 'tablet' ? '📟' : '🖥️'}</td>
                            <td>{v.browser}</td>
                            <td>{v.os}</td>
                            <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem', color: 'var(--gray-500)' }}>{v.referrer || '—'}</td>
                            <td style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: 'var(--gray-500)' }}>{new Date(v.visitedAt).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            ) : <div style={{ textAlign: 'center', padding: '3rem' }}><LoadingSpinner /></div>}
          </div>
        )}
      </div>

      {/* Create/Edit Referral Modal */}
      <Modal isOpen={showReferralModal} onClose={() => setShowReferralModal(false)} title={editingReferral ? 'Edit Referral Code' : 'Create Referral Code'}>
        <Input label="Code *" value={referralForm.code} onChange={e => setReferralForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/\s/g, '') }))} placeholder="e.g. SPRING2026, FARMERSMARKET" disabled={!!editingReferral} />
        {!editingReferral && <p style={{ margin: '-8px 0 12px', fontSize: '0.75rem', color: 'var(--gray-500)' }}>Uppercase, no spaces. Cannot be changed after creation.</p>}
        <Select label="Link to Organizer (optional)" value={referralForm.organizerId} onChange={e => { const org = data.organizers?.find(o => o._id === e.target.value); setReferralForm(f => ({ ...f, organizerId: e.target.value, organizerName: org ? (org.organizerProfile?.companyName || `${org.firstName} ${org.lastName}`) : f.organizerName })); }} options={[{ value: '', label: 'No organizer linked' }, ...(data.organizers || []).map(o => ({ value: o._id, label: `${o.organizerProfile?.companyName || ''} (${o.firstName} ${o.lastName})` }))]} />
        <Input label="Organizer Name (if not linked)" value={referralForm.organizerName} onChange={e => setReferralForm(f => ({ ...f, organizerName: e.target.value }))} placeholder="e.g. Austin Food Events LLC" />
        <Input label="Description / Campaign" value={referralForm.description} onChange={e => setReferralForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Spring 2026 Farmers Market flyer" />
        <Button onClick={async () => {
          if (!referralForm.code.trim()) { toast.error('Code is required'); return; }
          try {
            if (editingReferral) { await adminApi(`/admin/referral-codes/${editingReferral._id}`, 'PUT', referralForm); }
            else { await adminApi('/admin/referral-codes', 'POST', referralForm); }
            toast.success(editingReferral ? 'Updated' : 'Created');
            setShowReferralModal(false); fetchData('referrals');
          } catch (err) { toast.error(err.message); }
        }} style={{ marginTop: '1rem' }}>{editingReferral ? 'Update Code' : 'Create Code'}</Button>
      </Modal>

      {/* Signups Detail Modal */}
      <Modal isOpen={!!viewingReferral} onClose={() => setViewingReferral(null)} title={`Signups — ${viewingReferral?.code}`} size="lg">
        {viewingReferral && (
          <div>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1, textAlign: 'center', padding: '0.75rem', background: 'var(--gray-50)', borderRadius: 8 }}><span style={{ fontSize: '1.5rem', fontWeight: 700 }}>{viewingReferral.totalSignups || 0}</span><br/><span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Signups</span></div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0.75rem', background: '#dcfce7', borderRadius: 8 }}><span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#16a34a' }}>{viewingReferral.paidConversions || 0}</span><br/><span style={{ fontSize: '0.75rem', color: '#16a34a' }}>Paid</span></div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0.75rem', background: referralStats?.overallConversionRate >= 20 ? '#dcfce7' : '#fef3c7', borderRadius: 8 }}><span style={{ fontSize: '1.5rem', fontWeight: 700, color: (viewingReferral.conversionRate || 0) >= 20 ? '#16a34a' : '#ca8a04' }}>{viewingReferral.conversionRate || 0}%</span><br/><span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Conversion</span></div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0.75rem', background: 'var(--gray-50)', borderRadius: 8 }}><span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{viewingReferral.organizerName || 'Unlinked'}</span><br/><span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Organizer</span></div>
            </div>
            <div style={{ background: '#eff6ff', padding: '8px 12px', borderRadius: 6, marginBottom: '1rem', fontSize: '0.8125rem' }}>
              <strong>Link:</strong> <code>https://permitwise.app/app?register=true&ref={viewingReferral.code}</code>
            </div>
            {viewingReferral.signups?.length > 0 ? (
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Email</th><th>Type</th><th>Status</th><th>Plan</th><th>Date</th></tr></thead>
                <tbody>
                  {viewingReferral.signups.map((s, i) => (
                    <tr key={i}>
                      <td>{s.userName || '—'}</td>
                      <td style={{ fontSize: '0.8125rem' }}>{s.userEmail}</td>
                      <td><Badge variant={s.accountType === 'vendor' ? 'primary' : s.accountType === 'restaurant' ? 'warning' : 'default'}>{s.accountType}</Badge></td>
                      <td>{s.isPaid ? <Badge variant="success">Paid</Badge> : <Badge variant="default">Free/Trial</Badge>}</td>
                      <td style={{ fontSize: '0.8125rem', fontWeight: s.isPaid ? 600 : 400, color: s.isPaid ? '#16a34a' : 'var(--gray-400)' }}>{s.paidPlan ? s.paidPlan.charAt(0).toUpperCase() + s.paidPlan.slice(1) : '—'}</td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{new Date(s.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '2rem' }}>No signups yet for this code.</p>}
          </div>
        )}
      </Modal>
      
      {/* Duplicate Modal */}
      <Modal isOpen={!!duplicateTarget} onClose={() => { setDuplicateTarget(null); setDuplicateJurisdiction(''); }} title="Duplicate Permit Type">
        <p>Create a copy of this permit type. Optionally select a different jurisdiction.</p>
        <Select label="Target Jurisdiction (optional)" value={duplicateJurisdiction} onChange={(e) => setDuplicateJurisdiction(e.target.value)} options={[{ value: '', label: 'Same jurisdiction' }, ...data.jurisdictions.map(j => ({ value: j._id, label: `${j.name} (${j.city}, ${j.state})` }))]} />
        <div className="modal-actions">
          <Button variant="outline" onClick={() => { setDuplicateTarget(null); setDuplicateJurisdiction(''); }}>Cancel</Button>
          <Button onClick={duplicatePermitType}>Duplicate</Button>
        </div>
      </Modal>
      
      {/* Edit Permit Type Modal */}
      <Modal isOpen={!!editingPermitType} onClose={() => setEditingPermitType(null)} title="Edit Permit Type" size="lg">
        {editingPermitType && (
          <div className="admin-form-enhanced">
            <div className="form-section">
              <div className="form-section-title">Basic Information</div>
              <Input label="Permit Name *" value={editingPermitType.name} onChange={(e) => setEditingPermitType(p => ({ ...p, name: e.target.value }))} />
              <Input label="Description" value={editingPermitType.description || ''} onChange={(e) => setEditingPermitType(p => ({ ...p, description: e.target.value }))} />
              <Select label="Jurisdiction *" value={editingPermitType.jurisdictionId?._id || editingPermitType.jurisdictionId} onChange={(e) => setEditingPermitType(p => ({ ...p, jurisdictionId: e.target.value }))} options={[{ value: '', label: 'Select Jurisdiction' }, ...data.jurisdictions.map(j => ({ value: j._id, label: `${j.city}, ${j.state}` }))]} />
            </div>
            <div className="form-section">
              <div className="form-section-title">Classification</div>
              <div className="form-row">
                <Select label="Importance Level" value={editingPermitType.importanceLevel} onChange={(e) => setEditingPermitType(p => ({ ...p, importanceLevel: e.target.value }))} options={[
                  { value: 'critical', label: '🔴 Critical' },
                  { value: 'often_forgotten', label: '🟡 Often Forgotten' },
                  { value: 'event_required', label: '🔵 Event Required' }
                ]} />
                <Input label="Renewal Period (months)" type="number" value={editingPermitType.renewalPeriodMonths} onChange={(e) => setEditingPermitType(p => ({ ...p, renewalPeriodMonths: parseInt(e.target.value) || 12 }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Vendor Types</label>
                <div className="vendor-type-checkboxes">
                  {VENDOR_TYPES.map(vt => (
                    <label key={vt.value} className="vendor-checkbox">
                      <input 
                        type="checkbox" 
                        checked={editingPermitType.vendorTypes?.includes(vt.value)} 
                        onChange={(e) => {
                          const types = editingPermitType.vendorTypes || [];
                          if (e.target.checked) {
                            setEditingPermitType(p => ({ ...p, vendorTypes: [...types, vt.value] }));
                          } else {
                            setEditingPermitType(p => ({ ...p, vendorTypes: types.filter(t => t !== vt.value) }));
                          }
                        }}
                      />
                      <span>{vt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="form-section">
              <div className="form-section-title">Authority & Cost</div>
              <div className="form-row">
                <Input label="Issuing Authority" value={editingPermitType.issuingAuthorityName || ''} onChange={(e) => setEditingPermitType(p => ({ ...p, issuingAuthorityName: e.target.value }))} />
                <Input label="Estimated Cost" placeholder="$50-100" value={editingPermitType.estimatedCost || ''} onChange={(e) => setEditingPermitType(p => ({ ...p, estimatedCost: e.target.value }))} />
              </div>
              <div className="form-row">
                <Input label="Application Fee" type="number" value={editingPermitType.fees?.application || 0} onChange={(e) => setEditingPermitType(p => ({ ...p, fees: { ...p.fees, application: parseFloat(e.target.value) || 0 } }))} />
                <Input label="Renewal Fee" type="number" value={editingPermitType.fees?.renewal || 0} onChange={(e) => setEditingPermitType(p => ({ ...p, fees: { ...p.fees, renewal: parseFloat(e.target.value) || 0 } }))} />
              </div>
              <Input label="Required Documents (comma-separated)" placeholder="Business license, Food handler cert" value={editingPermitType.requiredDocuments || ''} onChange={(e) => setEditingPermitType(p => ({ ...p, requiredDocuments: e.target.value }))} />
            </div>
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setEditingPermitType(null)}>Cancel</Button>
              <Button onClick={updatePermitType}>Save Changes</Button>
            </div>
          </div>
        )}
      </Modal>
      
      {/* Subscription Management Modal */}
      <Modal isOpen={!!managingSubscription} onClose={() => { setManagingSubscription(null); setSubscriptionAction(''); }} title={`Manage Subscription`} size="lg">
        {managingSubscription && (
          <div className="subscription-manager">
            <div className="sub-business-header">
              <h3>{managingSubscription.businessName}</h3>
              <p className="text-muted">{managingSubscription.ownerId?.email}</p>
            </div>
            <div className="sub-current-status">
              <h4>Current Subscription</h4>
              <div className="sub-info-grid">
                <div className="sub-info-item"><span className="sub-label">Plan</span> <Badge variant={getSubscriptionStatusColor(managingSubscription.subscription) === 'green' ? 'success' : getSubscriptionStatusColor(managingSubscription.subscription) === 'purple' ? 'primary' : 'warning'}>{managingSubscription.subscription?.plan?.toUpperCase() || 'TRIAL'}</Badge></div>
                <div className="sub-info-item"><span className="sub-label">Status</span> <span className="sub-value">{managingSubscription.subscription?.status || 'trial'}</span></div>
                <div className="sub-info-item"><span className="sub-label">Expires</span> <span className="sub-value">{managingSubscription.subscription?.status === 'lifetime' || (managingSubscription.subscription?.promoGrantedBy === 'admin' && !managingSubscription.subscription?.promoExpiresAt) ? '∞ Never' : formatDate(managingSubscription.subscription?.promoExpiresAt || managingSubscription.subscription?.trialEndsAt || managingSubscription.subscription?.currentPeriodEnd)}</span></div>
                {managingSubscription.subscription?.promoNote && <div className="sub-info-item full-width"><span className="sub-label">Admin Note</span> <span className="sub-value">{managingSubscription.subscription.promoNote}</span></div>}
                {managingSubscription.subscription?.promoGrantedBy && <div className="sub-info-item"><span className="sub-label">Granted By</span> <span className="sub-value">🎁 Admin ({formatDate(managingSubscription.subscription.promoGrantedAt)})</span></div>}
              </div>
            </div>
            
            <div className="sub-actions">
              <h4>Quick Actions</h4>
              <div className="sub-action-buttons">
                <Button variant={subscriptionAction === 'extend' ? 'primary' : 'outline'} onClick={() => setSubscriptionAction('extend')}>
                  <Icons.Clock /> Extend Trial
                </Button>
                <Button variant={subscriptionAction === 'grant' ? 'primary' : 'outline'} onClick={() => setSubscriptionAction('grant')}>
                  <Icons.Check /> Grant Plan
                </Button>
                <Button variant={subscriptionAction === 'revoke' ? 'danger' : 'outline'} onClick={() => setSubscriptionAction('revoke')}>
                  <Icons.X /> Revoke Access
                </Button>
              </div>
            </div>
            
            {subscriptionAction === 'extend' && (
              <div className="sub-action-form">
                <h4>🕐 Extend Trial Period</h4>
                <p className="form-hint">Add additional days to the user's trial period.</p>
                <Input label="Days to Add" type="number" min="1" max="365" value={subscriptionForm.days} onChange={(e) => setSubscriptionForm(f => ({ ...f, days: parseInt(e.target.value) || 30 }))} />
                <Input label="Note (optional)" placeholder="e.g., Customer service extension" value={subscriptionForm.note} onChange={(e) => setSubscriptionForm(f => ({ ...f, note: e.target.value }))} />
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => setSubscriptionAction('')}>Cancel</Button>
                  <Button onClick={extendTrial}>Extend Trial by {subscriptionForm.days} Days</Button>
                </div>
              </div>
            )}
            
            {subscriptionAction === 'grant' && (
              <div className="sub-action-form">
                <h4>🎁 Grant Subscription</h4>
                <p className="form-hint">Give the user free access to paid features. Basic, Pro, Elite, and Lifetime grants are permanent unless revoked.</p>
                <Select label="Plan Type" value={subscriptionForm.plan} onChange={(e) => setSubscriptionForm(f => ({ ...f, plan: e.target.value }))} options={[
                  { value: 'promo', label: '🎉 Promo (Time-Limited Full Access)' },
                  { value: 'basic', label: '📋 Basic Plan (Permanent)' },
                  { value: 'pro', label: '⭐ Pro Plan (Permanent)' },
                  { value: 'elite', label: '👑 Elite Plan (Permanent)' },
                  { value: 'lifetime', label: '♾️ Lifetime (Permanent Full Access)' }
                ]} />
                {subscriptionForm.plan === 'promo' && (
                  <Input label="Duration (days)" type="number" min="1" max="3650" value={subscriptionForm.durationDays} onChange={(e) => setSubscriptionForm(f => ({ ...f, durationDays: parseInt(e.target.value) || 90 }))} />
                )}
                <Input label="Note (required for audit)" placeholder="e.g., Beta tester reward, Influencer deal" value={subscriptionForm.note} onChange={(e) => setSubscriptionForm(f => ({ ...f, note: e.target.value }))} />
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => setSubscriptionAction('')}>Cancel</Button>
                  <Button onClick={grantPromo} disabled={!subscriptionForm.note}>
                    Grant {subscriptionForm.plan === 'lifetime' ? 'Lifetime' : subscriptionForm.plan === 'promo' ? `${subscriptionForm.durationDays}-Day Promo` : subscriptionForm.plan.toUpperCase()} Access
                  </Button>
                </div>
              </div>
            )}
            
            {subscriptionAction === 'revoke' && (
              <div className="sub-action-form">
                <h4>⚠️ Revoke Subscription</h4>
                <p className="form-hint" style={{ color: 'var(--danger)' }}>This will remove the user's paid access and reset them to an expired trial.</p>
                <Input label="Reason (required)" placeholder="e.g., Refund processed, Abuse detected" value={subscriptionForm.note} onChange={(e) => setSubscriptionForm(f => ({ ...f, note: e.target.value }))} />
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => setSubscriptionAction('')}>Cancel</Button>
                  <Button variant="danger" onClick={revokeSubscription} disabled={!subscriptionForm.note}>Revoke Access</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      
      {/* Organizer Subscription Management Modal */}
      <Modal isOpen={!!managingOrgSub} onClose={() => { setManagingOrgSub(null); setOrgSubAction(''); }} title="Manage Organizer Subscription" size="lg">
        {managingOrgSub && (
          <div className="subscription-manager">
            <div className="sub-business-header">
              <h3>{managingOrgSub.firstName} {managingOrgSub.lastName}</h3>
              <p className="text-muted">{managingOrgSub.email} • {managingOrgSub.organizerProfile?.companyName || 'Event Organizer'}</p>
            </div>
            <div className="sub-current-status">
              <h4>Current Subscription</h4>
              <div className="sub-info-grid">
                <div className="sub-info-item"><span className="sub-label">Plan</span> <Badge variant={getSubscriptionStatusColor(managingOrgSub.subscription) === 'green' ? 'success' : getSubscriptionStatusColor(managingOrgSub.subscription) === 'purple' ? 'primary' : 'warning'}>{managingOrgSub.subscription?.plan?.toUpperCase() || 'TRIAL'}</Badge></div>
                <div className="sub-info-item"><span className="sub-label">Status</span> <span className="sub-value">{managingOrgSub.subscription?.status || 'trial'}</span></div>
                <div className="sub-info-item"><span className="sub-label">Price</span> <span className="sub-value">$79/mo</span></div>
                <div className="sub-info-item"><span className="sub-label">Expires</span> <span className="sub-value">{managingOrgSub.subscription?.status === 'lifetime' || (managingOrgSub.subscription?.promoGrantedBy === 'admin' && !managingOrgSub.subscription?.promoExpiresAt) ? '∞ Never' : formatDate(managingOrgSub.subscription?.promoExpiresAt || managingOrgSub.subscription?.trialEndsAt || managingOrgSub.subscription?.currentPeriodEnd)}</span></div>
                {managingOrgSub.subscription?.promoNote && <div className="sub-info-item full-width"><span className="sub-label">Admin Note</span> <span className="sub-value">{managingOrgSub.subscription.promoNote}</span></div>}
                {managingOrgSub.subscription?.promoGrantedBy && <div className="sub-info-item"><span className="sub-label">Granted By</span> <span className="sub-value">🎁 Admin ({formatDate(managingOrgSub.subscription.promoGrantedAt)})</span></div>}
              </div>
            </div>
            
            <div className="sub-actions">
              <h4>Quick Actions</h4>
              <div className="sub-action-buttons">
                <Button variant={orgSubAction === 'extend' ? 'primary' : 'outline'} onClick={() => setOrgSubAction('extend')}><Icons.Clock /> Extend Trial</Button>
                <Button variant={orgSubAction === 'grant' ? 'primary' : 'outline'} onClick={() => setOrgSubAction('grant')}><Icons.Check /> Grant Plan</Button>
                <Button variant={orgSubAction === 'revoke' ? 'danger' : 'outline'} onClick={() => setOrgSubAction('revoke')}><Icons.X /> Revoke Access</Button>
              </div>
            </div>
            
            {orgSubAction === 'extend' && (
              <div className="sub-action-form">
                <h4>🕐 Extend Organizer Trial</h4>
                <Input label="Days to Add" type="number" min="1" max="365" value={orgSubForm.days} onChange={(e) => setOrgSubForm(f => ({ ...f, days: parseInt(e.target.value) || 30 }))} />
                <Input label="Note (optional)" placeholder="e.g., Partnership extension" value={orgSubForm.note} onChange={(e) => setOrgSubForm(f => ({ ...f, note: e.target.value }))} />
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => setOrgSubAction('')}>Cancel</Button>
                  <Button onClick={extendOrgTrial}>Extend Trial by {orgSubForm.days} Days</Button>
                </div>
              </div>
            )}
            
            {orgSubAction === 'grant' && (
              <div className="sub-action-form">
                <h4>🎁 Grant Organizer Subscription</h4>
                <p className="form-hint">Give the organizer free access. Organizer and Lifetime grants are permanent unless revoked.</p>
                <Select label="Plan Type" value={orgSubForm.plan} onChange={(e) => setOrgSubForm(f => ({ ...f, plan: e.target.value }))} options={[
                  { value: 'promo', label: '🎉 Promo (Time-Limited Full Access)' },
                  { value: 'organizer', label: '🎪 Organizer Plan (Permanent - $79/mo value)' },
                  { value: 'lifetime', label: '♾️ Lifetime (Permanent Full Access)' }
                ]} />
                {orgSubForm.plan === 'promo' && (
                  <Input label="Duration (days)" type="number" min="1" max="3650" value={orgSubForm.durationDays} onChange={(e) => setOrgSubForm(f => ({ ...f, durationDays: parseInt(e.target.value) || 90 }))} />
                )}
                <Input label="Note (required for audit)" placeholder="e.g., Beta tester, Partner deal" value={orgSubForm.note} onChange={(e) => setOrgSubForm(f => ({ ...f, note: e.target.value }))} />
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => setOrgSubAction('')}>Cancel</Button>
                  <Button onClick={grantOrgPromo} disabled={!orgSubForm.note}>
                    Grant {orgSubForm.plan === 'lifetime' ? 'Lifetime' : orgSubForm.plan === 'promo' ? `${orgSubForm.durationDays}-Day Promo` : 'Organizer'} Access
                  </Button>
                </div>
              </div>
            )}
            
            {orgSubAction === 'revoke' && (
              <div className="sub-action-form">
                <h4>⚠️ Revoke Organizer Subscription</h4>
                <p className="form-hint" style={{ color: 'var(--danger)' }}>This will remove the organizer's paid access and reset them to an expired trial.</p>
                <Input label="Reason (required)" placeholder="e.g., Refund processed, Abuse detected" value={orgSubForm.note} onChange={(e) => setOrgSubForm(f => ({ ...f, note: e.target.value }))} />
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => setOrgSubAction('')}>Cancel</Button>
                  <Button variant="danger" onClick={revokeOrgSubscription} disabled={!orgSubForm.note}>Revoke Access</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* User Management Modal */}
      <Modal isOpen={!!managingUser} onClose={() => { setManagingUser(null); setUserAction(''); setResetPasswordValue(''); }} title="Manage User" size="lg">
        {managingUser && (
          <div className="user-manager">
            <div className="user-info-header">
              <div className="user-avatar">{(managingUser.firstName?.[0] || managingUser.email[0]).toUpperCase()}</div>
              <div className="user-info-details">
                <h3>{managingUser.firstName} {managingUser.lastName}</h3>
                <p className="text-muted">{managingUser.email}</p>
                <div className="user-meta">
                  <Badge variant={managingUser.role === 'admin' ? 'primary' : 'default'}>{managingUser.role}</Badge>
                  <Badge variant={managingUser.emailVerified ? 'success' : 'danger'}>{managingUser.emailVerified ? '✓ Verified' : '✗ Unverified'}</Badge>
                  <span className="text-muted">Joined {formatDate(managingUser.createdAt)}</span>
                </div>
              </div>
            </div>
            
            <div className="user-action-buttons">
              <Button variant={userAction === 'reset-password' ? 'primary' : 'outline'} onClick={() => setUserAction('reset-password')}>
                🔑 Set New Password
              </Button>
              <Button variant={userAction === 'send-reset-email' ? 'primary' : 'outline'} onClick={() => setUserAction('send-reset-email')}>
                ✉️ Send Reset Email
              </Button>
              <Button variant="outline" onClick={() => toggleEmailVerified(managingUser)}>
                {managingUser.emailVerified ? '✗ Unverify Email' : '✓ Verify Email'}
              </Button>
            </div>
            
            {userAction === 'reset-password' && (
              <div className="sub-action-form">
                <h4>🔑 Set New Password</h4>
                <p className="form-hint">Manually set a new password for this user. They will be able to use it immediately.</p>
                <Input label="New Password" type="password" placeholder="Minimum 8 characters" value={resetPasswordValue} onChange={(e) => setResetPasswordValue(e.target.value)} />
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => { setUserAction(''); setResetPasswordValue(''); }}>Cancel</Button>
                  <Button onClick={resetUserPassword} disabled={resetPasswordValue.length < 8}>Set Password</Button>
                </div>
              </div>
            )}
            
            {userAction === 'send-reset-email' && (
              <div className="sub-action-form">
                <h4>✉️ Send Password Reset Email</h4>
                <p className="form-hint">Send a password reset link to <strong>{managingUser.email}</strong>. The link will expire in 24 hours.</p>
                <div className="modal-actions">
                  <Button variant="outline" onClick={() => setUserAction('')}>Cancel</Button>
                  <Button onClick={sendResetEmail}>Send Reset Email</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      
      {/* Event Vendor Management Modal */}
      <Modal isOpen={!!managingEvent} onClose={() => { setManagingEvent(null); setVendorToAssign(''); }} title={`Manage Event: ${managingEvent?.eventName}`} size="lg">
        {managingEvent && (
          <div className="event-management">
            <div className="event-info-summary">
              <p><strong>Date:</strong> {formatDate(managingEvent.startDate)}</p>
              <p><strong>Location:</strong> {managingEvent.location?.city}, {managingEvent.location?.state}</p>
              <p><strong>Status:</strong> <Badge variant={managingEvent.status === 'published' ? 'success' : 'default'}>{managingEvent.status}</Badge></p>
            </div>
            
            <div className="event-required-permits">
              <h4>Required Permits ({managingEvent.requiredPermitTypes?.length || 0})</h4>
              {managingEvent.requiredPermitTypes?.length > 0 ? (
                <ul className="permit-list">
                  {managingEvent.requiredPermitTypes.map(pt => (
                    <li key={pt._id || pt}>{pt.name || pt}</li>
                  ))}
                </ul>
              ) : (
                <p className="empty-text">No permits required</p>
              )}
            </div>
            
            <div className="event-assigned-vendors">
              <h4>Assigned Vendors ({managingEvent.assignedVendors?.length || 0})</h4>
              
              <div className="assign-vendor-form">
                <Select 
                  label="Add Vendor" 
                  value={vendorToAssign} 
                  onChange={(e) => setVendorToAssign(e.target.value)}
                  options={[
                    { value: '', label: 'Select a business...' },
                    ...data.businesses
                      .filter(b => !managingEvent.assignedVendors?.some(v => v.vendorBusinessId?._id === b._id || v.vendorBusinessId === b._id))
                      .map(b => ({ value: b._id, label: `${b.businessName} (${b.primaryVendorType})` }))
                  ]}
                />
                <Button onClick={assignVendorToEvent} disabled={!vendorToAssign}>
                  <Icons.Plus /> Assign
                </Button>
              </div>
              
              {managingEvent.assignedVendors?.length > 0 ? (
                <table className="admin-table">
                  <thead><tr><th>Business</th><th>Type</th><th>Assigned</th><th>Actions</th></tr></thead>
                  <tbody>
                    {managingEvent.assignedVendors.map(v => (
                      <tr key={v.vendorBusinessId?._id || v.vendorBusinessId}>
                        <td>{v.vendorBusinessId?.businessName || 'Unknown'}</td>
                        <td>{v.vendorBusinessId?.primaryVendorType || '-'}</td>
                        <td>{formatDate(v.assignedAt)}</td>
                        <td>
                          <button className="delete-btn" onClick={() => removeVendorFromEvent(v.vendorBusinessId?._id || v.vendorBusinessId)}>
                            <Icons.Trash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="empty-text">No vendors assigned yet</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ===========================================
// DISABLED ACCOUNT PAGE
// ===========================================
const DisabledAccountPage = ({ reason, type, onLogout }) => (
  <div className="disabled-account-page">
    <div className="disabled-account-container">
      <div className="disabled-icon">
        <Icons.Alert />
      </div>
      <h1>Account {type === 'rejected' ? 'Not Approved' : 'Disabled'}</h1>
      <p className="disabled-reason">
        {reason || (type === 'rejected' 
          ? 'Your organizer application was not approved.' 
          : 'Your account has been disabled by an administrator.')}
      </p>
      <div className="disabled-help">
        <p>If you believe this is an error, please contact support at</p>
        <a href="mailto:support@permitwise.app">support@permitwise.app</a>
      </div>
      <Button variant="outline" onClick={onLogout}>Log Out</Button>
    </div>
  </div>
);

// ===========================================
// LAYOUT
// ===========================================
const Sidebar = ({ activePage, onNavigate, onLogout }) => {
  const { user, business, subscription, restaurantBusiness } = useAuth();
  
  // Check if user is an organizer (regardless of disabled status - handled elsewhere)
  const isOrganizer = user?.isOrganizer;
  const isRestaurant = user?.isRestaurant;
  
  // All nav items
  const allNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Icons.Dashboard, vendorOnly: true },
    { id: 'permits', label: 'Permits', icon: Icons.Permit, vendorOnly: true },
    { id: 'documents', label: 'Documents', icon: Icons.Document, vendorOnly: true },
    { id: 'inspections', label: 'Inspections', icon: Icons.Checklist, vendorOnly: true },
    { id: 'events', label: 'Events', icon: Icons.Event },
    { id: 'settings', label: 'Settings', icon: Icons.Settings }
  ];
  
  // Restaurant nav items
  const restaurantNavItems = [
    { id: 'restaurant-dashboard', label: 'Dashboard', icon: Icons.Dashboard },
    { id: 'restaurant-permits', label: 'Permits', icon: Icons.Permit },
    { id: 'restaurant-inspections', label: 'Inspections', icon: Icons.Checklist },
    { id: 'restaurant-documents', label: 'Documents', icon: Icons.Document },
    { id: 'restaurant-team', label: 'Team Certs', icon: Icons.Checklist },
    { id: 'restaurant-settings', label: 'Settings', icon: Icons.Settings }
  ];
  
  // Filter nav items based on user type
  const navItems = isRestaurant 
    ? restaurantNavItems
    : isOrganizer 
      ? allNavItems.filter(item => !item.vendorOnly)
      : allNavItems;
  
  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="logo"><Icons.Shield /><span>PermitWise</span></div>
      </div>
      {isRestaurant && restaurantBusiness ? (
        <div className="sidebar-business organizer">
          <span>{restaurantBusiness.businessName}</span>
          <Badge variant="primary">RESTAURANT</Badge>
        </div>
      ) : isOrganizer ? (
        <div className="sidebar-business organizer">
          <span>{user?.organizerProfile?.organizationName || 'Event Organizer'}</span>
          <Badge variant="primary">ORGANIZER</Badge>
        </div>
      ) : business && (
        <div className="sidebar-business">
          <span>{business.businessName}</span>
          <Badge>{subscription?.plan?.toUpperCase() || 'TRIAL'}</Badge>
        </div>
      )}
      <nav className="sidebar-nav">
        {navItems.map(item => (
          <button key={item.id} className={`nav-item ${activePage === item.id ? 'active' : ''}`} onClick={() => onNavigate(item.id)}>
            <item.icon /><span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="sidebar-footer">
        <button className="nav-item" onClick={onLogout}><Icons.Logout /><span>Logout</span></button>
      </div>
    </aside>
  );
};

// Notifications Dropdown Component
const NotificationsDropdown = () => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await api.get('/notifications?limit=15');
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    } catch (err) { console.error(err); }
  };
  
  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { console.error(err); }
  };
  
  const formatTime = (date) => {
    const now = new Date();
    const d = new Date(date);
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  const getCategoryIcon = (n) => {
    const cat = n.category;
    if (cat === 'permit_expiring') return <Icons.Alert />;
    if (cat === 'insurance_expiring') return <Icons.Alert />;
    if (cat === 'cert_expiring') return <Icons.Checklist />;
    if (cat === 'event_update') return <Icons.Event />;
    if (cat === 'application_status') return <Icons.Check />;
    return <Icons.Bell />;
  };
  
  return (
    <div className="notifications-dropdown" ref={dropdownRef}>
      <button className="notifications-trigger" onClick={() => setIsOpen(!isOpen)}>
        <Icons.Bell />
        {unreadCount > 0 && <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>
      {isOpen && (
        <div className="notifications-menu">
          <div className="notifications-header">
            <h4>Notifications</h4>
            {unreadCount > 0 && <button className="mark-all-read" onClick={markAllRead}>Mark all read</button>}
          </div>
          <div className="notifications-list">
            {loading && <div className="notification-loading"><LoadingSpinner /></div>}
            {!loading && notifications.length === 0 && (
              <div className="notification-empty">No notifications yet</div>
            )}
            {!loading && notifications.map(n => (
              <div 
                key={n._id} 
                className={`notification-item ${n.read ? '' : 'unread'}`}
                onClick={() => { if (!n.read) markAsRead(n._id); }}
              >
                <div className="notification-icon">
                  {getCategoryIcon(n)}
                </div>
                <div className="notification-content">
                  <p className="notification-message">{n.displayText || n.summary || n.subject || 'Notification'}</p>
                  <span className="notification-time">{formatTime(n.sendAt || n.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AppLayout = ({ children, activePage, onNavigate, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  return (
    <div className="app-layout">
      <Sidebar activePage={activePage} onNavigate={(p) => { onNavigate(p); setMobileMenuOpen(false); }} onLogout={onLogout} />
      <div className="mobile-header">
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}><Icons.Menu /></button>
        <div className="logo"><Icons.Shield /><span>PermitWise</span></div>
        <NotificationsDropdown />
      </div>
      {mobileMenuOpen && (
        <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)}>
          <Sidebar activePage={activePage} onNavigate={(p) => { onNavigate(p); setMobileMenuOpen(false); }} onLogout={onLogout} />
        </div>
      )}
      <main className="main-content">
        <div className="main-header">
          <div className="header-spacer"></div>
          <NotificationsDropdown />
        </div>
        <ExpiredSubscriptionBanner />
        {children}
      </main>
    </div>
  );
};

// ===========================================
// MAIN APP
// ===========================================
const App = () => {
  const { user, isAuthenticated, hasCompletedOnboarding, loading, logout } = useAuth();
  const toast = useToast();
  const isOrganizer = user?.isOrganizer;
  const isRestaurant = user?.isRestaurant;
  const isOrganizerDisabled = user?.isOrganizer && (user?.organizerProfile?.disabled || user?.organizerProfile?.verificationStatus === 'rejected');
  const [currentPage, setCurrentPage] = useState(isOrganizer ? 'events' : isRestaurant ? 'restaurant-dashboard' : 'dashboard');
  const [authView, setAuthView] = useState(null);
  const [showPermitChecker, setShowPermitChecker] = useState(false);
  // Initialize legalPage from URL immediately to avoid flash of login screen
  const [legalPage, setLegalPage] = useState(() => {
    const path = window.location.pathname;
    const pathLower = path.toLowerCase();
    if (path === '/privacy') return 'privacy';
    if (path === '/terms') return 'terms';
    if (pathLower === '/superadmin') return 'superadmin';
    return null;
  });
  const [trackingCode, setTrackingCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tracking')) return params.get('tracking').toUpperCase();
    return null;
  });
  const [resetToken, setResetToken] = useState(null);
  const [verifyEmailToken, setVerifyEmailToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('token') && window.location.pathname.includes('verify-email')) {
      return params.get('token');
    }
    return null;
  });
  const [registerAsOrganizer, setRegisterAsOrganizer] = useState(false);
  const [registerAsRestaurant, setRegisterAsRestaurant] = useState(false);
  const [referralCodeFromUrl, setReferralCodeFromUrl] = useState('');

  // Update default page when user loads
  useEffect(() => {
    if (isOrganizer && currentPage === 'dashboard') {
      setCurrentPage('events');
    }
    if (isRestaurant && currentPage === 'dashboard') {
      setCurrentPage('restaurant-dashboard');
    }
  }, [isOrganizer, isRestaurant, currentPage]);

  // Prevent organizers from navigating to vendor-only pages
  const handleNavigate = (page) => {
    const vendorOnlyPages = ['dashboard', 'permits', 'documents', 'inspections'];
    if (isOrganizer && vendorOnlyPages.includes(page)) {
      setCurrentPage('events');
    } else if (isRestaurant && vendorOnlyPages.includes(page)) {
      setCurrentPage('restaurant-dashboard');
    } else {
      setCurrentPage(page);
    }
  };

  // Listen for hash changes for navigation (used by buttons that set window.location.hash)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const validPages = ['dashboard', 'permits', 'documents', 'inspections', 'events', 'settings', 'restaurant-dashboard', 'restaurant-permits', 'restaurant-inspections', 'restaurant-documents', 'restaurant-settings'];
      if (hash && validPages.includes(hash)) {
        handleNavigate(hash);
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isOrganizer, isRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { 
    const params = new URLSearchParams(window.location.search);
    if (params.get('success')) { 
      setTimeout(() => toast.success('Subscription activated!'), 100);
      window.history.replaceState({}, '', window.location.pathname); 
    }
    if (params.get('canceled')) { 
      setTimeout(() => toast.info('Checkout canceled.'), 100);
      window.history.replaceState({}, '', window.location.pathname); 
    }
    // Check for password reset token
    if (params.get('token') && window.location.pathname.includes('reset-password')) {
      setResetToken(params.get('token'));
      return;
    }
    // Auto-detect login/register from URL params (from landing page links)
    if (params.get('register') === 'true') { 
      setAuthView('register'); 
      // Check if registering as organizer
      if (params.get('organizer') === 'true') {
        setRegisterAsOrganizer(true);
      }
      if (params.get('restaurant') === 'true') {
        setRegisterAsRestaurant(true);
      }
      if (params.get('ref')) {
        setReferralCodeFromUrl(params.get('ref').toUpperCase());
      }
      window.history.replaceState({}, '', window.location.pathname); 
    }
    else if (params.get('login') === 'true' || window.location.pathname === '/app') { setAuthView('login'); }
    // Handle direct URL access to legal/superadmin pages
    const path = window.location.pathname;
    const pathLower = path.toLowerCase();
    if (path === '/privacy') setLegalPage('privacy');
    else if (path === '/terms') setLegalPage('terms');
    else if (pathLower === '/superadmin') setLegalPage('superadmin');
    
    // Track page view
    try {
      const sid = sessionStorage.getItem('pw_sid') || (Math.random().toString(36).substring(2) + Date.now().toString(36));
      sessionStorage.setItem('pw_sid', sid);
      fetch('/api/analytics/track', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: window.location.pathname || '/app',
          referrer: document.referrer || '',
          utmSource: params.get('utm_source') || '',
          utmMedium: params.get('utm_medium') || '',
          utmCampaign: params.get('utm_campaign') || '',
          refCode: params.get('ref') || '',
          sessionId: sid
        })
      }).catch(() => {});
    } catch(e) {}
  }, [toast]);

  // Password reset page (accessible without auth)
  if (resetToken) {
    return <ResetPasswordPage token={resetToken} onSuccess={() => { setResetToken(null); setAuthView('login'); window.history.replaceState({}, '', '/app'); toast.success('Password reset successful! Please log in.'); }} />;
  }

  // Email verification page (accessible without auth)
  if (verifyEmailToken) {
    return <VerifyEmailPage 
      token={verifyEmailToken} 
      onSuccess={() => { 
        setVerifyEmailToken(null); 
        setAuthView('login'); 
        window.history.replaceState({}, '', '/app'); 
        toast.success('Email verified successfully! Please log in.'); 
      }} 
      onError={() => { 
        setVerifyEmailToken(null); 
        setAuthView('login'); 
        window.history.replaceState({}, '', '/app'); 
      }} 
    />;
  }

  // Legal pages are accessible without authentication
  if (legalPage === 'privacy') return <PrivacyPage onBack={() => { setLegalPage(null); window.history.pushState({}, '', '/app'); }} />;
  if (legalPage === 'terms') return <TermsPage onBack={() => { setLegalPage(null); window.history.pushState({}, '', '/app'); }} />;
  // Superadmin page - standalone with its own auth
  if (legalPage === 'superadmin') {
    return <SuperAdminPage onBack={() => { setLegalPage(null); window.history.pushState({}, '', '/'); }} />;
  }

  // Public referral tracking page - no auth required
  if (trackingCode) {
    return <ReferralTrackingPage code={trackingCode} onBack={() => { setTrackingCode(null); window.history.pushState({}, '', '/app'); }} />;
  }

  if (loading) return <div className="loading-screen"><LoadingSpinner /><p>Loading...</p></div>;
  if (!isAuthenticated) {
    if (showPermitChecker) return <PermitChecker onClose={() => setShowPermitChecker(false)} onGetStarted={(v) => { setShowPermitChecker(false); setAuthView(v); }} />;
    if (authView === 'login') return <LoginPage onSwitch={setAuthView} onSuccess={() => setAuthView(null)} />;
    if (authView === 'register') return <RegisterPage onSwitch={setAuthView} onSuccess={() => { setAuthView(null); setRegisterAsOrganizer(false); setRegisterAsRestaurant(false); setReferralCodeFromUrl(''); }} defaultOrganizer={registerAsOrganizer} defaultRestaurant={registerAsRestaurant} referralCode={referralCodeFromUrl} />;
    if (authView === 'forgot') return <ForgotPasswordPage onSwitch={setAuthView} />;
    return <LoginPage onSwitch={setAuthView} onSuccess={() => setAuthView(null)} />;
  }
  
  // Organizers and restaurants skip vendor onboarding
  if (!hasCompletedOnboarding && !isOrganizer && !isRestaurant) return <OnboardingPage onComplete={() => window.location.reload()} />;
  
  // Restaurant onboarding
  if (!hasCompletedOnboarding && isRestaurant) return <RestaurantOnboardingPage onComplete={() => window.location.reload()} />;
  
  // Show disabled page for disabled/rejected organizers
  if (isOrganizerDisabled) {
    return (
      <DisabledAccountPage 
        reason={user?.organizerProfile?.disabledReason} 
        type={user?.organizerProfile?.verificationStatus === 'rejected' ? 'rejected' : 'disabled'}
        onLogout={logout}
      />
    );
  }
  
  const renderPage = () => { 
    switch (currentPage) { 
      case 'dashboard': return isOrganizer ? <EventsPage /> : isRestaurant ? <RestaurantDashboard onNavigate={handleNavigate} /> : <Dashboard onNavigate={handleNavigate} />; 
      case 'permits': return isOrganizer ? <EventsPage /> : isRestaurant ? <RestaurantPermitsPage /> : <PermitsPage />; 
      case 'documents': return isOrganizer ? <EventsPage /> : isRestaurant ? <RestaurantDocumentsPage /> : <DocumentsPage />; 
      case 'inspections': return isOrganizer ? <EventsPage /> : isRestaurant ? <RestaurantInspectionsPage /> : <InspectionsPage />; 
      case 'restaurant-inspections': return <RestaurantInspectionsPage />;
      case 'events': return <EventsPage />; 
      case 'settings': return isOrganizer ? <OrganizerSettingsPage /> : isRestaurant ? <RestaurantSettingsPage /> : <SettingsPage />; 
      case 'restaurant-dashboard': return <RestaurantDashboard onNavigate={handleNavigate} />;
      case 'restaurant-permits': return <RestaurantPermitsPage />;
      case 'restaurant-documents': return <RestaurantDocumentsPage />;
      case 'restaurant-team': return <RestaurantTeamCertsPage />;
      case 'restaurant-settings': return <RestaurantSettingsPage />;
      default: return isOrganizer ? <EventsPage /> : isRestaurant ? <RestaurantDashboard onNavigate={handleNavigate} /> : <Dashboard onNavigate={handleNavigate} />; 
    } 
  };
  return <AppLayout activePage={currentPage} onNavigate={handleNavigate} onLogout={logout}>{renderPage()}</AppLayout>;
};

const AppWithAuth = () => (
  <AuthProvider>
    <ToastProvider>
      <ConfirmProvider>
        <App />
      </ConfirmProvider>
    </ToastProvider>
  </AuthProvider>
);
export default AppWithAuth;
